import { defineStore } from 'pinia'
import { nextTick } from 'vue'
import axios from 'axios'

// ===========================================================
// Toast helper — replaces browser _showToast() with non-blocking toasts
// ===========================================================
const _toastQueue = []
export function flushToastQueue() {
    while (_toastQueue.length && window.$toast) {
        const { t, title, m } = _toastQueue.shift()
        window.$toast[t]?.(title, m)
    }
}
function _showToast(msg, forceType) {
    const m = String(msg ?? '');
    if (!m) return;
    const t = forceType || (() => {
        if (/失敗|錯誤|error|fail|不能|不可|invalid|exception/i.test(m)) return 'error';
        if (/^已|成功|完成|更新|刪除|移除|發送|儲存|上傳|建立|啟動/.test(m)) return 'success';
        if (/注意|警告|權限|超過|請先|請輸入|請選擇|請確認|不支援/.test(m)) return 'warning';
        return 'info';
    })();
    const titles = { error: '操作失敗', success: '完成', warning: '注意', info: '提示' };
    if (window.$toast) {
        flushToastQueue()
        window.$toast[t]?.(titles[t], m)
    } else {
        _toastQueue.push({ t, title: titles[t], m })
        console.warn('[Toast queued]', m)
    }
}

// ===========================================================
// Session expiry warning
// ===========================================================
let _sessionWarnTimer = null
let _sessionExpireTimer = null

function _parseJwtExp(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
        return payload.exp ? payload.exp * 1000 : null
    } catch { return null }
}

function _clearSessionTimers() {
    if (_sessionWarnTimer) { clearTimeout(_sessionWarnTimer); _sessionWarnTimer = null }
    if (_sessionExpireTimer) { clearTimeout(_sessionExpireTimer); _sessionExpireTimer = null }
}

function _scheduleSessionWarning(token) {
    _clearSessionTimers()
    const expMs = _parseJwtExp(token)
    if (!expMs) return
    const now = Date.now()
    const msUntilWarn = expMs - 5 * 60 * 1000 - now   // 5 min before expiry
    const msUntilExpire = expMs - now
    if (msUntilWarn > 0) {
        _sessionWarnTimer = setTimeout(() => {
            window.$toast?.warning('登入即將過期', '5 分鐘後將自動登出，請儲存工作並重新登入', 30000)
        }, msUntilWarn)
    }
    if (msUntilExpire > 0) {
        _sessionExpireTimer = setTimeout(() => {
            window.$toast?.warning('登入已過期', '請重新登入以繼續操作', 8000)
        }, msUntilExpire)
    }
}

// ===========================================================
// API Config
// ===========================================================
const __fallbackHost = (window.location.hostname && window.location.hostname !== 'null')
  ? window.location.hostname : '127.0.0.1'
const __fallbackProto = (window.location.protocol === 'https:') ? 'https' : 'http'
let ADMIN_API_FALLBACK_8000 = `${__fallbackProto}://${__fallbackHost}:8088`
if (!ADMIN_API_FALLBACK_8000.startsWith('http')) ADMIN_API_FALLBACK_8000 = 'http://127.0.0.1:8088'
let ADMIN_API_FALLBACK_8010 = `${__fallbackProto}://${__fallbackHost}:8010`
if (!ADMIN_API_FALLBACK_8010.startsWith('http')) ADMIN_API_FALLBACK_8010 = 'http://127.0.0.1:8010'
export const ADMIN_API_FALLBACKS = [ADMIN_API_FALLBACK_8000, ADMIN_API_FALLBACK_8010]
const __isFileOrigin = (window.location.protocol === 'file:' || !window.location.hostname || window.location.origin === 'null')
const __isLocalHost = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
const __isHttpOrigin = (window.location.protocol === 'http:' || window.location.protocol === 'https:')
const __localPort = (window.location.port || ((window.location.protocol === 'https:') ? '443' : '80'))
const __isLocalDevOtherPort = (__isHttpOrigin && __isLocalHost && __localPort !== '8088' && __localPort !== '8010' && __localPort !== '3000' && __localPort !== '3010')
export const ADMIN_API = (__isFileOrigin || __isLocalDevOtherPort) ? `${ADMIN_API_FALLBACK_8000}/api` : `${window.location.origin}/api`
export const AI_API = ADMIN_API

// ===========================================================
// Pinia Store
// ===========================================================
export const useAppStore = defineStore('app', {
  state: () => ({
                        invoiceDriveState: {
                            selectedFiles: [],
                            uploadResults: [],
                            uploadError: '',
                            listError: '',
                            detailError: '',
                            loading: false,
                            uploading: false,
                            seedLoading: false,
                            exporting: false,
                            detailLoading: false,
                            detailModalOpen: false,
                            previewLoading: false,
                            previewError: '',
                            previewUrl: null,
                            previewMime: '',
                            previewName: '',
                            previewFileId: null,
                            pendingPreviewFileId: null,
                            editSnapshot: '',
                            saving: false,
                            confirming: false,
                            downloadingId: null,
                            reparsingId: null,
                            ocrExpandedFileId: null,
                            files: [],
                            detail: null,
                            edit: {
                                status: '',
                                vendor_name: '',
                                vendor_tax_id: '',
                                buyer_name: '',
                                buyer_tax_id: '',
                                invoice_number: '',
                                invoice_date: '',
                                currency: '',
                                subtotal: null,
                                tax: null,
                                total: null,
                                tax_rate: null,
                                pay_method: '',
                                notes: '',
                                items: []
                            },
                            filters: { status: '', q: '', date_from: '', date_to: '' },
                            batchMode: false,
                            batchSelected: [],
                            batchProcessing: false
                        },
                        aiMarketingState: {
                            step: 'generate',
                            form: { product_name: '', key_features: '', target_audience: '', tone: 'Professional' },
                            loading: false,
                            result: null,
                            fullResult: null,
                            segments: [],
                            templates: [
                                {
                                    id: 'builtin_new_product',
                                    name: '（內建）產品推廣：新品上市',
                                    description: '適用於新品上市/功能亮點介紹',
                                    subject: '新品上市｜{{產品名稱}} 限時優惠',
                                    category: 'general',
                                    content_html: '<div style="font-family: Arial, Helvetica, sans-serif; line-height: 1.6; color: #111827;"><h2 style="margin: 0 0 12px;">{{產品名稱}} 新品上市</h2><p>您好，</p><p>我們很高興向您介紹 <strong>{{產品名稱}}</strong>，主打特色如下：</p><ul><li>{{特色1}}</li><li>{{特色2}}</li><li>{{特色3}}</li></ul><p style="margin-top: 14px;"><a href="{{連結}}" style="display: inline-block; padding: 10px 14px; background: #2563eb; color: #ffffff; text-decoration: none; border-radius: 6px;">了解更多</a></p><p style="margin-top: 18px; color: #6b7280; font-size: 12px;">如不想再收到此類訊息，請回覆告知，我們將為您處理。</p></div>'
                                },
                                {
                                    id: 'builtin_event_invite',
                                    name: '（內建）活動邀請：研討會/說明會',
                                    description: '適用於線上/線下活動邀請',
                                    subject: '邀請您參加｜{{活動名稱}}（{{日期}}）',
                                    category: 'general',
                                    content_html: '<div style="font-family: Arial, Helvetica, sans-serif; line-height: 1.6; color: #111827;"><h2 style="margin: 0 0 12px;">{{活動名稱}}</h2><p>誠摯邀請您參加我們的活動：</p><p><strong>日期：</strong>{{日期}}<br/><strong>時間：</strong>{{時間}}<br/><strong>地點：</strong>{{地點}}</p><p>{{活動簡介}}</p><p style="margin-top: 14px;"><a href="{{報名連結}}" style="display: inline-block; padding: 10px 14px; background: #059669; color: #ffffff; text-decoration: none; border-radius: 6px;">立即報名</a></p><p style="margin-top: 18px; color: #6b7280; font-size: 12px;">若您不方便參與，也歡迎回信索取簡報或錄影連結。</p></div>'
                                },
                                {
                                    id: 'builtin_holiday_greeting',
                                    name: '（內建）節日問候：感謝與祝福',
                                    description: '適用於節日/里程碑的關係維護',
                                    subject: '{{節日}}快樂｜感謝一路支持',
                                    category: 'general',
                                    content_html: '<div style="font-family: Arial, Helvetica, sans-serif; line-height: 1.6; color: #111827;"><h2 style="margin: 0 0 12px;">{{節日}}快樂</h2><p>您好，</p><p>在這個特別的日子，感謝您一直以來的支持與信任。祝您與家人平安順心。</p><p style="margin-top: 14px;">若您近期有任何需求或想了解我們的最新方案，歡迎直接回信，我們很樂意協助。</p><p style="margin-top: 18px; color: #6b7280; font-size: 12px;">此信為關係維護訊息，若造成打擾敬請見諒。</p></div>'
                                }
                            ],
                            selectedSegment: null,
                            recipientSource: 'manual',
                            journeys: [],
                            scheduleEnabled: false,
                            scheduledAt: '',
                            abTestEnabled: false,
                            subjectB: '',
                            abSubjectGenerating: false,
                            abSubjectError: '',
                        },
                        fillVarsLoading: false,
                        fillVarsResult: null,
                        fillVarsError: '',
                        edmQualityLoading: false,
                        edmQualityResult: null,
                        edmQualityError: '',
                        edmTranslateLoading: false,
                        edmTranslateLang: 'English',
                        edmTranslateResult: null,
                        edmTranslateError: '',
                        supportChatOpen: false,
                        supportChatMessages: [],
                        supportChatLoading: false,
                        supportChatSessionId: '',
                        edmRedesignLoading: false,
                        edmRedesignStyle: 'modern',
                        edmRedesignResult: null,
                        edmRedesignError: '',
                        aiSocialState: {
                            form: { product_name: '', key_features: '', platforms: ['facebook'], target_audience: '', tone: 'Professional' },
                            loading: false,
                            result: null
                        },
                        showAiDraftModal: false,
                        showAiReplyModal: false,
                        showAiRewriteModal: false,
                        showAiTranslateModal: false,
                        showAiSummaryModal: false,
                        showAiAnalyzeModal: false,
                        showAiCustomerModal: false,
                        showCustomerBackgroundListModal: false,
                        showAiEventModal: false,
                        showLeadScoreModal: false,
                        aiAssistantMenuOpen: false,
                        showJourneyEnrollmentsModal: false,
                        journeyEnrollments: [],
                        journeyEnrollmentsLoading: false,
                        selectedJourneyForEnrollments: null,
                        showJourneyEditorModal: false,
                        journeyEditorSaving: false,
                        journeyEditorForm: null,
                        showJourneyFunnelModal: false,
                        journeyFunnelLoading: false,
                        selectedJourneyForFunnel: null,
                        journeyFunnel: null,
                        journeyFunnelFilters: { segment_id: '', days: 7 },
                        aiDraftForm: { instructions: '', tone: 'professional' },
                        aiReplyForm: { instructions: '', tone: 'professional', length: 'medium', language: 'auto', template: 'standard_reply', include_quote: false },
                        aiRewriteForm: { tone: 'professional' },
                        aiTranslateForm: { target_language: 'Traditional Chinese (繁體中文)' },
                        aiSummaryResult: '',
                        aiAnalyzeResult: null,
                        aiCustomerResult: null,
                        linkCbiToContactLoading: false,
                        cbiListLoading: false,
                        cbiSearch: '',
                        cbiSortBy: 'created_at',
                        cbiSortDir: 'desc',
                        cbiStatusFilter: '',
                        cbiList: [],
                        cbiListTotal: 0,
                        cbiSelected: null,
                        aiEventResult: null,
                        aiEventContext: 'mail',
                        aiEventSourceText: '',
                        calendarSavingFromAi: false,
                        calendarEvents: [],
                        calendarTotal: 0,
                        calendarLoading: false,
                        calendarCreating: false,
                        calendarSuggesting: false,
                        calendarSuggestedSlots: [],
                        calendarSelectedSlotStartAt: '',
                        showCalendarEditModal: false,
                        calendarEditSaving: false,
                        calendarEditForm: { id: null, title: '', start_time: '', end_time: '', location: '', description: '', contact_email: '', status: 'confirmed' },
                        calendarRangeDays: 14,
                        calendarCreateForm: { title: '', start_time: '', end_time: '', location: '', description: '', contact_email: '' },
                        calendarAiText: '',
                        calendarAiParsing: false,
                        leadScoreResult: null,
                        showAiFollowupModal: false,
                        aiFollowupForm: { recipient: '', tone: 'professional', instructions: '', length: 'medium', language: 'auto', template: 'gentle_nudge', include_quote: true, original_subject: '', original_text: '' },
                        aiFollowupResult: '',
                        aiFollowupSubject: '',
                        aiTranslateResult: '',
                        showSubjectOptimizerModal: false,
                        subjectVariations: [],
                        optimizingSubject: false,
                        showAiMarketingModal: false,

                        token: localStorage.getItem('token') || null,
                        currentUser: null,
                        loginForm: { username: '', password: '' },
                        loginError: '',
                        loginDomains: [],
                        mobileSidebarOpen: false,
                        // TOTP two-step login
                        showTotpModal: false,
                        totpPartialToken: '',
                        totpCode: '',
                        totpError: '',
                        totpLoading: false,

                        currentView: 'dashboard',

                        // Webmail
                        mailPassword: '',
                        mailPasswordInput: '',
                        mailSessionToken: '',
                        mailSessionExpiresAt: 0,
                        webmailLastError: '',
                        mailSignature: { enabled: false, signature_html: '', signature_text: '', loading: false, saving: false, logoUrl: '', showEditor: false },
                        sigGenModal: { open: false, loading: false, error: '', name: '', title: '', company: '', phone: '', website: '', style: 'modern' },
                        // Sieve filter
                        sieve: { selectedUserId: null, enabled: true, rules: [], loading: false, saving: false, scriptPreview: '', scriptLoading: false, users: [] },
                        // Alert Center
                        alertCenterAlerts: [],
                        alertSummary: { danger: 0, warning: 0, info: 0, total: 0 },
                        alertsLoading: false,
                        outgoingApprovalPendingCount: 0,
                        changePasswordForm: { current_password: '', new_password: '', confirm_password: '' },
                        changePasswordLoading: false,
                        changePasswordError: '',
                        changePasswordSuccess: '',
                        webmailSeedingExamples: false,
                        webmailBatchSeenLoading: false,
                        webmailBatchInvoiceImportLoading: false,
                        webmailBatchMoveLoading: false,
                        webmailBatchDeleteLoading: false,
                        webmailInvoiceScanLoading: false,
                        webmailInvoiceAutoScanLoading: false,
                        webmailAutoScanInvoices: false,
                        webmailInvoiceScanTimer: null,
                        webmailAutoScanLastImportedCount: 0,
                        webmailAutoScanLastAt: 0,
                        webmailAutoScanLastInvoiceId: 0,
                        webmailAutoScanMarkRead: false,
                        storedMailPasswordExists: false,
                        mailFolderList: [],
                        mailFolderNewName: '',
                        mailFolderRenaming: null,
                        mailRememberPassword: false,
                        webmailTab: 'mail',
                        webmailCalendarMonth: new Date().toISOString().slice(0, 7),
                        webmailCalendarEvents: [],
                        webmailCalendarEventMap: {},
                        webmailCalendarGridDays: [],
                        webmailCalendarLoading: false,
                        showWebmailMeetingModal: false,
                        webmailMeetingSaving: false,
                        webmailMeetingInviting: false,
                        webmailMeetingForm: { id: null, title: '', start_time: '', end_time: '', location: '', description: '', attendees: '', invite_message: '', send_invite: true },
                        currentFolder: 'INBOX',
                        folderUnreadCounts: {},
                        messages: [],
                        selectedMessageIds: [],
                        selectAllMessages: false,
                        currentMessage: null,
                        loadingMessages: false,
                        loadingContent: false,
                        invoiceImportingAttachmentKey: '',
                        invoiceImportingAllAttachments: false,
                        invoiceImportedAttachmentKeys: {},
                        showComposeModal: false,
                        showRecipientPickerModal: false,
                        recipientPickerTarget: 'to',
                        recipientPickerSelected: [],
                        recipientPickerLoading: false,
                        recipientPickerQuery: '',
                        recipientPickerResults: [],
                        recipientPickerError: '',
                        sendingEmail: false,
                        // 安全傳送
                        secureSendEnabled: false,
                        showSecureSendPanel: false,
                        secureSendOptions: {
                            mfa_type: 'password',
                            mfa_password: '',
                            expires_in_hours: 24,
                            max_open_count: 1,
                            burn_after_read: false,
                            burn_delay_seconds: 30,
                            no_download: true,
                            watermark: true,
                        },
                        secureSendResult: null,
                        composeEditorMode: 'rich',
                        composeToInput: '',
                        composeCcInput: '',
                        composeBccInput: '',
                        composeInvalidRecipientTokens: [],
                        showCcField: false,
                        showBccField: false,
                        composeForm: { to: '', cc: '', bcc: '', subject: '', body: '', html_body: '', attachments: [], in_reply_to: null, references: null },
                        composeAttachmentSources: [],
                        composeAttachmentPreviews: [],
                        composeResourceAttachments: [],
                        composeResourceErrors: {},
                        composeResourceErrorMeta: {},
                        composeResourceErrorExpanded: {},
                        composeResourceBanner: '',
                        composeResourcePool: [],
                        composeResourcePoolLoading: false,
                        composeResourceSearch: '',
                        composeResourceAddingId: null,
                        composeResourceValidating: {},
                        composeResourceValidateQueue: [],
                        composeResourceValidateTimer: null,
                        composeRecommendLoading: false,
                        composeRecommendedResources: [],
                        composeRecommendError: '',
                        composeRecommendTimer: null,
                        composeRecommendActiveKey: '',
                        composeRecommendLastKey: '',
                        composePromptedRecommendKey: '',
                        composeSnippetLoadingId: null,
                        composeApplyLoadingId: null,
                        composeLastAutoRecommendAt: 0,
                        composeAiReplySources: [],
                        composeKbQueryInput: '',
                        composeKbLoading: false,
                        composeKbResult: '',
                        composeKbSources: [],
                        composeKbError: '',
                        composeAiPanelOpen: false,
                        composeAiPanelCollapsed: false,
                        composeKbPanelOpen: false,
                        composeKbPanelCollapsed: false,
                        composeAttachPanelCollapsed: false,
                        kbSourceExpandedIdx: null,
                        kbDocPreviewModal: { open: false, loading: false, doc_id: null, source_name: '', content: '', error: '' },
                        composeMaximized: false,
                        composeIsDraggingFile: false,
                        composeDraftRestored: false,

                        // Pagination & Search
                        mailSearchQuery: '',
                        mailPage: 1,
                        mailLimit: 20,
                        mailTotal: 0,

                        serviceStatus: {
                            postfix: 'running',
                            dovecot: 'running',
                            mysql: 'running',
                            ai_service: 'running'
                        },
                        domains: [],
                        users: [],
                        departments: [],
                        groups: [],
                        logs: [],
                        mailAuditRecords: [],
                        mailAuditLoading: false,
                        mailAuditError: '',
                        mailAuditQuery: '',
                        mailAuditDirection: 'all',
                        mailAuditSkip: 0,
                        mailAuditLimit: 50,
                        mailAuditTotal: 0,
                        mailAuditQueryTimer: null,
                        mailAuditSince: '',
                        mailAuditUntil: '',
                        mailAuditMinScore: null,
                        mailAuditMaxScore: null,
                        mailAuditDomain: '',
                        mailAuditHasArchive: 'all',
                        mailAuditBatchArchiveLoading: false,
                        mailAuditBatchArchiveTotal: 0,
                        mailAuditBatchArchiveDone: 0,
                        mailAuditBatchArchiveFail: 0,
                        mailAuditBatchAllArchiveLoading: false,
                        mailAuditBatchAllArchiveTotal: 0,
                        mailAuditBatchAllArchiveDone: 0,
                        mailAuditBatchAllArchiveFail: 0,
                        mailAuditSelectedIds: {},
                        mailAuditBulkDownloadLoading: false,

                        securityAuditRecords: [],
                        securityAuditLoading: false,
                        securityAuditError: '',
                        securityAuditQuery: '',
                        securityAuditSkip: 0,
                        securityAuditLimit: 50,
                        securityAuditTotal: 0,
                        securityAuditSince: '',
                        securityAuditUntil: '',
                        securityAuditDomain: '',
                        securityAuditAction: '',
                        securityAuditQueryTimer: null,

                        contactsTab: 'contacts',
                        contactsLoading: false,
                        contacts: [],
                        contactsTotal: 0,
                        contactsPage: 1,
                        contactsLimit: 50,
                        contactTags: [],
                        contactGroups: [],
                        contactGroupsLoading: false,
                        contactFilters: { search: '', tag: '', group_id: null, sort_by: 'name', sort_dir: 'asc' },
                        showContactModal: false,
                        contactSaving: false,
                        contactFollowupGenerating: false,
                        editingContactId: null,
                        contactForm: { name: '', email: '', phone: '', notes: '', tags: '', group_id: null, user_id: null },
                        showContactGroupModal: false,
                        contactGroupSaving: false,
                        showContactImportModal: false,
                        contactImportPreview: [],
                        contactImportLoading: false,
                        contactImportResult: null,
                        editingContactGroupId: null,
                        contactGroupForm: { name: '', description: '' },
                        showLeadHistoryModal: false,
                        leadHistoryLoading: false,
                        leadHistoryContact: null,
                        leadHistoryEvents: [],

                        quoteMgmtLoading: false,
                        quoteMgmtQuotes: [],
                        quoteMgmtFilters: { status: '', search: '' },
                        showQuoteMgmtCreateModal: false,
                        showQuoteMgmtEditorModal: false,
                        showQuoteMgmtPricebookModal: false,
                        quoteMgmtSaving: false,
                        quoteMgmtEditorLoading: false,
                        quoteMgmtSelectedQuote: null,
                        quoteMgmtProducts: [],
                        quoteMgmtProductSpecs: [],
                        quoteMgmtPricebooks: [],
                        quoteMgmtSelectedPricebookId: null,
                        quoteMgmtPricebookItems: [],
                        quoteMgmtPricebookSearch: '',
                        quoteMgmtPricebookLoading: false,
                        quoteMgmtPricebookSaving: false,
                        productMgmtTab: 'products',
                        quoteMgmtNewPricebookForm: { name: '', currency: 'TWD', tax_mode: 'exclusive' },
                        quoteMgmtNewPricebookItemForm: { product_id: null, product_spec_id: null, unit_price: 0, min_qty: 1 },
                        quoteMgmtPricebookProductSpecs: [],
                        quoteMgmtNewProductForm: { sku: '', name: '', category: '', description: '', active: true },
                        quoteMgmtNewProductSpecForm: { spec_name: '', spec_value: '', active: true },
                        quoteMgmtContactSearch: '',
                        quoteMgmtContactResults: [],
                        quoteMgmtCreateForm: { customer_name: '', contact_id: null, tax_mode: 'exclusive', tax_rate: 0 },
                        quoteMgmtEditForm: { status: 'draft', customer_name: '', contact_id: null, tax_mode: 'exclusive', tax_rate: 0, notes: '', terms: '' },
                        quoteMgmtNewItem: { product_id: null, product_spec_id: null, product_label: '', title: '', spec_text: '', qty: 1, unit: '', unit_price: 0, discount_type: 'none', discount_value: 0, remark: '' },
                        quoteMgmtRenderPayload: '',
                        quoteMgmtHtmlLoading: false,
                        quoteMgmtPdfLoading: false,
                        quoteMgmtInvoicePdfLoading: false,
                        quoteMgmtAISourceText: '',
                        quoteMgmtAIFile: null,
                        quoteMgmtAIExtracting: false,
                        quoteMgmtAIDraft: null,
                        quoteMgmtAIPricingPricebookId: '',
                        quoteMgmtAIPreferPricebook: true,
                        quoteMgmtCreateModalPos: { x: 0, y: 0 },
                        quoteMgmtCreateModalDragging: false,
                        quoteMgmtCreateModalDragOffset: { x: 0, y: 0 },
                        quoteMgmtEditorModalPos: { x: 0, y: 0 },
                        quoteMgmtEditorModalDragging: false,
                        quoteMgmtEditorModalDragOffset: { x: 0, y: 0 },

                        newDomain: { name: '', max_users: 0, max_aliases: 0, max_quota: 0, audit_email: '' },
                        departmentPermissionOptions: [
                            { key: 'dashboard', label: '儀表板' },
                            { key: 'rag_chat', label: '企業知識庫' },
                            { key: 'unreplied', label: '待跟進(Unreplied)' },
                            { key: 'ai_sales', label: 'AI 業務助手' },
                            { key: 'crm', label: 'CRM（搜尋）' },
                            { key: 'contacts', label: '聯絡人管理' },
                            { key: 'marketing_center', label: 'AI 智能行銷中心' },
                        ],
                        newDepartment: { name: '', description: '', domain_id: null, permission_keys: [] },
                        newUser: { username: '', password: '', domain_id: null, role: 'user', quota: 0, company_name: '', department_id: null, phone_ext: '' },
                        newGroup: { name: '', domain_id: null, members: [] },

                        editingDomain: null,
                        editDomainForm: { id: null, name: '', max_users: 0, max_aliases: 0, max_quota: 0, audit_email: '', active: true },

                        editingDepartment: null,
                        editDepartmentForm: { id: null, name: '', description: '', domain_id: null, permission_keys: [] },

                        editingUser: null,
                        editUserForm: { id: null, username: '', domain_id: null, password: '', quota: 0, active: true, role: 'user', company_name: '', department_id: null, phone_ext: '' },

                        editingGroup: null,

                        aiTest: {
                            sender: 'spammer@example.com',
                            subject: 'Win a free lottery!',
                            body: 'Click here to claim your prize now.',
                            images: [],
                            tone: 'professional'
                        },
                        aiResult: null,

                        // Server Management
                        mailConfigTab: 'status',
                        mailSettings: {
                            myhostname: '',
                            smtpd_banner: '',
                            message_size_limit: 0,
                            smtpd_recipient_limit: 0,
                            mynetworks: '',
                            force_tls: false,
                            smtp_tls_security_level: 'may',
                        },
                        dovecotSettings: {
                            protocols: '',
                            listen: '',
                            disable_plaintext_auth: '',
                            ssl: ''
                        },
                        dbStats: null,
                        aiSettings: [],
                        editingAiSetting: null,

                        // AI Enhanced
                        aiTab: 'rag',
                        aiLoading: false,
                        aiSettingsForm: {
                            google_api_key: '',
                            image_provider: 'google_imagen',
                            image_api_key: '',
                            spam_threshold: 5.0,
                            use_ai_analysis: true,
                            ai_provider: 'gateway',
                            gateway_base_url: 'http://host.docker.internal:4000',
                            gateway_api_key: '',
                            gateway_model: 'gpt-4o',
                            ollama_base_url: 'http://host.docker.internal:11434',
                            ollama_model: 'llama3',
                            lmstudio_base_url: 'http://host.docker.internal:1234',
                            lmstudio_model: 'local-model',
                            vision_provider: 'ollama',
                            vision_base_url: '',
                            vision_model: '',
                            security_auto_enabled: false,
                            security_auto_interval_seconds: 60,
                            security_auto_folder: 'INBOX',
                            security_auto_limit: 30,
                            security_auto_unseen_only: true,
                            security_auto_phishing_enabled: true,
                            security_auto_phishing_threshold: 0.7,
                            security_auto_phishing_target: 'Phishing',
                            security_auto_spam_enabled: true,
                            security_auto_spam_threshold: 5.0,
                            security_auto_spam_target: 'Spam',
                            cbi_crawl_enabled: true,
                            cbi_crawl_max_pages: 5,
                            cbi_crawl_timeout_seconds: 8,
                            cbi_crawl_allowed_paths: '/,/about,/products,/solutions,/contact',
                            cbi_crawl_user_agent: 'ai-mail-cbi/1.0'
                        },
                        branding: {
                            loginTitle: '',
                            headerTitle: '',
                            headerLogoUrl: ''
                        },
                        brandingForm: {
                            loginTitle: '',
                            headerTitle: '',
                            headerLogoUrl: ''
                        },
                        backupSettingsLoading: false,
                        backupSettingsSaving: false,
                        backupTestLoading: false,
                        backupRunLoading: false,
                        backupJobsLoading: false,
                        backupFilesLoading: false,
                        backupLastRunJobId: '',
                        backupSettingsForm: {
                            destination: 'local',
                            keep_last: 20,
                            auto_enabled: false,
                            interval_hours: 24,
                            last_run_ts: 0,
                            nas_path: '',
                            nas_ip: '',
                            nas_type: 'cifs',
                            nas_share: '',
                            nas_username: '',
                            nas_password: '',
                            nas_password_set: false,
                            gdrive_method: 'rclone',
                            gdrive_remote: '',
                            gdrive_folder: '',
                            gdrive_config_path: ''
                        },
                        backupJobs: [],
                        backupFiles: [],
                        backupRunningJob: null,
                        backupPollerTimer: null,
                        showRestoreModal: false,
                        restoreTarget: '',
                        restoreOptions: { restore_db: true, restore_mail: true, restore_resources: false, restart_services: true },
                        restoreLoading: false,
                        restoreJobId: '',
                        restoreJobs: [],
                        restoreJobsLoading: false,
                        restorePollerTimer: null,
                        brandingApiBase: '',
                        availableModels: [],
                        ollamaModels: [],
                        lmstudioModels: [],
                        gatewayModels: [],
                        cbiFilters: {
                            search: '',
                            company: '',
                            sender: '',
                            status: '',
                            sort_by: 'created_at',
                            sort_dir: 'desc'
                        },
                        cbiItems: [],
                        cbiTotal: 0,
                        cbiLoading: false,
                        cbiLimit: 50,
                        cbiOffset: 0,
                        cbiShareDraft: {},
                        cbiShareSavingId: null,

                        selectedDnsDomain: null,
                        dnsRecords: [],
                        newDnsRecord: { type: 'A', host: '@', value: '', priority: 10 },

                        // Mail Queue
                        mailQueue: [],

                        // Unreplied Detection
                        unrepliedMessages: [],
                        unrepliedLoading: false,
                        selectedUnrepliedIds: [],
                        selectAllUnreplied: false,

                        // AI Sales
                        aiSalesTab: 'followups',
                        autoReplies: [],
                        autoRepliesLoading: false,
                        autoRepliesFilterStatus: 'pending',
                        selectedAutoReply: null,
                        autoReplyEdit: { status: 'pending', draft_subject: '', draft_body: '' },
                        autoReplyRegenerateForm: {
                            tone: 'professional',
                            language: 'auto',
                            length: 'medium',
                            template: 'standard_reply',
                            include_quote: false
                        },
                        contactCache: {},
                        contactCacheLoading: {},
                        autoRepliesScanLoading: false,
                        showOriginalMailModal: false,
                        originalMailLoading: false,
                        originalMailMessage: null,
                        originalMailFolder: 'INBOX',

                        leadScoreWeightsLoading: false,
                        leadScoreWeightsSaving: false,
                        leadScoreWeightsSaveTimer: null,
                        leadScoreWeights: { intent: 0.30, engagement: 0.20, recency: 0.20, value: 0.20, fit: 0.10 },
                        leadFeaturesLoading: false,
                        leadFeatures: [],
                        leadFeaturesDays: 30,
                        leadFeaturesLimit: 200,
                        leadFactorsExpanded: {},

                        followupSequenceLoading: false,
                        followupSequenceForm: {
                            level: 'warm',
                            recipient_email: '',
                            recipient_name: '',
                            context_subject: '',
                            context_summary: '',
                            quote_summary: ''
                        },
                        followupSequencePreview: null,

                        pipelineForecastLoading: false,
                        pipelineForecastDays: 60,
                        pipelineForecast: null,
                        pipelinePdfLoading: false,
                        marketingReportPdfLoading: false,

                        quoteLoading: false,
                        quoteForm: { company_size: '11-50', plan: 'pro', billing: 'annual', seats: 20, recipient_email: '', recipient_name: '' },
                        quotePreview: null,

                        showNotificationsModal: false,
                        notificationsLoading: false,
                        notificationsIncludeCooling: false,
                        notificationsDays: 7,
                        notifications: [],

                        // Command Palette
                        showCmdPalette: false,
                        cmdQuery: '',
                        cmdResults: [],
                        cmdSelIdx: 0,

                        // Notification Panel (topbar bell)
                        darkMode: (localStorage.getItem('darkMode') === '1'),

                        showNotifPanel: false,
                        sysNotifs: [],
                        sysNotifUnread: 0,
                        sysNotifLoading: false,

                        showCrmModal: false,
                        showCrmPickerModal: false,
                        crmPickerLoading: false,
                        crmPickerQuery: '',
                        crmPickerResults: [],
                        crmPickerError: '',
                        crmContactLoading: false,
                        crmContactSaving: false,
                        crmContactEditOpen: false,
                        crmContact: null,
                        crmContactForm: { id: null, name: '', email: '', phone: '', tags: '' },
                        crmLoading: false,
                        crmTimeline: null,
                        crmTimelineFilterType: 'all',
                        crmTimelineQuery: '',
                        crmNote: { contact_id: null, note: '' },
                        crmFollowupGenerating: false,
                        crmCalendarLoading: false,
                        crmCalendarCreating: false,
                        crmCalendarRangeDays: 30,
                        crmCalendarEvents: [],
                        crmCalendarTotal: 0,
                        crmCalendarCreateForm: { title: '', start_time: '', end_time: '', location: '', description: '' },
                        showCrmAutoReplyModal: false,
                        crmAutoReplyLoading: false,
                        crmAutoReplySaving: false,
                        crmAutoReplySending: false,
                        crmAutoReplyDetail: null,
                        crmAutoReplyEditForm: { status: 'pending', draft_subject: '', draft_body: '' },

                        composeAttachPanelOpen: false,

                        showOpportunityModal: false,
                        opportunityLoading: false,
                        opportunityResult: null,
                        opportunityError: null,
                        opportunityEditMode: false,
                        opportunitySaving: false,
                        opportunityContactLookupLoading: false,
                        opportunityContactLinking: false,
                        opportunityContactCreateLoading: false,
                        opportunityContactLookupAttemptedEmail: '',
                        opportunityContactLookupResult: null,
                        opportunityContactLookupError: '',
                        opportunityEditForm: {
                            status: 'inbound',
                            company: '',
                            decision_maker: '',
                            budget_text: '',
                            budget_min: null,
                            budget_max: null,
                            currency: '',
                            timeline: '',
                            timeline_start: '',
                            timeline_end: '',
                            need: '',
                            next_step: '',
                            summary: '',
                            confidence: null,
                        },

                        opportunities: [],
                        opportunitiesLoading: false,
                        opportunitiesSearch: '',
                        opportunitiesStatus: '',
                        opportunitiesMinBudget: null,
                        opportunitiesMaxBudget: null,
                        opportunitiesSortBy: 'created_at',
                        opportunitiesSortDir: 'desc',
                        opportunitiesLimit: 100,
                        opportunitiesOffset: 0,
                        opportunitiesTotal: 0,
                        opportunitiesScanLoading: false,
                        opportunitiesReextractingId: null,

                        lineSettings: [],
                        lineSettingsAll: [],
                        lineSettingsLoading: false,
                        lineSettingHealthLoadingId: null,
                        lineSettingDeletingId: null,
                        lineSettingRestoringId: null,
                        selectedLineSettingId: null,
                        lineEditingSettingId: null,
                        lineSettingSaving: false,
                        lineSettingForm: {
                            name: 'LINE OA',
                            channel_id: '',
                            channel_secret: '',
                            channel_access_token: '',
                            active: true,
                            auto_reply_enabled: false,
                            auto_reply_tone: 'professional',
                            auto_reply_only_unassigned: true,
                            auto_reply_cooldown_seconds: 60,
                            auto_reply_pause_after_human_seconds: 600,
                            auto_reply_rag_enabled: false,
                            auto_reply_rag_only: false,
                            auto_reply_rag_department_id: '',
                            auto_reply_rag_doc_ids: ''
                        },
                        showLineRagDocPicker: false,
                        kbPickerLoading: false,
                        kbPickerDocs: [],
                        kbPickerSearch: '',
                        kbPickerDepartmentId: '',
                        kbPickerSelectedIds: [],
                        kbPickerSkip: 0,
                        kbPickerLimit: 50,
                        kbPickerHasMore: false,

                        lineConversations: [],
                        lineConversationsLoading: false,
                        lineConversationsError: '',
                        lineConversationActionLoading: false,
                        lineConversationSearch: '',
                        lineConversationStatus: '',
                        lineConversationAssigned: 'any',
                        lineConversationSettingId: 0,
                        lineConversationOffset: 0,
                        lineConversationHasMore: false,
                        lineConversationTagFilter: '',
                        // Feature I: batch mode
                        lineBatchMode: false,
                        lineBatchSelected: [],
                        lineBatchLoading: false,
                        // Feature H: dashboard
                        lineDashboard: null,
                        lineDashboardLoading: false,
                        lineDashboardSettingId: 0,
                        // Feature L: conversation events
                        lineConversationEvents: [],
                        lineConversationEventsLoading: false,
                        showLineConversationEvents: false,
                        // Feature K: broadcast
                        showLineBroadcastModal: false,
                        lineBroadcastForm: { setting_id: '', text: '', tag: '', status: 'open' },
                        lineBroadcastLoading: false,
                        lineBroadcastResult: null,
                        lineDebugLoading: false,
                        lineSupportRefreshing: false,
                        lineSupportRefreshPromise: null,
                        lineSupportTab: 'conversations',
                        selectedLineConversation: null,
                        lineMessages: [],
                        lineMessagesLoading: false,
                        lineMessagesError: '',
                        lineMessagesFetchEpoch: 0,
                        lineUiVersion: '20260321.3',
                        lineLastSendPreview: '',
                        lineSendStatus: '',
                        lineSendError: '',
                        lineMessagesPadBottom: 180,
                        lineSendText: '',
                        lineSending: false,
                        lineAiTone: 'professional',
                        lineAiSuggestLoading: false,
                        lineAiSuggestion: null,
                        lineAiSourcesExpanded: false,
                        lineAiOpenSourceIndexes: [],
                        lineDepartments: [],
                        lineDepartmentsLoading: false,
                        lineUsers: [],
                        lineUsersLoading: false,
                        lineAssignDepartmentId: '',
                        lineAssignUserId: '',
                        lineConversationAssignLoading: false,

                        quickReplies: [],
                        quickRepliesLoading: false,
                        quickRepliesError: '',
                        quickRepliesSearch: '',
                        quickReplyInsertMode: 'append',
                        quickReplyCategoryFilter: '',
                        quickRepliesIncludeInactive: false,
                        editingQuickReplyId: null,
                        createQuickReplyForm: { title: '', body: '', sort_order: 0, active: true, category: '' },
                        createQuickReplySaving: false,
                        quickReplyDeletingId: null,

                        resources: [],
                        resourcesLoading: false,
                        resourcesUploading: false,
                        resourcesSearch: '',
                        resourcesTypeFilter: '',
                        resourceUpload: { file: null, tags: '', description: '', visibility: 'private' },

                        showShareResourceModal: false,
                        shareResourceTarget: null,
                        usersBrief: [],
                        usersBriefLoading: false,
                        shareUserIds: [],
                        shareSaving: false,

                        showEditResourceModal: false,
                        editResourceTarget: null,
                        editResourceForm: { visibility: 'private', tags: '', description: '' },
                        editSaving: false,

                        nasSubdir: '',
                        nasFiles: [],
                        nasLoading: false,
                        nasImportingPath: '',

                        // Log Analysis
                        logFilter: { service: 'postfix', search: '', lines: 100 },
                        serviceLogs: [],

                        // SSL
                        letsEncryptCerts: [],
                        newLeCert: { domain: '', email: '' },
                        sslInfo: null,
                        sslForm: {
                            domain: 'localhost',
                            certFile: null,
                            keyFile: null
                        },

                        // Security Wizard
                        securityDomain: '',
                        securitySelector: 'default',
                        securityDmarcPolicy: 'none',
                        securityRuaEmail: '',
                        securityDmarcPct: 100,
                        securityDmarcAdkim: 'r',
                        securityDmarcAspf: 'r',
                        securityStatus: null,
                        dnsSuggestions: [],
                        dnsCheckResult: null,
                        dnsCheckLoading: false,

                        // Security Filter
                        rspamdActions: { reject: 15, add_header: 6, greylist: 4 },
                        greylistWhitelistText: '',
                        senderAllowlistText: '',
                        senderBlocklistText: '',
                        senderMapsLoaded: false,
                        senderMapsLoading: false,
                        rspamdLearnEml: '',
                        rspamdLearnEmlLoadedKey: '',
                        rspamdLearnLoading: false,
                        rspamdLearnResult: null,
                        rspamdLearnResultKey: '',
                        rspamdScanLoading: false,
                        rspamdScanResult: null,
                        rspamdScanResultKey: '',
                        rspamdLearnCache: {},
                        rspamdScanCache: {},
                        rspamdStats: null,
                        rspamdStatsLoading: false,
                        securityMailSummary: null,
                        securityMailSummaryLoading: false,
                        securityMailDays: 30,
                        securityMailSpamThreshold: 10,
                        securityMailTrend: [],
                        securityMailTrendLoading: false,
                        securityMailTrendChartInstance: null,
                        showSecurityHighSpamModal: false,
                        securityHighSpamLogs: [],
                        securityHighSpamLoading: false,
                        securityHighSpamOpenLoadingId: null,
                        securityHighSpamFilter: { sender: '', status: '' },
                        showQuarantineModal: false,
                        quarantineMode: 'spam',
                        quarantineFolders: [],
                        quarantineFolder: 'Spam',
                        quarantineItems: [],
                        quarantineTotal: 0,
                        quarantineLimit: 50,
                        quarantineOffset: 0,
                        quarantineSearch: '',
                        quarantineLoading: false,
                        quarantineError: '',
                        quarantineActionLoadingId: null,
                        quarantineSeedingExamples: false,
                        quarantineScanning: false,
                        quarantineScanFolder: 'INBOX',
                        quarantineScanLimit: 50,
                        quarantineScanUnseenOnly: false,
                        quarantineScanPhishingThreshold: 0.7,
                        quarantineScanSpamThreshold: 5.0,
                        quarantineShowAnalysis: false,
                        quarantineCacheInfo: null,
                        quarantineSelectedIds: [],
                        quarantineBatch: { running: false, action: '', done: 0, total: 0, errors: [] },
                        showQuarantineMapModal: false,
                        quarantineMapKind: 'allow',
                        quarantineMapText: '',
                        quarantineMapLoading: false,
                        quarantineMapError: '',

                        // RAG
                        ragStats: {},
                        ragQueryInput: '',
                        ragLoading: false,
                        ragResult: null,
                        chatMessages: [],
                        chatInput: '',
                        ingestForm: { subject: '', sender: '', body: '' },
                        isChatLoading: false,
                        isIngesting: false,
                        ingestMessage: '',
                        isSyncing: false,
                        syncMessage: '',
                        kbAdminQueryInput: '',
                        kbAdminLoading: false,
                        kbAdminResult: '',
                        kbAdminSources: [],
                        kbAdminError: '',
                        kbDocs: [],
                        kbDocsLoading: false,
                        kbNewDocTitle: '',
                        kbNewDocContent: '',
                        kbSaving: false,
                        ragState: {
                            activeTab: 'docs',
                            knowledgeDocs: [],
                            knowledgeDocsLoading: false,
                            uploadLoading: false,
                            uploadError: '',
                            deletingIds: [],
                        },

                        campaignForm: {
                            recipients: ''
                        },
                        campaignSending: false,
                        campaignTestSending: false,
                        campaignCreateSegmentLoading: false,
                        campaignCreateSegmentName: '',
                        showTemplateManagerModal: false,
                        templateMgmt: { items: [], loading: false, saving: false, deleting: null, form: { name: '', description: '', subject: '', content_html: '' }, editingId: null, importUrl: '', importLoading: false, filterCategory: '', filterTag: '' },
                        showInboxPreviewModal: false,
                        inboxPreviewHtml: '',
                        inboxPreviewTab: 'desktop',
                        spamCheck: { loading: false, result: null, error: '' },
                        campaignPdfLoading: false,
                        showUnsubscribeModal: false,
                        unsubscribeMgmt: { items: [], loading: false, addEmail: '', adding: false },
                        showCampaignPreviewModal: false,
                        campaignResult: null,
                        campaignProgress: {
                            campaign_id: null,
                            loading: false,
                            error: '',
                            status: '',
                            total_recipients: 0,
                            counts: { pending: 0, sent: 0, failed: 0, moved: 0, other: 0 },
                            progress: { processed: 0, pending: 0, percent: 0, finished: false },
                            tracking: { open_count: 0, click_count: 0, open_rate: 0, click_rate: 0 },
                            failures: [],
                            server_time: null
                        },
                        campaignProgressTimer: null,
                        showCampaignDetailsModal: false,
                        campaignDetails: {
                            loading: false,
                            error: '',
                            data: null
                        },
                        showCampaignListModal: false,
                        campaignList: {
                            loading: false,
                            error: '',
                            items: [],
                            offset: 0,
                            limit: 20,
                            q: '',
                            status: 'all'
                        },
                        campaignOps: {
                            retryLoading: false,
                            downloadLoading: false,
                            exportStatus: 'all'
                        },
                        warmupStatus: null,
                        warmupLoading: false,
                        remarketForm: { subject: '', delay_days: 3 },
                        remarketLoading: false,
                        remarketResult: null,
                        sendTimeStats: null,
                        sendTimeLoading: false,
                        listHealth: null,
                        listHealthLoading: false,
                        webhookConfig: { webhook_url: '', enabled: true, events: 'completed,failed' },
                        webhookSaving: false,
                        showSubscribeFormModal: false,
                        subscribeFormConfig: { title: '訂閱我們的電子報', button_text: '立即訂閱', accent_color: '#4f46e5' },
                        subscribeFormEmbed: null,
                        subscribeFormLoading: false,
                        showAdvancedSegmentModal: false,
                        advancedSegmentForm: { name: '', filter_type: 'opened_campaign', campaign_id: '', inactive_days: 90, min_open_count: 3 },
                        advancedSegmentLoading: false,
                        advancedSegmentResult: null,

                        // Dashboard Visualization
                        trafficChartInstance: null,
                        spamChartInstance: null,
                        spamStats: {},
                        realtimeLogs: [],
                        logService: 'postfix',
                        logWs: null
  }),

  getters: {
                    // Command Palette grouped results
                    cmdGrouped() {
                        const results = Array.isArray(this.cmdResults) ? this.cmdResults : [];
                        const map = {};
                        let globalIdx = 0;
                        for (const item of results) {
                            const cat = item.category || '功能';
                            if (!map[cat]) map[cat] = { label: cat, items: [] };
                            map[cat].items.push({ ...item, _globalIdx: globalIdx++ });
                        }
                        return Object.values(map);
                    },
                    // Default cmd palette suggestions (no query)
                    cmdDefaults() {
                        const nav = [
                            { id: 'webmail', title: '我的信箱', category: '導航', icon: '✉', iconClass: '', action: 'webmail' },
                            { id: 'dashboard', title: '儀表板', category: '導航', icon: '📊', iconClass: '', action: 'dashboard' },
                            { id: 'contacts', title: '聯絡人管理', category: '導航', icon: '👥', iconClass: '', action: 'contacts' },
                            { id: 'users', title: '使用者管理', category: '導航', icon: '👤', iconClass: '', action: 'users' },
                        ];
                        return nav.map((item, i) => ({ ...item, _globalIdx: i }));
                    },
                    // 登入帳號輸入時，根據已輸入的本地部分組出 user@domain 建議清單（給 <datalist>）
                    loginEmailSuggestions() {
                        const domains = Array.isArray(this.loginDomains) ? this.loginDomains : [];
                        if (!domains.length) return [];
                        const raw = (this.loginForm?.username || '').toString();
                        const local = raw.includes('@') ? raw.split('@', 1)[0] : raw;
                        if (!local) return domains.map(d => `@${d}`);
                        return domains.map(d => `${local}@${d}`);
                    },
                    // 帳號已含 @ 但網域不在清單內時的提示文字（非阻擋，僅提醒）
                    loginDomainHint() {
                        const domains = Array.isArray(this.loginDomains) ? this.loginDomains : [];
                        const raw = (this.loginForm?.username || '').toString();
                        if (!domains.length || !raw.includes('@')) return '';
                        const dom = raw.split('@', 2)[1]?.trim().toLowerCase() || '';
                        if (!dom) return '';
                        const known = domains.some(d => d.toLowerCase() === dom);
                        return known ? '' : `網域 @${dom} 不在本系統清單中，請確認是否正確`;
                    },
                    isSuperAdmin() {
                        return this.currentUser && this.currentUser.role === 'super_admin';
                    },
                    isDomainAdmin() {
                        return this.currentUser && this.currentUser.role === 'domain_admin';
                    },
                    safeUsers() {
                        const items = Array.isArray(this.users) ? this.users : [];
                        return items.filter(x => x && typeof x === 'object' && (Number(x.id) || 0));
                    },
                    cbiAssignableUsers() {
                        if (!this.currentUser) return this.safeUsers;
                        if (this.isSuperAdmin) return this.safeUsers;
                        const domainId = this.currentUser.domain_id;
                        return this.safeUsers.filter(u => u.domain_id === domainId);
                    },
                    safeLineSettings() {
                        const items = Array.isArray(this.lineSettings) ? this.lineSettings : [];
                        return items.filter(x => x && typeof x === 'object' && (Number(x.id) || 0));
                    },
                    safeLineSettingsAll() {
                        const items = Array.isArray(this.lineSettingsAll) ? this.lineSettingsAll : [];
                        return items.filter(x => x && typeof x === 'object' && (Number(x.id) || 0));
                    },
                    selectedConversationSetting() {
                        const sid = Number(this.selectedLineConversation?.setting_id) || 0;
                        if (!sid) return null;
                        const items = this.safeLineSettingsAll;
                        const found = items.find(s => (Number(s?.id) || 0) === sid);
                        return found || null;
                    },
                    safeLineConversations() {
                        const items = Array.isArray(this.lineConversations) ? this.lineConversations : [];
                        return items.filter(x => x && typeof x === 'object' && (Number(x.id) || 0));
                    },
                    safeLineMessages() {
                        const items = Array.isArray(this.lineMessages) ? this.lineMessages : [];
                        const filtered = items.filter(x => x && typeof x === 'object' && (
                            (x.id !== undefined && x.id !== null) ||
                            (x.created_at != null) ||
                            (x.text != null)
                        ));
                        return filtered;
                    },
                    activeQuickReplies() {
                        const items = Array.isArray(this.quickReplies) ? this.quickReplies : [];
                        return items.filter(x => x && typeof x === 'object' && x.active === true);
                    },
                    filteredQuickReplies() {
                        const q = (this.quickRepliesSearch || '').toString().trim().toLowerCase();
                        const items = Array.isArray(this.quickReplies) ? this.quickReplies : [];
                        if (!q) return items;
                        return items.filter(x => {
                            const t = (x?.title || '').toString().toLowerCase();
                            const b = (x?.body || '').toString().toLowerCase();
                            return t.includes(q) || b.includes(q);
                        });
                    },
                    filteredActiveQuickReplies() {
                        const q = (this.quickRepliesSearch || '').toString().trim().toLowerCase();
                        const cat = (this.quickReplyCategoryFilter || '').toString().trim();
                        const items = Array.isArray(this.quickReplies) ? this.quickReplies.filter(x => x && x.active === true) : [];
                        const byCat = cat ? items.filter(x => (x?.category || '') === cat) : items;
                        if (!q) return byCat;
                        return byCat.filter(x => {
                            const t = (x?.title || '').toString().toLowerCase();
                            const b = (x?.body || '').toString().toLowerCase();
                            return t.includes(q) || b.includes(q);
                        });
                    },
                    quickReplyCategories() {
                        const items = Array.isArray(this.quickReplies) ? this.quickReplies : [];
                        const cats = new Set(items.map(x => (x?.category || '').trim()).filter(Boolean));
                        return [...cats].sort();
                    },
                    lineDepartmentNameMap() {
                        const items = Array.isArray(this.lineDepartments) ? this.lineDepartments : [];
                        const map = {};
                        for (const d of items) {
                            const id = Number(d?.id) || 0;
                            if (!id) continue;
                            map[id] = (d?.name || '').toString();
                        }
                        return map;
                    },
                    lineUserLabelMap() {
                        const items = Array.isArray(this.lineUsers) ? this.lineUsers : [];
                        const map = {};
                        for (const u of items) {
                            const id = Number(u?.id) || 0;
                            if (!id) continue;
                            map[id] = (u?.email || u?.username || `#${id}`).toString();
                        }
                        return map;
                    },
                    safeMessages() {
                        const items = Array.isArray(this.messages) ? this.messages : [];
                        return items.filter(x => x && typeof x === 'object' && x.id !== undefined && x.id !== null);
                    },
                    hasValidMailSession() {
                        return !!this.mailSessionToken && Date.now() < (Number(this.mailSessionExpiresAt) || 0);
                    },
                    hasStoredMailPassword() {
                        return !!this.storedMailPasswordExists;
                    },
                    mailAuditSelectedCount() {
                        const m = this.mailAuditSelectedIds && typeof this.mailAuditSelectedIds === 'object' ? this.mailAuditSelectedIds : {};
                        return Object.keys(m).filter(k => !!m[k]).length;
                    },
                    mailAuditAllSelectedOnPage() {
                        const rows = Array.isArray(this.mailAuditRecords) ? this.mailAuditRecords : [];
                        const ids = rows.map(r => Number(r?.id) || 0).filter(id => id > 0);
                        if (ids.length === 0) return false;
                        const m = this.mailAuditSelectedIds && typeof this.mailAuditSelectedIds === 'object' ? this.mailAuditSelectedIds : {};
                        for (const id of ids) {
                            if (!m[id]) return false;
                        }
                        return true;
                    },
                    roleLabel() {
                        if (!this.currentUser) return '';
                        const map = {
                            'super_admin': '超級管理員',
                            'domain_admin': '網域管理員',
                            'user': '一般使用者'
                        };
                        return map[this.currentUser.role] || this.currentUser.role;
                    },
                    departmentPermissionList() {
                        const raw = (this.currentUser && this.currentUser.department_permissions) ? this.currentUser.department_permissions : null;
                        if (!raw) return null;
                        if (Array.isArray(raw)) return raw.map(x => (x ?? '').toString()).filter(x => x);
                        try {
                            const v = JSON.parse((raw ?? '').toString());
                            if (Array.isArray(v)) return v.map(x => (x ?? '').toString()).filter(x => x);
                            if (v && typeof v === 'object') {
                                const arr = Array.isArray(v.permissions) ? v.permissions : (Array.isArray(v.views) ? v.views : null);
                                if (Array.isArray(arr)) return arr.map(x => (x ?? '').toString()).filter(x => x);
                            }
                        } catch (e) {
                        }
                        return null;
                    },
                    rspamdScanDetails() {
                        return this.extractRspamdScanDetailsFromResult(this.rspamdScanResult);
                    },
                    currentMessageKey() {
                        if (!this.currentMessage || !this.currentMessage.id) return '';
                        return `${this.currentFolder || 'INBOX'}:${this.currentMessage.id}`;
                    },
                    rspamdScanDetailsForCurrentMessage() {
                        const key = this.currentMessageKey;
                        if (!key) return null;
                        const cached = (this.rspamdScanCache && typeof this.rspamdScanCache === 'object') ? this.rspamdScanCache[key] : null;
                        return this.extractRspamdScanDetailsFromResult(cached);
                    },
                    rspamdLearnResultForCurrentMessage() {
                        const key = this.currentMessageKey;
                        if (!key) return null;
                        return (this.rspamdLearnCache && typeof this.rspamdLearnCache === 'object') ? (this.rspamdLearnCache[key] || null) : null;
                    },
                    filteredResources() {
                        const items = Array.isArray(this.resources) ? this.resources : [];
                        const type = (this.resourcesTypeFilter || '').trim().toLowerCase();
                        const q = (this.resourcesSearch || '').trim().toLowerCase();

                        let list = items;
                        if (type) {
                            list = list.filter(r => ((r?.file_type || '').toLowerCase() === type));
                        }
                        if (q) {
                            list = list.filter(r => {
                                const hay = `${r?.original_filename || ''} ${r?.tags || ''} ${r?.description || ''}`.toLowerCase();
                                return hay.includes(q);
                            });
                        }
                        return list;
                    },
                    composeFilteredResources() {
                        const items = Array.isArray(this.composeResourcePool) ? this.composeResourcePool : [];
                        const q = (this.composeResourceSearch || '').trim().toLowerCase();
                        if (!q) return items.slice(0, 30);
                        return items.filter(r => {
                            const hay = `${r?.original_filename || ''} ${r?.tags || ''} ${r?.description || ''}`.toLowerCase();
                            return hay.includes(q);
                        }).slice(0, 30);
                    },
                    hasComposeResourceErrors() {
                        return !!(this.composeResourceErrors && Object.keys(this.composeResourceErrors).length > 0);
                    },
                    hasComposeResourceChecking() {
                        const hasValidating = !!(this.composeResourceValidating && Object.keys(this.composeResourceValidating).length > 0);
                        const hasQueued = !!(Array.isArray(this.composeResourceValidateQueue) && this.composeResourceValidateQueue.length > 0);
                        const hasTimer = !!this.composeResourceValidateTimer;
                        return hasValidating || hasQueued || hasTimer;
                    },
                    normalizedLeadWeights() {
                        const raw = this.leadScoreWeights || {};
                        const keys = ["intent", "engagement", "recency", "value", "fit"];
                        const cleaned = {};
                        let sum = 0;
                        for (const k of keys) {
                            const v = Number(raw[k]);
                            const nv = Number.isFinite(v) && v >= 0 ? v : 0;
                            cleaned[k] = nv;
                            sum += nv;
                        }
                        if (!(sum > 0)) {
                            return { intent: 0.30, engagement: 0.20, recency: 0.20, value: 0.20, fit: 0.10 };
                        }
                        const out = {};
                        for (const k of keys) out[k] = cleaned[k] / sum;
                        return out;
                    },
                    rankedLeadFeatures() {
                        const items = Array.isArray(this.leadFeatures) ? this.leadFeatures : [];
                        const w = this.normalizedLeadWeights || {};
                        const scored = items.map(x => {
                            const dims = x?.dims || {};
                            const total =
                                (Number(dims.intent) || 0) * (Number(w.intent) || 0) +
                                (Number(dims.engagement) || 0) * (Number(w.engagement) || 0) +
                                (Number(dims.recency) || 0) * (Number(w.recency) || 0) +
                                (Number(dims.value) || 0) * (Number(w.value) || 0) +
                                (Number(dims.fit) || 0) * (Number(w.fit) || 0);
                            return { ...x, total_score: total };
                        });
                        scored.sort((a, b) => {
                            const da = Number(a.total_score) || 0;
                            const db = Number(b.total_score) || 0;
                            if (db !== da) return db - da;
                            const ia = Number(a.contact_id) || 0;
                            const ib = Number(b.contact_id) || 0;
                            return ib - ia;
                        });
                        return scored;
                    },
                    flattenedForecastItems() {
                        const out = [];
                        const stages = this.pipelineForecast?.stages || {};
                        for (const k of Object.keys(stages)) {
                            const items = stages?.[k]?.items;
                            if (Array.isArray(items)) {
                                for (const it of items) out.push(it);
                            }
                        }
                        out.sort((a, b) => {
                            const ta = (a?.created_at || "").toString();
                            const tb = (b?.created_at || "").toString();
                            if (tb !== ta) return tb.localeCompare(ta);
                            return (Number(b?.id) || 0) - (Number(a?.id) || 0);
                        });
                        return out;
                    },
                    filteredCrmTimelineEvents() {
                        const items = Array.isArray(this.crmTimeline?.events) ? this.crmTimeline.events : [];
                        const type = (this.crmTimelineFilterType || 'all').toString().trim();
                        const q = (this.crmTimelineQuery || '').toString().trim().toLowerCase();
                        let list = items;
                        if (type && type !== 'all') {
                            list = list.filter(e => (e?.event_type || '') === type);
                        }
                        if (q) {
                            list = list.filter(e => {
                                const hay = `${e?.event_type || ''} ${e?.title || ''} ${e?.content || ''}`.toLowerCase();
                                return hay.includes(q);
                            });
                        }
                        return list;
                    },
                    quoteMgmtSelectedQuoteCurrency() {
                        const c = (this.quoteMgmtSelectedQuote?.currency || '').toString().trim().toUpperCase();
                        return c || null;
                    },
                    quoteMgmtPricebooksForAI() {
                        const list = Array.isArray(this.quoteMgmtPricebooks) ? this.quoteMgmtPricebooks : [];
                        const cur = this.quoteMgmtSelectedQuoteCurrency;
                        if (!cur) return list;
                        const matched = list.filter(pb => ((pb?.currency || '').toString().trim().toUpperCase() || 'TWD') === cur);
                        return matched.length ? matched : list;
                    }
  },

  actions: {
    // ---- Command Palette ----
    openCommandPalette() {
        this.showCmdPalette = true;
        this.cmdQuery = '';
        this.cmdSelIdx = 0;
        this.updateCmdResults();
        nextTick(() => { document.getElementById('cmd-input')?.focus(); });
    },
    closeCmdPalette() {
        this.showCmdPalette = false;
        this.cmdQuery = '';
        this.cmdSelIdx = 0;
    },
    updateCmdResults() {
        const q = (this.cmdQuery || '').toLowerCase().trim();
        const navItems = [
            { id: 'webmail', title: '我的信箱', category: '導航', icon: '✉', action: 'webmail' },
            { id: 'dashboard', title: '儀表板', category: '導航', icon: '📊', action: 'dashboard' },
            { id: 'contacts', title: '聯絡人管理', category: '導航', icon: '👥', action: 'contacts' },
            { id: 'users', title: '使用者管理', category: '導航', icon: '👤', action: 'users' },
            { id: 'domains', title: '網域管理', category: '導航', icon: '🌐', action: 'domains' },
            { id: 'rag_chat', title: '企業知識庫', category: '導航', icon: '💬', action: 'rag_chat' },
            { id: 'ai_sales', title: 'AI 業務助手', category: '導航', icon: '🤖', action: 'ai_sales' },
            { id: 'logs', title: '郵件日誌', category: '導航', icon: '📋', action: 'logs' },
            { id: 'server_ssl', title: 'SSL 憑證', category: '系統', icon: '🔒', action: 'server_ssl' },
        ];
        this.cmdResults = q ? navItems.filter(x => x.title.toLowerCase().includes(q) || x.category.toLowerCase().includes(q)) : navItems;
        this.cmdSelIdx = 0;
    },
    moveCmdSel(dir) {
        const total = this.cmdResults.length;
        if (!total) return;
        this.cmdSelIdx = (this.cmdSelIdx + dir + total) % total;
    },
    execCmdItem() {
        const item = this.cmdResults[this.cmdSelIdx];
        if (item) this.execCmdItemById(item);
    },
    execCmdItemById(item) {
        if (!item) return;
        this.closeCmdPalette();
        if (item.action) this.setCurrentView(item.action);
    },

    // ---- Notification Panel ----
    toggleNotifPanel() {
        this.showNotifPanel = !this.showNotifPanel;
        if (this.showNotifPanel) this.fetchSysNotifs();
    },
    async fetchSysNotifs(force = false) {
        if (this.sysNotifLoading && !force) return;
        this.sysNotifLoading = true;
        const allNotifs = [];
        const _ts = (ts) => ts ? new Date(ts).toLocaleString('zh-TW') : '';
        try {
            // 1. Sales / CRM notifications
            try {
                const res = await axios.get(`${ADMIN_API}/sales/notifications`, {
                    params: { days: 7, include_cooling: false, limit: 20 }
                });
                const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
                for (const x of items) {
                    allNotifs.push({
                        id: `crm_${x.id || Math.random()}`,
                        title: x.subject || x.title || '業務通知',
                        message: x.body || x.message || '',
                        level: x.level || 'info',
                        read: !!x.read,
                        timeAgo: _ts(x.created_at),
                        _ts: x.created_at || '',
                        category: 'crm',
                    });
                }
            } catch (_) {}

            // 2. Outgoing approval (pending count)
            if (this.isSuperAdmin || this.isDomainAdmin) {
                try {
                    const res = await axios.get(`${ADMIN_API}/outgoing-approvals/pending`, {
                        params: { limit: 50 }
                    });
                    const items = Array.isArray(res.data) ? res.data : [];
                    this.outgoingApprovalPendingCount = items.length;
                    if (items.length > 0) {
                        allNotifs.push({
                            id: 'approval_pending',
                            title: `寄件審核：${items.length} 封等待審核`,
                            message: `有 ${items.length} 封郵件等待管理員審核後發送`,
                            level: 'warning',
                            read: false,
                            timeAgo: _ts(items[0]?.created_at),
                            _ts: items[0]?.created_at || '',
                            category: 'approval',
                        });
                    }
                } catch (_) {}
            }

            // 3. Recent security audit events (last 24h, high-severity)
            if (this.isSuperAdmin || this.isDomainAdmin) {
                try {
                    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 19);
                    const res = await axios.get(`${ADMIN_API}/security/audit/logs`, {
                        params: { limit: 10, since }
                    });
                    const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
                    const highSev = items.filter(x => {
                        const a = (x.action || '').toLowerCase();
                        return a.includes('delete') || a.includes('login_fail') || a.includes('brute') || a.includes('block') || a.includes('reset_password');
                    });
                    if (highSev.length > 0) {
                        allNotifs.push({
                            id: `sec_${highSev[0].id || Math.random()}`,
                            title: `安全警報：${highSev.length} 個異常事件`,
                            message: `最近 24 小時內偵測到 ${highSev.length} 個高風險操作（${highSev[0].action}…）`,
                            level: 'danger',
                            read: false,
                            timeAgo: _ts(highSev[0].timestamp),
                            _ts: highSev[0].timestamp || '',
                            category: 'security',
                        });
                    }
                } catch (_) {}
            }

            // 4. Backup status (super admin only)
            if (this.isSuperAdmin) {
                try {
                    const res = await axios.get(`${ADMIN_API}/backup/list`);
                    const files = Array.isArray(res.data?.items) ? res.data.items : [];
                    if (files.length > 0) {
                        const latest = files[0];
                        const latestDate = latest.created_at ? new Date(latest.created_at) : null;
                        const hoursSince = latestDate ? (Date.now() - latestDate.getTime()) / 3600000 : 9999;
                        if (hoursSince < 2) {
                            allNotifs.push({
                                id: `backup_ok_${latest.name}`,
                                title: '備份完成',
                                message: `最新備份 ${latest.name} 已成功建立`,
                                level: 'success',
                                read: true,
                                timeAgo: _ts(latest.created_at),
                                _ts: latest.created_at || '',
                                category: 'backup',
                            });
                        } else if (hoursSince > 26) {
                            allNotifs.push({
                                id: 'backup_overdue',
                                title: '備份逾期提醒',
                                message: `距上次備份已超過 ${Math.floor(hoursSince)} 小時，建議檢查備份設定`,
                                level: 'warning',
                                read: false,
                                timeAgo: _ts(latest.created_at),
                                _ts: latest.created_at || '',
                                category: 'backup',
                            });
                        }
                    }
                } catch (_) {}
            }

            // Sort by timestamp descending
            allNotifs.sort((a, b) => {
                if (!a._ts) return 1;
                if (!b._ts) return -1;
                return new Date(b._ts) - new Date(a._ts);
            });
            this.sysNotifs = allNotifs;
            this.sysNotifUnread = allNotifs.filter(n => !n.read).length;
        } catch (e) {
            this.sysNotifs = [];
            this.sysNotifUnread = 0;
        } finally {
            this.sysNotifLoading = false;
        }
    },
    markAllNotifRead() {
        this.sysNotifs = this.sysNotifs.map(n => ({ ...n, read: true }));
        this.sysNotifUnread = 0;
    },
    handleNotifClick(notif) {
        notif.read = true;
        this.sysNotifUnread = Math.max(0, this.sysNotifUnread - 1);
    },
    markNotifRead(notif) { notif.read = true; },
    clearNotifs() { this.sysNotifs = []; this.sysNotifUnread = 0; },

    // ---- Dark Mode ----
    toggleDarkMode() {
        this.darkMode = !this.darkMode;
        localStorage.setItem('darkMode', this.darkMode ? '1' : '0');
        document.documentElement.setAttribute('data-theme', this.darkMode ? 'dark' : 'light');
    },
    applyDarkMode() {
        document.documentElement.setAttribute('data-theme', this.darkMode ? 'dark' : 'light');
    },

    // ---- App Lifecycle ----
    async onAppMounted() {
                    // Global axios interceptor: auto-attach Bearer token to all API requests
                    if (!this.__axiosInterceptorsInstalled) {
                        this.__axiosInterceptorsInstalled = true;

                        axios.interceptors.request.use((config) => {
                            const t = this.token || localStorage.getItem('token');
                            if (t && config.url && config.url.includes('/api/')) {
                                config.headers = config.headers || {};
                                if (!config.headers['Authorization']) {
                                    config.headers['Authorization'] = `Bearer ${t}`;
                                }
                            }
                            return config;
                        });

                        // Response interceptor: handle 401 (session expired) silently
                        axios.interceptors.response.use(
                            (res) => res,
                            (error) => {
                                const status = error?.response?.status;
                                const url = error?.config?.url || '';
                                const isAdminApi = url.includes('/api/');
                                const isLoginEndpoint = url.endsWith('/token') || url.includes('/token?');
                                const isMailEndpoint = url.includes('/mail/') || url.includes('/imap');
                                if (status === 401 && isAdminApi && !isLoginEndpoint && !isMailEndpoint) {
                                    if (this.token) {
                                        this.token = null;
                                        this.currentUser = null;
                                        localStorage.removeItem('token');
                                        _showToast('登入已過期，請重新登入');
                                    }
                                }
                                return Promise.reject(error);
                            }
                        );
                    }
                    this.applyDarkMode();
                    // Restore session expiry warning after page refresh
                    if (this.token) _scheduleSessionWarning(this.token);
                    this.loadBrandingFromLocal();
                    this.restoreMailSessionFromStorage();
                    this.syncStoredMailPasswordExists();
                    this.loadInvoiceImportedAttachmentKeys(false);
                    try {
                        const v = (localStorage.getItem('webmailAutoScanInvoices') || '').trim().toLowerCase();
                        this.webmailAutoScanInvoices = (v === '1' || v === 'true' || v === 'yes' || v === 'on');
                        const mr = (localStorage.getItem('webmailAutoScanMarkRead') || '').trim().toLowerCase();
                        this.webmailAutoScanMarkRead = (mr === '1' || mr === 'true' || mr === 'yes' || mr === 'on');
                        if (this.webmailAutoScanInvoices) {
                            this.enableAutoInvoiceScan();
                        }
                    } catch (e) {}
                    try {
                        this.__invoiceDriveKeydownHandler = (ev) => {
                            try {
                                if (!ev || !this.invoiceDriveState?.detailModalOpen) return;
                                const tag = (ev?.target?.tagName || '').toString().toUpperCase();
                                const editable = !!(ev?.target && (ev.target.isContentEditable || ev.target.getAttribute?.('contenteditable') === 'true'));
                                const inForm = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || editable;
                                if (inForm) return;
                                if (ev.key === 'Escape') {
                                    this.invoiceDriveCloseDetail();
                                }
                                if (ev.key === 'ArrowLeft') {
                                    this.invoiceDriveGotoAdjacent(-1);
                                }
                                if (ev.key === 'ArrowRight') {
                                    this.invoiceDriveGotoAdjacent(1);
                                }
                            } catch (e) {
                            }
                        };
                        document.addEventListener('keydown', this.__invoiceDriveKeydownHandler);
                    } catch (e) {
                    }
                    // 登入頁帳號欄位的網域建議清單（公開端點，未登入也可取）
                    if (!this.token) {
                        this.fetchLoginDomains();
                    }
                    if (this.token) {
                        await this.fetchCurrentUser();
                        if (!this.token || !this.currentUser) return;
                        this.refreshData();
                        if (this.currentView === 'dashboard') {
                            this.initDashboard();
                        }
                        if (this.currentView === 'webmail') {
                            nextTick(() => {
                                this.autoEnterWebmail();
                            });
                        }
                        if (this.currentView === 'line_support') {
                            await this.openLineSupportView();
                        }
                    }
    },

    async onBeforeUnmount() {
                    try {
                        if (this.__invoiceDriveKeydownHandler) {
                            document.removeEventListener('keydown', this.__invoiceDriveKeydownHandler);
                        }
                    } catch (e) {
                    }
                    try {
                        if (this.webmailInvoiceScanTimer) {
                            clearInterval(this.webmailInvoiceScanTimer);
                            this.webmailInvoiceScanTimer = null;
                        }
                    } catch (e) {}
                    try {
                        if (document && document.body && document.body.style && document.body.style.overflow === 'hidden') {
                            document.body.style.overflow = '';
                        }
                    } catch (e) {
                    }
    },

    // ---- Watch Handlers ----
    // In App.vue setup:
    //   watch(() => store.currentView, (v) => store.onWatch_currentView(v))
                    async onWatch_currentView(newVal) {
                        if (newVal === 'dashboard') {
                            // Wait for DOM update
                            nextTick(() => {
                                this.initDashboard();
                            });
                        }
                        if (newVal === 'webmail') {
                            nextTick(async () => {
                                try {
                                    await this.autoEnterWebmail();
                                    if (this.currentView !== 'webmail') return;
                                    if (this.webmailTab !== 'mail') return;
                                    if (!this.hasValidMailSession) return;
                                    if (this.loadingMessages) return;
                                    const items = Array.isArray(this.messages) ? this.messages : [];
                                    if (items.length === 0) {
                                        await this.fetchMessages(this.currentFolder || 'INBOX');
                                    }
                                } catch (e) {
                                }
                            });
                        }
                        if (newVal === 'security_filter') {
                            this.fetchRspamdActions();
                            this.fetchGreylistWhitelist();
                            if (this.isSuperAdmin) {
                                this.fetchSenderMaps();
                            } else {
                                this.senderAllowlistText = '';
                                this.senderBlocklistText = '';
                                this.senderMapsLoaded = false;
                            }
                            this.fetchRspamdStats();
                            this.refreshSecurityMailAudit();
                            this.fetchServiceStatus();
                        }
                        if (newVal === 'server_ai') {
                            this.fetchAiSettings();
                            this.fetchRagStats();
                            this.fetchKnowledgeDocuments();
                            if (this.aiTab === 'cbi') {
                                this.fetchCbiInsights(true);
                            }
                            this.showAiFollowupModal = false;
                            this.showLeadScoreModal = false;
                        }
                        if (newVal === 'contacts') {
                            this.initContactsView();
                        }
                        if (newVal === 'line_support') {
                            try {
                                this.lineSupportTab = 'conversations';
                                await this.refreshLineSupport();
                                await this.fetchQuickReplies();
                                await nextTick();
                                const scroller = this.$refs?.lineSupportScroller;
                                if (scroller) scroller.scrollTop = 0;
                                try {
                                    this.updateLineMessagesPadBottom();
                                } catch (e0) {
                                }
                            } catch (e) {
                                const status = e?.response?.status;
                                const detail = e?.response?.data?.detail || e?.message;
                                this.lineConversationsError = `載入 LINE 客服失敗${status ? `（${status}）` : ''}：${detail || 'unknown error'}`;
                            }
                        }
                        if (newVal === 'rag_chat') {
                            this.fetchRagStats();
                            this.fetchRagDocuments();
                        }
                        if (newVal === 'backup_settings') {
                            try {
                                await this.fetchBackupSettings();
                                await Promise.all([this.fetchBackupJobs(), this.fetchBackupFiles(), this.fetchRestoreJobs()]);
                            } catch (e) {
                            }
                        }
                        if (newVal === 'security_audit') {
                            try {
                                await this.resetSecurityAuditAndFetch();
                            } catch (e) {
                            }
                        }
                    },
                    onWatch_invoiceDriveState_detailModalOpen(newVal) {
                        try {
                            const open = !!newVal;
                            if (open) {
                                document.body.style.overflow = 'hidden';
                            } else {
                                document.body.style.overflow = '';
                            }
                        } catch (e) {
                        }
                    },
                    onWatch_showComposeModal(newVal) {
                        if (!newVal) {
                            this.closeRecipientPicker();
                            if (this.composeRecommendTimer) clearTimeout(this.composeRecommendTimer);
                            this.composeRecommendTimer = null;
                            this.composeRecommendActiveKey = '';
                            this.composePromptedRecommendKey = '';
                            return;
                        }
                        const now = Date.now();
                        if (this.composeLastAutoRecommendAt && (now - this.composeLastAutoRecommendAt) < 8000) return;
                        const ctx = this.getComposeRecommendationInput();
                        const subject = (ctx.subject || '').toString().trim();
                        const body = (ctx.body || '').toString().trim();
                        if (!subject && body.length < 20) return;
                        if (this.composeRecommendLoading) return;
                        if (Array.isArray(this.composeRecommendedResources) && this.composeRecommendedResources.length) return;
                        const key = (subject + "\n" + body.slice(0, 2000)).replace(/\s+/g, " ").trim();
                        this.composeRecommendActiveKey = key;
                        this.composeRecommendLastKey = '';
                        this.composePromptedRecommendKey = '';
                        this.composeLastAutoRecommendAt = now;
                        nextTick(() => {
                            this.fetchComposeRecommendations();
                        });
                    },
                    onWatch_composeForm_subject() {
                        this.scheduleComposeRecommendations();
                        if (this.showComposeModal) this.saveComposeDraft();
                    },
                    onWatch_composeForm_body() {
                        this.scheduleComposeRecommendations();
                        if (this.showComposeModal) this.saveComposeDraft();
                    },
                    onWatch_composeForm_html_body() {
                        this.scheduleComposeRecommendations();
                        if (this.showComposeModal) this.saveComposeDraft();
                    },
                    onWatch_showAiMarketingModal(newVal) {
                        if (!newVal) {
                            this.stopCampaignProgressPolling();
                        }
                    },
                    onWatch_aiTab(newVal) {
                        if (this.currentView === 'server_ai' && newVal === 'cbi') {
                            this.fetchCbiInsights(true);
                        }
                    },

    // ---- Application Methods ----
                    invoiceDriveCanGotoAdjacent(delta) {
                        const d = Number(delta || 0);
                        const id = Number(this.invoiceDriveState?.detail?.invoice?.id || 0);
                        const list = Array.isArray(this.invoiceDriveState?.files) ? this.invoiceDriveState.files : [];
                        if (!id || !list.length || (d !== 1 && d !== -1)) return false;
                        const idx = list.findIndex(x => Number(x?.invoice_id || 0) === id);
                        if (idx < 0) return false;
                        if (d < 0) return idx > 0;
                        return idx < (list.length - 1);
                    },
                    invoiceDriveNormalizeEdit(e) {
                        const src = e || {};
                        const items = Array.isArray(src.items) ? src.items : [];
                        return {
                            status: (src.status ?? '').toString(),
                            vendor_name: (src.vendor_name ?? '').toString(),
                            vendor_tax_id: (src.vendor_tax_id ?? '').toString(),
                            buyer_name: (src.buyer_name ?? '').toString(),
                            buyer_tax_id: (src.buyer_tax_id ?? '').toString(),
                            invoice_number: (src.invoice_number ?? '').toString(),
                            invoice_date: (src.invoice_date ?? '').toString(),
                            currency: (src.currency ?? '').toString(),
                            subtotal: (src.subtotal === null || src.subtotal === undefined) ? null : Number(src.subtotal),
                            tax: (src.tax === null || src.tax === undefined) ? null : Number(src.tax),
                            total: (src.total === null || src.total === undefined) ? null : Number(src.total),
                            tax_rate: (src.tax_rate === null || src.tax_rate === undefined) ? null : Number(src.tax_rate),
                            pay_method: (src.pay_method ?? '').toString(),
                            notes: (src.notes ?? '').toString(),
                            items: items.map(x => ({
                                description: (x?.description ?? '').toString(),
                                quantity: (x?.quantity === null || x?.quantity === undefined) ? null : Number(x.quantity),
                                unit: (x?.unit ?? '').toString(),
                                unit_price: (x?.unit_price === null || x?.unit_price === undefined) ? null : Number(x.unit_price),
                                amount: (x?.amount === null || x?.amount === undefined) ? null : Number(x.amount),
                                tax_rate: (x?.tax_rate === null || x?.tax_rate === undefined) ? null : Number(x.tax_rate)
                            }))
                        };
                    },
                    invoiceDriveSetEditSnapshot() {
                        if (!this.invoiceDriveState) return;
                        try {
                            const norm = this.invoiceDriveNormalizeEdit(this.invoiceDriveState.edit);
                            this.invoiceDriveState.editSnapshot = JSON.stringify(norm);
                        } catch (e) {
                            this.invoiceDriveState.editSnapshot = '';
                        }
                    },
                    invoiceDriveHasUnsavedChanges() {
                        if (!this.invoiceDriveState) return false;
                        try {
                            const snap = (this.invoiceDriveState.editSnapshot || '').toString();
                            if (!snap) return false;
                            const norm = this.invoiceDriveNormalizeEdit(this.invoiceDriveState.edit);
                            const now = JSON.stringify(norm);
                            return now !== snap;
                        } catch (e) {
                            return false;
                        }
                    },
                    async invoiceDriveConfirmDiscardIfDirty() {
                        try {
                            if (!this.invoiceDriveState?.detailModalOpen) return true;
                            if (!this.invoiceDriveHasUnsavedChanges()) return true;
                            return await window.$confirm('尚未儲存的變更將會遺失，確定要離開嗎？');
                        } catch (e) {
                            return true;
                        }
                    },
                    async invoiceDriveGotoAdjacent(delta) {
                        const d = Number(delta || 0);
                        const id = Number(this.invoiceDriveState?.detail?.invoice?.id || 0);
                        const list = Array.isArray(this.invoiceDriveState?.files) ? this.invoiceDriveState.files : [];
                        if (!id || !list.length || (d !== 1 && d !== -1)) return;
                        const idx = list.findIndex(x => Number(x?.invoice_id || 0) === id);
                        if (idx < 0) return;
                        const next = list[idx + d];
                        const nextId = Number(next?.invoice_id || 0);
                        if (!nextId) return;
                        await this.invoiceDriveOpenDetail(nextId);
                    },
                    invoiceDriveResetFilters() {
                        if (!this.invoiceDriveState) return;
                        this.invoiceDriveState.filters = { status: '', q: '', date_from: '', date_to: '' };
                        this.invoiceDriveFetchFiles();
                    },
                    invoiceDriveStatusClass(status) {
                        const s = (status || '').toString().trim().toLowerCase();
                        if (s === 'confirmed') return 'bg-emerald-100 text-emerald-800';
                        if (s === 'parsed') return 'bg-blue-100 text-blue-800';
                        if (s === 'needs_review') return 'bg-amber-100 text-amber-800';
                        if (s === 'failed') return 'bg-red-100 text-red-800';
                        if (s === 'pending') return 'bg-gray-100 text-gray-800';
                        return 'bg-gray-100 text-gray-800';
                    },
                    invoiceDriveFmtTime(ts) {
                        if (!ts && ts !== 0) return '-';
                        const v = Number(ts);
                        if (!Number.isFinite(v)) return '-';
                        const ms = v < 1000000000000 ? v * 1000 : v;
                        const d = new Date(ms);
                        if (Number.isNaN(d.getTime())) return '-';
                        try {
                            return d.toLocaleString();
                        } catch (e) {
                            return d.toISOString();
                        }
                    },
                    invoiceDriveCompactText(v) {
                        try {
                            const s = (v ?? '').toString();
                            const t = s.replace(/\s+/g, ' ').trim();
                            return t || '-';
                        } catch (e) {
                            return '-';
                        }
                    },
                    async invoiceDriveAxiosErrorDetail(e, fallback = '失敗') {
                        try {
                            const d0 = e?.response?.data?.detail;
                            if (d0) return (d0 ?? '').toString() || fallback;
                        } catch (e0) {}
                        try {
                            const data = e?.response?.data;
                            if (data && typeof Blob !== 'undefined' && data instanceof Blob) {
                                let text = '';
                                try {
                                    text = await data.text();
                                } catch (e1) {
                                    text = '';
                                }
                                const raw = (text || '').trim();
                                if (raw) {
                                    try {
                                        const j = JSON.parse(raw);
                                        const d1 = j?.detail;
                                        if (d1) return (d1 ?? '').toString() || fallback;
                                    } catch (e2) {}
                                    return raw.length > 300 ? (raw.slice(0, 300) + '…') : raw;
                                }
                            }
                        } catch (e3) {}
                        try {
                            const msg = (e?.message || '').toString().trim();
                            if (msg) return msg;
                        } catch (e4) {}
                        return fallback;
                    },
                    invoiceDriveOnFilesSelected(ev) {
                        try {
                            const files = Array.from(ev?.target?.files || []);
                            this.invoiceDriveState.selectedFiles = files;
                            this.invoiceDriveState.uploadError = '';
                            this.invoiceDriveState.uploadResults = [];
                        } catch (e) {
                            this.invoiceDriveState.selectedFiles = [];
                        }
                    },
                    invoiceDriveClearSelection() {
                        if (!this.invoiceDriveState) return;
                        this.invoiceDriveState.selectedFiles = [];
                        this.invoiceDriveState.uploadError = '';
                        this.invoiceDriveState.uploadResults = [];
                    },
                    async invoiceDriveUploadSelected() {
                        if (!this.invoiceDriveState) return;
                        const files = Array.isArray(this.invoiceDriveState.selectedFiles) ? this.invoiceDriveState.selectedFiles : [];
                        if (!files.length) return;
                        this.invoiceDriveState.uploading = true;
                        this.invoiceDriveState.uploadError = '';
                        this.invoiceDriveState.uploadResults = [];
                        try {
                            const results = [];
                            for (const f of files) {
                                try {
                                    const form = new FormData();
                                    form.append('file', f);
                                    const res = await axios.post(`${ADMIN_API}/drive/invoices/upload`, form, {
                                        headers: { 'Content-Type': 'multipart/form-data' },
                                        validateStatus: () => true
                                    });
                                    const ok = res.status >= 200 && res.status < 300 && !!res.data?.ok;
                                    results.push({ name: f?.name || 'file', ok, invoice_id: res.data?.invoice_id || null, detail: ok ? '' : (res.data?.detail || `HTTP ${res.status}`) });
                                } catch (e) {
                                    results.push({ name: f?.name || 'file', ok: false, invoice_id: null, detail: e?.response?.data?.detail || e?.message || 'upload failed' });
                                }
                            }
                            this.invoiceDriveState.uploadResults = results;
                            await this.invoiceDriveFetchFiles();
                        } finally {
                            this.invoiceDriveState.uploading = false;
                        }
                    },
                    async invoiceDriveFetchFiles() {
                        if (!this.invoiceDriveState) return;
                        this.invoiceDriveState.loading = true;
                        this.invoiceDriveState.listError = '';
                        try {
                            const params = { limit: 200 };
                            const status = (this.invoiceDriveState.filters?.status || '').toString().trim();
                            const q = (this.invoiceDriveState.filters?.q || '').toString().trim();
                            const date_from = (this.invoiceDriveState.filters?.date_from || '').toString().trim();
                            const date_to = (this.invoiceDriveState.filters?.date_to || '').toString().trim();
                            if (status) params.status = status;
                            if (q) params.q = q;
                            if (date_from) params.date_from = date_from;
                            if (date_to) params.date_to = date_to;
                            const res = await axios.get(`${ADMIN_API}/drive/invoices/files`, { params });
                            const items = Array.isArray(res.data?.items) ? res.data.items : [];
                            this.invoiceDriveState.files = items;
                        } catch (e) {
                            this.invoiceDriveState.listError = e?.response?.data?.detail || e?.message || '載入失敗';
                            this.invoiceDriveState.files = [];
                        } finally {
                            this.invoiceDriveState.loading = false;
                        }
                    },
                    async invoiceDriveSeedExamples() {
                        if (!this.invoiceDriveState) return;
                        this.invoiceDriveState.seedLoading = true;
                        this.invoiceDriveState.listError = '';
                        try {
                            const res = await axios.post(`${ADMIN_API}/drive/invoices/seed_examples`, null, { params: { count: 3 } });
                            await this.invoiceDriveFetchFiles();
                            const created = Array.isArray(res.data?.created) ? res.data.created : [];
                            const firstId = Number(created?.[0]?.invoice_id || 0);
                            if (firstId) {
                                await this.invoiceDriveOpenDetail(firstId);
                            }
                        } catch (e) {
                            this.invoiceDriveState.listError = e?.response?.data?.detail || e?.message || '建立範例失敗';
                        } finally {
                            this.invoiceDriveState.seedLoading = false;
                        }
                    },
                    async openInvoiceDriveView() {
                        if (!this.setCurrentView('invoice_drive')) return;
                        this.invoiceDriveState.detailModalOpen = false;
                        this.invoiceDriveState.detail = null;
                        this.invoiceDriveState.detailError = '';
                        await this.invoiceDriveFetchFiles();
                    },
                    async invoiceDriveOpenDetail(invoiceId) {
                        const id = Number(invoiceId || 0);
                        if (!id || !this.invoiceDriveState) return;
                        const currentId = Number(this.invoiceDriveState?.detail?.invoice?.id || 0);
                        if (this.invoiceDriveState.detailModalOpen && currentId && currentId !== id) {
                            if (!await this.invoiceDriveConfirmDiscardIfDirty()) return;
                        }
                        this.invoiceDriveState.detailModalOpen = true;
                        this.invoiceDriveState.detail = null;
                        this.invoiceDriveClearPreview();
                        this.invoiceDriveState.detailLoading = true;
                        this.invoiceDriveState.detailError = '';
                        this.invoiceDriveState.ocrExpandedFileId = null;
                        try {
                            const res = await axios.get(`${ADMIN_API}/invoices/${id}`);
                            const detail = res.data || null;
                            this.invoiceDriveState.detail = detail;
                            const inv = detail?.invoice || {};
                            const items = Array.isArray(detail?.items) ? detail.items : [];
                            this.invoiceDriveState.edit = {
                                status: inv.status || '',
                                vendor_name: inv.vendor_name || '',
                                vendor_tax_id: inv.vendor_tax_id || '',
                                buyer_name: inv.buyer_name || '',
                                buyer_tax_id: inv.buyer_tax_id || '',
                                invoice_number: inv.invoice_number || '',
                                invoice_date: inv.invoice_date || '',
                                currency: inv.currency || '',
                                subtotal: inv.subtotal ?? null,
                                tax: inv.tax ?? null,
                                total: inv.total ?? null,
                                tax_rate: inv.tax_rate ?? null,
                                pay_method: inv.pay_method || '',
                                notes: inv.notes || '',
                                items: items.map(x => ({
                                    description: x?.description || '',
                                    quantity: x?.quantity ?? null,
                                    unit: x?.unit || '',
                                    unit_price: x?.unit_price ?? null,
                                    amount: x?.amount ?? null,
                                    tax_rate: x?.tax_rate ?? null
                                }))
                            };
                            this.invoiceDriveSetEditSnapshot();
                            try {
                                const pid = Number(this.invoiceDriveState.pendingPreviewFileId || 0);
                                if (pid) {
                                    const files = Array.isArray(detail?.files) ? detail.files : [];
                                    const f = files.find(x => Number(x?.file_id || x?.id || 0) === pid) || { file_id: pid, id: pid };
                                    this.invoiceDriveState.pendingPreviewFileId = null;
                                    await this.invoiceDrivePreviewFile(f);
                                }
                            } catch (e0) {
                                this.invoiceDriveState.pendingPreviewFileId = null;
                            }
                        } catch (e) {
                            this.invoiceDriveState.detailError = e?.response?.data?.detail || e?.message || '讀取失敗';
                        } finally {
                            this.invoiceDriveState.detailLoading = false;
                        }
                    },
                    async invoiceDriveCloseDetail() {
                        if (!this.invoiceDriveState) return;
                        if (!await this.invoiceDriveConfirmDiscardIfDirty()) return;
                        this.invoiceDriveState.detailModalOpen = false;
                        this.invoiceDriveState.detail = null;
                        this.invoiceDriveState.detailError = '';
                        this.invoiceDriveState.ocrExpandedFileId = null;
                        this.invoiceDriveState.pendingPreviewFileId = null;
                        this.invoiceDriveState.editSnapshot = '';
                        this.invoiceDriveClearPreview();
                    },
                    async invoiceDriveOpenDetailAndPreview(it) {
                        if (!this.invoiceDriveState) return;
                        const invId = Number(it?.invoice_id || 0);
                        const fileId = Number(it?.file_id || 0);
                        if (!invId) return;
                        if (fileId) {
                            this.invoiceDriveState.pendingPreviewFileId = fileId;
                        } else {
                            this.invoiceDriveState.pendingPreviewFileId = null;
                        }
                        await this.invoiceDriveOpenDetail(invId);
                    },
                    invoiceDriveClearPreview() {
                        if (!this.invoiceDriveState) return;
                        const url = this.invoiceDriveState.previewUrl;
                        if (url && typeof url === 'string' && url.startsWith('blob:')) {
                            try {
                                URL.revokeObjectURL(url);
                            } catch (e) {
                            }
                        }
                        this.invoiceDriveState.previewLoading = false;
                        this.invoiceDriveState.previewError = '';
                        this.invoiceDriveState.previewUrl = null;
                        this.invoiceDriveState.previewMime = '';
                        this.invoiceDriveState.previewName = '';
                        this.invoiceDriveState.previewFileId = null;
                    },
                    async invoiceDrivePreviewFile(f) {
                        if (!this.invoiceDriveState) return;
                        const id = Number(f?.file_id || f?.id || 0);
                        if (!id) return;
                        if (this.invoiceDriveState.previewFileId === id && this.invoiceDriveState.previewUrl) {
                            this.invoiceDriveClearPreview();
                            return;
                        }
                        this.invoiceDriveClearPreview();
                        this.invoiceDriveState.previewLoading = true;
                        this.invoiceDriveState.previewError = '';
                        try {
                            const res = await axios.get(`${ADMIN_API}/drive/invoices/files/${id}/download`, { responseType: 'blob' });
                            const blob = res.data;
                            const url = window.URL.createObjectURL(blob);
                            this.invoiceDriveState.previewUrl = url;
                            this.invoiceDriveState.previewMime = (blob?.type || f?.mime_type || '').toString();
                            this.invoiceDriveState.previewName = (f?.original_filename || `file_${id}`).toString();
                            this.invoiceDriveState.previewFileId = id;
                        } catch (e) {
                            this.invoiceDriveState.previewError = await this.invoiceDriveAxiosErrorDetail(e, '預覽失敗');
                        } finally {
                            this.invoiceDriveState.previewLoading = false;
                        }
                    },
                    invoiceDriveAddItem() {
                        if (!this.invoiceDriveState?.edit?.items) this.invoiceDriveState.edit.items = [];
                        this.invoiceDriveState.edit.items.push({ description: '', quantity: null, unit: '', unit_price: null, amount: null, tax_rate: null });
                    },
                    invoiceDriveRecalcTotals() {
                        const e = this.invoiceDriveState?.edit || null;
                        if (!e) return;
                        const items = Array.isArray(e.items) ? e.items : [];
                        let subtotal = 0;
                        let hasAnyAmount = false;
                        for (const row of items) {
                            if (!row || typeof row !== 'object') continue;
                            const q = (row.quantity === null || row.quantity === undefined) ? null : Number(row.quantity);
                            const up = (row.unit_price === null || row.unit_price === undefined) ? null : Number(row.unit_price);
                            let amt = (row.amount === null || row.amount === undefined) ? null : Number(row.amount);
                            if ((amt === null || !Number.isFinite(amt)) && q !== null && up !== null && Number.isFinite(q) && Number.isFinite(up)) {
                                amt = q * up;
                                row.amount = amt;
                            }
                            if (amt !== null && Number.isFinite(amt)) {
                                subtotal += amt;
                                hasAnyAmount = true;
                            }
                        }
                        if (hasAnyAmount) {
                            e.subtotal = subtotal;
                            const tr = (e.tax_rate === null || e.tax_rate === undefined) ? null : Number(e.tax_rate);
                            const tax = (e.tax === null || e.tax === undefined) ? null : Number(e.tax);
                            if ((tax === null || !Number.isFinite(tax)) && tr !== null && Number.isFinite(tr)) {
                                e.tax = subtotal * tr;
                            }
                            const tax2 = (e.tax === null || e.tax === undefined) ? 0 : (Number.isFinite(Number(e.tax)) ? Number(e.tax) : 0);
                            e.total = subtotal + tax2;
                        }
                    },
                    invoiceDriveRemoveItem(idx) {
                        if (!this.invoiceDriveState?.edit?.items) return;
                        const i = Number(idx);
                        if (!Number.isFinite(i)) return;
                        this.invoiceDriveState.edit.items.splice(i, 1);
                    },
                    async invoiceDriveSaveDetail() {
                        const invId = Number(this.invoiceDriveState?.detail?.invoice?.id || 0);
                        if (!invId) return;
                        this.invoiceDriveState.saving = true;
                        this.invoiceDriveState.detailError = '';
                        try {
                            const e = this.invoiceDriveState.edit || {};
                            const payload = {
                                status: (e.status || '').toString().trim() || null,
                                vendor_name: (e.vendor_name || '').toString().trim() || null,
                                vendor_tax_id: (e.vendor_tax_id || '').toString().trim() || null,
                                buyer_name: (e.buyer_name || '').toString().trim() || null,
                                buyer_tax_id: (e.buyer_tax_id || '').toString().trim() || null,
                                invoice_number: (e.invoice_number || '').toString().trim() || null,
                                invoice_date: (e.invoice_date || '').toString().trim() || null,
                                currency: (e.currency || '').toString().trim() || null,
                                subtotal: (e.subtotal === null || e.subtotal === undefined) ? null : Number(e.subtotal),
                                tax: (e.tax === null || e.tax === undefined) ? null : Number(e.tax),
                                total: (e.total === null || e.total === undefined) ? null : Number(e.total),
                                tax_rate: (e.tax_rate === null || e.tax_rate === undefined) ? null : Number(e.tax_rate),
                                pay_method: (e.pay_method || '').toString().trim() || null,
                                notes: (e.notes || '').toString().trim() || null,
                                items: Array.isArray(e.items) ? e.items.map(x => ({
                                    description: (x?.description || '').toString().trim() || null,
                                    quantity: (x?.quantity === null || x?.quantity === undefined) ? null : Number(x.quantity),
                                    unit: (x?.unit || '').toString().trim() || null,
                                    unit_price: (x?.unit_price === null || x?.unit_price === undefined) ? null : Number(x.unit_price),
                                    amount: (x?.amount === null || x?.amount === undefined) ? null : Number(x.amount),
                                    tax_rate: (x?.tax_rate === null || x?.tax_rate === undefined) ? null : Number(x.tax_rate)
                                })) : []
                            };
                            await axios.put(`${ADMIN_API}/invoices/${invId}`, payload);
                            await this.invoiceDriveOpenDetail(invId);
                            await this.invoiceDriveFetchFiles();
                        } catch (e) {
                            this.invoiceDriveState.detailError = e?.response?.data?.detail || e?.message || '儲存失敗';
                        } finally {
                            this.invoiceDriveState.saving = false;
                        }
                    },
                    async invoiceDriveConfirm(invoiceId) {
                        const id = Number(invoiceId || 0);
                        if (!id || !this.invoiceDriveState) return;
                        this.invoiceDriveState.confirming = true;
                        this.invoiceDriveState.detailError = '';
                        try {
                            await axios.post(`${ADMIN_API}/invoices/${id}/confirm`, {});
                            await this.invoiceDriveOpenDetail(id);
                            await this.invoiceDriveFetchFiles();
                        } catch (e) {
                            this.invoiceDriveState.detailError = e?.response?.data?.detail || e?.message || '確認失敗';
                        } finally {
                            this.invoiceDriveState.confirming = false;
                        }
                    },
                    async invoiceDriveReparseFile(fileId) {
                        const id = Number(fileId || 0);
                        if (!id || !this.invoiceDriveState) return;
                        this.invoiceDriveState.reparsingId = id;
                        this.invoiceDriveState.listError = '';
                        try {
                            await axios.post(`${ADMIN_API}/drive/invoices/files/${id}/reparse`, {});
                            await this.invoiceDriveFetchFiles();
                        } catch (e) {
                            this.invoiceDriveState.listError = e?.response?.data?.detail || e?.message || '重解析失敗';
                        } finally {
                            this.invoiceDriveState.reparsingId = null;
                        }
                    },
                    async invoiceDriveDownloadFile(it) {
                        const id = Number(it?.file_id || 0);
                        if (!id || !this.invoiceDriveState) return;
                        this.invoiceDriveState.downloadingId = id;
                        try {
                            const res = await axios.get(`${ADMIN_API}/drive/invoices/files/${id}/download`, { responseType: 'blob' });
                            const blob = res.data;
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = (it?.original_filename || `invoice_${id}`);
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            window.URL.revokeObjectURL(url);
                        } catch (e) {
                            this.invoiceDriveState.listError = await this.invoiceDriveAxiosErrorDetail(e, '下載失敗');
                        } finally {
                            this.invoiceDriveState.downloadingId = null;
                        }
                    },
                    invoiceDriveDetailDownload(f) {
                        if (!this.invoiceDriveState) return;
                        const it = {
                            file_id: f?.file_id || f?.id || null,
                            original_filename: f?.original_filename || null
                        };
                        return this.invoiceDriveDownloadFile(it);
                    },
                    invoiceDriveToggleOcr(f) {
                        if (!this.invoiceDriveState) return;
                        const id = Number(f?.file_id || f?.id || 0);
                        if (!id) return;
                        this.invoiceDriveState.ocrExpandedFileId = (this.invoiceDriveState.ocrExpandedFileId === id) ? null : id;
                    },
                    async invoiceDriveExportCsv() {
                        if (!this.invoiceDriveState) return;
                        this.invoiceDriveState.exporting = true;
                        this.invoiceDriveState.listError = '';
                        try {
                            const params = { format: 'csv', limit: 20000 };
                            const status = (this.invoiceDriveState.filters?.status || '').toString().trim();
                            const q = (this.invoiceDriveState.filters?.q || '').toString().trim();
                            if (status) params.status = status;
                            if (q) params.q = q;
                            const res = await axios.get(`${ADMIN_API}/invoices/export`, { params, responseType: 'blob' });
                            const blob = res.data;
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'invoices.csv';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            window.URL.revokeObjectURL(url);
                        } catch (e) {
                            this.invoiceDriveState.listError = await this.invoiceDriveAxiosErrorDetail(e, '匯出失敗');
                        } finally {
                            this.invoiceDriveState.exporting = false;
                        }
                    },
                    async invoiceDriveExportPdf(invoiceId) {
                        if (!invoiceId || !this.invoiceDriveState) return;
                        this.invoiceDriveState.pdfExporting = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
                            const inv = this.invoiceDriveState?.detail?.invoice;
                            const invNo = (inv?.invoice_number || `invoice-${invoiceId}`).toString().replace(/[/\\]/g, '-');
                            const filename = `${invNo}.pdf`;
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        } catch (e) {
                            this.invoiceDriveState.detailError = await this.invoiceDriveAxiosErrorDetail(e, '匯出 PDF 失敗');
                        } finally {
                            this.invoiceDriveState.pdfExporting = false;
                        }
                    },

                    async invoiceDriveDeleteInvoice(invoiceId) {
                        if (!invoiceId) return;
                        if (!window.confirm('確定要刪除此發票？此操作無法復原。')) return;
                        if (!this.invoiceDriveState) return;
                        this.invoiceDriveState.deletingId = invoiceId;
                        try {
                            await axios.delete(`${ADMIN_API}/invoices/${invoiceId}`);
                            if (this.invoiceDriveState.detailModalOpen) {
                                await this.invoiceDriveCloseDetail();
                            }
                            await this.invoiceDriveFetchFiles();
                        } catch (e) {
                            this.invoiceDriveState.listError = await this.invoiceDriveAxiosErrorDetail(e, '刪除失敗');
                        } finally {
                            this.invoiceDriveState.deletingId = null;
                        }
                    },
                    invoiceDriveBatchToggle(invoiceId) {
                        if (!this.invoiceDriveState) return;
                        const id = Number(invoiceId || 0);
                        if (!id) return;
                        const sel = this.invoiceDriveState.batchSelected;
                        const idx = sel.indexOf(id);
                        if (idx === -1) sel.push(id);
                        else sel.splice(idx, 1);
                    },
                    invoiceDriveBatchSelectAll() {
                        if (!this.invoiceDriveState) return;
                        const ids = (this.invoiceDriveState.files || []).map(f => Number(f.invoice_id || 0)).filter(Boolean);
                        this.invoiceDriveState.batchSelected = [...new Set(ids)];
                    },
                    invoiceDriveBatchClear() {
                        if (!this.invoiceDriveState) return;
                        this.invoiceDriveState.batchSelected = [];
                    },
                    invoiceDriveBatchToggleMode() {
                        if (!this.invoiceDriveState) return;
                        this.invoiceDriveState.batchMode = !this.invoiceDriveState.batchMode;
                        this.invoiceDriveState.batchSelected = [];
                    },
                    async invoiceDriveBatchConfirm() {
                        if (!this.invoiceDriveState) return;
                        const ids = [...(this.invoiceDriveState.batchSelected || [])];
                        if (!ids.length) return;
                        if (!window.confirm(`確定要批次確認 ${ids.length} 筆發票？`)) return;
                        this.invoiceDriveState.batchProcessing = true;
                        this.invoiceDriveState.listError = '';
                        let failed = 0;
                        for (const id of ids) {
                            try {
                                await axios.post(`${ADMIN_API}/invoices/${id}/confirm`);
                            } catch (e) {
                                failed++;
                            }
                        }
                        this.invoiceDriveState.batchSelected = [];
                        this.invoiceDriveState.batchMode = false;
                        this.invoiceDriveState.batchProcessing = false;
                        await this.invoiceDriveFetchFiles();
                        if (failed) this.invoiceDriveState.listError = `批次確認完成，${failed} 筆失敗`;
                    },
                    async invoiceDriveBatchDelete() {
                        if (!this.invoiceDriveState) return;
                        const ids = [...(this.invoiceDriveState.batchSelected || [])];
                        if (!ids.length) return;
                        if (!window.confirm(`確定要批次刪除 ${ids.length} 筆發票？此操作無法復原。`)) return;
                        this.invoiceDriveState.batchProcessing = true;
                        this.invoiceDriveState.listError = '';
                        let failed = 0;
                        for (const id of ids) {
                            try {
                                await axios.delete(`${ADMIN_API}/invoices/${id}`);
                            } catch (e) {
                                failed++;
                            }
                        }
                        this.invoiceDriveState.batchSelected = [];
                        this.invoiceDriveState.batchMode = false;
                        this.invoiceDriveState.batchProcessing = false;
                        await this.invoiceDriveFetchFiles();
                        if (failed) this.invoiceDriveState.listError = `批次刪除完成，${failed} 筆失敗`;
                    },
                    resetCbiFilters() {
                        this.cbiFilters = { search: '', company: '', sender: '', status: '', sort_by: 'created_at', sort_dir: 'desc' };
                        this.fetchCbiInsights(true);
                    },
                    async fetchCbiInsights(resetOffset) {
                        if (resetOffset) {
                            this.cbiOffset = 0;
                        }
                        this.cbiLoading = true;
                        try {
                            const params = {
                                sort_by: (this.cbiFilters.sort_by || 'created_at'),
                                sort_dir: (this.cbiFilters.sort_dir || 'desc'),
                                limit: this.cbiLimit,
                                offset: this.cbiOffset
                            };
                            if ((this.cbiFilters.search || '').trim()) params.search = this.cbiFilters.search.trim();
                            if ((this.cbiFilters.company || '').trim()) params.company = this.cbiFilters.company.trim();
                            if ((this.cbiFilters.sender || '').trim()) params.sender = this.cbiFilters.sender.trim();
                            if ((this.cbiFilters.status || '').trim()) params.status = this.cbiFilters.status.trim();

                            const resp = await axios.get(`${ADMIN_API}/ai/customer/background/list`, { params });
                            const items = (resp.data && Array.isArray(resp.data.items)) ? resp.data.items : [];
                            this.cbiItems = items;
                            this.cbiTotal = Number(resp.data?.total || 0) || 0;
                            const nextDraft = { ...(this.cbiShareDraft || {}) };
                            for (const it of items) {
                                const id = Number(it?.id || 0);
                                if (!id) continue;
                                const isShared = !!it.is_shared;
                                const defaultUserId = (it.user_id !== null && it.user_id !== undefined) ? Number(it.user_id) : (this.currentUser ? Number(this.currentUser.id) : null);
                                nextDraft[id] = nextDraft[id] || { scope: isShared ? 'domain' : 'user', user_id: defaultUserId };
                                nextDraft[id].scope = isShared ? 'domain' : 'user';
                                if (!isShared && (nextDraft[id].user_id === null || nextDraft[id].user_id === undefined)) {
                                    nextDraft[id].user_id = defaultUserId;
                                }
                            }
                            this.cbiShareDraft = nextDraft;
                        } catch (error) {
                            _showToast('載入 CBI 失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.cbiLoading = false;
                        }
                    },
                    async applyCbiShare(it) {
                        if (!(this.isDomainAdmin || this.isSuperAdmin)) return;
                        const id = Number(it?.id || 0);
                        if (!id) return;
                        const draft = (this.cbiShareDraft && this.cbiShareDraft[id]) ? this.cbiShareDraft[id] : { scope: 'domain', user_id: null };
                        const scope = String(draft.scope || 'domain');
                        const payload = { scope };
                        if (scope === 'user') {
                            payload.user_id = draft.user_id;
                        }
                        this.cbiShareSavingId = id;
                        try {
                            const resp = await axios.post(`${ADMIN_API}/ai/customer/background/${id}/share`, payload);
                            const userId = (resp.data && resp.data.user_id !== undefined) ? resp.data.user_id : null;
                            const isShared = !!resp.data?.is_shared;
                            const idx = Array.isArray(this.cbiItems) ? this.cbiItems.findIndex(x => Number(x?.id || 0) === id) : -1;
                            if (idx >= 0) {
                                this.cbiItems[idx].user_id = userId;
                                this.cbiItems[idx].is_shared = isShared;
                            }
                            if (this.cbiShareDraft && this.cbiShareDraft[id]) {
                                this.cbiShareDraft[id].scope = isShared ? 'domain' : 'user';
                                if (!isShared) {
                                    this.cbiShareDraft[id].user_id = userId;
                                }
                            }
                        } catch (error) {
                            _showToast('授權更新失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.cbiShareSavingId = null;
                        }
                    },
                    cbiPrevPage() {
                        this.cbiOffset = Math.max(0, Number(this.cbiOffset || 0) - Number(this.cbiLimit || 50));
                        this.fetchCbiInsights(false);
                    },
                    cbiNextPage() {
                        const total = Number(this.cbiTotal || 0);
                        const next = Number(this.cbiOffset || 0) + Number(this.cbiLimit || 50);
                        if (next < total) {
                            this.cbiOffset = next;
                            this.fetchCbiInsights(false);
                        }
                    },
                    loadBrandingFromLocal() {
                        try {
                            this.branding.loginTitle = localStorage.getItem('ui_login_title') || '';
                            this.branding.headerTitle = localStorage.getItem('ui_header_title') || '';
                            this.branding.headerLogoUrl = localStorage.getItem('ui_header_logo_url') || '';
                            this.brandingForm.loginTitle = this.branding.loginTitle;
                            this.brandingForm.headerTitle = this.branding.headerTitle;
                            this.brandingForm.headerLogoUrl = this.branding.headerLogoUrl;
                        } catch (e) {}
                    },
                    async loadBrandingFromServer() {
                        try {
                            const disabled = (() => {
                                try { return sessionStorage.getItem('brandingApiDisabled') === '1'; } catch (e) { return false; }
                            })();
                            if (disabled) return;
                            const url = `${ADMIN_API}/branding`;
                            const res = await axios.get(url, {
                                params: { _ts: Date.now() },
                                validateStatus: () => true
                            });
                            if (Number(res?.status) === 404) {
                                try { sessionStorage.setItem('brandingApiDisabled', '1'); } catch (e) {}
                                return;
                            }
                            if (Number(res?.status) !== 200) return;
                            const data = res?.data || {};
                            const loginTitle = (data?.loginTitle || '').toString();
                            const headerTitle = (data?.headerTitle || '').toString();
                            const headerLogoUrl = (data?.headerLogoUrl || '').toString();
                            if (loginTitle || headerTitle || headerLogoUrl) {
                                this.brandingApiBase = url.replace(/\/branding$/, '');
                                this.branding.loginTitle = loginTitle;
                                this.branding.headerTitle = headerTitle;
                                this.branding.headerLogoUrl = headerLogoUrl;
                                this.brandingForm.loginTitle = loginTitle;
                                this.brandingForm.headerTitle = headerTitle;
                                this.brandingForm.headerLogoUrl = headerLogoUrl;
                                try {
                                    localStorage.setItem('ui_login_title', loginTitle);
                                    localStorage.setItem('ui_header_title', headerTitle);
                                    localStorage.setItem('ui_header_logo_url', headerLogoUrl);
                                } catch (e) {}
                            }
                        } catch (e) {}
                    },
                    applyHeaderLogoUrl() {
                        const url = (this.brandingForm.headerLogoUrl || '').toString().trim();
                        this.branding.headerLogoUrl = url;
                        try { localStorage.setItem('ui_header_logo_url', url); } catch (e) {}
                        this.saveBrandingText('ui_header_logo_url', url, false);
                    },
                    async uploadBrandLogo(ev) {
                        const file = ev?.target?.files?.[0];
                        if (!file) return;
                        const form = new FormData();
                        form.append('file', file);
                        form.append('visibility', 'public');
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.post(`${ADMIN_API}/resources/upload`, form, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                            const path = (res?.data?.file_path || '').toString().replace(/^\/+/, '');
                            const url = path.startsWith('static/') ? `/${path}` : path;
                            this.brandingForm.headerLogoUrl = url;
                            this.applyHeaderLogoUrl();
                        } catch (e) {
                            _showToast('上傳 Logo 失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            try { ev.target.value = ''; } catch (e) {}
                        }
                    },
                    async saveBrandingText(key, value, alertOk = true) {
                        const v = (value || '').toString();
                        try {
                            const payload = {};
                            if (key === 'ui_login_title') payload.loginTitle = v;
                            if (key === 'ui_header_title') payload.headerTitle = v;
                            if (key === 'ui_header_logo_url') payload.headerLogoUrl = v;
                            const disabled = (() => {
                                try { return sessionStorage.getItem('brandingApiDisabled') === '1'; } catch (e) { return false; }
                            })();
                            if (!disabled) {
                                const url = `${ADMIN_API}/branding`;
                                const res = await axios.post(url, payload, { validateStatus: () => true });
                                if (Number(res?.status) === 404) {
                                    try { sessionStorage.setItem('brandingApiDisabled', '1'); } catch (e) {}
                                } else if (Number(res?.status) === 200) {
                                    this.brandingApiBase = url.replace(/\/branding$/, '');
                                }
                            }
                        } catch (e) {}
                        try { localStorage.setItem(key, v); } catch (e) {}
                        if (key === 'ui_login_title') this.branding.loginTitle = v;
                        if (key === 'ui_header_title') this.branding.headerTitle = v;
                        if (key === 'ui_header_logo_url') this.branding.headerLogoUrl = v;
                        if (alertOk) _showToast('已儲存');
                    },
                    safeArr(items) {
                        const list = Array.isArray(items) ? items : [];
                        return list.filter(x => x && typeof x === 'object');
                    },
                    keyById(item, prefix, idx) {
                        const id = item?.id;
                        if (id !== undefined && id !== null && id !== '') return id;
                        return `${(prefix || 'k')}-${Number(idx) || 0}`;
                    },
                    canAccess(key) {
                        const target = (key ?? '').toString().trim();
                        if (!target) return true;
                        const adminOnly = new Set([
                            'mail_audit_records',
                            'security_audit',
                            'dashboard',
                            'ai_sales',
                        ]);
                        if (target === 'rag_chat') return !!this.currentUser;
                        if (adminOnly.has(target)) return !!(this.isSuperAdmin || this.isDomainAdmin);
                        const superAdminOnly = new Set([
                            'server_mail',
                            'server_dns',
                            'server_db',
                            'server_ai',
                            'mail_queue',
                            'security_filter',
                            'security_wizard',
                            'server_ssl',
                            'log_analysis',
                            'logs',
                            'backup_settings'
                        ]);
                        if (superAdminOnly.has(target)) return !!this.isSuperAdmin;
                        if (this.isSuperAdmin || this.isDomainAdmin) return true;
                        if (target === 'webmail' || target === 'invoice_drive') return true;
                        const perms = this.departmentPermissionList;
                        if (!perms || perms.length === 0) return true;
                        const list = perms.map(x => (x ?? '').toString().trim()).filter(x => x);
                        if (list.includes('*') || list.includes('view:*')) return true;
                        if (list.includes(target) || list.includes(`view:${target}`)) return true;
                        for (const p of list) {
                            if (p.endsWith('*')) {
                                const prefix = p.slice(0, -1);
                                if (prefix && target.startsWith(prefix)) return true;
                            }
                            if (p.startsWith('view:') && p.endsWith('*')) {
                                const prefix = p.slice('view:'.length, -1);
                                if (prefix && target.startsWith(prefix)) return true;
                            }
                        }
                        return false;
                    },
                    isSensitiveSettingKey(key) {
                        const k = (key || '').toString().trim().toLowerCase();
                        if (!k) return false;
                        return k.includes('key') || k.includes('token') || k.includes('secret') || k.includes('password');
                    },
                    maskSettingValue(key, value) {
                        const v = (value ?? '').toString();
                        if (!this.isSensitiveSettingKey(key)) return v;
                        const s = v.trim();
                        if (!s) return '';
                        if (s.length <= 8) return '••••••••';
                        return `${s.slice(0, 3)}••••••${s.slice(-3)}`;
                    },
                    setCurrentView(view) {
                        const v = (view ?? '').toString();
                        if (!this.canAccess(v)) {
                            _showToast('沒有權限使用此功能');
                            return false;
                        }
                        this.currentView = v;
                        return true;
                    },
                    toggleMobileSidebar() { this.mobileSidebarOpen = !this.mobileSidebarOpen; },
                    closeMobileSidebar() { this.mobileSidebarOpen = false; },

                    async switchSidebarView(view) {
                        this.mobileSidebarOpen = false;
                        try {
                            this.showAiMarketingModal = false;
                            this.showAiDraftModal = false;
                            this.showAiReplyModal = false;
                            this.showAiRewriteModal = false;
                            this.showAiTranslateModal = false;
                            this.showAiSummaryModal = false;
                            this.showAiAnalyzeModal = false;
                            this.showAiEventModal = false;
                            this.showAiFollowupModal = false;
                            this.showLeadScoreModal = false;
                            this.showJourneyEnrollmentsModal = false;
                            this.showJourneyEditorModal = false;
                            this.showJourneyFunnelModal = false;
                            this.showContactModal = false;
                            this.showContactGroupModal = false;
                            this.editingDomain = null;
                            this.editingUser = null;
                            this.editingGroup = null;
                            this.showOriginalMailModal = false;
                            this.originalMailMessage = null;
                            this.showSubjectOptimizerModal = false;
                            this.showNotificationsModal = false;
                            this.showCrmModal = false;
                            this.followupSequencePreview = null;
                            this.pipelineForecast = null;
                            this.quotePreview = null;

                            if (!this.canAccess(view)) {
                                _showToast('沒有權限使用此功能');
                                return;
                            }
                            if (view === 'contacts') {
                                await this.openContactsView();
                                return;
                            }
                            if (view === 'quotes') {
                                await this.openQuotesView();
                                return;
                            }
                            if (view === 'invoice_drive') {
                                await this.openInvoiceDriveView();
                                return;
                            }
                            if (view === 'line_support') {
                                await this.openLineSupportView();
                                return;
                            }
                            if (view === 'branding') {
                                this.currentView = view;
                                this.loadBrandingFromServer();
                                return;
                            }

                            this.currentView = view;

                            if (view === 'domains') {
                                await this.fetchDomains();
                            } else if (view === 'users') {
                                await this.fetchUsers();
                                await this.fetchDepartments(this.newUser?.domain_id);
                            } else if (view === 'departments') {
                                await this.fetchDepartments(this.newDepartment?.domain_id);
                            } else if (view === 'groups') {
                                await this.fetchGroups();
                            } else if (view === 'mail_audit_records') {
                                await this.resetMailAuditAndFetch();
                            } else if (view === 'server_ssl') {
                                await Promise.all([this.fetchSslInfo(), this.fetchLeCerts()]);
                            }
                        } catch (e) {
                            _showToast('切換頁面失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    getComposeRecommendationInput() {
                        try {
                            const isReplying = !!(this.composeForm && this.composeForm.in_reply_to);
                            const hasCurrent = !!(this.currentMessage && (this.currentMessage.subject || this.currentMessage.body));

                            const subjectFromCompose = (this.composeForm?.subject || '').toString();
                            const bodyFromCompose = (this.composeForm?.html_body && this.composeForm.html_body.toString().trim())
                                ? this.stripHtml(this.composeForm.html_body)
                                : (this.composeForm?.body || '');

                            if (isReplying && hasCurrent) {
                                const subjectFromIncoming = (this.currentMessage.subject || '').toString();
                                const bodyFromIncoming = this.stripHtml((this.currentMessage.body || '').toString());
                                const subject = subjectFromCompose.trim() ? subjectFromCompose : subjectFromIncoming;
                                const composeBodyTrim = (bodyFromCompose || '').toString().trim();
                                const body = composeBodyTrim.length >= 10 ? composeBodyTrim : bodyFromIncoming;
                                return { subject, body };
                            }

                            return {
                                subject: subjectFromCompose,
                                body: (bodyFromCompose || '').toString()
                            };
                        } catch (e) {
                            return { subject: '', body: '' };
                        }
                    },
                    scheduleComposeRecommendations() {
                        try {
                            if (!this.showComposeModal) return;
                            const ctx = this.getComposeRecommendationInput();
                            const subject = (ctx.subject || '').toString().trim();
                            const body = (ctx.body || '').toString().trim();
                            if (!subject && body.length < 20) return;
                            const key = (subject + "\n" + body.slice(0, 2000)).replace(/\s+/g, " ").trim();
                            if (!key) return;
                            if ((this.composeRecommendLastKey || '') === key) return;
                            this.composeRecommendActiveKey = key;
                            if (this.composeRecommendTimer) clearTimeout(this.composeRecommendTimer);
                            this.composeRecommendTimer = setTimeout(() => {
                                if ((this.composeRecommendLastKey || '') === key) return;
                                if (this.composeRecommendLoading) return;
                                this.fetchComposeRecommendations();
                            }, 800);
                        } catch (e) {
                        }
                    },
                    openMarketingContent() {
                        const existing = (this.aiMarketingState && this.aiMarketingState.result) ? this.aiMarketingState.result.toString() : '';
                        if (existing && existing.length > 200000) {
                            this.aiMarketingState.result = null;
                            this.aiMarketingState.fullResult = null;
                        }
                        this.currentView = 'webmail';
                        this.aiMarketingState.step = 'generate';
                        this.showAiMarketingModal = true;
                        this.fetchSegments();
                        this.fetchTemplates();
                    },
                    isSelectedMessage(id) {
                        const v = Number(id) || 0;
                        if (!v) return false;
                        return this.selectedMessageIds.includes(v);
                    },
                    toggleMessageSelection(id) {
                        const v = Number(id) || 0;
                        if (!v) return;
                        const idx = this.selectedMessageIds.indexOf(v);
                        if (idx >= 0) {
                            this.selectedMessageIds.splice(idx, 1);
                        } else {
                            this.selectedMessageIds.push(v);
                        }
                        const allIds = this.safeArr(this.messages).map(m => Number(m?.id) || 0).filter(x => x > 0);
                        this.selectAllMessages = (allIds.length > 0) && allIds.every(x => this.selectedMessageIds.includes(x));
                    },
                    toggleSelectAllMessages() {
                        const allIds = this.safeArr(this.messages).map(m => Number(m?.id) || 0).filter(x => x > 0);
                        if (this.selectAllMessages) {
                            this.selectedMessageIds = [];
                            this.selectAllMessages = false;
                        } else {
                            this.selectedMessageIds = [...new Set(allIds)];
                            this.selectAllMessages = true;
                        }
                    },
                    async setMailMessageSeen(msgId, seen, folder = 'INBOX', _retry = false) {
                        try {
                            const id = Number(msgId) || 0;
                            if (!id) return false;
                            if (!await this.ensureMailSession('標記已讀')) return false;
                            await axios.post(`${ADMIN_API}/mail/message/seen`, {
                                msg_id: id,
                                folder: (folder || 'INBOX').toString(),
                                seen: !!seen,
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            return true;
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                const canRetry = this.handleMailAuth401(error, '標記已讀');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('標記已讀');
                                        if (ok) return await this.setMailMessageSeen(msgId, seen, folder, true);
                                    } catch (e) {
                                    }
                                }
                                return false;
                            }
                            throw error;
                        }
                    },
                    async onMailMessageRowClick(msgId, ev = null) {
                        const id = Number(msgId) || 0;
                        if (!id) return;
                        try {
                            if (ev && ev.shiftKey) {
                                try { ev.preventDefault(); } catch (e) {}
                                try { ev.stopPropagation(); } catch (e) {}
                                const idx = this.messages.findIndex(m => m && Number(m.id) === id);
                                const wasRead = (idx !== -1 && this.messages[idx]) ? !!this.messages[idx].is_read : false;
                                const nextRead = !wasRead;
                                if (idx !== -1 && this.messages[idx]) this.messages[idx].is_read = nextRead;
                                try {
                                    const ok = await this.setMailMessageSeen(id, nextRead, this.currentFolder);
                                    if (!ok) {
                                        if (idx !== -1 && this.messages[idx]) this.messages[idx].is_read = wasRead;
                                    }
                                } catch (e) {
                                    if (idx !== -1 && this.messages[idx]) this.messages[idx].is_read = wasRead;
                                    _showToast('標記失敗: ' + (e.response?.data?.detail || e.message));
                                }
                                return;
                            }
                        } catch (e) {
                        }
                        return await this.openMessage(id);
                    },
                    async batchSetSelectedMessagesSeen(seen, _retry = false) {
                        if (!this.selectedMessageIds.length) return;
                        const ids = [...new Set(this.selectedMessageIds.map(x => Number(x) || 0).filter(x => x > 0))].slice(0, 200);
                        if (!ids.length) return;
                        const map = new Map();
                        for (const m of this.safeArr(this.messages)) {
                            const mid = Number(m?.id) || 0;
                            if (mid && ids.includes(mid)) map.set(mid, !!m.is_read);
                        }
                        try {
                            this.webmailBatchSeenLoading = true;
                            for (const m of this.safeArr(this.messages)) {
                                const mid = Number(m?.id) || 0;
                                if (mid && ids.includes(mid)) m.is_read = !!seen;
                            }
                            const ok = await this.ensureMailSession('批次標記已讀');
                            if (!ok) return;
                            const res = await axios.post(`${ADMIN_API}/mail/messages/seen_batch`, {
                                msg_ids: ids,
                                folder: (this.currentFolder || 'INBOX').toString(),
                                seen: !!seen,
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const updated = Number(res?.data?.updated || 0);
                            const failed = Number(res?.data?.failed || 0);
                            const errors = Array.isArray(res?.data?.errors) ? res.data.errors : [];
                            if (errors.length) {
                                const bad = new Set(errors.map(e => Number(e?.msg_id) || 0).filter(x => x > 0));
                                for (const m of this.safeArr(this.messages)) {
                                    const mid = Number(m?.id) || 0;
                                    if (!mid) continue;
                                    if (bad.has(mid) && map.has(mid)) m.is_read = map.get(mid);
                                }
                            }
                            this.selectedMessageIds = [];
                            this.selectAllMessages = false;
                            _showToast(`已更新 ${updated} 筆${failed ? `，失敗 ${failed} 筆` : ''}`);
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                const canRetry = this.handleMailAuth401(error, '批次標記已讀');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('批次標記已讀');
                                        if (ok) return await this.batchSetSelectedMessagesSeen(seen, true);
                                    } catch (e) {
                                    }
                                }
                                return;
                            }
                            for (const m of this.safeArr(this.messages)) {
                                const mid = Number(m?.id) || 0;
                                if (!mid) continue;
                                if (map.has(mid)) m.is_read = map.get(mid);
                            }
                            _showToast('批次標記失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.webmailBatchSeenLoading = false;
                        }
                    },
                    async batchImportInvoicesFromSelectedMessages(_retry = false, ev = null, markReadOverride = null) {
                        let markRead = false;
                        const ids = [...new Set(this.selectedMessageIds.map(x => Number(x) || 0).filter(x => x > 0))].slice(0, 200);
                        if (!ids.length) return;
                        try {
                            if (!await this.ensureMailSession('批次匯入發票')) return;
                            markRead = (markReadOverride === null || markReadOverride === undefined) ? !!(ev && ev.shiftKey) : !!markReadOverride;
                            const ok = confirm(`將從所選 ${ids.length} 封郵件中批次匯入 PDF/圖片發票附件${markRead ? '（匯入後標記已讀）' : ''}，確定要繼續嗎？`);
                            if (!ok) return;
                            this.webmailBatchInvoiceImportLoading = true;
                            const res = await axios.post(`${ADMIN_API}/mail/messages/invoice_import`, {
                                msg_ids: ids,
                                folder: (this.currentFolder || 'INBOX').toString(),
                                max_imports: 60,
                                mark_read: markRead,
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const imported = Array.isArray(res?.data?.imported) ? res.data.imported : [];
                            try {
                                for (const it of imported) {
                                    this.invoiceRememberImported(it);
                                }
                            } catch (e) {}
                            const importedCount = Number(res?.data?.imported_count || 0);
                            const dup = Number(res?.data?.duplicates || 0);
                            const newCount = Math.max(0, importedCount - dup);
                            const skippedUnsupported = Number(res?.data?.skipped_unsupported || 0);
                            const skippedLarge = Number(res?.data?.skipped_too_large || 0);
                            const errors = Array.isArray(res?.data?.errors) ? res.data.errors : [];
                            const checkedAttachments = Number(res?.data?.checked_attachments || 0);
                            const scannedMessages = Number(res?.data?.scanned_messages || 0);
                            const lastInvoiceId = Number(res?.data?.last_invoice_id || 0);
                            const markedReadIds = Array.isArray(res?.data?.marked_read_msg_ids) ? res.data.marked_read_msg_ids : [];
                            if (markRead && markedReadIds.length) {
                                const set = new Set(markedReadIds.map(x => Number(x) || 0).filter(x => x > 0));
                                for (const m of this.safeArr(this.messages)) {
                                    const mid = Number(m?.id) || 0;
                                    if (mid && set.has(mid)) m.is_read = true;
                                }
                                try {
                                    const curId = Number(this.currentMessage?.id) || 0;
                                    if (curId && set.has(curId) && this.currentMessage) this.currentMessage.is_read = true;
                                } catch (e) {}
                            }
                            this.selectedMessageIds = [];
                            this.selectAllMessages = false;
                            if (lastInvoiceId) {
                                await this.openInvoiceDriveView();
                                await this.invoiceDriveOpenDetail(lastInvoiceId);
                            }
                            const parts = [];
                            parts.push(`新增 ${newCount} 筆`);
                            if (dup) parts.push(`重複 ${dup} 筆`);
                            if (scannedMessages) parts.push(`掃描 ${scannedMessages} 封`);
                            if (checkedAttachments) parts.push(`檢查 ${checkedAttachments} 個附件`);
                            if (skippedUnsupported) parts.push(`不支援 ${skippedUnsupported} 筆`);
                            if (skippedLarge) parts.push(`過大略過 ${skippedLarge} 筆`);
                            if (errors.length) parts.push(`錯誤 ${errors.length} 筆`);
                            _showToast(parts.join(' / '));
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                const canRetry = this.handleMailAuth401(error, '批次匯入發票');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('批次匯入發票');
                                        if (ok) return await this.batchImportInvoicesFromSelectedMessages(true, ev, markRead);
                                    } catch (e) {
                                    }
                                }
                                return;
                            }
                            _showToast('批次匯入失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.webmailBatchInvoiceImportLoading = false;
                        }
                    },
                    async batchMoveSelectedMessages(_retry = false) {
                        const ids = [...new Set(this.selectedMessageIds.map(x => Number(x) || 0).filter(x => x > 0))].slice(0, 200);
                        if (!ids.length) return;
                        const sourceFolder = (this.currentFolder || 'INBOX').toString();
                        let targetFolder = '';
                        try {
                            const suggested = (sourceFolder === 'INBOX') ? 'Archive' : 'INBOX';
                            targetFolder = (prompt(`要將所選 ${ids.length} 封郵件從「${sourceFolder}」移到哪個資料夾？\n例：Archive / Trash / Junk / INBOX / Sent`, suggested) || '').toString().trim();
                        } catch (e) {
                            targetFolder = '';
                        }
                        if (!targetFolder) return;
                        if (targetFolder === sourceFolder) {
                            _showToast('目標資料夾不能與來源相同');
                            return;
                        }
                        try {
                            this.webmailBatchMoveLoading = true;
                            if (!await this.ensureMailSession('批次移動郵件')) return;
                            const res = await axios.post(`${ADMIN_API}/mail/move_batch`, {
                                msg_ids: ids,
                                source_folder: sourceFolder,
                                target_folder: targetFolder,
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const moved = Number(res?.data?.moved || 0);
                            const failed = Number(res?.data?.failed || 0);
                            const errors = Array.isArray(res?.data?.errors) ? res.data.errors : [];
                            const bad = new Set(errors.map(e => Number(e?.msg_id) || 0).filter(x => x > 0));
                            const movedIds = ids.filter(x => !bad.has(x));
                            if (movedIds.length) {
                                const set = new Set(movedIds);
                                this.messages = this.safeArr(this.messages).filter(m => {
                                    const mid = Number(m?.id) || 0;
                                    return !mid || !set.has(mid);
                                });
                                this.mailTotal = Math.max(0, (Number(this.mailTotal) || 0) - movedIds.length);
                            }
                            this.selectedMessageIds = [];
                            this.selectAllMessages = false;
                            _showToast(`已移動 ${moved} 封${failed ? `，失敗 ${failed} 封` : ''}`);
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                const canRetry = this.handleMailAuth401(error, '批次移動郵件');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('批次移動郵件');
                                        if (ok) return await this.batchMoveSelectedMessages(true);
                                    } catch (e) {
                                    }
                                }
                                return;
                            }
                            _showToast('批次移動失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.webmailBatchMoveLoading = false;
                        }
                    },
                    async batchDeleteSelectedMessages(_retry = false) {
                        const ids = [...new Set(this.selectedMessageIds.map(x => Number(x) || 0).filter(x => x > 0))].slice(0, 200);
                        if (!ids.length) return;
                        const folder = (this.currentFolder || 'INBOX').toString();
                        const ok = confirm(`確定要刪除所選 ${ids.length} 封郵件嗎？\n（將移到 Trash；若目前就在 Trash/Junk 可能會永久刪除）`);
                        if (!ok) return;
                        try {
                            this.webmailBatchDeleteLoading = true;
                            if (!await this.ensureMailSession('批次刪除郵件')) return;
                            const res = await axios.post(`${ADMIN_API}/mail/delete_batch`, {
                                msg_ids: ids,
                                folder,
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const deleted = Number(res?.data?.deleted || 0);
                            const failed = Number(res?.data?.failed || 0);
                            const errors = Array.isArray(res?.data?.errors) ? res.data.errors : [];
                            const bad = new Set(errors.map(e => Number(e?.msg_id) || 0).filter(x => x > 0));
                            const deletedIds = ids.filter(x => !bad.has(x));
                            if (deletedIds.length) {
                                const set = new Set(deletedIds);
                                this.messages = this.safeArr(this.messages).filter(m => {
                                    const mid = Number(m?.id) || 0;
                                    return !mid || !set.has(mid);
                                });
                                this.mailTotal = Math.max(0, (Number(this.mailTotal) || 0) - deletedIds.length);
                            }
                            this.selectedMessageIds = [];
                            this.selectAllMessages = false;
                            _showToast(`已刪除 ${deleted} 封${failed ? `，失敗 ${failed} 封` : ''}`);
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                const canRetry = this.handleMailAuth401(error, '批次刪除郵件');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('批次刪除郵件');
                                        if (ok) return await this.batchDeleteSelectedMessages(true);
                                    } catch (e) {
                                    }
                                }
                                return;
                            }
                            _showToast('批次刪除失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.webmailBatchDeleteLoading = false;
                        }
                    },
                    async batchAnalyzeCustomerBackground() {
                        if (!this.selectedMessageIds.length) return;
                        try {
                            const ok = await this.ensureMailSession('批次 CBI');
                            if (!ok) return;
                            const items = [];
                            for (const id of this.selectedMessageIds.slice(0, 50)) {
                                try {
                                    const res = await axios.get(`${ADMIN_API}/mail/message/${id}?folder=${this.currentFolder}`, {
                                        headers: { 'X-Mail-Session': this.mailSessionToken }
                                    });
                                    const msg = res?.data || {};
                                    items.push({
                                        subject: (msg?.subject || '').toString(),
                                        sender: (msg?.from || '').toString(),
                                        body_text: (msg?.body_text || '').toString(),
                                        body_html: (msg?.body_html || '').toString(),
                                        folder: this.currentFolder,
                                        imap_id: id,
                                        message_id: (msg?.message_id || null)
                                    });
                                } catch (e) {}
                            }
                            if (!items.length) {
                                _showToast('沒有可分析的郵件');
                                return;
                            }
                            const res = await axios.post(`${ADMIN_API}/ai/customer/background/batch`, { items });
                            const created = Number(res?.data?.created) || 0;
                            this.selectedMessageIds = [];
                            this.selectAllMessages = false;
                            _showToast(`已完成批次分析，共 ${created} 筆。`);
                            this.openCustomerBackgroundList();
                        } catch (error) {
                            _showToast('批次分析失敗: ' + (error.response?.data?.detail || error.message));
                        }
                    },
                    async fetchSegments() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/marketing/segments`);
                            this.aiMarketingState.segments = res.data;
                        } catch (e) {
                            window.$toast?.error('載入 segments 失敗', e?.response?.data?.detail || e?.message || '');
                            console.error("Failed to fetch segments", e);
                        }
                    },
                    async fetchTemplates() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/marketing/templates`);
                            const arr = Array.isArray(res.data) ? res.data : [];
                            if (arr.length) {
                                this.aiMarketingState.templates = arr;
                            }
                        } catch (e) {
                            window.$toast?.error('載入 templates 失敗', e?.response?.data?.detail || e?.message || '');
                            console.error("Failed to fetch templates", e);
                        }
                    },
                    loadTemplate(event) {
                        const templateId = event.target.value;
                        if (!templateId) return;
                        const template = this.aiMarketingState.templates.find(t => t.id == templateId);
                        if (template) {
                            if (confirm('是否載入此範本？這將會覆蓋目前的生成結果。')) {
                                this.aiMarketingState.result = template.content_html;
                                this.aiMarketingState.fullResult = { subject: template.subject };
                            }
                        }
                    },
                    async selectSegment(seg) {
                        this.aiMarketingState.selectedSegment = seg.id;
                        try {
                            const res = await axios.get(`${ADMIN_API}/marketing/segments/${seg.id}/contacts`);
                            const contacts = Array.isArray(res.data) ? res.data : [];
                            const byEmail = new Map();
                            for (const c of contacts) {
                                const email = (c?.email || '').trim();
                                if (!email) continue;
                                const name = (c?.name || '').trim();
                                const key = email.toLowerCase();
                                if (!byEmail.has(key)) {
                                    byEmail.set(key, name ? `${email}, ${name}` : email);
                                }
                            }
                            this.campaignForm.recipients = Array.from(byEmail.values()).join('\n');
                        } catch (e) {
                            window.$toast?.error('載入 segment contacts 失敗', e?.response?.data?.detail || e?.message || '');
                            console.error("Failed to fetch segment contacts", e);
                            _showToast("無法載入分群聯絡人");
                        }
                    },
                    openMarketingJourney() {
                        this.currentView = 'webmail';
                        this.aiMarketingState.step = 'journey';
                        this.showAiMarketingModal = true;
                        this.fetchSegments();
                        this.fetchJourneys();
                    },
                    async fetchJourneys() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/journey`);
                            this.aiMarketingState.journeys = (res.data || []).map(j => ({
                                ...j,
                                _stats: null,
                                _selectedSegment: ''
                            }));
                            await this.fetchJourneyStats();
                        } catch (e) {
                            window.$toast?.error('載入 journeys 失敗', e?.response?.data?.detail || e?.message || '');
                            console.error("Failed to fetch journeys", e);
                        }
                    },
                    async fetchJourneyStats() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/journey/stats/summary`);
                            const statsMap = res.data || {};
                            for (const j of this.aiMarketingState.journeys) {
                                j._stats = statsMap[j.id] || null;
                            }
                        } catch (e) {
                            window.$toast?.error('載入 journey stats 失敗', e?.response?.data?.detail || e?.message || '');
                            console.error("Failed to fetch journey stats", e);
                        }
                    },
                    async openJourneyFunnel(journey) {
                        if (!journey || !journey.id) return;
                        this.selectedJourneyForFunnel = journey;
                        this.journeyFunnelFilters = { segment_id: '', days: 7 };
                        this.showJourneyFunnelModal = true;
                        await this.fetchJourneyFunnel(journey.id);
                    },
                    async fetchJourneyFunnel(journeyId) {
                        if (!journeyId) return;
                        this.journeyFunnelLoading = true;
                        try {
                            const params = { journey_id: journeyId };
                            if (this.journeyFunnelFilters?.segment_id) params.segment_id = this.journeyFunnelFilters.segment_id;
                            if (typeof this.journeyFunnelFilters?.days === 'number') params.days = this.journeyFunnelFilters.days;
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/journey/stats/funnel`, { params });
                            const data = res.data || {};
                            this.journeyFunnel = data[journeyId] || null;
                        } catch (e) {
                            this.journeyFunnel = null;
                            _showToast('載入漏斗失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.journeyFunnelLoading = false;
                        }
                    },
                    async enrollJourneyFromSegment(journey) {
                        if (!journey || !journey.id) return;
                        if (!journey._selectedSegment) return _showToast('請先選擇一個分眾');
                        try {
                            const contactsRes = await axios.get(`${ADMIN_API}/marketing/segments/${journey._selectedSegment}/contacts`);
                            const contactIds = (contactsRes.data || []).map(c => c.id).filter(Boolean);
                            if (contactIds.length === 0) return _showToast('此分眾沒有聯絡人');

                            const enrollRes = await axios.post(`${ADMIN_API}/ai/marketing/journey/${journey.id}/enroll`, contactIds);
                            _showToast(`已加入旅程：${enrollRes.data.enrolled} 人（略過：${enrollRes.data.skipped} 人）`);
                            await this.fetchJourneyStats();
                        } catch (e) {
                            _showToast('加入失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async runJourneyNow(journey) {
                        if (!journey || !journey.id) return;
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/journey/${journey.id}/run_now?force=true`);
                            _showToast(`已執行處理：${res.data.processed} 筆`);
                            await this.fetchJourneyStats();
                        } catch (e) {
                            _showToast('執行失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async toggleJourneyStatus(journey) {
                        if (!journey || !journey.id) return;
                        const nextStatus = journey.status === 'active' ? 'paused' : 'active';
                        try {
                            await axios.put(`${ADMIN_API}/ai/marketing/journey/${journey.id}`, { status: nextStatus });
                            journey.status = nextStatus;
                            await this.fetchJourneyStats();
                        } catch (e) {
                            _showToast('更新狀態失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async deleteJourney(journey) {
                        if (!journey || !journey.id) return;
                        if (!confirm(`確定刪除旅程「${journey.name}」？此操作無法復原。`)) return;
                        try {
                            await axios.delete(`${ADMIN_API}/ai/marketing/journey/${journey.id}`);
                            await this.fetchJourneys();
                        } catch (e) {
                            _showToast('刪除失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async openJourneyEnrollments(journey) {
                        if (!journey || !journey.id) return;
                        this.selectedJourneyForEnrollments = journey;
                        this.showJourneyEnrollmentsModal = true;
                        await this.fetchJourneyEnrollments(journey.id);
                    },
                    async fetchJourneyEnrollments(journeyId) {
                        this.journeyEnrollmentsLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/journey/${journeyId}/enrollments`);
                            this.journeyEnrollments = res.data || [];
                        } catch (e) {
                            this.journeyEnrollments = [];
                            _showToast('載入參與者失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.journeyEnrollmentsLoading = false;
                        }
                    },
                    openJourneyEditor(journey) {
                        const defaultSender = this.currentUser?.email || '';
                        if (!journey) {
                            this.journeyEditorForm = {
                                id: null,
                                name: '',
                                description: '',
                                status: 'draft',
                                trigger_type: 'manual',
                                trigger_tag: '',
                                optimize_send_time: false,
                                sender: defaultSender,
                                sender_password: '',
                                steps: [
                                    {
                                        step_order: 1,
                                        delay_hours: 0,
                                        action_type: 'email',
                                        condition_type: 'always',
                                        condition_arg: '',
                                        subject_template: '',
                                        body_template: ''
                                    }
                                ]
                            };
                        } else {
                            const steps = (journey.steps || [])
                                .slice()
                                .sort((a, b) => (a.step_order || 0) - (b.step_order || 0))
                                .map(s => ({
                                    step_order: s.step_order,
                                    delay_hours: s.delay_hours ?? 24,
                                    action_type: s.action_type || 'email',
                                    condition_type: s.condition_type || 'always',
                                    condition_arg: s.condition_arg || '',
                                    subject_template: s.subject_template || '',
                                    body_template: s.body_template || ''
                                }));
                            this.journeyEditorForm = {
                                id: journey.id,
                                name: journey.name || '',
                                description: journey.description || '',
                                status: journey.status || 'draft',
                                trigger_type: journey.trigger_type || 'manual',
                                trigger_tag: journey.trigger_tag || '',
                                optimize_send_time: !!journey.optimize_send_time,
                                sender: journey.sender || defaultSender,
                                sender_password: journey.sender_password || '',
                                steps: steps.length ? steps : [
                                    {
                                        step_order: 1,
                                        delay_hours: 0,
                                        action_type: 'email',
                                        condition_type: 'always',
                                        condition_arg: '',
                                        subject_template: '',
                                        body_template: ''
                                    }
                                ]
                            };
                        }
                        this.journeyEditorSaving = false;
                        this.showJourneyEditorModal = true;
                    },
                    normalizeJourneySteps() {
                        if (!this.journeyEditorForm?.steps) return;
                        this.journeyEditorForm.steps.forEach((s, idx) => {
                            s.step_order = idx + 1;
                            if (s.delay_hours === '' || s.delay_hours === null || s.delay_hours === undefined) s.delay_hours = 0;
                        });
                    },
                    addJourneyStep() {
                        if (!this.journeyEditorForm) return;
                        const nextOrder = (this.journeyEditorForm.steps?.length || 0) + 1;
                        this.journeyEditorForm.steps.push({
                            step_order: nextOrder,
                            delay_hours: 24,
                            action_type: 'email',
                            condition_type: 'always',
                            condition_arg: '',
                            subject_template: '',
                            body_template: ''
                        });
                        this.normalizeJourneySteps();
                    },
                    removeJourneyStep(index) {
                        if (!this.journeyEditorForm?.steps) return;
                        this.journeyEditorForm.steps.splice(index, 1);
                        if (this.journeyEditorForm.steps.length === 0) {
                            this.journeyEditorForm.steps.push({
                                step_order: 1,
                                delay_hours: 0,
                                action_type: 'email',
                                condition_type: 'always',
                                condition_arg: '',
                                subject_template: '',
                                body_template: ''
                            });
                        }
                        this.normalizeJourneySteps();
                    },
                    async saveJourneyEditor() {
                        if (!this.journeyEditorForm) return;
                        if (!this.journeyEditorForm.name) return _showToast('請輸入旅程名稱');
                        this.normalizeJourneySteps();
                        this.journeyEditorSaving = true;
                        try {
                            const steps = (this.journeyEditorForm.steps || []).map(s => ({
                                step_order: s.step_order,
                                delay_hours: Number.isFinite(s.delay_hours) ? s.delay_hours : 0,
                                action_type: s.action_type || 'email',
                                condition_type: s.condition_type || 'always',
                                condition_arg: s.condition_arg || null,
                                subject_template: s.subject_template || null,
                                body_template: s.body_template || null
                            }));
                            const journeyPayload = {
                                name: this.journeyEditorForm.name,
                                description: this.journeyEditorForm.description || null,
                                status: this.journeyEditorForm.status || 'draft',
                                trigger_type: this.journeyEditorForm.trigger_type || 'manual',
                                trigger_tag: this.journeyEditorForm.trigger_tag || null,
                                sender: this.journeyEditorForm.sender || null,
                                sender_password: this.journeyEditorForm.sender_password || null,
                                optimize_send_time: !!this.journeyEditorForm.optimize_send_time,
                                steps
                            };
                            if (this.journeyEditorForm.id) {
                                await axios.put(`${ADMIN_API}/ai/marketing/journey/${this.journeyEditorForm.id}`, journeyPayload);
                            } else {
                                await axios.post(`${ADMIN_API}/ai/marketing/journey/`, journeyPayload);
                            }
                            this.showJourneyEditorModal = false;
                            this.journeyEditorForm = null;
                            await this.fetchJourneys();
                        } catch (e) {
                            _showToast('儲存失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.journeyEditorSaving = false;
                        }
                    },
                    async createDemoJourney() {
                        try {
                            await axios.post(`${ADMIN_API}/ai/marketing/journey/demo`);
                            _showToast('示範旅程建立成功！');
                            this.fetchJourneys();
                        } catch (e) {
                            _showToast('建立失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async generateSocialContent() {
                        if (!this.aiSocialState.form.product_name) return _showToast('請輸入產品名稱');
                        this.aiSocialState.loading = true;
                        this.aiSocialState.result = null;
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/social`, this.aiSocialState.form);
                            const data = res?.data || {};
                            if (typeof data.result === 'string' && data.result.trim()) {
                                this.aiSocialState.result = data.result;
                            } else if (data.results && typeof data.results === 'object') {
                                const platforms = Array.isArray(this.aiSocialState.form.platforms) ? this.aiSocialState.form.platforms : [];
                                const ordered = platforms.length ? platforms : Object.keys(data.results);
                                const blocks = [];
                                for (const p of ordered) {
                                    const v = data.results[p];
                                    if (typeof v === 'string') {
                                        blocks.push(`【${p}】\n${v}`.trim());
                                    } else if (v != null) {
                                        try {
                                            blocks.push(`【${p}】\n${JSON.stringify(v, null, 2)}`.trim());
                                        } catch (e) {
                                            blocks.push(`【${p}】\n${String(v)}`.trim());
                                        }
                                    }
                                }
                                this.aiSocialState.result = blocks.join('\n\n').trim();
                            } else {
                                this.aiSocialState.result = JSON.stringify(data, null, 2);
                            }
                        } catch (e) {
                            _showToast('生成失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.aiSocialState.loading = false;
                        }
                    },

                    async generateSmartDraft() {
                        this.aiLoading = true;
                        try {
                            const response = await axios.post(`${ADMIN_API}/ai/draft`, {
                                text: this.aiDraftForm.instructions,
                                tone: this.aiDraftForm.tone
                            });

                            let result = response.data.result || '';
                            let subject = '無主旨';

                            // Simple parsing for "Subject: ..." format
                            const lines = result.split('\n');
                            if (lines.length > 0 && lines[0].trim().startsWith('Subject:')) {
                                subject = lines[0].replace('Subject:', '').trim();
                                // Remove first line and trim empty lines
                                result = lines.slice(1).join('\n').trim();
                            }

                            this.composeForm.subject = subject;
                            this.composeForm.html_body = '';
                            this.composeForm.body = result;
                            this.composeEditorMode = 'rich';
                            this.showAiDraftModal = false;
                            this.composeAttachmentSources = (this.composeForm.attachments || []).map(() => ({ type: 'upload' }));
                            this.composeResourceSearch = '';
                            this.composeResourceAttachments = [];
                            this.composeResourceErrors = {};
                            this.composeResourceErrorMeta = {};
                            this.composeResourceErrorExpanded = {};
                            this.composeResourceValidating = {};
                            this.composeResourceValidateQueue = [];
                            if (this.composeResourceValidateTimer) clearTimeout(this.composeResourceValidateTimer);
                            this.composeResourceValidateTimer = null;
                            this.composeResourceBanner = '';
                            this.composeRecommendLoading = false;
                            this.composeRecommendedResources = [];
                            this.composeRecommendError = '';
                            this.composeKbQueryInput = '';
                            this.composeKbLoading = false;
                            this.composeKbResult = '';
                            this.composeKbSources = [];
                            this.composeKbError = '';
                            this.composeAiReplySources = [];
                            this.showComposeModal = true;
                            this.ensureComposeResourcePoolLoaded();
                        } catch (error) {
                            _showToast('生成草稿失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.aiLoading = false;
                        }
                    },
                    async generateSmartReply() {
                        this.aiLoading = true;
                        try {
                            this.composeForm.to = this.extractEmail(this.currentMessage.from);
                            const defaultSubject = this.currentMessage.subject.startsWith('Re:') ? this.currentMessage.subject : 'Re: ' + this.currentMessage.subject;

                            if (this.aiReplyForm.tone === 'multi-tone') {
                                const response = await axios.post(`${ADMIN_API}/ai/reply`, {
                                    text: (this.currentMessage.body || '') + '\n' + (this.aiReplyForm.instructions || ''),
                                    tone: this.aiReplyForm.tone
                                });

                                this.composeForm.subject = defaultSubject;
                                this.composeForm.html_body = '';

                                let result = response.data.result || '';
                                if (typeof result === 'string' && (result.trim().startsWith('[') || result.trim().startsWith('{'))) {
                                    try {
                                        const parsed = JSON.parse(result);
                                        if (Array.isArray(parsed)) {
                                            result = parsed.map(p => (p.title ? `[${p.title}]\n` : '') + (p.content || p)).join('\n\n---\n\n');
                                        } else if (parsed.content) {
                                            result = parsed.content;
                                        }
                                    } catch (e) { }
                                }
                                this.composeForm.body = result;
                                this.composeEditorMode = 'rich';
                            } else {
                                const toneToTemplate = {
                                    professional: 'standard_reply',
                                    friendly: 'standard_reply',
                                    concise: 'acknowledge_and_confirm',
                                    refusal: 'polite_decline',
                                };

                                const response = await axios.post(`${ADMIN_API}/ai/reply_draft_with_context`, {
                                    email_body: this.stripHtml(this.currentMessage.body || ''),
                                    original_subject: this.currentMessage.subject || '',
                                    sender: this.currentMessage.from || '',
                                    instructions: this.aiReplyForm.instructions || '',
                                    tone: this.aiReplyForm.tone || 'professional',
                                    language: this.aiReplyForm.language || 'auto',
                                    length: this.aiReplyForm.length || 'medium',
                                    template: this.aiReplyForm.template || toneToTemplate[this.aiReplyForm.tone] || 'standard_reply',
                                    include_quote: !!this.aiReplyForm.include_quote
                                });

                                this.composeForm.subject = response.data.subject || defaultSubject;
                                const rawBody = (response.data.body || '').toString();
                                const isHtml = /<[a-z][\s\S]*>/i.test(rawBody);
                                this.composeForm.html_body = isHtml ? rawBody : '';
                                this.composeForm.body = isHtml ? this.stripHtml(rawBody) : rawBody;
                                this.composeEditorMode = 'rich';
                                const sources = response.data?.sources || [];
                                this.composeAiReplySources = Array.isArray(sources) ? sources : [];
                            }

                            // Set threading headers
                            this.composeForm.in_reply_to = this.currentMessage.message_id;
                            const oldRefs = this.currentMessage.references || '';
                            this.composeForm.references = oldRefs ? (oldRefs + ' ' + this.currentMessage.message_id) : this.currentMessage.message_id;

                            this.showAiReplyModal = false;
                            this.composeAttachmentSources = (this.composeForm.attachments || []).map(() => ({ type: 'upload' }));
                            this.composeResourceSearch = '';
                            this.composeResourceAttachments = [];
                            this.composeResourceErrors = {};
                            this.composeResourceErrorMeta = {};
                            this.composeResourceErrorExpanded = {};
                            this.composeResourceValidating = {};
                            this.composeResourceValidateQueue = [];
                            if (this.composeResourceValidateTimer) clearTimeout(this.composeResourceValidateTimer);
                            this.composeResourceValidateTimer = null;
                            this.composeResourceBanner = '';
                            this.composeRecommendLoading = false;
                            this.composeRecommendedResources = [];
                            this.composeRecommendError = '';
                            this.composeKbQueryInput = '';
                            this.composeKbLoading = false;
                            this.composeKbResult = '';
                            this.composeKbSources = [];
                            this.composeKbError = '';
                            this.showComposeModal = true;
                            this.ensureComposeResourcePoolLoaded();
                        } catch (error) {
                            _showToast('生成回覆失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.aiLoading = false;
                        }
                    },
                    async generateSmartRewrite() {
                        if (!this.composeForm.body) {
                            _showToast('請先輸入要潤飾的內容');
                            this.showAiRewriteModal = false;
                            return;
                        }
                        this.aiLoading = true;
                        try {
                            const response = await axios.post(`${ADMIN_API}/ai/rewrite`, {
                                text: this.composeForm.body,
                                tone: this.aiRewriteForm.tone
                            });

                            this.composeForm.html_body = '';
                            this.composeForm.body = response.data.result || response.data.rewritten_text || '';
                            this.showAiRewriteModal = false;
                        } catch (error) {
                            _showToast('潤飾失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.aiLoading = false;
                        }
                    },
                    async generateSmartTranslate() {
                        if (!this.currentMessage) return;
                        this.aiLoading = true;
                        this.aiTranslateResult = ''; // Clear previous result
                        try {
                            const response = await axios.post(`${ADMIN_API}/ai/translate`, {
                                text: this.stripHtml(this.currentMessage.body),
                                target_language: this.aiTranslateForm.target_language
                            });
                            this.aiTranslateResult = response.data.result || response.data.translated_text || '';
                        } catch (error) {
                            _showToast('翻譯失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.aiLoading = false;
                        }
                    },
                    async generateSmartSummary() {
                        if (!this.currentMessage) return;
                        this.aiLoading = true;
                        this.showAiSummaryModal = true;
                        this.aiSummaryResult = '生成中...';
                        try {
                            const response = await axios.post(`${ADMIN_API}/ai/summary`, {
                                text: this.stripHtml(this.currentMessage.body)
                            });
                            this.aiSummaryResult = response.data.result || response.data.summary || '';
                        } catch (error) {
                            this.aiSummaryResult = '摘要生成失敗: ' + (error.response?.data?.detail || error.message);
                        } finally {
                            this.aiLoading = false;
                        }
                    },
                    async generateSmartAnalyze() {
                        if (!this.currentMessage) return;
                        this.aiLoading = true;
                        this.showAiAnalyzeModal = true;
                        this.aiAnalyzeResult = null;
                        try {
                            const response = await axios.post(`${ADMIN_API}/ai/analyze`, {
                                subject: this.currentMessage.subject,
                                body: this.stripHtml(this.currentMessage.body),
                                sender: this.currentMessage.from
                            });

                            let result = response.data.result || '';
                            // Try to parse JSON if it's a string
                            if (typeof result === 'string') {
                                try {
                                    // Extract JSON from markdown block if present
                                    const jsonMatch = result.match(/```json\n([\s\S]*?)\n```/) || result.match(/\{[\s\S]*\}/);
                                    if (jsonMatch) {
                                        result = JSON.parse(jsonMatch[1] || jsonMatch[0]);
                                    } else {
                                        // Fallback object
                                        result = { category: 'Unknown', priority: 'Medium', spam_score: 0, explanation: result };
                                    }
                                } catch (e) {
                                    result = { category: 'Unknown', priority: 'Medium', spam_score: 0, explanation: result };
                                }
                            }
                            this.aiAnalyzeResult = result;
                        } catch (error) {
                            _showToast('分析失敗: ' + (error.response?.data?.detail || error.message));
                            this.showAiAnalyzeModal = false;
                        } finally {
                            this.aiLoading = false;
                        }
                    },
                    async generateCustomerBackground() {
                        if (!this.currentMessage) return;
                        this.aiLoading = true;
                        this.showAiCustomerModal = true;
                        this.aiCustomerResult = null;
                        try {
                            const actualFolder = (this.currentFolder || 'INBOX');
                            const actualMsgId = this.currentMessage.id;
                            const payload = {
                                subject: this.currentMessage.subject,
                                sender: this.currentMessage.from,
                                body_text: this.stripHtml(this.currentMessage.body),
                                folder: actualFolder,
                                imap_id: (actualMsgId === undefined || actualMsgId === null || actualMsgId === '' || Number.isNaN(Number(actualMsgId))) ? null : Number(actualMsgId),
                                message_id: this.currentMessage.message_id || null,
                                language: 'Traditional Chinese (繁體中文)'
                            };
                            const bases = [ADMIN_API, window.location.origin, ADMIN_API_FALLBACK_8000];
                            const paths = [
                                "/ai/customer/background/analyze",
                                "/ai/customer/backgrounds/analyze",
                                "/ai/customer/background/analyze/"
                            ];
                            let response = null;
                            for (const base of bases) {
                                for (const p of paths) {
                                    try {
                                        response = await axios.post(`${base}${p}`, payload);
                                        if (response && response.status === 200) {
                                            throw { __break: true, response };
                                        }
                                    } catch (err) {
                                        if (err && err.__break) {
                                            response = err.response;
                                            break;
                                        }
                                        const sc = Number(err?.response?.status || 0);
                                        if (sc === 404) {
                                            continue;
                                        } else {
                                            throw err;
                                        }
                                    }
                                }
                                if (response) break;
                            }
                            if (!response) {
                                throw new Error('查無相容分析路徑');
                            }
                            this.aiCustomerResult = response.data || {};
                        } catch (error) {
                            try {
                                const bases2 = [ADMIN_API, window.location.origin, ADMIN_API_FALLBACK_8000];
                                const p2 = "/ai/extract/opportunity";
                                let opp = null;
                                for (const b of bases2) {
                                    try {
                                        opp = await axios.post(`${b}${p2}`, {
                                            subject: this.currentMessage.subject,
                                            body: this.stripHtml(this.currentMessage.body),
                                            sender: this.currentMessage.from,
                                            language: 'Traditional Chinese (繁體中文)'
                                        });
                                        if (opp && opp.status === 200) break;
                                    } catch (e2) {
                                        const sc2 = Number(e2?.response?.status || 0);
                                        if (sc2 === 404) continue;
                                        throw e2;
                                    }
                                }
                                const d = opp?.data || {};
                                const body = this.stripHtml(this.currentMessage.body);
                                const confidence = (typeof d.confidence === 'number') ? d.confidence : 0.2;
                                this.aiCustomerResult = {
                                    company_profile: {
                                        name: (d.company || '') || null,
                                        website: null,
                                        registration: null,
                                        industry: [],
                                        size_estimate: { band: 'unknown', confidence },
                                    },
                                    email_intent: {
                                        type: 'other',
                                        urgency: 'unknown',
                                        budget_hint: (d.budget_text || '') || null,
                                        timeline_hint: (d.timeline || '') || null,
                                    },
                                    requirements_guess: (d.need ? [{
                                        category: 'general_need',
                                        spec_range: null,
                                        rationale: d.need || '',
                                        confidence,
                                        evidence: [{ source: 'email', url: null, snippet: body.slice(0, 500) }]
                                    }] : []),
                                    next_questions: [
                                        '請問您的預算範圍與預計時程？',
                                        '主要使用情境與規格需求有哪些？',
                                    ],
                                    recommended_reply_outline: [
                                        '確認需求與使用情境',
                                        '提供初步建議與規格方案',
                                        '詢問預算與時程以安排後續',
                                    ],
                                    risk_flags: [],
                                    evidence: [{ source: 'email', url: null, snippet: body.slice(0, 500) }],
                                    confidence,
                                };
                            } catch (fallbackErr) {
                                _showToast('分析失敗: ' + (error.response?.data?.detail || error.message));
                                this.showAiCustomerModal = false;
                            }
                        } finally {
                            this.aiLoading = false;
                        }
                    },
                    openCustomerBackgroundList() {
                        this.showCustomerBackgroundListModal = true;
                        this.cbiList = [];
                        this.cbiListTotal = 0;
                        this.cbiSelected = null;
                        this.fetchCustomerBackgroundList();
                    },
                    async fetchCustomerBackgroundList() {
                        this.cbiListLoading = true;
                        try {
                            const params = {};
                            const s = (this.cbiSearch || '').toString().trim();
                            if (s) params.search = s;
                            const sortBy = (this.cbiSortBy || 'created_at').toString();
                            const sortDir = (this.cbiSortDir || 'desc').toString();
                            if (sortBy) params.sort_by = sortBy;
                            if (sortDir) params.sort_dir = sortDir;
                            const status = (this.cbiStatusFilter || '').toString().trim();
                            if (status) params.status = status;
                            const bases = [ADMIN_API, window.location.origin, ADMIN_API_FALLBACK_8000];
                            const paths = [
                                "/ai/customer/background/list",
                                "/ai/customer/backgrounds",
                                "/ai/customer/backgrounds/list",
                                "/ai/customer/background"
                            ];
                            let res = null;
                            for (const base of bases) {
                                for (const p of paths) {
                                    try {
                                        res = await axios.get(`${base}${p}`, { params });
                                        if (res && res.status === 200) {
                                            throw { __break: true, res };
                                        }
                                    } catch (err) {
                                        if (err && err.__break) {
                                            res = err.res;
                                            break;
                                        }
                                        const sc = Number(err?.response?.status || 0);
                                        if (sc === 404) {
                                            continue;
                                        } else {
                                            throw err;
                                        }
                                    }
                                }
                                if (res) break;
                            }
                            if (!res) {
                                throw new Error('查無相容路徑');
                            }
                            this.cbiList = res.data?.items || [];
                            this.cbiListTotal = res.data?.total || 0;
                        } catch (error) {
                            _showToast('載入 CBI 列表失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.cbiListLoading = false;
                        }
                    },
                    async viewCustomerBackground(id) {
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/customer/background/${id}`);
                            this.aiCustomerResult = res.data || {};
                            this.showCustomerBackgroundListModal = false;
                            this.showAiCustomerModal = true;
                        } catch (error) {
                            _showToast('載入 CBI 內容失敗: ' + (error.response?.data?.detail || error.message));
                        }
                    },
                    async linkCustomerBackgroundRow(item) {
                        const id = Number(item?.id) || 0;
                        if (!id) return;
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/customer/background/${id}/to_contact`, {});
                            const c = res.data?.contact || null;
                            if (!c || !c.id) throw new Error('Contact not returned');
                            const idx = this.cbiList.findIndex(x => Number(x?.id) === id);
                            if (idx >= 0) {
                                const next = { ...(this.cbiList[idx] || {}), contact_id: c.id };
                                this.cbiList.splice(idx, 1, next);
                            }
                            _showToast('已建立/更新聯絡人');
                        } catch (error) {
                            _showToast('建立/更新聯絡人失敗: ' + (error.response?.data?.detail || error.message));
                        }
                    },
                    async linkCustomerBackgroundToContact() {
                        let id = this.aiCustomerResult?._meta?.id ?? this.aiCustomerResult?.id;
                        // If not persisted, attempt to persist by re-analyzing with multi-base fallback
                        if (!id) {
                            try {
                                const actualFolder = (this.currentFolder || 'INBOX');
                                const actualMsgId = this.currentMessage?.id;
                                const payload = {
                                    subject: this.currentMessage?.subject,
                                    sender: this.currentMessage?.from,
                                    body_text: this.stripHtml(this.currentMessage?.body || ''),
                                    folder: actualFolder,
                                    imap_id: (actualMsgId === undefined || actualMsgId === null || actualMsgId === '' || Number.isNaN(Number(actualMsgId))) ? null : Number(actualMsgId),
                                    message_id: this.currentMessage?.message_id || null,
                                    language: 'Traditional Chinese (繁體中文)'
                                };
                                const bases = [ADMIN_API, window.location.origin, ADMIN_API_FALLBACK_8000];
                                const paths = [
                                    "/ai/customer/background/analyze",
                                    "/ai/customer/backgrounds/analyze",
                                    "/ai/customer/background/analyze/"
                                ];
                                let response = null;
                                for (const base of bases) {
                                    for (const p of paths) {
                                        try {
                                            response = await axios.post(`${base}${p}`, payload);
                                            if (response && response.status === 200) {
                                                throw { __break: true, response };
                                            }
                                        } catch (err) {
                                            if (err && err.__break) {
                                                response = err.response;
                                                break;
                                            }
                                            const sc = Number(err?.response?.status || 0);
                                            if (sc === 404) {
                                                continue;
                                            } else {
                                                throw err;
                                            }
                                        }
                                    }
                                    if (response) break;
                                }
                                if (response && response.data) {
                                    this.aiCustomerResult = response.data || {};
                                    id = this.aiCustomerResult?._meta?.id ?? this.aiCustomerResult?.id;
                                }
                            } catch (e) {
                                // fall through; will alert below
                            }
                            if (!id) {
                                try {
                                    const savePayload = {
                                        subject: this.currentMessage?.subject || null,
                                        sender: this.currentMessage?.from || null,
                                        folder: (this.currentFolder || 'INBOX'),
                                        imap_id: (this.currentMessage?.id === undefined || this.currentMessage?.id === null || this.currentMessage?.id === '' || Number.isNaN(Number(this.currentMessage?.id))) ? null : Number(this.currentMessage?.id),
                                        message_id: this.currentMessage?.message_id || null,
                                        result: (this.aiCustomerResult || null)
                                    };
                                    let newId = 0;
                                    for (const b of [ADMIN_API, window.location.origin, ADMIN_API_FALLBACK_8000]) {
                                        try {
                                            const resSave = await axios.post(`${b}/ai/customer/background/save`, savePayload);
                                            newId = Number(resSave?.data?.id || 0);
                                            if (newId) break;
                                        } catch (e2) {
                                            const sc2 = Number(e2?.response?.status || 0);
                                            if (sc2 === 404) continue;
                                            throw e2;
                                        }
                                    }
                                    if (newId) {
                                        id = newId;
                                        const meta0 = (this.aiCustomerResult && typeof this.aiCustomerResult === 'object') ? (this.aiCustomerResult._meta || {}) : {};
                                        if (this.aiCustomerResult && typeof this.aiCustomerResult === 'object') {
                                            this.aiCustomerResult._meta = { ...meta0, id: newId };
                                        }
                                    }
                                } catch (e2) {}
                            }
                            if (!id) {
                                try {
                                    const senderRaw = (this.currentMessage?.from || this.aiCustomerResult?._meta?.sender_email || '').toString();
                                    const senderEmail = (this.extractEmail(senderRaw) || '').toString().trim();
                                    const nameGuess = (this.aiCustomerResult?.company_profile?.name || '').toString().trim() || (senderRaw || '').toString().replace(/<[^>]+>/g, '').trim();
                                    if (!senderEmail) {
                                        throw new Error('Missing sender email');
                                    }
                                    let created = null;
                                    for (const b of [ADMIN_API, window.location.origin, ADMIN_API_FALLBACK_8000]) {
                                        try {
                                            const resp = await axios.post(`${b}/contacts`, {
                                                name: nameGuess || senderEmail.split('@')[0],
                                                email: senderEmail,
                                                tags: 'cbi'
                                            });
                                            created = resp?.data || null;
                                            if (created && created.id) break;
                                        } catch (e4) {
                                            const sc4 = Number(e4?.response?.status || 0);
                                            if (sc4 === 404) continue;
                                            throw e4;
                                        }
                                    }
                                    if (created && created.id) {
                                        this.showAiCustomerModal = false;
                                        this.currentView = 'contacts';
                                        this.contactsTab = 'contacts';
                                        this.contactFilters.search = created.email || '';
                                        const summary = this.formatCustomerSummary(this.aiCustomerResult || {});
                                        for (const b2 of [ADMIN_API, window.location.origin, ADMIN_API_FALLBACK_8000]) {
                                            try {
                                                await axios.post(`${b2}/sales/crm/note`, { contact_id: parseInt(created.id, 10), note: `CBI：${summary || '—'}（${this.currentMessage?.subject || ''}）` });
                                                break;
                                            } catch (e5) {
                                                const sc5 = Number(e5?.response?.status || 0);
                                                if (sc5 === 404) continue;
                                                break;
                                            }
                                        }
                                        await Promise.allSettled([
                                            this.fetchContactGroups(),
                                            this.fetchContactTags(),
                                            this.fetchUsers(),
                                            this.fetchContacts()
                                        ]);
                                        this.openEditContact(created);
                                        return;
                                    }
                                } catch (fallbackCreateErr) {
                                    _showToast('此分析結果尚未入庫，請先重新分析或從 CBI 列表開啟後再建立聯絡人');
                                    return;
                                }
                            }
                        }
                        this.linkCbiToContactLoading = true;
                        try {
                            let c = null;
                            for (const b of [ADMIN_API, window.location.origin, ADMIN_API_FALLBACK_8000]) {
                                try {
                                    const res = await axios.post(`${b}/ai/customer/background/${id}/to_contact`, {});
                                    c = res.data?.contact || null;
                                    if (c && c.id) break;
                                } catch (e3) {
                                    const sc3 = Number(e3?.response?.status || 0);
                                    if (sc3 === 404) continue;
                                    throw e3;
                                }
                            }
                            if (!c || !c.id) {
                                throw new Error('Contact not returned');
                            }
                            if (this.aiCustomerResult && typeof this.aiCustomerResult === 'object') {
                                const meta = (this.aiCustomerResult._meta && typeof this.aiCustomerResult._meta === 'object') ? this.aiCustomerResult._meta : {};
                                this.aiCustomerResult._meta = { ...meta, contact_id: c.id };
                            }
                            const summary2 = this.formatCustomerSummary(this.aiCustomerResult || {});
                            for (const b2 of [ADMIN_API, window.location.origin, ADMIN_API_FALLBACK_8000]) {
                                try {
                                    await axios.post(`${b2}/sales/crm/note`, { contact_id: parseInt(c.id, 10), note: `CBI：${summary2 || '—'}（${this.currentMessage?.subject || ''}）` });
                                    break;
                                } catch (e6) {
                                    const sc6 = Number(e6?.response?.status || 0);
                                    if (sc6 === 404) continue;
                                    break;
                                }
                            }
                            this.showAiCustomerModal = false;
                            this.currentView = 'contacts';
                            this.contactsTab = 'contacts';
                            this.contactFilters.search = c.email || '';
                            await Promise.allSettled([
                                this.fetchContactGroups(),
                                this.fetchContactTags(),
                                this.fetchUsers(),
                                this.fetchContacts()
                            ]);
                            this.openEditContact(c);
                        } catch (error) {
                            try {
                                const payload = {
                                    subject: this.currentMessage?.subject || null,
                                    sender: this.currentMessage?.from || null,
                                    folder: (this.currentFolder || 'INBOX'),
                                    imap_id: (this.currentMessage?.id === undefined || this.currentMessage?.id === null || this.currentMessage?.id === '' || Number.isNaN(Number(this.currentMessage?.id))) ? null : Number(this.currentMessage?.id),
                                    message_id: this.currentMessage?.message_id || null,
                                    result: (this.aiCustomerResult || null)
                                };
                                const res2 = await axios.post(`${ADMIN_API}/ai/customer/background/save`, payload);
                                const newId = Number(res2?.data?.id || 0);
                                if (!newId) throw new Error('Persist failed');
                                const res3 = await axios.post(`${ADMIN_API}/ai/customer/background/${newId}/to_contact`, {});
                                const c2 = res3?.data?.contact || null;
                                if (!c2 || !c2.id) throw new Error('Contact not returned');
                                this.showAiCustomerModal = false;
                                this.currentView = 'contacts';
                                this.contactsTab = 'contacts';
                                this.contactFilters.search = c2.email || '';
                                await Promise.allSettled([
                                    this.fetchContactGroups(),
                                    this.fetchContactTags(),
                                    this.fetchUsers(),
                                    this.fetchContacts()
                                ]);
                                this.openEditContact(c2);
                            } catch (err2) {
                                _showToast('建立/更新聯絡人失敗: ' + (error.response?.data?.detail || error.message));
                            }
                        } finally {
                            this.linkCbiToContactLoading = false;
                        }
                    },
                    async openContactById(contactId) {
                        const id = parseInt(contactId, 10);
                        if (!id) return;
                        try {
                            this.showCustomerBackgroundListModal = false;
                            this.showAiCustomerModal = false;
                            this.currentView = 'contacts';
                            this.contactsTab = 'contacts';
                            await Promise.allSettled([this.fetchContactGroups(), this.fetchContactTags(), this.fetchUsers()]);
                            const res = await axios.get(`${ADMIN_API}/contacts/${id}`);
                            const c = res.data || null;
                            if (!c || !c.id) {
                                throw new Error('Contact not found');
                            }
                            await this.fetchContacts();
                            this.openEditContact(c);
                        } catch (error) {
                            _showToast('開啟聯絡人失敗: ' + (error.response?.data?.detail || error.message));
                        }
                    },
                    formatCustomerSummary(res) {
                        try {
                            const name = res?.company_profile?.name || '';
                            const website = res?.company_profile?.website || '';
                            const intent = res?.email_intent?.type || '';
                            const urgency = res?.email_intent?.urgency || '';
                            const reqs = (res?.requirements_guess || []).map(r => `${r.category}${r.spec_range ? `（${r.spec_range}）` : ''}`).join('、');
                            const qs = (res?.next_questions || []).join('；');
                            const lines = [
                                name ? `公司：${name}` : '',
                                website ? `網站：${website}` : '',
                                intent ? `意圖：${intent}` : '',
                                urgency ? `急迫度：${urgency}` : '',
                                reqs ? `可能需求：${reqs}` : '',
                                qs ? `建議追問：${qs}` : ''
                            ].filter(Boolean);
                            return lines.join('\n');
                        } catch (e) {
                            return '';
                        }
                    },
                    pickFirstValue(obj, keys) {
                        if (!obj || typeof obj !== 'object') return '';
                        for (const k of keys) {
                            const v = obj?.[k];
                            if (v === null || v === undefined) continue;
                            const s = (typeof v === 'string' ? v : String(v)).trim();
                            if (s) return s;
                        }
                        return '';
                    },
                    parseAiEventFromResponseData(data) {
                        let result = data?.result ?? data?.event ?? data;
                        if (typeof result === 'string') {
                            const s = result.toString();
                            try {
                                const jsonMatch =
                                    s.match(/```json\s*\n([\s\S]*?)\n```/i) ||
                                    s.match(/```[\s\S]*?\n([\s\S]*?)\n```/i) ||
                                    s.match(/\{[\s\S]*\}/) ||
                                    s.match(/\[[\s\S]*\]/);
                                if (jsonMatch) {
                                    result = JSON.parse(jsonMatch[1] || jsonMatch[0]);
                                }
                            } catch (e) {}
                        }

                        if (Array.isArray(result)) {
                            result = result[0] || {};
                        }

                        if (!result || typeof result !== 'object') {
                            const text = (data?.result ?? '').toString().trim() || (typeof data === 'string' ? data : '');
                            return { title: '', start_time: '', end_time: '', location: '', description: text };
                        }

                        const title = this.pickFirstValue(result, ['title', 'summary', 'subject', 'name', '標題', '主題', '名稱']);
                        const startRaw = this.pickFirstValue(result, ['start_time', 'start', 'start_at', '開始時間', '開始', 'date', '日期']);
                        const endRaw = this.pickFirstValue(result, ['end_time', 'end', 'end_at', '結束時間', '結束']);
                        const location = this.pickFirstValue(result, ['location', 'place', 'where', '地點', '地點/連結', '會議地點', 'meeting_location']);
                        const description = this.pickFirstValue(result, ['description', 'details', 'note', 'notes', '內容', '描述', '備註']);
                        return {
                            title,
                            start_time: this.normalizeDateTimeLocal(startRaw),
                            end_time: this.normalizeDateTimeLocal(endRaw),
                            location,
                            description
                        };
                    },
                    async generateSmartEvent() {
                        if (!this.currentMessage) return;
                        this.aiLoading = true;
                        this.calendarSavingFromAi = false;
                        this.showAiEventModal = true;
                        this.aiEventResult = null;
                        this.aiEventContext = 'mail';
                        this.aiEventSourceText = this.stripHtml(this.currentMessage.body);
                        try {
                            const today = new Date().toISOString().split('T')[0];
                            const response = await axios.post(`${ADMIN_API}/ai/event`, {
                                text: this.aiEventSourceText,
                                current_date: today
                            });
                            this.aiEventResult = this.parseAiEventFromResponseData(response.data);
                        } catch (error) {
                            _showToast('事件提取失敗: ' + (error.response?.data?.detail || error.message));
                            this.showAiEventModal = false;
                        } finally {
                            this.aiLoading = false;
                        }
                    },
                    async parseCalendarAiText() {
                        const raw = (this.calendarAiText || '').toString().trim();
                        if (!raw) return;
                        this.calendarAiParsing = true;
                        this.calendarSavingFromAi = false;
                        this.showAiEventModal = true;
                        this.aiEventResult = null;
                        this.aiEventContext = 'calendar_text';
                        this.aiEventSourceText = raw;
                        try {
                            const today = new Date().toISOString().split('T')[0];
                            const response = await axios.post(`${ADMIN_API}/ai/event`, { text: raw, current_date: today });
                            this.aiEventResult = this.parseAiEventFromResponseData(response.data);
                        } catch (e) {
                            _showToast('AI 解析事件失敗: ' + (e.response?.data?.detail || e.message));
                            this.showAiEventModal = false;
                        } finally {
                            this.calendarAiParsing = false;
                        }
                    },
                    async parseCalendarAiTextAndApply() {
                        const raw = (this.calendarAiText || '').toString().trim();
                        if (!raw) return;
                        this.calendarAiParsing = true;
                        this.calendarSavingFromAi = false;
                        this.aiEventContext = 'calendar_text';
                        this.aiEventSourceText = raw;
                        try {
                            const today = new Date().toISOString().split('T')[0];
                            const response = await axios.post(`${ADMIN_API}/ai/event`, { text: raw, current_date: today });
                            this.aiEventResult = this.parseAiEventFromResponseData(response.data);
                            this.applyAiEventToCreateForm();
                        } catch (e) {
                            _showToast('AI 解析事件失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.calendarAiParsing = false;
                        }
                    },
                    applyAiEventToCreateForm() {
                        if (!this.aiEventResult) {
                            _showToast('尚未有解析結果，請先執行 AI 解析');
                            return;
                        }
                        this.calendarSavingFromAi = false;
                        const title =
                            (this.aiEventResult.title || '').toString().trim() ||
                            (this.aiEventSourceText || '').toString().trim().slice(0, 40);
                        const startTime = this.normalizeDateTimeLocal(this.aiEventResult.start_time);
                        const endTime = this.normalizeDateTimeLocal(this.aiEventResult.end_time);
                        const location = (this.aiEventResult.location || '').toString().trim();
                        const description = (this.aiEventResult.description || '').toString().trim() || (this.aiEventSourceText || '').toString().trim();
                        const extractedEmail = this.extractEmailAddress(this.aiEventSourceText || '') || '';

                        this.calendarCreateForm = {
                            ...(this.calendarCreateForm || {}),
                            title: title || (this.calendarCreateForm?.title || ''),
                            start_time: startTime || '',
                            end_time: endTime || '',
                            location,
                            description,
                            contact_email: (this.calendarCreateForm?.contact_email || '').toString().trim() || extractedEmail
                        };

                        if (this.currentView !== 'ai_sales') {
                            this.openAiSalesView();
                        }
                        this.aiSalesTab = 'calendar';
                        this.showAiEventModal = false;
                        nextTick(() => {
                            try {
                                const el = this.$refs.calendarCreateStartInput;
                                if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                if (el && typeof el.focus === 'function') el.focus();
                            } catch (e) {}
                        });

                        const msg = startTime ? '已帶入到新增事件' : '已帶入到新增事件（但 AI 沒有解析出開始時間，請手動補上）';
                        _showToast(msg);
                    },
                    normalizeDateTimeLocal(value) {
                        const raw = (value || '').toString().trim();
                        if (!raw) return '';
                        let v = raw.replace(/\//g, '-').replace(' ', 'T');
                        v = v.replace(/Z$/, '').replace(/([+-]\d{2}:\d{2})$/, '');
                        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
                            return `${v}T09:00`;
                        }
                        if (v.length >= 16) return v.slice(0, 16);
                        return v;
                    },
                    downloadIcs() {
                        if (!this.aiEventResult) return;

                        const event = this.aiEventResult;
                        const formatTime = (dateStr) => {
                            if (!dateStr) return '';
                            return dateStr.replace(/[-:]/g, '').replace(' ', 'T') + '00';
                        };

                        const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                        const start = formatTime(event.start_time);
                        const end = formatTime(event.end_time) || start;

                        const icsContent = [
                            'BEGIN:VCALENDAR',
                            'VERSION:2.0',
                            'PRODID:-//AI Mail//EN',
                            'BEGIN:VEVENT',
                            `UID:${now}-${Math.random().toString(36).substr(2, 9)}@aimail.local`,
                            `DTSTAMP:${now}`,
                            `DTSTART:${start}`,
                            `DTEND:${end}`,
                            `SUMMARY:${event.title || 'New Event'}`,
                            `DESCRIPTION:${event.description || ''}`,
                            `LOCATION:${event.location || ''}`,
                            'END:VEVENT',
                            'END:VCALENDAR'
                        ].join('\r\n');

                        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = 'event.ics';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    },
                    extractEmailAddress(text) {
                        const s = (text || '').toString();
                        const m = s.match(/([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i);
                        return m ? m[1].toLowerCase() : '';
                    },
                    getPrimaryEmailForEventLink() {
                        if (!this.currentMessage) return '';
                        if (this.currentFolder === 'Sent') {
                            const toVal = this.currentMessage.to;
                            if (Array.isArray(toVal)) return this.extractEmailAddress(toVal[0] || '');
                            return this.extractEmailAddress(toVal || '');
                        }
                        return this.extractEmailAddress(this.currentMessage.from || '');
                    },
                    async saveAiEventToCalendar() {
                        if (!this.aiEventResult) return;
                        const title = (this.aiEventResult.title || '').toString().trim() || (this.currentMessage?.subject || '').toString().trim() || 'New Event';
                        const startTime = this.normalizeDateTimeLocal(this.aiEventResult.start_time);
                        if (!startTime) {
                            _showToast('缺少開始時間，請先補上');
                            return;
                        }
                        this.calendarSavingFromAi = true;
                        try {
                            const candidateEmail = this.getPrimaryEmailForEventLink() || this.extractEmailAddress(this.aiEventSourceText || '') || (this.calendarCreateForm?.contact_email || '').toString().trim();
                            const payload = {
                                title,
                                start_time: startTime,
                                end_time: this.normalizeDateTimeLocal(this.aiEventResult.end_time) || null,
                                location: (this.aiEventResult.location || '').toString().trim() || null,
                                description: (this.aiEventResult.description || '').toString().trim() || null,
                                contact_email: candidateEmail ? candidateEmail.toString().trim() : null,
                                source_type: 'ai'
                            };
                            await axios.post(`${ADMIN_API}/calendar/events`, payload);
                            _showToast('已建立行事曆事件');
                            if (this.aiSalesTab === 'calendar') {
                                this.fetchCalendarEvents();
                            }
                        } catch (e) {
                            _showToast('建立行事曆事件失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.calendarSavingFromAi = false;
                        }
                    },
                    formatCalendarTime(value) {
                        if (!value) return '—';
                        const s = value.toString();
                        return s.replace('T', ' ').slice(0, 16);
                    },
                    async fetchCalendarEvents() {
                        this.calendarLoading = true;
                        try {
                            const days = Math.max(1, Math.min(Number(this.calendarRangeDays) || 14, 90));
                            this.calendarRangeDays = days;
                            const start = new Date();
                            const end = new Date();
                            end.setDate(end.getDate() + days);
                            const res = await axios.get(`${ADMIN_API}/calendar/events`, {
                                params: { start: start.toISOString(), end: end.toISOString(), limit: 500 }
                            });
                            this.calendarEvents = Array.isArray(res?.data?.items) ? res.data.items : [];
                            this.calendarTotal = Number(res?.data?.total) || this.calendarEvents.length || 0;
                        } catch (e) {
                            _showToast('載入行事曆失敗: ' + (e.response?.data?.detail || e.message));
                            this.calendarEvents = [];
                            this.calendarTotal = 0;
                        } finally {
                            this.calendarLoading = false;
                        }
                    },
                    async createCalendarEventManual() {
                        const title = (this.calendarCreateForm.title || '').toString().trim();
                        const startTime = this.normalizeDateTimeLocal(this.calendarCreateForm.start_time);
                        if (!title || !startTime) {
                            _showToast('標題與開始時間必填');
                            return;
                        }
                        this.calendarCreating = true;
                        try {
                            const payload = {
                                title,
                                start_time: startTime,
                                end_time: this.normalizeDateTimeLocal(this.calendarCreateForm.end_time) || null,
                                location: (this.calendarCreateForm.location || '').toString().trim() || null,
                                description: (this.calendarCreateForm.description || '').toString().trim() || null,
                                contact_email: (this.calendarCreateForm.contact_email || '').toString().trim() || null,
                                source_type: 'manual'
                            };
                            await axios.post(`${ADMIN_API}/calendar/events`, payload);
                            this.calendarCreateForm = { title: '', start_time: '', end_time: '', location: '', description: '', contact_email: '' };
                            this.fetchCalendarEvents();
                        } catch (e) {
                            _showToast('建立事件失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.calendarCreating = false;
                        }
                    },
                    async suggestCalendarTimes() {
                        const startStr = this.normalizeDateTimeLocal(this.calendarCreateForm.start_time);
                        const endStr = this.normalizeDateTimeLocal(this.calendarCreateForm.end_time);
                        let duration = 60;
                        if (startStr && endStr) {
                            const s = new Date(startStr.replace(' ', 'T'));
                            const e = new Date(endStr.replace(' ', 'T'));
                            const diffMin = Math.max(15, Math.round((e - s) / 60000));
                            if (isFinite(diffMin)) duration = diffMin;
                        }
                        const contactEmail = (this.calendarCreateForm?.contact_email || '').toString().trim() || null;
                        this.calendarSuggesting = true;
                        this.calendarSuggestedSlots = [];
                        try {
                            const res = await axios.post(`${ADMIN_API}/calendar/ai/suggest_times`, {
                                duration_minutes: duration,
                                days: 14,
                                work_hours_start: '09:00',
                                work_hours_end: '18:00',
                                mine: true,
                                contact_email: contactEmail
                            });
                            const slots = Array.isArray(res?.data?.slots) ? res.data.slots : [];
                            this.calendarSuggestedSlots = slots.slice(0, 10);
                        } catch (e) {
                            _showToast('取得建議時段失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.calendarSuggesting = false;
                        }
                    },
                    applySuggestedSlot(s) {
                        if (!s) return;
                        const st = (s.start_at || '').toString();
                        const et = (s.end_at || '').toString();
                        this.calendarCreateForm.start_time = this.normalizeDateTimeLocal(st);
                        this.calendarCreateForm.end_time = this.normalizeDateTimeLocal(et);
                        this.calendarSelectedSlotStartAt = st;
                        nextTick(() => {
                            try {
                                const el = this.$refs.calendarCreateStartInput;
                                if (el && typeof el.focus === 'function') el.focus();
                            } catch (e) {}
                        });
                    },
                    async downloadCalendarIcs(eventId) {
                        const id = Number(eventId) || 0;
                        if (!id) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/calendar/events/${id}/ics`, { responseType: 'blob' });
                            const blob = new Blob([res.data], { type: 'text/calendar;charset=utf-8' });
                            const link = document.createElement('a');
                            link.href = URL.createObjectURL(blob);
                            link.download = `event-${id}.ics`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        } catch (e) {
                            _showToast('下載 ICS 失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async openCalendarEditById(eventId) {
                        const id = Number(eventId) || 0;
                        if (!id) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/calendar/events/${id}`);
                            const ev = res?.data || null;
                            if (!ev) {
                                _showToast('載入事件失敗: empty response');
                                return;
                            }
                            this.openCalendarEdit(ev);
                        } catch (e) {
                            _showToast('載入事件失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async openOpportunityDetailById(opportunityId) {
                        const id = Number(opportunityId) || 0;
                        if (!id) return;
                        this.currentView = 'webmail';
                        this.showOpportunityModal = true;
                        this.opportunityLoading = true;
                        this.opportunityResult = null;
                        this.opportunityError = null;
                        this.opportunityEditMode = false;
                        this.opportunitySaving = false;
                        this.resetOpportunityContactPanel();
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/opportunities/${id}`);
                            this.opportunityResult = res?.data || null;
                            this.setOpportunityEditFormFromResult(this.opportunityResult);
                            this.initOpportunityContactPanel();
                        } catch (e) {
                            this.opportunityResult = null;
                            this.opportunityError = e.response?.data?.detail || e.message || '載入失敗';
                            _showToast('載入商機失敗: ' + this.opportunityError);
                        } finally {
                            this.opportunityLoading = false;
                        }
                    },
                    resetOpportunityContactPanel() {
                        this.opportunityContactLookupLoading = false;
                        this.opportunityContactLinking = false;
                        this.opportunityContactCreateLoading = false;
                        this.opportunityContactLookupAttemptedEmail = '';
                        this.opportunityContactLookupResult = null;
                        this.opportunityContactLookupError = '';
                    },
                    initOpportunityContactPanel() {
                        this.resetOpportunityContactPanel();
                        const cid = Number(this.opportunityResult?.contact_id) || 0;
                        if (cid) {
                            this.ensureContactLoaded(cid);
                            return;
                        }
                        this.refreshOpportunityContactLookup(false);
                    },
                    normalizeEmail(v) {
                        return (v || '').toString().trim().toLowerCase();
                    },
                    opportunitySenderEmail() {
                        return this.normalizeEmail(this.opportunityResult?.sender_email || '');
                    },
                    opportunitySenderName() {
                        const name = (this.opportunityResult?.sender_name || '').toString().trim();
                        if (name) return name;
                        const email = this.opportunitySenderEmail();
                        if (!email) return '';
                        const local = email.split('@')[0] || '';
                        return local ? local.replace(/[._-]+/g, ' ').trim() : '';
                    },
                    async refreshOpportunityContactLookup(force) {
                        const email = this.opportunitySenderEmail();
                        if (!email) return;
                        if (!force && this.opportunityContactLookupAttemptedEmail === email) return;
                        if (this.opportunityContactLookupLoading) return;
                        this.opportunityContactLookupAttemptedEmail = email;
                        this.opportunityContactLookupError = '';
                        this.opportunityContactLookupResult = null;
                        this.opportunityContactLookupLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/contacts`, {
                                params: { search: email, limit: 10, sort_by: 'name', sort_dir: 'asc' }
                            });
                            const items = Array.isArray(res?.data?.items) ? res.data.items : (Array.isArray(res?.data) ? res.data : []);
                            const exact = items.find(x => this.normalizeEmail(x?.email) === email) || null;
                            this.opportunityContactLookupResult = exact;
                        } catch (e) {
                            this.opportunityContactLookupError = (e.response?.data?.detail || e.message || '查找失敗').toString();
                        } finally {
                            this.opportunityContactLookupLoading = false;
                        }
                    },
                    async linkOpportunityToContact(contactId) {
                        const cid = Number(contactId) || 0;
                        const oid = Number(this.opportunityResult?.id) || 0;
                        if (!cid || !oid) return;
                        if (this.opportunityContactLinking) return;
                        this.opportunityContactLinking = true;
                        try {
                            const res = await axios.put(`${ADMIN_API}/ai/opportunities/${oid}`, { contact_id: cid });
                            const updated = res?.data || null;
                            if (updated) {
                                this.opportunityResult = updated;
                                this.setOpportunityEditFormFromResult(updated);
                                this.ensureContactLoaded(cid);
                                const list = Array.isArray(this.opportunities) ? this.opportunities : [];
                                const idx = list.findIndex(x => Number(x?.id) === oid);
                                if (idx !== -1) this.opportunities.splice(idx, 1, updated);
                            }
                        } catch (e) {
                            _showToast('連結失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.opportunityContactLinking = false;
                        }
                    },
                    async unlinkOpportunityContact() {
                        const oid = Number(this.opportunityResult?.id) || 0;
                        if (!oid) return;
                        if (this.opportunityContactLinking) return;
                        this.opportunityContactLinking = true;
                        try {
                            const res = await axios.put(`${ADMIN_API}/ai/opportunities/${oid}`, { contact_id: null });
                            const updated = res?.data || null;
                            if (updated) {
                                this.opportunityResult = updated;
                                this.setOpportunityEditFormFromResult(updated);
                                const list = Array.isArray(this.opportunities) ? this.opportunities : [];
                                const idx = list.findIndex(x => Number(x?.id) === oid);
                                if (idx !== -1) this.opportunities.splice(idx, 1, updated);
                            }
                            this.resetOpportunityContactPanel();
                            this.opportunityContactLookupAttemptedEmail = this.opportunitySenderEmail();
                        } catch (e) {
                            _showToast('解除連結失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.opportunityContactLinking = false;
                        }
                    },
                    async createContactFromOpportunity() {
                        const email = this.opportunitySenderEmail();
                        if (!email) return;
                        if (this.opportunityContactCreateLoading || this.opportunityContactLinking) return;
                        this.opportunityContactLookupError = '';
                        this.opportunityContactCreateLoading = true;
                        try {
                            await this.refreshOpportunityContactLookup(true);
                            const existing = this.opportunityContactLookupResult;
                            if (existing?.id) {
                                await this.linkOpportunityToContact(existing.id);
                                return;
                            }
                            const payload = { name: this.opportunitySenderName() || email, email };
                            const res = await axios.post(`${ADMIN_API}/contacts`, payload);
                            const created = res?.data || null;
                            if (!created?.id) throw new Error('empty response');
                            this.contactCache[created.id] = created;
                            this.opportunityContactLookupResult = created;
                            await this.linkOpportunityToContact(created.id);
                        } catch (e) {
                            const detail = (e.response?.data?.detail || e.message || '建立失敗').toString();
                            if (detail.toLowerCase().includes('email already registered')) {
                                await this.refreshOpportunityContactLookup(true);
                                const existing = this.opportunityContactLookupResult;
                                if (existing?.id) {
                                    await this.linkOpportunityToContact(existing.id);
                                } else {
                                    this.opportunityContactLookupError = 'Email 已存在，但查找不到聯絡人';
                                }
                            } else {
                                this.opportunityContactLookupError = detail;
                            }
                        } finally {
                            this.opportunityContactCreateLoading = false;
                        }
                    },
                    setOpportunityEditFormFromResult(r) {
                        const x = r || {};
                        this.opportunityEditForm = {
                            status: (x.status || 'inbound').toString().trim().toLowerCase() || 'inbound',
                            company: (x.company || '').toString(),
                            decision_maker: (x.decision_maker || '').toString(),
                            budget_text: (x.budget_text || '').toString(),
                            budget_min: (x.budget_min !== null && x.budget_min !== undefined) ? Number(x.budget_min) : null,
                            budget_max: (x.budget_max !== null && x.budget_max !== undefined) ? Number(x.budget_max) : null,
                            currency: (x.currency || '').toString(),
                            timeline: (x.timeline || '').toString(),
                            timeline_start: (x.timeline_start || '').toString(),
                            timeline_end: (x.timeline_end || '').toString(),
                            need: (x.need || '').toString(),
                            next_step: (x.next_step || '').toString(),
                            summary: (x.summary || '').toString(),
                            confidence: (x.confidence !== null && x.confidence !== undefined) ? Number(x.confidence) : null,
                        };
                    },
                    enterOpportunityEditMode() {
                        if (!this.opportunityResult) return;
                        this.setOpportunityEditFormFromResult(this.opportunityResult);
                        this.opportunityEditMode = true;
                    },
                    cancelOpportunityEdit() {
                        this.opportunitySaving = false;
                        this.opportunityEditMode = false;
                        this.setOpportunityEditFormFromResult(this.opportunityResult);
                    },
                    updateCrmTimelineOpportunity(opportunity) {
                        const id = Number(opportunity?.id) || 0;
                        if (!id) return;
                        if (!this.crmTimeline || !Array.isArray(this.crmTimeline.events)) return;
                        const company = (opportunity?.company || '—').toString().trim() || '—';
                        const content = (opportunity?.next_step || opportunity?.need || opportunity?.summary || '').toString().trim();
                        this.crmTimeline.events = this.crmTimeline.events.map(x => {
                            if ((x?.event_type || '') === 'opportunity' && Number(x?.ref_id) === id) {
                                return {
                                    ...x,
                                    title: `商機：${company}`,
                                    content,
                                };
                            }
                            return x;
                        });
                    },
                    async saveOpportunityEdit() {
                        const id = Number(this.opportunityResult?.id) || 0;
                        if (!id) return;
                        this.opportunitySaving = true;
                        try {
                            const payload = {
                                status: (this.opportunityEditForm.status || 'inbound').toString().trim().toLowerCase() || 'inbound',
                                company: (this.opportunityEditForm.company || '').toString(),
                                decision_maker: (this.opportunityEditForm.decision_maker || '').toString(),
                                budget_text: (this.opportunityEditForm.budget_text || '').toString(),
                                budget_min: (this.opportunityEditForm.budget_min !== null && this.opportunityEditForm.budget_min !== undefined && this.opportunityEditForm.budget_min !== '') ? Number(this.opportunityEditForm.budget_min) : null,
                                budget_max: (this.opportunityEditForm.budget_max !== null && this.opportunityEditForm.budget_max !== undefined && this.opportunityEditForm.budget_max !== '') ? Number(this.opportunityEditForm.budget_max) : null,
                                currency: (this.opportunityEditForm.currency || '').toString(),
                                timeline: (this.opportunityEditForm.timeline || '').toString(),
                                timeline_start: (this.opportunityEditForm.timeline_start || '').toString(),
                                timeline_end: (this.opportunityEditForm.timeline_end || '').toString(),
                                need: (this.opportunityEditForm.need || '').toString(),
                                next_step: (this.opportunityEditForm.next_step || '').toString(),
                                summary: (this.opportunityEditForm.summary || '').toString(),
                                confidence: (this.opportunityEditForm.confidence !== null && this.opportunityEditForm.confidence !== undefined && this.opportunityEditForm.confidence !== '') ? Number(this.opportunityEditForm.confidence) : null,
                            };
                            const res = await axios.put(`${ADMIN_API}/ai/opportunities/${id}`, payload);
                            const updated = res?.data || null;
                            if (updated) {
                                this.opportunityResult = updated;
                                this.setOpportunityEditFormFromResult(updated);
                                this.opportunityEditMode = false;
                                this.updateCrmTimelineOpportunity(updated);
                                const list = Array.isArray(this.opportunities) ? this.opportunities : [];
                                const idx = list.findIndex(x => Number(x?.id) === id);
                                if (idx !== -1) this.opportunities.splice(idx, 1, updated);
                            }
                        } catch (e) {
                            _showToast('儲存商機失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.opportunitySaving = false;
                        }
                    },
                    closeCrmAutoReplyModal() {
                        this.showCrmAutoReplyModal = false;
                        this.crmAutoReplyLoading = false;
                        this.crmAutoReplySaving = false;
                        this.crmAutoReplySending = false;
                        this.crmAutoReplyDetail = null;
                        this.crmAutoReplyEditForm = { status: 'pending', draft_subject: '', draft_body: '' };
                    },
                    async openCrmAutoReplyById(replyId) {
                        const id = Number(replyId) || 0;
                        if (!id) return;
                        this.showCrmAutoReplyModal = true;
                        this.crmAutoReplyLoading = true;
                        this.crmAutoReplySaving = false;
                        this.crmAutoReplySending = false;
                        this.crmAutoReplyDetail = null;
                        this.crmAutoReplyEditForm = { status: 'pending', draft_subject: '', draft_body: '' };
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/auto-replies/${id}`);
                            const reply = res?.data || null;
                            if (!reply) {
                                _showToast('載入草稿失敗: empty response');
                                this.closeCrmAutoReplyModal();
                                return;
                            }
                            this.crmAutoReplyDetail = reply;
                            this.crmAutoReplyEditForm = {
                                status: (reply.status || 'pending').toString(),
                                draft_subject: (reply.ai_draft_subject || reply.original_subject || '').toString(),
                                draft_body: (reply.ai_draft_body || '').toString(),
                            };
                            if (reply?.contact_id) this.ensureContactLoaded(reply.contact_id);
                        } catch (e) {
                            _showToast('載入草稿失敗: ' + (e.response?.data?.detail || e.message));
                            this.closeCrmAutoReplyModal();
                        } finally {
                            this.crmAutoReplyLoading = false;
                        }
                    },
                    updateCrmTimelineAutoReply(reply) {
                        const id = Number(reply?.id) || 0;
                        if (!id) return;
                        if (!this.crmTimeline || !Array.isArray(this.crmTimeline.events)) return;
                        const status = (reply?.status || '').toString().trim() || 'pending';
                        const subj = (reply?.ai_draft_subject || reply?.original_subject || '').toString().trim();
                        this.crmTimeline.events = this.crmTimeline.events.map(x => {
                            if ((x?.event_type || '') === 'auto_reply' && Number(x?.ref_id) === id) {
                                return {
                                    ...x,
                                    title: `自動跟進草稿 · ${status}`,
                                    content: subj,
                                };
                            }
                            return x;
                        });
                    },
                    async crmSaveAutoReply() {
                        const id = Number(this.crmAutoReplyDetail?.id) || 0;
                        if (!id) return;
                        this.crmAutoReplySaving = true;
                        try {
                            const payload = {
                                status: (this.crmAutoReplyEditForm.status || 'pending').toString().trim(),
                                draft_subject: (this.crmAutoReplyEditForm.draft_subject || '').toString(),
                                draft_body: (this.crmAutoReplyEditForm.draft_body || '').toString(),
                            };
                            const res = await axios.put(`${ADMIN_API}/ai/auto-replies/${id}`, payload);
                            const updated = res?.data || null;
                            if (updated) {
                                this.crmAutoReplyDetail = updated;
                                this.crmAutoReplyEditForm = {
                                    status: (updated.status || 'pending').toString(),
                                    draft_subject: (updated.ai_draft_subject || updated.original_subject || '').toString(),
                                    draft_body: (updated.ai_draft_body || '').toString(),
                                };
                                this.updateCrmTimelineAutoReply(updated);
                                if (updated?.contact_id) this.ensureContactLoaded(updated.contact_id);
                            }
                        } catch (e) {
                            _showToast('儲存草稿失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.crmAutoReplySaving = false;
                        }
                    },
                    async crmSendAutoReply() {
                        const id = Number(this.crmAutoReplyDetail?.id) || 0;
                        if (!id) return;
                        if (!await this.ensureMailSession('送出草稿')) return;
                        this.crmAutoReplySending = true;
                        try {
                            await axios.post(`${ADMIN_API}/ai/auto-replies/${id}/send`, null, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            if (this.crmAutoReplyDetail) {
                                this.crmAutoReplyDetail = { ...this.crmAutoReplyDetail, status: 'sent' };
                                this.crmAutoReplyEditForm = { ...this.crmAutoReplyEditForm, status: 'sent' };
                                this.updateCrmTimelineAutoReply(this.crmAutoReplyDetail);
                            }
                        } catch (e) {
                            if (e.response && e.response.status === 401) {
                                this.handleMailAuth401(e, '送出草稿');
                                return;
                            }
                            _showToast('送出失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.crmAutoReplySending = false;
                        }
                    },
                    async crmOpenAutoReplyOriginal() {
                        if (!this.crmAutoReplyDetail?.id) return;
                        this.selectedAutoReply = this.crmAutoReplyDetail;
                        await this.openSelectedAutoReplyOriginal();
                    },
                    async crmApplyAutoReplyToCompose() {
                        if (!this.crmAutoReplyDetail?.id) return;
                        this.selectedAutoReply = this.crmAutoReplyDetail;
                        this.autoReplyEdit = {
                            status: (this.crmAutoReplyEditForm.status || 'pending').toString(),
                            draft_subject: (this.crmAutoReplyEditForm.draft_subject || '').toString(),
                            draft_body: (this.crmAutoReplyEditForm.draft_body || '').toString(),
                        };
                        this.closeCrmAutoReplyModal();
                        await this.applyAutoReplyToCompose();
                    },
                    async openAutoReplyById(replyId) {
                        const id = Number(replyId) || 0;
                        if (!id) return;
                        this.closeCrmModal();
                        this.openAiSalesView();
                        this.aiSalesTab = 'followups';
                        this.autoRepliesFilterStatus = '';
                        try {
                            await this.fetchAutoReplies();
                            const found = (Array.isArray(this.autoReplies) ? this.autoReplies : []).find(x => Number(x?.id) === id) || null;
                            if (found) {
                                this.selectAutoReply(found);
                                return;
                            }
                        } catch (_) {
                        }
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/auto-replies/${id}`);
                            const reply = res?.data || null;
                            if (!reply) {
                                _showToast('載入草稿失敗: empty response');
                                return;
                            }
                            const list = Array.isArray(this.autoReplies) ? this.autoReplies : [];
                            this.autoReplies = [reply, ...list.filter(x => Number(x?.id) !== id)];
                            this.selectAutoReply(reply);
                        } catch (e) {
                            _showToast('載入草稿失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    openCalendarEdit(e) {
                        if (!e) return;
                        this.showCalendarEditModal = true;
                        this.calendarEditForm = {
                            id: Number(e.id) || null,
                            title: (e.title || '').toString(),
                            start_time: this.normalizeDateTimeLocal(e.start_at),
                            end_time: this.normalizeDateTimeLocal(e.end_at),
                            location: (e.location || '').toString(),
                            description: (e.description || '').toString(),
                            contact_email: (e.contact_email || '').toString(),
                            status: ((e.status || 'confirmed').toString().trim() || 'confirmed')
                        };
                    },
                    closeCalendarEditModal() {
                        this.showCalendarEditModal = false;
                        this.calendarEditSaving = false;
                        this.calendarEditForm = { id: null, title: '', start_time: '', end_time: '', location: '', description: '', contact_email: '', status: 'confirmed' };
                    },
                    async saveCalendarEdit() {
                        const id = Number(this.calendarEditForm?.id) || 0;
                        if (!id) return;
                        const title = (this.calendarEditForm.title || '').toString().trim();
                        const startTime = this.normalizeDateTimeLocal(this.calendarEditForm.start_time);
                        if (!title || !startTime) {
                            _showToast('標題與開始時間必填');
                            return;
                        }
                        this.calendarEditSaving = true;
                        try {
                            const payload = {
                                title,
                                start_time: startTime,
                                end_time: this.normalizeDateTimeLocal(this.calendarEditForm.end_time) || null,
                                location: (this.calendarEditForm.location || '').toString().trim() || null,
                                description: (this.calendarEditForm.description || '').toString().trim() || null,
                                contact_email: (this.calendarEditForm.contact_email || '').toString().trim() || null,
                                status: (this.calendarEditForm.status || '').toString().trim() || null
                            };
                            const res = await axios.put(`${ADMIN_API}/calendar/events/${id}`, payload);
                            const updated = res?.data || null;
                            if (updated) {
                                this.calendarEvents = this.calendarEvents.map(x => (Number(x.id) === id ? updated : x));
                                this.crmCalendarEvents = this.crmCalendarEvents.map(x => (Number(x.id) === id ? updated : x));
                                this.syncCrmTimelineCalendarEvent(updated);
                            }
                            this.closeCalendarEditModal();
                        } catch (e) {
                            _showToast('儲存事件失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.calendarEditSaving = false;
                        }
                    },
                    async aiAgendaForEdit() {
                        const id = Number(this.calendarEditForm?.id) || 0;
                        if (!id) {
                            _showToast('請先儲存事件後再產生議程');
                            return;
                        }
                        try {
                            const res = await axios.post(`${ADMIN_API}/calendar/events/${id}/ai/agenda`, { bullets: 5, language: 'Traditional Chinese (繁體中文)' });
                            const txt = (res?.data?.agenda_text || '').toString().trim();
                            if (!txt) {
                                _showToast('未取得議程建議');
                                return;
                            }
                            const current = (this.calendarEditForm.description || '').toString().trim();
                            this.calendarEditForm.description = current ? (current + '\\n\\n' + txt) : txt;
                        } catch (e) {
                            _showToast('產生議程失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async aiInviteDraftForEdit() {
                        const id = Number(this.calendarEditForm?.id) || 0;
                        if (!id) {
                            _showToast('請先儲存事件後再產生邀請文案');
                            return;
                        }
                        try {
                            const res = await axios.post(`${ADMIN_API}/calendar/events/${id}/ai/invite_draft`, { tone: 'professional', language: 'Traditional Chinese (繁體中文)' });
                            const message = (res?.data?.message || '').toString().trim();
                            if (!message) {
                                _showToast('未取得邀請文案');
                                return;
                            }
                            const current = (this.calendarEditForm.description || '').toString().trim();
                            const block = `---\n邀請信建議：\n${message}`;
                            this.calendarEditForm.description = current ? (current + '\n\n' + block) : block;
                        } catch (e) {
                            _showToast('產生邀請文案失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async aiInviteDraftForWebmail() {
                        const eventId = Number(this.webmailMeetingForm?.id) || 0;
                        const title = (this.webmailMeetingForm?.title || '').toString().trim();
                        const startTime = this.normalizeDateTimeLocal(this.webmailMeetingForm?.start_time);
                        const endTime = this.normalizeDateTimeLocal(this.webmailMeetingForm?.end_time);
                        const location = (this.webmailMeetingForm?.location || '').toString().trim() || '—';
                        const desc = (this.webmailMeetingForm?.description || '').toString().trim() || '—';
                        if (!title || !startTime) {
                            _showToast('請先填寫主題與開始時間');
                            return;
                        }
                        const whenText = endTime ? `${startTime} ~ ${endTime}` : startTime;
                        try {
                            let message = '';
                            if (eventId) {
                                const res = await axios.post(`${ADMIN_API}/calendar/events/${eventId}/ai/invite_draft`, { tone: 'professional', language: 'Traditional Chinese (繁體中文)' });
                                message = (res?.data?.message || '').toString().trim();
                            } else {
                                const prompt = `請以繁體中文撰寫一封專業禮貌的會議邀請信內文：\\n- 會議主題：${title}\\n- 時間：${whenText}\\n- 地點/連結：${location}\\n- 會議說明：${desc}\\n請簡潔明確，結尾邀請對方回覆是否可出席。`;
                                const res = await axios.post(`${ADMIN_API}/ai/summary`, { text: prompt });
                                message = (res?.data?.result || '').toString().trim();
                            }
                            if (!message) {
                                _showToast('未取得邀請文案');
                                return;
                            }
                            this.webmailMeetingForm.invite_message = message;
                        } catch (e) {
                            _showToast('產生邀請文案失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async cancelCalendarEvent(eventId) {
                        const id = Number(eventId) || 0;
                        if (!id) return;
                        if (!confirm('確定要取消這個事件？')) return;
                        try {
                            const res = await axios.put(`${ADMIN_API}/calendar/events/${id}`, { status: 'canceled' });
                            const updated = res?.data || null;
                            if (updated) {
                                this.calendarEvents = this.calendarEvents.map(x => (Number(x.id) === id ? updated : x));
                                this.crmCalendarEvents = this.crmCalendarEvents.map(x => (Number(x.id) === id ? updated : x));
                                this.syncCrmTimelineCalendarEvent(updated);
                            } else {
                                this.calendarEvents = this.calendarEvents.map(x => (Number(x.id) === id ? { ...x, status: 'canceled' } : x));
                                this.crmCalendarEvents = this.crmCalendarEvents.map(x => (Number(x.id) === id ? { ...x, status: 'canceled' } : x));
                                const fallback = this.calendarEvents.find(x => Number(x.id) === id) || this.crmCalendarEvents.find(x => Number(x.id) === id) || null;
                                if (fallback) this.syncCrmTimelineCalendarEvent(fallback);
                            }
                        } catch (e) {
                            _showToast('取消事件失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async deleteCalendarEvent(eventId) {
                        const id = Number(eventId) || 0;
                        if (!id) return;
                        if (!confirm('確定要刪除這個事件？')) return;
                        try {
                            await axios.delete(`${ADMIN_API}/calendar/events/${id}`);
                            this.calendarEvents = this.calendarEvents.filter(x => Number(x.id) !== id);
                            this.calendarTotal = Math.max(0, (Number(this.calendarTotal) || 0) - 1);
                            this.crmCalendarEvents = this.crmCalendarEvents.filter(x => Number(x.id) !== id);
                            this.crmCalendarTotal = Math.max(0, (Number(this.crmCalendarTotal) || 0) - 1);
                            this.removeCrmTimelineCalendarEvent(id);
                        } catch (e) {
                            _showToast('刪除事件失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    openUnrepliedView() {
                        this.currentFolder = 'Unreplied';
                        this.currentMessage = null;
                        this.selectedUnrepliedIds = [];
                        this.selectAllUnreplied = false;
                        this.fetchUnrepliedMessages();
                    },
                    async fetchUnrepliedMessages() {
                        this.unrepliedLoading = true;
                        try {
                            if (!await this.ensureMailSession('載入待跟進郵件')) return;
                            const response = await axios.get(`${ADMIN_API}/mail/unreplied?days=14`, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            this.unrepliedMessages = response.data.messages || [];
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                this.handleMailAuth401(error, '載入待跟進郵件');
                                return;
                            }
                            _showToast('載入待跟進郵件失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.unrepliedLoading = false;
                        }
                    },
                    isSelectedUnreplied(id) {
                        const v = Number(id) || 0;
                        if (!v) return false;
                        return this.selectedUnrepliedIds.includes(v);
                    },
                    toggleUnrepliedSelection(id) {
                        const v = Number(id) || 0;
                        if (!v) return;
                        const idx = this.selectedUnrepliedIds.indexOf(v);
                        if (idx >= 0) {
                            this.selectedUnrepliedIds.splice(idx, 1);
                        } else {
                            this.selectedUnrepliedIds.push(v);
                        }
                    },
                    toggleSelectAllUnreplied() {
                        const allIds = (Array.isArray(this.unrepliedMessages) ? this.unrepliedMessages : []).map(m => Number(m?.id) || 0).filter(x => x > 0);
                        if (this.selectAllUnreplied) {
                            this.selectedUnrepliedIds = [];
                            this.selectAllUnreplied = false;
                        } else {
                            this.selectedUnrepliedIds = [...new Set(allIds)];
                            this.selectAllUnreplied = true;
                        }
                    },
                    async batchAnalyzeUnrepliedCustomerBackground() {
                        if (!this.selectedUnrepliedIds.length) return;
                        try {
                            const ok = await this.ensureMailSession('批次 CBI');
                            if (!ok) return;
                            const items = [];
                            for (const id of this.selectedUnrepliedIds.slice(0, 50)) {
                                try {
                                    const res = await axios.get(`${ADMIN_API}/mail/message/${id}?folder=Sent`, {
                                        headers: { 'X-Mail-Session': this.mailSessionToken }
                                    });
                                    const msg = res?.data || {};
                                    items.push({
                                        subject: (msg?.subject || '').toString(),
                                        sender: (msg?.from || '').toString(),
                                        body_text: (msg?.body_text || '').toString(),
                                        body_html: (msg?.body_html || '').toString(),
                                        folder: 'Sent',
                                        imap_id: id,
                                        message_id: (msg?.message_id || null)
                                    });
                                } catch (e) {}
                            }
                            if (!items.length) {
                                _showToast('沒有可分析的郵件');
                                return;
                            }
                            const res = await axios.post(`${ADMIN_API}/ai/customer/background/batch`, { items });
                            const created = Number(res?.data?.created) || 0;
                            this.selectedUnrepliedIds = [];
                            this.selectAllUnreplied = false;
                            _showToast(`已完成批次分析，共 ${created} 筆。`);
                            this.openCustomerBackgroundList();
                        } catch (error) {
                            _showToast('批次分析失敗: ' + (error.response?.data?.detail || error.message));
                        }
                    },

                    async followUpUnreplied(msgId) {
                        this.loadingContent = true;
                        try {
                            if (!await this.ensureMailSession('查看原信')) return;
                            // Fetch full message from Sent folder
                            const response = await axios.get(`${ADMIN_API}/mail/message/${msgId}?folder=Sent`, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            this.currentMessage = response.data;
                            this.currentFolder = 'Sent'; // Switch context to Sent folder

                            // Wait for state update then call prepareFollowup
                            nextTick(() => {
                                this.prepareFollowup();
                            });
                        } catch (error) {
                            _showToast('無法載入郵件內容: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.loadingContent = false;
                        }
                    },

                    openAiSalesView() {
                        if (!this.setCurrentView('ai_sales')) return;
                        if (!this.aiSalesTab || this.aiSalesTab === 'line_support') this.aiSalesTab = 'followups';
                        this.refreshAiSales();
                    },
                    resetMailSessionOnly() {
                        this.mailSessionToken = '';
                        this.mailSessionExpiresAt = 0;
                        try {
                            sessionStorage.removeItem('mailSessionToken');
                            sessionStorage.removeItem('mailSessionExpiresAt');
                        } catch (e) {
                        }
                        try {
                            localStorage.removeItem('mailSessionToken');
                            localStorage.removeItem('mailSessionExpiresAt');
                        } catch (e) {
                        }
                    },
                    resetMailAuth() {
                        this.mailPassword = '';
                        this.mailPasswordInput = '';
                        this.resetMailSessionOnly();
                        try { sessionStorage.removeItem('mailPassword'); } catch (e) {}
                        try { localStorage.removeItem('mailPassword'); } catch (e) {}
                        this.storedMailPasswordExists = false;
                        this.mailRememberPassword = false;
                    },
                    restoreMailSessionFromStorage() {
                        try {
                            const token = (sessionStorage.getItem('mailSessionToken') || localStorage.getItem('mailSessionToken') || '').toString();
                            const expiresAt = Number(sessionStorage.getItem('mailSessionExpiresAt') || localStorage.getItem('mailSessionExpiresAt') || 0);
                            if (token && expiresAt && Date.now() < expiresAt) {
                                this.mailSessionToken = token;
                                this.mailSessionExpiresAt = expiresAt;
                            } else {
                                sessionStorage.removeItem('mailSessionToken');
                                sessionStorage.removeItem('mailSessionExpiresAt');
                                localStorage.removeItem('mailSessionToken');
                                localStorage.removeItem('mailSessionExpiresAt');
                            }
                        } catch (e) {
                        }
                    },
                    persistMailSessionToStorage() {
                        try {
                            if (this.mailSessionToken && this.mailSessionExpiresAt) {
                                sessionStorage.setItem('mailSessionToken', this.mailSessionToken);
                                sessionStorage.setItem('mailSessionExpiresAt', String(this.mailSessionExpiresAt));
                            }
                        } catch (e) {
                        }
                        try {
                            if (this.mailSessionToken && this.mailSessionExpiresAt) {
                                localStorage.setItem('mailSessionToken', this.mailSessionToken);
                                localStorage.setItem('mailSessionExpiresAt', String(this.mailSessionExpiresAt));
                            }
                        } catch (e) {
                        }
                    },
                    restoreMailPasswordFromStorage() {
                        try {
                            // Check sessionStorage first, then localStorage (remember me)
                            const pwd = (sessionStorage.getItem('mailPassword') || localStorage.getItem('mailPassword') || '').toString();
                            if (pwd && localStorage.getItem('mailPassword')) {
                                this.mailRememberPassword = true;
                            }
                            return pwd || '';
                        } catch (e) {
                            return '';
                        }
                    },
                    syncStoredMailPasswordExists() {
                        try {
                            const pwd = (sessionStorage.getItem('mailPassword') || localStorage.getItem('mailPassword') || '').toString();
                            this.storedMailPasswordExists = !!pwd;
                            if (pwd && localStorage.getItem('mailPassword')) {
                                this.mailRememberPassword = true;
                            }
                        } catch (e) {
                            this.storedMailPasswordExists = false;
                        }
                    },
                    persistMailPasswordToStorage(pwd) {
                        try {
                            const v = (pwd || '').toString();
                            if (v) {
                                sessionStorage.setItem('mailPassword', v);
                                // If "remember me" is enabled, also save to localStorage
                                if (this.mailRememberPassword) {
                                    localStorage.setItem('mailPassword', v);
                                }
                            }
                        } catch (e) {
                        }
                        this.storedMailPasswordExists = !!(pwd || '').toString();
                    },
                    handleMailAuth401(error, actionLabel) {
                        const detail = (error?.response?.data?.detail || '').toString().trim();
                        const who = (this.currentUser?.email || '').toString().trim();
                        const ctx = (actionLabel || '').toString().trim();
                        let msg = '信箱密碼錯誤或登入失敗，請重新輸入';
                        if (detail) {
                            if (/mail session/i.test(detail)) {
                                this.resetMailSessionOnly();
                                return true;
                            } else {
                                const cleaned = detail.replace(/^mail login failed:\s*/i, '').trim();
                                msg = `信箱登入失敗${who ? `（${who}）` : ''}${ctx ? `：${ctx}` : ''}\n${cleaned || detail}`;
                            }
                        } else if (who || ctx) {
                            msg = `信箱密碼錯誤或登入失敗，請重新輸入${who ? `（${who}）` : ''}${ctx ? `：${ctx}` : ''}`;
                        }
                        this.webmailLastError = msg;
                        _showToast(msg);
                        this.resetMailAuth();
                        return true;
                    },
                    async ensureMailPassword(actionLabel) {
                        if (this.mailPassword) return true;
                        const saved = this.restoreMailPasswordFromStorage();
                        if (saved) {
                            this.mailPassword = saved;
                            return true;
                        }
                        const msg = `尚未登入信箱，請先在信箱頁面輸入郵件密碼後再${(actionLabel || '').toString() || '操作'}`;
                        this.webmailLastError = msg;
                        _showToast(msg);
                        return false;
                    },
                    async ensureMailSession(actionLabel, forceRefresh = false) {
                        if (!forceRefresh && this.mailSessionToken && Date.now() < (Number(this.mailSessionExpiresAt) || 0)) return true;
                        // Prevent concurrent re-auth floods: queue behind an in-progress renewal
                        if (this._mailSessionRenewing) {
                            await new Promise(r => setTimeout(r, 1200));
                            return !!(this.mailSessionToken && Date.now() < (Number(this.mailSessionExpiresAt) || 0));
                        }
                        this._mailSessionRenewing = true;
                        try {
                        // Try auto-login first (server stored password)
                        if (!this.mailPassword) {
                            try {
                                const res = await axios.post(`${ADMIN_API}/mail/session`, { password: '' });
                                const token = (res?.data?.token || '').toString();
                                const expiresIn = Number(res?.data?.expires_in) || 0;
                                if (token && expiresIn > 0) {
                                    this.mailSessionToken = token;
                                    this.mailSessionExpiresAt = Date.now() + expiresIn * 1000;
                                    this.persistMailSessionToStorage();
                                    this.webmailLastError = '';
                                    return true;
                                }
                            } catch (e) { /* no stored password, continue to manual */ }
                        }
                        if (!await this.ensureMailPassword(actionLabel)) return false;
                        try {
                            const res = await axios.post(`${ADMIN_API}/mail/session`, { password: this.mailPassword });
                            const token = (res?.data?.token || '').toString();
                            const expiresIn = Number(res?.data?.expires_in) || 0;
                            if (!token || expiresIn <= 0) throw new Error('empty session token');
                            this.mailSessionToken = token;
                            this.mailSessionExpiresAt = Date.now() + expiresIn * 1000;
                            this.persistMailSessionToStorage();
                            this.mailPassword = '';
                            this.webmailLastError = '';
                            return true;
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                this.handleMailAuth401(e, actionLabel);
                                return false;
                            }
                            const msg = '建立信箱工作階段失敗: ' + (e.response?.data?.detail || e.message);
                            this.webmailLastError = msg;
                            _showToast(msg);
                            return false;
                        }
                        } finally { this._mailSessionRenewing = false; }
                    },
                    async refreshAiSales() {
                        if (this.mailSessionToken) {
                            await this.fetchUnrepliedMessages();
                        }
                        await this.fetchAutoReplies();
                        if (this.aiSalesTab === 'opportunities') {
                            await this.fetchOpportunities();
                            if (!this.leadFeaturesLoading && (!Array.isArray(this.leadFeatures) || this.leadFeatures.length === 0)) {
                                this.loadLeadScoring();
                            }
                            if (!this.pipelineForecastLoading && !this.pipelineForecast) {
                                this.fetchPipelineForecast();
                            }
                        }
                        if (this.aiSalesTab === 'assets') {
                            await this.fetchResources();
                        }
                        if (this.aiSalesTab === 'calendar') {
                            await this.fetchCalendarEvents();
                        }
                    },
                    stageLabel(stage) {
                        const s = (stage || '').toString().trim().toLowerCase();
                        const map = {
                            inbound: '進線',
                            qualified: '已確認',
                            proposal: '提案/報價',
                            negotiation: '議價/簽約',
                            won: '成交',
                            lost: '流失'
                        };
                        return map[s] || (stage || '—');
                    },
                    toggleLeadFactors(contactId) {
                        const id = Number(contactId) || 0;
                        if (!id) return;
                        if (!this.leadFactorsExpanded) this.leadFactorsExpanded = {};
                        this.leadFactorsExpanded[id] = !this.leadFactorsExpanded[id];
                        this.$forceUpdate();
                    },
                    async loadLeadScoring() {
                        this.leadScoreWeightsLoading = true;
                        this.leadFeaturesLoading = true;
                        try {
                            const [wRes, fRes] = await Promise.all([
                                axios.get(`${ADMIN_API}/sales/leads/weights`),
                                axios.get(`${ADMIN_API}/sales/leads/features`, {
                                    params: {
                                        limit: Number(this.leadFeaturesLimit) || 200,
                                        days: Number(this.leadFeaturesDays) || 30
                                    }
                                })
                            ]);
                            if (wRes?.data && typeof wRes.data === 'object') {
                                this.leadScoreWeights = {
                                    intent: Number(wRes.data.intent) || 0,
                                    engagement: Number(wRes.data.engagement) || 0,
                                    recency: Number(wRes.data.recency) || 0,
                                    value: Number(wRes.data.value) || 0,
                                    fit: Number(wRes.data.fit) || 0
                                };
                            }
                            const items = fRes?.data?.items;
                            this.leadFeatures = Array.isArray(items) ? items : [];
                        } catch (e) {
                            _showToast('載入潛客評分失敗: ' + (e.response?.data?.detail || e.message));
                            this.leadFeatures = [];
                        } finally {
                            this.leadScoreWeightsLoading = false;
                            this.leadFeaturesLoading = false;
                        }
                    },
                    setLeadWeight(key, val) {
                        const k = (key || '').toString().trim();
                        if (!k) return;
                        const pct = Math.max(0, Math.min(100, Number(val) || 0));
                        const next = { ...(this.leadScoreWeights || {}) };
                        next[k] = pct / 100.0;
                        this.leadScoreWeights = next;
                        this.scheduleLeadScoreWeightsSave();
                    },
                    scheduleLeadScoreWeightsSave() {
                        if (this.leadScoreWeightsSaveTimer) clearTimeout(this.leadScoreWeightsSaveTimer);
                        this.leadScoreWeightsSaveTimer = setTimeout(() => {
                            this.saveLeadScoreWeights();
                        }, 700);
                    },
                    async saveLeadScoreWeights() {
                        if (this.leadScoreWeightsSaving) return;
                        this.leadScoreWeightsSaving = true;
                        try {
                            const w = this.leadScoreWeights || {};
                            const payload = {
                                intent: Number(w.intent) || 0,
                                engagement: Number(w.engagement) || 0,
                                recency: Number(w.recency) || 0,
                                value: Number(w.value) || 0,
                                fit: Number(w.fit) || 0
                            };
                            const res = await axios.post(`${ADMIN_API}/sales/leads/weights`, payload);
                            if (res?.data && typeof res.data === 'object') {
                                this.leadScoreWeights = {
                                    intent: Number(res.data.intent) || 0,
                                    engagement: Number(res.data.engagement) || 0,
                                    recency: Number(res.data.recency) || 0,
                                    value: Number(res.data.value) || 0,
                                    fit: Number(res.data.fit) || 0
                                };
                            }
                        } catch (e) {
                            _showToast('儲存權重失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.leadScoreWeightsSaving = false;
                        }
                    },
                    async previewFollowupSequence() {
                        this.followupSequenceLoading = true;
                        try {
                            const p = this.followupSequenceForm || {};
                            const email = (p.recipient_email || '').toString().trim();
                            if (!email) {
                                _showToast('請先填寫收件者 Email');
                                return;
                            }
                            const payload = {
                                level: (p.level || 'warm').toString(),
                                recipient_email: email,
                                recipient_name: (p.recipient_name || '').toString(),
                                context_subject: (p.context_subject || '').toString(),
                                context_summary: (p.context_summary || '').toString(),
                                quote_summary: (p.quote_summary || '').toString()
                            };
                            const res = await axios.post(`${ADMIN_API}/sales/followup/sequence_preview`, payload);
                            this.followupSequencePreview = res.data || null;
                        } catch (e) {
                            _showToast('產生跟進序列失敗: ' + (e.response?.data?.detail || e.message));
                            this.followupSequencePreview = null;
                        } finally {
                            this.followupSequenceLoading = false;
                        }
                    },
                    async previewQuote() {
                        this.quoteLoading = true;
                        try {
                            const q = this.quoteForm || {};
                            const payload = {
                                company_size: (q.company_size || '').toString(),
                                plan: (q.plan || '').toString(),
                                billing: (q.billing || 'monthly').toString(),
                                seats: Number(q.seats) || 1
                            };
                            const res = await axios.post(`${ADMIN_API}/sales/quote/preview`, payload);
                            this.quotePreview = res.data || null;
                        } catch (e) {
                            _showToast('報價試算失敗: ' + (e.response?.data?.detail || e.message));
                            this.quotePreview = null;
                        } finally {
                            this.quoteLoading = false;
                        }
                    },
                    async fetchPipelineForecast() {
                        this.pipelineForecastLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/sales/pipeline/forecast`, {
                                params: {
                                    days: Number(this.pipelineForecastDays) || 60,
                                    limit: 200
                                }
                            });
                            this.pipelineForecast = res.data || null;
                        } catch (e) {
                            _showToast('載入成交預測失敗: ' + (e.response?.data?.detail || e.message));
                            this.pipelineForecast = null;
                        } finally {
                            this.pipelineForecastLoading = false;
                        }
                    },
                    async crmExportPipelinePdf() {
                        if (this.pipelinePdfLoading) return;
                        this.pipelinePdfLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/crm/pipeline/pdf`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            link.href = url;
                            const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                            link.setAttribute('download', `crm_pipeline_${today}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        } catch (e) {
                            _showToast('匯出 PDF 失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.pipelinePdfLoading = false;
                        }
                    },
                    async exportMarketingReportPdf() {
                        if (this.marketingReportPdfLoading) return;
                        this.marketingReportPdfLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/analysis/report/pdf`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            link.href = url;
                            const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                            link.setAttribute('download', `marketing_report_${today}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        } catch (e) {
                            _showToast('匯出 PDF 失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.marketingReportPdfLoading = false;
                        }
                    },
                    applyDraftToCompose(to, subject, body) {
                        this.openComposeModal();
                        this.composeForm.to = (to || '').toString();
                        this.composeForm.subject = (subject || '').toString();
                        this.composeForm.body = (body || '').toString();
                        this.composeForm.html_body = '';
                        this.composeEditorMode = 'rich';
                        this.showComposeModal = true;
                    },
                    openNotificationsModal() {
                        this.showNotificationsModal = true;
                        this.fetchNotifications();
                    },
                    async fetchNotifications() {
                        this.notificationsLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/sales/notifications`, {
                                params: {
                                    include_cooling: !!this.notificationsIncludeCooling,
                                    days: Number(this.notificationsDays) || 7
                                }
                            });
                            const raw = res.data?.items;
                            const items = Array.isArray(raw) ? raw : [];
                            this.notifications = items.map((x, idx) => {
                                const ts = (x?.timestamp || '').toString();
                                return {
                                    id: `${x?.type || 'n'}_${ts}_${idx}`,
                                    created_at: ts,
                                    rule: x?.type || '',
                                    title: x?.title || '',
                                    message: x?.detail || '',
                                    contact_id: x?.contact_id || null,
                                    opportunity_id: x?.opportunity_id || null
                                };
                            });
                        } catch (e) {
                            _showToast('載入通知失敗: ' + (e.response?.data?.detail || e.message));
                            this.notifications = [];
                        } finally {
                            this.notificationsLoading = false;
                        }
                    },
                    async openCrmTimeline(lead) {
                        const id = Number(lead?.contact_id) || 0;
                        if (!id) {
                            _showToast('找不到 contact_id');
                            return;
                        }
                        this.currentView = 'webmail';
                        this.showCrmModal = true;
                        this.crmLoading = true;
                        this.crmTimeline = null;
                        this.crmNote = { contact_id: id, note: '' };
                        this.crmTimelineFilterType = 'all';
                        this.crmTimelineQuery = '';
                        this.crmContactLoading = false;
                        this.crmContactSaving = false;
                        this.crmContactEditOpen = false;
                        this.crmContact = null;
                        this.crmContactForm = { id, name: '', email: '', phone: '', tags: '' };
                        this.crmCalendarEvents = [];
                        this.crmCalendarTotal = 0;
                        this.crmCalendarCreateForm = { title: '', start_time: '', end_time: '', location: '', description: '' };
                        try {
                            const res = await axios.get(`${ADMIN_API}/sales/crm/timeline`, {
                                params: { contact_id: id, days: 90 }
                            });
                            const data = res.data || {};
                            const events = Array.isArray(data.events) ? data.events : [];
                            this.crmTimeline = {
                                contact: data.contact || null,
                                events: events.map((e, idx) => ({
                                    id: `${e?.type || 'e'}_${e?.timestamp || ''}_${idx}`,
                                    event_type: e?.type || '',
                                    ref_id: e?.calendar_event_id || e?.opportunity_id || e?.auto_reply_id || null,
                                    created_at: e?.timestamp || '',
                                    title: e?.title || '',
                                    content: e?.detail || ''
                                }))
                            };
                            await this.fetchCrmContactDetail(id);
                            const email = (this.crmTimeline?.contact?.email || '').toString().trim();
                            if (email && !this.crmCalendarCreateForm.title) {
                                this.crmCalendarCreateForm.title = '會議';
                            }
                            await this.fetchCrmCalendarEvents();
                        } catch (e) {
                            _showToast('載入 CRM 時間線失敗: ' + (e.response?.data?.detail || e.message));
                            this.crmTimeline = null;
                        } finally {
                            this.crmLoading = false;
                        }
                    },
                    async fetchCrmContactDetail(contactId) {
                        const id = Number(contactId) || 0;
                        if (!id) return;
                        this.crmContactLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/contacts/${id}`);
                            const c = res?.data || null;
                            this.crmContact = c;
                            this.crmContactForm = {
                                id,
                                name: (c?.name || '').toString(),
                                email: (c?.email || '').toString(),
                                phone: (c?.phone || '').toString(),
                                tags: (c?.tags || '').toString(),
                            };
                        } catch (e) {
                            this.crmContact = null;
                        } finally {
                            this.crmContactLoading = false;
                        }
                    },
                    async saveCrmContact() {
                        const id = Number(this.crmContact?.id || this.crmContactForm?.id) || 0;
                        if (!id) return;
                        const name = (this.crmContactForm.name || '').toString().trim();
                        const email = (this.crmContactForm.email || '').toString().trim();
                        if (!name || !email) {
                            _showToast('姓名與 Email 必填');
                            return;
                        }
                        this.crmContactSaving = true;
                        try {
                            const payload = {
                                name,
                                email,
                                phone: (this.crmContactForm.phone || '').toString().trim() || null,
                                tags: (this.crmContactForm.tags || '').toString().trim() || null,
                            };
                            const res = await axios.put(`${ADMIN_API}/contacts/${id}`, payload);
                            const c = res?.data || null;
                            if (c) {
                                this.crmContact = c;
                                if (this.crmTimeline?.contact) {
                                    this.crmTimeline = {
                                        ...this.crmTimeline,
                                        contact: { ...this.crmTimeline.contact, name: c.name, email: c.email }
                                    };
                                }
                            }
                            this.crmContactEditOpen = false;
                        } catch (e) {
                            _showToast('儲存聯絡人失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.crmContactSaving = false;
                        }
                    },
                    insertCrmNoteTemplate(prefix) {
                        const p = (prefix || '').toString();
                        if (!p) return;
                        const cur = (this.crmNote?.note || '').toString();
                        const next = cur.trim() ? (cur.replace(/\s*$/, '') + '\n' + p + ' ') : (p + ' ');
                        this.crmNote.note = next;
                    },
                    crmFormatDateTimeForNote(iso) {
                        const s = (iso || '').toString().trim();
                        if (!s) return '';
                        try {
                            const d = new Date(s);
                            if (Number.isNaN(d.getTime())) return s;
                            return d.toLocaleString();
                        } catch (_) {
                            return s;
                        }
                    },
                    crmBuildCalendarTimelineFields(ev) {
                        if (!ev) return null;
                        const startIso = (ev.start_at || ev.start_time || '').toString();
                        const endIso = (ev.end_at || ev.end_time || '').toString();
                        const start = this.crmFormatDateTimeForNote(startIso);
                        const end = this.crmFormatDateTimeForNote(endIso);
                        const loc = (ev.location || '').toString().trim();
                        const status = (ev.status || '').toString().trim().toLowerCase();
                        const baseTitle = `行事曆：${(ev.title || '事件').toString()}`;
                        const title = status === 'canceled' ? `${baseTitle}（已取消）` : baseTitle;
                        let content = start || '';
                        if (end) content = content ? `${content} ～ ${end}` : end;
                        if (loc) content = content ? `${content}\n地點：${loc}` : `地點：${loc}`;
                        if (status === 'canceled') content = content ? `${content}\n狀態：已取消` : '狀態：已取消';
                        return { created_at: startIso || '', title, content };
                    },
                    syncCrmTimelineCalendarEvent(ev) {
                        const id = Number(ev?.id) || 0;
                        if (!id) return;
                        if (!this.crmTimeline || !Array.isArray(this.crmTimeline.events)) return;
                        const fields = this.crmBuildCalendarTimelineFields(ev);
                        if (!fields) return;
                        this.crmTimeline.events = this.crmTimeline.events.map(x => {
                            if ((x?.event_type || '') === 'calendar_event' && Number(x?.ref_id) === id) {
                                return { ...x, ...fields, ref_id: id };
                            }
                            return x;
                        });
                    },
                    removeCrmTimelineCalendarEvent(eventId) {
                        const id = Number(eventId) || 0;
                        if (!id) return;
                        if (!this.crmTimeline || !Array.isArray(this.crmTimeline.events)) return;
                        this.crmTimeline.events = this.crmTimeline.events.filter(x => !((x?.event_type || '') === 'calendar_event' && Number(x?.ref_id) === id));
                    },
                    openCrmPicker() {
                        if (!this.canAccess('crm')) {
                            _showToast('沒有權限使用此功能');
                            return;
                        }
                        this.currentView = 'webmail';
                        this.showCrmPickerModal = true;
                        this.crmPickerLoading = false;
                        this.crmPickerQuery = '';
                        this.crmPickerResults = [];
                        this.crmPickerError = '';
                    },
                    closeCrmPicker() {
                        this.showCrmPickerModal = false;
                        this.crmPickerLoading = false;
                        this.crmPickerQuery = '';
                        this.crmPickerResults = [];
                        this.crmPickerError = '';
                    },
                    async searchCrmContacts() {
                        const q = (this.crmPickerQuery || '').toString().trim();
                        this.crmPickerError = '';
                        this.crmPickerResults = [];
                        if (!q) return;
                        if (/^\d+$/.test(q)) {
                            this.closeCrmPicker();
                            await this.openCrmTimeline({ contact_id: Number(q) });
                            return;
                        }
                        this.crmPickerLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/contacts`, { params: { search: q, limit: 20, skip: 0 } });
                            const items = Array.isArray(res?.data?.items) ? res.data.items : (Array.isArray(res?.data) ? res.data : []);
                            this.crmPickerResults = items;
                            if (items.length === 0) {
                                this.crmPickerError = '找不到符合的聯絡人';
                            }
                        } catch (e) {
                            this.crmPickerError = (e.response?.data?.detail || e.message || '搜尋失敗');
                        } finally {
                            this.crmPickerLoading = false;
                        }
                    },
                    async openCrmFromPicked(c) {
                        const id = Number(c?.id) || 0;
                        if (!id) return;
                        this.closeCrmPicker();
                        await this.openCrmTimeline({ contact_id: id });
                    },
                    crmBuildOriginalTextFromTimeline() {
                        const evs = Array.isArray(this.crmTimeline?.events) ? this.crmTimeline.events : [];
                        const notes = evs.filter(e => (e?.event_type || '') === 'note');
                        if (notes.length > 0) {
                            return (notes[0]?.content || notes[0]?.title || '').toString();
                        }
                        const top = evs.slice(0, 3);
                        const lines = [];
                        for (const e of top) {
                            const t = (e?.title || '').toString().trim();
                            const c = (e?.content || '').toString().trim();
                            if (t) lines.push(t);
                            if (c) lines.push(c);
                        }
                        const base = lines.join('\n').trim();
                        if (base) return base;
                        const name = (this.crmTimeline?.contact?.name || '').toString().trim();
                        const email = (this.crmTimeline?.contact?.email || '').toString().trim();
                        return `聯絡人：${name || '—'} (${email || '—'})`;
                    },
                    async crmQuickFollowup() {
                        const recipient = (this.crmTimeline?.contact?.email || '').toString().trim();
                        if (!recipient) {
                            _showToast('此聯絡人沒有 Email');
                            return;
                        }
                        const originalText = this.crmBuildOriginalTextFromTimeline();
                        const subjectHint = (this.crmTimeline?.events || [])[0]?.title || '';
                        const payload = {
                            original_text: originalText || '',
                            recipient,
                            tone: 'professional',
                            instructions: '禮貌且具體，提出明確下一步與時間，簡短友善。',
                            original_subject: subjectHint || null,
                            language: 'Traditional Chinese (繁體中文)',
                            length: 'medium',
                            template: 'gentle_nudge',
                            include_quote: false
                        };
                        this.crmFollowupGenerating = true;
                        try {
                            let data = null;
                            for (const b of [ADMIN_API, window.location.origin, ADMIN_API_FALLBACK_8000]) {
                                try {
                                    const res = await axios.post(`${b}/ai/generate/followup`, payload);
                                    data = res?.data || null;
                                    if (data) break;
                                } catch (e) {
                                    const sc = Number(e?.response?.status || 0);
                                    if (sc === 404) continue;
                                    throw e;
                                }
                            }
                            if (!data) {
                                throw new Error('無法生成跟進草稿');
                            }
                            const subject = (data?.subject || '').toString().trim() || '跟進';
                            const body = (data?.body || data?.result || '').toString();
                            this.composeForm = {
                                to: recipient,
                                subject,
                                body,
                                attachments: [],
                                in_reply_to: null,
                                references: null
                            };
                            this.showComposeModal = true;
                        } catch (e) {
                            _showToast('生成跟進草稿失敗: ' + (e?.response?.data?.detail || e?.message || ''));
                        } finally {
                            this.crmFollowupGenerating = false;
                        }
                    },
                    closeCrmModal() {
                        this.showCrmModal = false;
                        this.crmTimeline = null;
                        this.crmNote = { contact_id: null, note: '' };
                        this.crmTimelineFilterType = 'all';
                        this.crmTimelineQuery = '';
                        this.crmContactLoading = false;
                        this.crmContactSaving = false;
                        this.crmContactEditOpen = false;
                        this.crmContact = null;
                        this.crmContactForm = { id: null, name: '', email: '', phone: '', tags: '' };
                        this.crmCalendarEvents = [];
                        this.crmCalendarTotal = 0;
                        this.crmCalendarCreateForm = { title: '', start_time: '', end_time: '', location: '', description: '' };
                    },
                    async fetchCrmCalendarEvents() {
                        const cid = Number(this.crmNote?.contact_id) || 0;
                        if (!cid) return;
                        this.crmCalendarLoading = true;
                        try {
                            const days = Math.max(1, Math.min(Number(this.crmCalendarRangeDays) || 30, 90));
                            this.crmCalendarRangeDays = days;
                            const start = new Date();
                            const end = new Date();
                            end.setDate(end.getDate() + days);
                            const res = await axios.get(`${ADMIN_API}/calendar/events`, {
                                params: { contact_id: cid, start: start.toISOString(), end: end.toISOString(), limit: 200 }
                            });
                            this.crmCalendarEvents = Array.isArray(res?.data?.items) ? res.data.items : [];
                            this.crmCalendarTotal = Number(res?.data?.total) || this.crmCalendarEvents.length || 0;
                        } catch (e) {
                            _showToast('載入行事曆（聯絡人）失敗: ' + (e.response?.data?.detail || e.message));
                            this.crmCalendarEvents = [];
                            this.crmCalendarTotal = 0;
                        } finally {
                            this.crmCalendarLoading = false;
                        }
                    },
                    async createCrmCalendarEvent() {
                        const cid = Number(this.crmNote?.contact_id) || 0;
                        if (!cid) return;
                        const title = (this.crmCalendarCreateForm.title || '').toString().trim();
                        const startTime = this.normalizeDateTimeLocal(this.crmCalendarCreateForm.start_time);
                        if (!title || !startTime) {
                            _showToast('標題與開始時間必填');
                            return;
                        }
                        this.crmCalendarCreating = true;
                        try {
                            const payload = {
                                title,
                                start_time: startTime,
                                end_time: this.normalizeDateTimeLocal(this.crmCalendarCreateForm.end_time) || null,
                                location: (this.crmCalendarCreateForm.location || '').toString().trim() || null,
                                description: (this.crmCalendarCreateForm.description || '').toString().trim() || null,
                                contact_id: cid,
                                contact_email: (this.crmTimeline?.contact?.email || '').toString().trim() || null,
                                source_type: 'manual'
                            };
                            const res = await axios.post(`${ADMIN_API}/calendar/events`, payload);
                            const created = res?.data || null;
                            this.crmCalendarCreateForm.start_time = '';
                            this.crmCalendarCreateForm.end_time = '';
                            this.crmCalendarCreateForm.location = '';
                            this.crmCalendarCreateForm.description = '';
                            await this.fetchCrmCalendarEvents();
                            if (created && this.crmTimeline && Array.isArray(this.crmTimeline.events)) {
                                const fields = this.crmBuildCalendarTimelineFields(created);
                                if (fields) {
                                    const evId = Number(created?.id) || null;
                                    const item = {
                                        id: `calendar_event_${fields.created_at || ''}_${evId || ''}`,
                                        event_type: 'calendar_event',
                                        ref_id: evId,
                                        created_at: fields.created_at,
                                        title: fields.title,
                                        content: fields.content
                                    };
                                    this.crmTimeline.events = [item, ...this.crmTimeline.events];
                                }
                            }
                            const nowIso = new Date().toISOString();
                            if (this.crmTimeline?.contact) {
                                this.crmTimeline = { ...this.crmTimeline, contact: { ...this.crmTimeline.contact, last_interaction_at: nowIso } };
                            }
                        } catch (e) {
                            _showToast('建立行事曆事件失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.crmCalendarCreating = false;
                        }
                    },
                    async saveCrmNote() {
                        const cid = Number(this.crmNote?.contact_id) || 0;
                        const note = (this.crmNote?.note || '').toString().trim();
                        if (!cid) {
                            _showToast('找不到 contact_id');
                            return;
                        }
                        if (!note) {
                            _showToast('請輸入摘要');
                            return;
                        }
                        this.crmLoading = true;
                        try {
                            await axios.post(`${ADMIN_API}/sales/crm/note`, { contact_id: cid, note });
                            const nowIso = new Date().toISOString();
                            if (this.crmTimeline && Array.isArray(this.crmTimeline.events)) {
                                const item = {
                                    id: `note_${nowIso}`,
                                    event_type: 'note',
                                    ref_id: null,
                                    created_at: nowIso,
                                    title: '摘要',
                                    content: note
                                };
                                this.crmTimeline.events = [item, ...this.crmTimeline.events];
                            }
                            if (this.crmTimeline?.contact) {
                                this.crmTimeline = { ...this.crmTimeline, contact: { ...this.crmTimeline.contact, last_interaction_at: nowIso } };
                            }
                            this.crmNote.note = '';
                        } catch (e) {
                            _showToast('儲存摘要失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.crmLoading = false;
                        }
                    },
                    async scanInboundReplies() {
                        this.autoRepliesScanLoading = true;
                        try {
                            if (!await this.ensureMailSession('掃描收件匣')) return;
                            const res = await axios.post(`${ADMIN_API}/ai/auto-replies/scan`, null, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const s = res.data || {};
                            _showToast(`掃描完成：掃描 ${s.scanned || 0} 封，命中聯絡人 ${s.matched_contacts || 0}，新增草稿 ${s.created || 0}`);
                            await this.fetchAutoReplies();
                        } catch (e) {
                            _showToast('掃描失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.autoRepliesScanLoading = false;
                        }
                    },
                    async scanOpportunities() {
                        this.opportunitiesScanLoading = true;
                        try {
                            if (!await this.ensureMailSession('掃描收件匣')) return;
                            const res = await axios.post(`${ADMIN_API}/ai/opportunities/scan`, null, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const s = res.data || {};
                            _showToast(`商機掃描完成：掃描 ${s.scanned || 0} 封，新增 ${s.created || 0}，略過重複 ${s.skipped_existing || 0}，AI 失敗 ${s.ai_errors || 0}`);
                            if (this.aiSalesTab === 'opportunities') {
                                this.resetOpportunitiesPaging();
                                await this.fetchOpportunities();
                            }
                        } catch (e) {
                            _showToast('商機掃描失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.opportunitiesScanLoading = false;
                        }
                    },
                    getLineWebhookUrl(settingId) {
                        const id = Number(settingId) || 0;
                        const hostname = (window.location.hostname || '').toString().toLowerCase();
                        const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
                        const scheme = isLocal ? (window.location.protocol || 'http:') : 'https:';
                        return `${scheme}//${window.location.host}/api/line/webhook/${id}`;
                    },
                    scrollToLineConversations() {
                        this.lineSupportTab = 'conversations';
                        nextTick(() => {
                            const scroller = this.$refs?.lineSupportScroller;
                            if (scroller) scroller.scrollTop = 0;
                        });
                    },
                    scrollToLineSettings() {
                        if (!(this.isDomainAdmin || this.isSuperAdmin)) {
                            this.lineSupportTab = 'conversations';
                            return;
                        }
                        this.lineSupportTab = 'settings';
                        nextTick(() => {
                            const scroller = this.$refs?.lineSupportScroller;
                            if (scroller) scroller.scrollTop = 0;
                        });
                    },
                    async openLineSupportView() {
                        if (!this.setCurrentView('line_support')) return;
                        this.lineSupportTab = 'conversations';
                        await this.refreshLineSupport();
                        await nextTick();
                        const scroller = this.$refs?.lineSupportScroller;
                        if (scroller) scroller.scrollTop = 0;
                        try {
                            this.updateLineMessagesPadBottom();
                        } catch (e0) {
                        }
                    },
                    async refreshLineSupport() {
                        if (this.lineSupportRefreshPromise) {
                            await this.lineSupportRefreshPromise;
                            return;
                        }
                        this.lineSupportRefreshing = true;
                        const p = (async () => {
                            await this.ensureLineSupportLookupsLoaded();
                            if (this.isDomainAdmin || this.isSuperAdmin) {
                                await this.fetchLineSettings();
                            }
                            await this.fetchLineConversations();
                            if (this.selectedLineConversation?.id) {
                                await this.fetchLineMessages(this.selectedLineConversation.id);
                            }
                        })();
                        this.lineSupportRefreshPromise = p;
                        try {
                            await p;
                        } finally {
                            this.lineSupportRefreshPromise = null;
                            this.lineSupportRefreshing = false;
                        }
                    },
                    async ensureLineSupportLookupsLoaded(force = false) {
                        const needUsers = force || !(Array.isArray(this.lineUsers) && this.lineUsers.length);
                        const needDepartments = (this.isDomainAdmin || this.isSuperAdmin) && (force || !(Array.isArray(this.lineDepartments) && this.lineDepartments.length));
                        const tasks = [];
                        if (needUsers) tasks.push(this.fetchLineUsers());
                        if (needDepartments) tasks.push(this.fetchLineDepartments());
                        if (tasks.length) await Promise.all(tasks);
                    },
                    async fetchLineDepartments() {
                        if (!(this.isDomainAdmin || this.isSuperAdmin)) return;
                        this.lineDepartmentsLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/departments`, { params: { limit: 200 } });
                            const items = res.data || [];
                            const list = Array.isArray(items) ? items : [];
                            this.lineDepartments = list
                                .filter(x => x && typeof x === 'object' && (Number(x.id) || 0))
                                .sort((a, b) => ((a?.name || '').toString()).localeCompare((b?.name || '').toString(), 'zh-Hant'));
                        } catch (e) {
                            this.lineDepartments = [];
                        } finally {
                            this.lineDepartmentsLoading = false;
                        }
                    },
                    async fetchLineUsers() {
                        this.lineUsersLoading = true;
                        try {
                            let items = null;
                            try {
                                const res = await axios.get(`${ADMIN_API}/users/brief`, { params: { _ts: Date.now() } });
                                items = res?.data ?? null;
                            } catch (e1) {
                                if (this.isDomainAdmin || this.isSuperAdmin) {
                                    const res = await axios.get(`${ADMIN_API}/users`, { params: { limit: 200, _ts: Date.now() } });
                                    items = res?.data ?? null;
                                } else {
                                    throw e1;
                                }
                            }
                            const list = Array.isArray(items) ? items : (Array.isArray(items?.items) ? items.items : []);
                            this.lineUsers = list
                                .filter(x => x && typeof x === 'object' && (Number(x.id) || 0))
                                .sort((a, b) => ((a?.email || '').toString()).localeCompare((b?.email || '').toString()));
                        } catch (e) {
                            this.lineUsers = [];
                        } finally {
                            this.lineUsersLoading = false;
                        }
                    },
                    selectLineSetting(settingId) {
                        const id = Number(settingId) || 0;
                        if (!id) return;
                        this.selectedLineSettingId = id;
                        this.lineConversationSettingId = id;
                        this.fetchLineConversations();
                    },
                    startCreateLineSetting() {
                        this.lineEditingSettingId = null;
                        this.lineSettingForm = {
                            name: 'LINE OA',
                            channel_id: '',
                            channel_secret: '',
                            channel_access_token: '',
                            active: true,
                            auto_reply_enabled: false,
                            auto_reply_tone: 'professional',
                            auto_reply_only_unassigned: true,
                            auto_reply_cooldown_seconds: 60,
                            auto_reply_pause_after_human_seconds: 600,
                            auto_reply_rag_enabled: false,
                            auto_reply_rag_only: false,
                            auto_reply_rag_department_id: '',
                            auto_reply_rag_doc_ids: ''
                        };
                    },
                    startEditLineSetting(setting) {
                        const id = Number(setting?.id) || 0;
                        if (!id) return;
                        this.lineEditingSettingId = id;
                        this.lineSettingForm = {
                            name: (setting?.name || 'LINE OA').toString(),
                            channel_id: (setting?.channel_id || '').toString(),
                            channel_secret: '',
                            channel_access_token: '',
                            active: !!setting?.active,
                            auto_reply_enabled: !!setting?.auto_reply_enabled,
                            auto_reply_tone: (setting?.auto_reply_tone || 'professional').toString(),
                            auto_reply_only_unassigned: setting?.auto_reply_only_unassigned !== false,
                            auto_reply_cooldown_seconds: Number(setting?.auto_reply_cooldown_seconds ?? 60) || 0,
                            auto_reply_pause_after_human_seconds: Number(setting?.auto_reply_pause_after_human_seconds ?? 600) || 0,
                            auto_reply_rag_enabled: !!setting?.auto_reply_rag_enabled,
                            auto_reply_rag_only: !!setting?.auto_reply_rag_only,
                            auto_reply_rag_department_id: setting?.auto_reply_rag_department_id != null ? String(setting.auto_reply_rag_department_id) : '',
                            auto_reply_rag_doc_ids: (setting?.auto_reply_rag_doc_ids || '').toString()
                        };
                    },
                    getDepartmentNameById(departmentId) {
                        const id = Number(departmentId) || 0;
                        if (!id) return '';
                        const items = Array.isArray(this.lineDepartments) ? this.lineDepartments : [];
                        const found = items.find(d => (Number(d?.id) || 0) === id);
                        return (found?.name || '').toString();
                    },
                    _parseIntListText(text) {
                        const raw = (text || '').toString().trim();
                        if (!raw) return [];
                        const parts = raw.split(/[\s,]+/g);
                        const out = [];
                        const seen = new Set();
                        for (const p of parts) {
                            const n = Number(String(p || '').trim());
                            const id = Number.isFinite(n) ? Math.trunc(n) : 0;
                            if (!id) continue;
                            if (seen.has(id)) continue;
                            seen.add(id);
                            out.push(id);
                        }
                        return out;
                    },
                    openLineRagDocPicker() {
                        this.showLineRagDocPicker = true;
                        this.kbPickerSearch = '';
                        this.kbPickerDepartmentId = (this.lineSettingForm?.auto_reply_rag_department_id ?? '').toString();
                        const current = this._parseIntListText(this.lineSettingForm?.auto_reply_rag_doc_ids || '');
                        this.kbPickerSelectedIds = current;
                        this.kbPickerSkip = 0;
                        this.kbPickerHasMore = false;
                        this.kbPickerDocs = [];
                        this.fetchKbPickerDocs(true);
                    },
                    closeLineRagDocPicker() {
                        this.showLineRagDocPicker = false;
                    },
                    async fetchKbPickerDocs(reset = true) {
                        this.kbPickerLoading = true;
                        try {
                            if (reset) {
                                this.kbPickerSkip = 0;
                                this.kbPickerHasMore = false;
                                this.kbPickerDocs = [];
                            }
                            const skip = Number(this.kbPickerSkip) || 0;
                            const limit = Math.max(10, Math.min(Number(this.kbPickerLimit) || 50, 200));
                            const params = { skip, limit };
                            const q = (this.kbPickerSearch || '').toString().trim();
                            if (q) params.q = q;
                            const dep = (this.kbPickerDepartmentId || '').toString().trim();
                            if (dep !== '') params.department_id = Number(dep);
                            const res = await axios.get(`${ADMIN_API}/ai/knowledge_base`, { params });
                            const items = res?.data ?? [];
                            const rows = Array.isArray(items)
                                ? items.filter(x => x && typeof x === 'object' && (Number(x.id) || 0))
                                : [];
                            if (reset) {
                                this.kbPickerDocs = rows;
                            } else {
                                const existing = Array.isArray(this.kbPickerDocs) ? this.kbPickerDocs : [];
                                const byId = new Map(existing.map(x => [Number(x?.id) || 0, x]));
                                for (const r of rows) {
                                    const id = Number(r?.id) || 0;
                                    if (!id) continue;
                                    if (!byId.has(id)) byId.set(id, r);
                                }
                                this.kbPickerDocs = Array.from(byId.values());
                            }
                            const got = rows.length;
                            this.kbPickerSkip = skip + got;
                            this.kbPickerHasMore = got >= limit;
                        } catch (e) {
                            this.kbPickerDocs = [];
                            this.kbPickerSkip = 0;
                            this.kbPickerHasMore = false;
                            _showToast('載入知識文件失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.kbPickerLoading = false;
                        }
                    },
                    loadMoreKbPickerDocs() {
                        return this.fetchKbPickerDocs(false);
                    },
                    isKbPickerSelected(docId) {
                        const id = Number(docId) || 0;
                        if (!id) return false;
                        const ids = Array.isArray(this.kbPickerSelectedIds) ? this.kbPickerSelectedIds : [];
                        return ids.includes(id);
                    },
                    toggleKbPickerSelected(docId) {
                        const id = Number(docId) || 0;
                        if (!id) return;
                        const ids = Array.isArray(this.kbPickerSelectedIds) ? [...this.kbPickerSelectedIds] : [];
                        const idx = ids.indexOf(id);
                        if (idx >= 0) ids.splice(idx, 1);
                        else ids.push(id);
                        ids.sort((a, b) => a - b);
                        this.kbPickerSelectedIds = ids;
                    },
                    applyKbPickerSelection() {
                        const ids = Array.isArray(this.kbPickerSelectedIds) ? this.kbPickerSelectedIds : [];
                        const normalized = ids
                            .map(x => Number(x) || 0)
                            .filter(Boolean)
                            .sort((a, b) => a - b);
                        this.lineSettingForm.auto_reply_rag_doc_ids = normalized.length ? normalized.join(',') : '';
                        this.closeLineRagDocPicker();
                    },
                    async fetchLineSettings() {
                        this.lineSettingsLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/line/settings`);
                            const items = res.data || [];
                            const list = Array.isArray(items) ? items.filter(x => x && typeof x === 'object') : [];
                            this.lineSettingsAll = list;
                            this.lineSettings = list;
                            const allowedIds = new Set(list.map(x => Number(x?.id) || 0).filter(Boolean));
                            if (Number(this.selectedLineSettingId) && !allowedIds.has(Number(this.selectedLineSettingId))) {
                                this.selectedLineSettingId = null;
                            }
                            if (Number(this.lineConversationSettingId) && !allowedIds.has(Number(this.lineConversationSettingId))) {
                                this.lineConversationSettingId = 0;
                            }
                        } catch (e) {
                            this.lineSettings = [];
                            this.lineSettingsAll = [];
                            this.selectedLineSettingId = null;
                        } finally {
                            this.lineSettingsLoading = false;
                        }
                    },
                    async restoreWorkingLineSetting() {
                        if (this.lineSettingRestoringId != null) return;
                        this.lineSettingRestoringId = -1;
                        try {
                            const res = await axios.post(`${ADMIN_API}/line/settings/restore_working`, {});
                            const id = Number(res?.data?.restored_id) || 0;
                            const info = res?.data?.bot_info || {};
                            const displayName = (info?.displayName || '').toString().trim();
                            const basicId = (info?.basicId || '').toString().trim();
                            const userId = (info?.userId || '').toString().trim();
                            await this.fetchLineSettings();
                            if (id) this.selectedLineSettingId = id;
                            if (id) this.lineConversationSettingId = id;
                            await this.fetchLineConversations();
                            if (id && Number(this.lineConversationSettingId) === id && Array.isArray(this.lineConversations) && this.lineConversations.length === 0) {
                                this.lineConversationSettingId = 0;
                                await this.fetchLineConversations();
                            }
                            if (this.selectedLineConversation?.id) {
                                await this.fetchLineMessages(this.selectedLineConversation.id);
                            }
                            _showToast(`已抓回可用設定${displayName ? '：' + displayName : ''}${basicId ? '（' + basicId + '）' : (userId ? '（' + userId + '）' : '')}`);
                        } catch (e) {
                            _showToast('抓回失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.lineSettingRestoringId = null;
                        }
                    },
                    async restoreLineSetting(settingId) {
                        const id = Number(settingId) || 0;
                        if (!id) return;
                        if (this.lineSettingRestoringId != null) return;
                        this.lineSettingRestoringId = id;
                        try {
                            await axios.put(`${ADMIN_API}/line/settings/${id}`, { active: true });
                            await this.fetchLineSettings();
                            this.selectedLineSettingId = id;
                            this.lineConversationSettingId = id;
                            await this.fetchLineConversations();
                            if (Number(this.lineConversationSettingId) === id && Array.isArray(this.lineConversations) && this.lineConversations.length === 0) {
                                this.lineConversationSettingId = 0;
                                await this.fetchLineConversations();
                            }
                            if (this.selectedLineConversation?.id) {
                                await this.fetchLineMessages(this.selectedLineConversation.id);
                            }
                            _showToast('已恢復（已啟用）');
                        } catch (e) {
                            _showToast('恢復失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.lineSettingRestoringId = null;
                        }
                    },
                    async saveLineSetting() {
                        const name = (this.lineSettingForm?.name || 'LINE OA').toString().trim() || 'LINE OA';
                        const channelId = (this.lineSettingForm?.channel_id || '').toString().trim();
                        const secret = (this.lineSettingForm?.channel_secret || '').toString().trim();
                        const token = (this.lineSettingForm?.channel_access_token || '').toString().trim();
                        const active = !!this.lineSettingForm?.active;
                        const auto_reply_enabled = !!this.lineSettingForm?.auto_reply_enabled;
                        const auto_reply_tone = (this.lineSettingForm?.auto_reply_tone || 'professional').toString().trim() || 'professional';
                        const auto_reply_only_unassigned = !!this.lineSettingForm?.auto_reply_only_unassigned;
                        const auto_reply_cooldown_seconds = Number(this.lineSettingForm?.auto_reply_cooldown_seconds || 0) || 0;
                        const auto_reply_pause_after_human_seconds = Number(this.lineSettingForm?.auto_reply_pause_after_human_seconds || 0) || 0;
                        const auto_reply_rag_enabled = !!this.lineSettingForm?.auto_reply_rag_enabled;
                        const auto_reply_rag_only = !!this.lineSettingForm?.auto_reply_rag_only;
                        const auto_reply_rag_department_id = (this.lineSettingForm?.auto_reply_rag_department_id || '').toString().trim();
                        const auto_reply_rag_doc_ids = (this.lineSettingForm?.auto_reply_rag_doc_ids || '').toString().trim();

                        this.lineSettingSaving = true;
                        try {
                            if (this.lineEditingSettingId) {
                                const payload = { name, channel_id: channelId || null, active };
                                if (secret) payload.channel_secret = secret;
                                if (token) payload.channel_access_token = token;
                                payload.auto_reply_enabled = auto_reply_enabled;
                                payload.auto_reply_tone = auto_reply_tone;
                                payload.auto_reply_only_unassigned = auto_reply_only_unassigned;
                                payload.auto_reply_cooldown_seconds = auto_reply_cooldown_seconds;
                                payload.auto_reply_pause_after_human_seconds = auto_reply_pause_after_human_seconds;
                                payload.auto_reply_rag_enabled = auto_reply_rag_enabled;
                                payload.auto_reply_rag_only = auto_reply_rag_only;
                                payload.auto_reply_rag_department_id = auto_reply_rag_department_id ? Number(auto_reply_rag_department_id) : null;
                                payload.auto_reply_rag_doc_ids = auto_reply_rag_doc_ids || null;
                                await axios.put(`${ADMIN_API}/line/settings/${this.lineEditingSettingId}`, payload);
                                await this.fetchLineSettings();
                                this.startCreateLineSetting();
                                return;
                            }

                            if (!secret || !token) {
                                _showToast('建立設定需要 Channel Secret 與 Channel Access Token');
                                return;
                            }
                            await axios.post(`${ADMIN_API}/line/settings`, {
                                name,
                                channel_id: channelId || null,
                                channel_secret: secret,
                                channel_access_token: token,
                                active,
                                auto_reply_enabled,
                                auto_reply_tone,
                                auto_reply_only_unassigned,
                                auto_reply_cooldown_seconds,
                                auto_reply_pause_after_human_seconds,
                                auto_reply_rag_enabled,
                                auto_reply_rag_only,
                                auto_reply_rag_department_id: auto_reply_rag_department_id ? Number(auto_reply_rag_department_id) : null,
                                auto_reply_rag_doc_ids: auto_reply_rag_doc_ids || null
                            });
                            await this.fetchLineSettings();
                            this.startCreateLineSetting();
                        } catch (e) {
                            _showToast('儲存 LINE 設定失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.lineSettingSaving = false;
                        }
                    },
                    async testLineSettingHealth(settingId) {
                        const id = Number(settingId) || 0;
                        if (!id) return;
                        if (this.lineSettingHealthLoadingId) return;
                        this.lineSettingHealthLoadingId = id;
                        try {
                            const res = await axios.get(`${ADMIN_API}/line/settings/${id}/health`);
                            const info = res?.data?.bot_info || {};
                            const displayName = (info?.displayName || '').toString().trim();
                            const basicId = (info?.basicId || '').toString().trim();
                            const userId = (info?.userId || '').toString().trim();
                            _showToast(`連線成功${displayName ? '：' + displayName : ''}${basicId ? '（' + basicId + '）' : (userId ? '（' + userId + '）' : '')}`);
                            await this.fetchLineSettings();
                        } catch (e) {
                            _showToast('測試連線失敗: ' + (e.response?.data?.detail || e.message));
                            await this.fetchLineSettings();
                        } finally {
                            this.lineSettingHealthLoadingId = null;
                        }
                    },
                    async deleteLineSetting(settingId) {
                        const id = Number(settingId) || 0;
                        if (!id) return;
                        if (this.lineSettingDeletingId) return;
                        const ok = confirm('確定要刪除這筆 LINE 官方帳號設定？\n\n刪除後會停用此設定，並無法再用它接收/回覆訊息。');
                        if (!ok) return;
                        this.lineSettingDeletingId = id;
                        try {
                            await axios.delete(`${ADMIN_API}/line/settings/${id}`);
                            if (this.selectedLineSettingId === id) this.selectedLineSettingId = null;
                            if (Number(this.lineConversationSettingId) === id) {
                                this.lineConversationSettingId = 0;
                                await this.fetchLineConversations();
                            }
                            if (this.lineEditingSettingId === id) this.startCreateLineSetting();
                            await this.fetchLineSettings();
                            _showToast('已刪除（已停用）');
                        } catch (e) {
                            _showToast('刪除失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.lineSettingDeletingId = null;
                        }
                    },
                    async fetchLineConversations({ append = false } = {}) {
                        this.lineConversationsLoading = true;
                        this.lineConversationsError = '';
                        const LIMIT = 50;
                        if (!append) this.lineConversationOffset = 0;
                        const selectedId = Number(this.selectedLineConversation?.id) || 0;
                        try {
                            const params = {
                                limit: LIMIT,
                                offset: this.lineConversationOffset,
                                assigned: this.lineConversationAssigned
                            };
                            const s = (this.lineConversationSearch || '').trim();
                            if (s) params.search = s;
                            const st = (this.lineConversationStatus || '').trim();
                            if (st) params.status = st;
                            const sid = Number(this.lineConversationSettingId) || 0;
                            if (sid) params.setting_id = sid;
                            const tag = (this.lineConversationTagFilter || '').trim();
                            if (tag) params.tag = tag;
                            const res = await axios.get(`${ADMIN_API}/line/conversations`, { params: { ...params, _ts: Date.now() } });
                            const ct = (res?.headers?.['content-type'] || '').toString().toLowerCase();
                            const items = res?.data;
                            if (!Array.isArray(items)) {
                                const preview = (typeof items === 'string' ? items : JSON.stringify(items || null)).slice(0, 240);
                                this.lineConversationsError = `載入對話失敗：API 回應格式不正確${ct ? `（${ct}）` : ''}${preview ? `：${preview}` : ''}`;
                                if (!append) this.lineConversations = [];
                                return;
                            }
                            this.lineConversationHasMore = items.length >= LIMIT;
                            if (append) {
                                // Merge: avoid duplicates by id
                                const existingIds = new Set((this.lineConversations || []).map(x => Number(x?.id) || 0));
                                const newItems = items.filter(x => x && !existingIds.has(Number(x.id) || 0));
                                this.lineConversations = [...(this.lineConversations || []), ...newItems];
                                // Update existing entries in place
                                items.forEach(item => {
                                    const idx = (this.lineConversations || []).findIndex(x => (Number(x?.id) || 0) === (Number(item?.id) || 0));
                                    if (idx >= 0) this.lineConversations[idx] = item;
                                });
                            } else {
                                this.lineConversations = items;
                            }
                            if (selectedId) {
                                const updated = this.lineConversations.find(x => x && (Number(x.id) || 0) === selectedId);
                                if (updated) {
                                    this.selectedLineConversation = updated;
                                }
                            }
                            if (!selectedId && this.lineConversations.length > 0) {
                                const first = this.lineConversations[0];
                                this.selectedLineConversation = first;
                                this.lineAiSuggestion = null;
                                this.lineAssignDepartmentId = first?.assigned_department_id != null ? String(first.assigned_department_id) : '';
                                this.lineAssignUserId = first?.assigned_user_id != null ? String(first.assigned_user_id) : '';
                                await this.fetchLineMessages(first.id);
                                try {
                                    await axios.post(`${ADMIN_API}/line/conversations/${first.id}/mark_read`);
                                } catch (e) {
                                }
                            }
                        } catch (e) {
                            const status = e?.response?.status;
                            const detail = e?.response?.data?.detail || e?.message;
                            this.lineConversationsError = `載入對話失敗${status ? `（${status}）` : ''}：${detail || 'unknown error'}`;
                        } finally {
                            this.lineConversationsLoading = false;
                        }
                    },
                    async loadMoreLineConversations() {
                        if (!this.lineConversationHasMore || this.lineConversationsLoading) return;
                        this.lineConversationOffset += 50;
                        await this.fetchLineConversations({ append: true });
                    },
                    // Feature F: update tags
                    async updateLineConversationTags(conv_id, tags_str) {
                        const cid = Number(conv_id) || 0;
                        if (!cid) return;
                        await axios.post(`${ADMIN_API}/line/conversations/${cid}/tags`, { tags: tags_str || '' });
                        const norm = (tags_str || '').split(',').map(t => t.trim()).filter(Boolean).join(',');
                        if (this.selectedLineConversation && Number(this.selectedLineConversation.id) === cid) {
                            this.selectedLineConversation = { ...this.selectedLineConversation, tags: norm || null };
                        }
                        const idx = (this.lineConversations || []).findIndex(x => Number(x?.id) === cid);
                        if (idx >= 0) this.lineConversations[idx] = { ...this.lineConversations[idx], tags: norm || null };
                    },
                    // Feature I: batch operations
                    toggleLineBatchMode() {
                        this.lineBatchMode = !this.lineBatchMode;
                        if (!this.lineBatchMode) this.lineBatchSelected = [];
                    },
                    toggleLineBatchSelect(id) {
                        const n = Number(id) || 0;
                        if (!n) return;
                        const idx = this.lineBatchSelected.indexOf(n);
                        if (idx >= 0) this.lineBatchSelected.splice(idx, 1);
                        else this.lineBatchSelected.push(n);
                    },
                    selectAllLineBatch() {
                        this.lineBatchSelected = (this.safeLineConversations || []).map(c => Number(c?.id) || 0).filter(Boolean);
                    },
                    clearLineBatchSelect() {
                        this.lineBatchSelected = [];
                    },
                    async batchLineConversations(action, extra = {}) {
                        if (!this.lineBatchSelected.length) return;
                        this.lineBatchLoading = true;
                        try {
                            await axios.post(`${ADMIN_API}/line/conversations/batch`, {
                                ids: this.lineBatchSelected,
                                action,
                                ...extra,
                            });
                            this.lineBatchSelected = [];
                            await this.fetchLineConversations();
                            _showToast(`批次操作完成（${action}）`);
                        } catch (e) {
                            _showToast('批次操作失敗: ' + (e?.response?.data?.detail || e?.message));
                        } finally {
                            this.lineBatchLoading = false;
                        }
                    },
                    // Feature H: dashboard
                    async fetchLineDashboard() {
                        this.lineDashboardLoading = true;
                        try {
                            const params = {};
                            const sid = Number(this.lineDashboardSettingId) || 0;
                            if (sid) params.setting_id = sid;
                            const res = await axios.get(`${ADMIN_API}/line/dashboard`, { params });
                            this.lineDashboard = res.data;
                        } catch (e) {
                            _showToast('載入統計失敗: ' + (e?.response?.data?.detail || e?.message));
                        } finally {
                            this.lineDashboardLoading = false;
                        }
                    },
                    // Feature K: broadcast
                    async previewLineBroadcast() {
                        const sid = Number(this.lineBroadcastForm.setting_id) || 0;
                        if (!sid) { _showToast('請選擇官方帳號'); return; }
                        this.lineBroadcastLoading = true;
                        this.lineBroadcastResult = null;
                        try {
                            const res = await axios.post(`${ADMIN_API}/line/broadcast`, {
                                setting_id: sid,
                                text: (this.lineBroadcastForm.text || '').trim() || '(預覽)',
                                tag: (this.lineBroadcastForm.tag || '').trim() || null,
                                status: this.lineBroadcastForm.status || 'open',
                                dry_run: true,
                            });
                            this.lineBroadcastResult = { ...res.data, preview: true };
                        } catch (e) {
                            _showToast('預覽失敗: ' + (e?.response?.data?.detail || e?.message));
                        } finally {
                            this.lineBroadcastLoading = false;
                        }
                    },
                    async sendLineBroadcast() {
                        const sid = Number(this.lineBroadcastForm.setting_id) || 0;
                        const text = (this.lineBroadcastForm.text || '').trim();
                        if (!sid) { _showToast('請選擇官方帳號'); return; }
                        if (!text) { _showToast('請輸入訊息內容'); return; }
                        const total = this.lineBroadcastResult?.total;
                        if (!confirm(`確定要對 ${total ?? '?'} 個對話發送廣播訊息？此操作無法撤銷。`)) return;
                        this.lineBroadcastLoading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/line/broadcast`, {
                                setting_id: sid,
                                text,
                                tag: (this.lineBroadcastForm.tag || '').trim() || null,
                                status: this.lineBroadcastForm.status || 'open',
                                dry_run: false,
                            });
                            this.lineBroadcastResult = { ...res.data, preview: false };
                            _showToast(`廣播完成：成功 ${res.data.sent} / 失敗 ${res.data.failed}`);
                        } catch (e) {
                            _showToast('廣播失敗: ' + (e?.response?.data?.detail || e?.message));
                        } finally {
                            this.lineBroadcastLoading = false;
                        }
                    },
                    async fetchLineConversationEvents() {
                        const cid = Number(this.selectedLineConversation?.id) || 0;
                        if (!cid) return;
                        this.lineConversationEventsLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/line/conversations/${cid}/events`);
                            this.lineConversationEvents = Array.isArray(res.data) ? res.data : [];
                        } catch (e) {
                            _showToast('載入歷程失敗: ' + (e?.response?.data?.detail || e?.message));
                        } finally {
                            this.lineConversationEventsLoading = false;
                        }
                    },
                    async debugLineConversations() {
                        if (this.lineDebugLoading) return;
                        this.lineDebugLoading = true;
                        try {
                            const token = (localStorage.getItem('token') || '').toString();
                            const assigned = (this.lineConversationAssigned || 'any').toString();
                            const url = `${window.location.origin}/api/line/conversations?limit=5&assigned=${encodeURIComponent(assigned)}`;
                            const res = await fetch(url, {
                                headers: token ? { Authorization: `Bearer ${token}` } : {}
                            });
                            const ct = (res.headers.get('content-type') || '').toString();
                            const txt = await res.text();
                            this.lineConversationsError = `debug: ${res.status}${ct ? ` ${ct}` : ''} url=${url}`;
                            const short = txt.length > 1400 ? (txt.slice(0, 1400) + '...(truncated)') : txt;
                            _showToast(short || '(empty)');
                        } catch (e) {
                            _showToast('偵錯失敗: ' + (e?.message || e));
                        } finally {
                            this.lineDebugLoading = false;
                        }
                    },
                    async claimLineConversation() {
                        const id = Number(this.selectedLineConversation?.id) || 0;
                        if (!id || this.lineConversationActionLoading) return;
                        this.lineConversationActionLoading = true;
                        try {
                            await axios.post(`${ADMIN_API}/line/conversations/${id}/claim`);
                            await this.fetchLineConversations();
                        } catch (e) {
                            _showToast('指派失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.lineConversationActionLoading = false;
                        }
                    },
                    async unassignLineConversation() {
                        const id = Number(this.selectedLineConversation?.id) || 0;
                        if (!id || this.lineConversationActionLoading) return;
                        this.lineConversationActionLoading = true;
                        try {
                            await axios.post(`${ADMIN_API}/line/conversations/${id}/unassign`);
                            await this.fetchLineConversations();
                        } catch (e) {
                            _showToast('取消指派失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.lineConversationActionLoading = false;
                        }
                    },
                    async toggleLineConversationStatus() {
                        const id = Number(this.selectedLineConversation?.id) || 0;
                        if (!id || this.lineConversationActionLoading) return;
                        const current = (this.selectedLineConversation?.status || 'open').toString().trim().toLowerCase();
                        const next = current === 'closed' ? 'open' : 'closed';
                        this.lineConversationActionLoading = true;
                        try {
                            await axios.post(`${ADMIN_API}/line/conversations/${id}/status`, { status: next });
                            await this.fetchLineConversations();
                        } catch (e) {
                            _showToast('更新狀態失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.lineConversationActionLoading = false;
                        }
                    },
                    async selectLineConversation(conv) {
                        if (!conv?.id) return;
                        this.selectedLineConversation = conv;
                        this.lineAiSuggestion = null;
                        this.lineAssignDepartmentId = conv?.assigned_department_id != null ? String(conv.assigned_department_id) : '';
                        this.lineAssignUserId = conv?.assigned_user_id != null ? String(conv.assigned_user_id) : '';
                        await this.fetchLineMessages(conv.id);
                        await nextTick();
                        this.scrollLineMessagesToBottom();
                        try {
                            await axios.post(`${ADMIN_API}/line/conversations/${conv.id}/mark_read`);
                        } catch (e) {
                        }
                        this.fetchLineConversations();
                    },
                    async assignLineConversationDepartment() {
                        const cid = Number(this.selectedLineConversation?.id) || 0;
                        if (!cid) return;
                        if (!(this.isDomainAdmin || this.isSuperAdmin)) return;
                        if (this.lineConversationAssignLoading) return;
                        this.lineConversationAssignLoading = true;
                        try {
                            const department_id = this.lineAssignDepartmentId ? (Number(this.lineAssignDepartmentId) || 0) : null;
                            const res = await axios.post(`${ADMIN_API}/line/conversations/${cid}/assign_department`, { department_id });
                            const assigned_department_id = res?.data?.assigned_department_id ?? null;
                            if (this.selectedLineConversation && (Number(this.selectedLineConversation.id) || 0) === cid) {
                                this.selectedLineConversation = { ...this.selectedLineConversation, assigned_department_id };
                            }
                            await this.fetchLineConversations();
                        } catch (e) {
                            _showToast('指派部門失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.lineConversationAssignLoading = false;
                        }
                    },
                    async assignLineConversationUser() {
                        const cid = Number(this.selectedLineConversation?.id) || 0;
                        if (!cid) return;
                        if (!(this.isDomainAdmin || this.isSuperAdmin)) return;
                        if (this.lineConversationAssignLoading) return;
                        this.lineConversationAssignLoading = true;
                        try {
                            const user_id = this.lineAssignUserId ? (Number(this.lineAssignUserId) || 0) : null;
                            const res = await axios.post(`${ADMIN_API}/line/conversations/${cid}/assign_user`, { user_id });
                            const assigned_user_id = res?.data?.assigned_user_id ?? null;
                            if (this.selectedLineConversation && (Number(this.selectedLineConversation.id) || 0) === cid) {
                                this.selectedLineConversation = { ...this.selectedLineConversation, assigned_user_id };
                            }
                            await this.fetchLineConversations();
                        } catch (e) {
                            _showToast('指派使用者失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.lineConversationAssignLoading = false;
                        }
                    },
                    scrollLineMessagesToBottom() {
                        try {
                            try {
                                this.updateLineMessagesPadBottom();
                            } catch (e0) {
                            }
                            const el = this.$refs?.lineMessagesScroller;
                            if (!(el instanceof HTMLElement)) return;
                            const scrollNow = () => {
                                el.scrollTop = el.scrollHeight;
                            };
                            const scrollBottomIntoView = () => {
                                try {
                                    const b = this.$refs?.lineMessagesBottom;
                                    if (b instanceof HTMLElement) b.scrollIntoView({ block: 'end' });
                                } catch (e) {
                                }
                            };
                            requestAnimationFrame(() => {
                                scrollNow();
                                scrollBottomIntoView();
                                requestAnimationFrame(scrollNow);
                            });
                            setTimeout(scrollNow, 0);
                            setTimeout(scrollNow, 50);
                            setTimeout(scrollNow, 200);
                            setTimeout(scrollBottomIntoView, 0);
                            setTimeout(scrollBottomIntoView, 50);
                            setTimeout(scrollBottomIntoView, 200);
                            setTimeout(scrollBottomIntoView, 800);
                        } catch (e) {
                        }
                    },
                    updateLineMessagesPadBottom() {
                        try {
                            const el = this.$refs?.lineComposer;
                            if (!(el instanceof HTMLElement)) return;
                            const h = Math.max(0, Math.ceil(el.getBoundingClientRect().height || 0));
                            const next = Math.min(600, Math.max(140, h + 24));
                            if ((Number(this.lineMessagesPadBottom) || 0) !== next) {
                                this.lineMessagesPadBottom = next;
                            }
                        } catch (e) {
                        }
                    },
                    async fetchLineMessages(conversationId) {
                        const id = Number(conversationId) || 0;
                        if (!id) return;
                        const epoch = (Number(this.lineMessagesFetchEpoch) || 0) + 1;
                        this.lineMessagesFetchEpoch = epoch;
                        this.lineMessagesLoading = true;
                        this.lineMessagesError = '';
                        try {
                            const prev = Array.isArray(this.lineMessages) ? this.lineMessages : [];
                            const prevById = new Map();
                            for (const m of prev) {
                                const mid = Number(m?.id) || 0;
                                if (mid > 0) prevById.set(mid, m);
                            }
                            const nowMs = Date.now();
                            const localPending = prev.filter(m => {
                                if (!m || typeof m !== 'object') return false;
                                const createdAt = Date.parse((m?.created_at || '').toString());
                                const recentKeep = !!m.local_keep && Number.isFinite(createdAt) && (nowMs - createdAt) <= (5 * 60 * 1000);
                                return (
                                    m.local_status === 'sending' ||
                                    m.local_status === 'sent_pending' ||
                                    m.local_status === 'failed' ||
                                    (Number(m.id) || 0) < 0 ||
                                    recentKeep
                                );
                            });
                            const res = await axios.get(`${ADMIN_API}/line/conversations/${id}/messages`, { params: { limit: 200, _ts: Date.now() } });
                            const ct = (res?.headers?.['content-type'] || '').toString().toLowerCase();
                            const items = res?.data;
                            if (!Array.isArray(items)) {
                                if ((Number(this.lineMessagesFetchEpoch) || 0) !== epoch) return;
                                const preview = (typeof items === 'string' ? items : JSON.stringify(items || null)).slice(0, 240);
                                this.lineMessagesError = `載入訊息失敗：API 回應格式不正確${ct ? `（${ct}）` : ''}${preview ? `：${preview}` : ''}`;
                                await nextTick();
                                this.scrollLineMessagesToBottom();
                                return;
                            }
                            const remoteRaw = items;
                            const remote = remoteRaw.map(r => {
                                const rid = Number(r?.id) || 0;
                                if (!rid) return r;
                                const pm = prevById.get(rid);
                                if (!pm) return r;
                                const localClientId = (pm?.local_client_id || '').toString();
                                const serverMessageId = Number(pm?.server_message_id) || 0;
                                const localKeep = !!pm?.local_keep;
                                const localStatus = (pm?.local_status || '').toString();
                                const keepStatus = (localStatus === 'failed') ? localStatus : '';
                                return {
                                    ...r,
                                    ...(localKeep ? { local_keep: true } : {}),
                                    ...(localClientId ? { local_client_id: localClientId } : {}),
                                    ...(serverMessageId ? { server_message_id: serverMessageId } : {}),
                                    ...(keepStatus ? { local_status: keepStatus } : {}),
                                };
                            });
                            const remoteIds = new Set();
                            for (const r of remote) {
                                const rid = Number(r?.id) || 0;
                                if (rid > 0) remoteIds.add(rid);
                            }
                            const remoteOldestTs = remote.length ? Date.parse((remote?.[0]?.created_at || '').toString()) : NaN;
                            const nowForKeep = Date.now();
                            const missingPrev = prev.filter(m => {
                                if (!m || typeof m !== 'object') return false;
                                const mid = Number(m?.id) || 0;
                                if (mid <= 0) return false;
                                if (remoteIds.has(mid)) return false;
                                const t = Date.parse((m?.created_at || '').toString());
                                if (!Number.isFinite(t)) return false;
                                if (Number.isFinite(remoteOldestTs) && t < remoteOldestTs) return false;
                                return (nowForKeep - t) <= (24 * 60 * 60 * 1000);
                            });
                            const merged = [...remote, ...missingPrev];
                            for (const lp of localPending) {
                                const serverId = Number(lp?.server_message_id) || 0;
                                if (serverId > 0 && merged.some(r => (Number(r?.id) || 0) === serverId)) continue;
                                const cid = (lp?.local_client_id || '').toString();
                                if (cid && merged.some(r => (r?.local_client_id || '').toString() === cid)) continue;
                                merged.push(lp);
                            }
                            merged.sort((a, b) => {
                                const ta = Date.parse((a?.created_at || '').toString());
                                const tb = Date.parse((b?.created_at || '').toString());
                                if (Number.isFinite(ta) && Number.isFinite(tb)) return ta - tb;
                                return (Number(a?.id) || 0) - (Number(b?.id) || 0);
                            });
                            if ((Number(this.lineMessagesFetchEpoch) || 0) !== epoch) return;
                            this.lineMessages = merged;
                            await nextTick();
                            this.scrollLineMessagesToBottom();
                        } catch (e) {
                            const msg = e?.response?.data?.detail || e?.message || '載入訊息失敗';
                            this.lineMessagesError = `載入訊息失敗：${msg}`;
                        } finally {
                            if ((Number(this.lineMessagesFetchEpoch) || 0) === epoch) {
                                this.lineMessagesLoading = false;
                            }
                        }
                    },
                    async manualCreateLineConversation({ setting_id, line_user_id, display_name }) {
                        const res = await axios.post(`${ADMIN_API}/line/conversations/manual_create`, {
                            setting_id: Number(setting_id),
                            line_user_id: (line_user_id || '').trim(),
                            display_name: (display_name || '').trim() || null,
                        });
                        await this.fetchLineConversations();
                        // 自動選取剛建立的對話
                        const created = this.lineConversations.find(c => c && (Number(c.id) || 0) === (Number(res.data?.id) || 0));
                        if (created) await this.selectLineConversation(created);
                        return res.data;
                    },
                    async sendLineMessage() {
                        const id = Number(this.selectedLineConversation?.id) || 0;
                        const text = (this.lineSendText || '').toString();
                        if (!id || !text.trim()) return;

                        this.lineSending = true;
                        this.lineSendError = '';
                        this.lineSendStatus = '送出中...';
                        this.lineLastSendPreview = text.trim();
                        try {
                            this.lineMessagesFetchEpoch = (Number(this.lineMessagesFetchEpoch) || 0) + 1;
                        } catch (e0) {
                        }
                        try {
                            await nextTick();
                            this.updateLineMessagesPadBottom();
                        } catch (e) {
                        }
                        try {
                            if (this.__lineLastSendTimer) clearTimeout(this.__lineLastSendTimer);
                        } catch (e) {
                        }
                        try {
                            this.__lineLastSendTimer = setTimeout(() => {
                                try {
                                    this.lineLastSendPreview = '';
                                    try {
                                        nextTick(() => {
                                            try {
                                                this.updateLineMessagesPadBottom();
                                            } catch (e2) {
                                            }
                                        });
                                    } catch (e1) {
                                    }
                                } catch (e) {
                                }
                            }, 8000);
                        } catch (e) {
                        }
                        const localClientId = `lc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
                        const optimisticMsg = {
                            id: -Date.now(),
                            direction: 'out',
                            message_type: 'text',
                            text: text.trim(),
                            created_at: new Date().toISOString(),
                            local_status: 'sending',
                            local_keep: true,
                            local_client_id: localClientId,
                            server_message_id: null,
                        };
                        const base = Array.isArray(this.lineMessages) ? this.lineMessages : [];
                        this.lineMessages = [...base, optimisticMsg];
                        this.lineSendText = '';
                        await nextTick();
                        this.scrollLineMessagesToBottom();
                        try {
                            const mode = 'auto';
                            const res = await axios.post(`${ADMIN_API}/line/conversations/${id}/send`, { text, mode });
                            const sentMode = (res?.data?.mode || '').toString();
                            const messageId = Number(res?.data?.message_id) || 0;
                            const serverMessage = (res?.data?.message && typeof res.data.message === 'object') ? res.data.message : null;
                            this.lineSendStatus = `已送出${sentMode ? '（' + sentMode + '）' : ''}${messageId ? ' #' + messageId : ''}`;
                            try {
                                await nextTick();
                                this.updateLineMessagesPadBottom();
                                this.scrollLineMessagesToBottom();
                            } catch (e4) {
                            }
                            try {
                                const arr = Array.isArray(this.lineMessages) ? this.lineMessages : [];
                                this.lineMessages = arr.map(m => {
                                    if (!(m && m.id === optimisticMsg.id)) return m;
                                    const mid = Number(serverMessage?.id) || messageId || null;
                                    return {
                                        ...m,
                                        ...(serverMessage || {}),
                                        id: mid || m.id,
                                        server_message_id: mid,
                                        local_status: 'sent_pending',
                                        local_keep: true,
                                        local_client_id: localClientId,
                                    };
                                });
                            } catch (e3) {
                            }
                            await this.fetchLineMessages(id);
                            await nextTick();
                            this.scrollLineMessagesToBottom();
                            this.fetchLineConversations();
                        } catch (e) {
                            const msg = e?.response?.data?.detail || e?.message || '送出失敗';
                            this.lineSendError = `送出失敗：${msg}`;
                            this.lineSendStatus = '送出失敗';
                            try {
                                await nextTick();
                                this.updateLineMessagesPadBottom();
                                this.scrollLineMessagesToBottom();
                            } catch (e5) {
                            }
                            try {
                                const arr = Array.isArray(this.lineMessages) ? this.lineMessages : [];
                                this.lineMessages = arr.map(m => (m && m.id === optimisticMsg.id) ? { ...m, local_status: 'failed' } : m);
                            } catch (e2) {
                            }
                        } finally {
                            this.lineSending = false;
                        }
                    },
                    retryLineMessage(m) {
                        const id = Number(this.selectedLineConversation?.id) || 0;
                        if (!id) return;
                        const text = (m?.text || '').toString();
                        if (!text.trim()) return;
                        if (this.lineSending) return;
                        this.lineSendText = text;
                        this.sendLineMessage();
                    },
                    async suggestLineReply() {
                        const id = Number(this.selectedLineConversation?.id) || 0;
                        if (!id) return;

                        this.lineAiSuggestLoading = true;
                        this.lineAiSuggestion = null;
                        this.lineAiSourcesExpanded = false;
                        this.lineAiOpenSourceIndexes = [];
                        try {
                            const res = await axios.post(`${ADMIN_API}/line/conversations/${id}/ai_suggest`, {
                                tone: this.lineAiTone,
                                max_messages: 20
                            });
                            this.lineAiSuggestion = res.data || null;
                        } catch (e) {
                            _showToast('產生建議失敗: ' + (e.response?.data?.detail || e.message));
                            this.lineAiSuggestion = null;
                        } finally {
                            this.lineAiSuggestLoading = false;
                        }
                    },
                    isLineAiSourceOpen(index) {
                        const i = Number(index);
                        if (!Number.isFinite(i)) return false;
                        const arr = Array.isArray(this.lineAiOpenSourceIndexes) ? this.lineAiOpenSourceIndexes : [];
                        return arr.includes(i);
                    },
                    toggleLineAiSource(index) {
                        const i = Number(index);
                        if (!Number.isFinite(i)) return;
                        const arr = Array.isArray(this.lineAiOpenSourceIndexes) ? [...this.lineAiOpenSourceIndexes] : [];
                        const idx = arr.indexOf(i);
                        if (idx >= 0) arr.splice(idx, 1);
                        else arr.push(i);
                        arr.sort((a, b) => a - b);
                        this.lineAiOpenSourceIndexes = arr;
                    },
                    applyLineSuggestion() {
                        const draft = (this.lineAiSuggestion?.reply_draft || '').toString();
                        if (!draft.trim()) return;
                        this.lineSendText = draft;
                    },
                    async fetchQuickReplies() {
                        this.quickRepliesLoading = true;
                        this.quickRepliesError = '';
                        try {
                            const params = { _ts: Date.now() };
                            if (this.isDomainAdmin || this.isSuperAdmin) {
                                params.include_inactive = !!this.quickRepliesIncludeInactive;
                            }
                            const res = await axios.get(`${ADMIN_API}/line/quick_replies`, { params });
                            const items = res?.data ?? [];
                            this.quickReplies = Array.isArray(items) ? items.filter(x => x && typeof x === 'object') : [];
                        } catch (e) {
                            this.quickReplies = [];
                            this.quickRepliesError = e?.response?.data?.detail || e?.message || '載入快速回覆失敗';
                        } finally {
                            this.quickRepliesLoading = false;
                        }
                    },
                    startCreateQuickReply() {
                        this.editingQuickReplyId = null;
                        this.createQuickReplyForm = { title: '', body: '', sort_order: 0, active: true, category: '' };
                    },
                    startEditQuickReply(qr) {
                        const id = Number(qr?.id) || 0;
                        if (!id) return;
                        this.editingQuickReplyId = id;
                        this.createQuickReplyForm = {
                            title: (qr?.title || '').toString(),
                            body: (qr?.body || '').toString(),
                            sort_order: Number(qr?.sort_order || 0) || 0,
                            active: !!qr?.active,
                            category: (qr?.category || '').toString(),
                        };
                    },
                    async deleteQuickReply(qr) {
                        if (!(this.isDomainAdmin || this.isSuperAdmin)) return;
                        const id = Number(qr?.id) || 0;
                        if (!id) return;
                        const ok = confirm('確定要刪除此快速回覆？（刪除後會變成停用）');
                        if (!ok) return;
                        if (this.quickReplyDeletingId != null) return;
                        this.quickReplyDeletingId = id;
                        try {
                            await axios.delete(`${ADMIN_API}/line/quick_replies/${id}`);
                            if (Number(this.editingQuickReplyId || 0) === id) {
                                this.startCreateQuickReply();
                            }
                            await this.fetchQuickReplies();
                        } catch (e) {
                            _showToast('刪除快速回覆失敗: ' + (e?.response?.data?.detail || e?.message));
                        } finally {
                            this.quickReplyDeletingId = null;
                        }
                    },
                    async saveQuickReply() {
                        if (!(this.isDomainAdmin || this.isSuperAdmin)) return;
                        const title = (this.createQuickReplyForm?.title || '').toString().trim();
                        const body = (this.createQuickReplyForm?.body || '').toString().trim();
                        const sort_order = Number(this.createQuickReplyForm?.sort_order || 0) || 0;
                        const active = !!this.createQuickReplyForm?.active;
                        const category = (this.createQuickReplyForm?.category || '').toString().trim() || null;
                        if (!title || !body) {
                            _showToast('請填寫標題與內容');
                            return;
                        }
                        this.createQuickReplySaving = true;
                        try {
                            const editingId = Number(this.editingQuickReplyId || 0) || 0;
                            if (editingId) {
                                await axios.put(`${ADMIN_API}/line/quick_replies/${editingId}`, { title, body, sort_order, active, category });
                            } else {
                                await axios.post(`${ADMIN_API}/line/quick_replies`, { title, body, sort_order, active, category });
                            }
                            await this.fetchQuickReplies();
                            this.startCreateQuickReply();
                            _showToast(editingId ? '已更新快速回覆' : '已建立快速回覆');
                        } catch (e) {
                            _showToast((this.editingQuickReplyId ? '更新快速回覆失敗: ' : '建立快速回覆失敗: ') + (e?.response?.data?.detail || e?.message));
                        } finally {
                            this.createQuickReplySaving = false;
                        }
                    },
                    applyQuickReplyBody(body) {
                        const text = (body || '').toString();
                        if (!text.trim()) return;
                        if (this.quickReplyInsertMode === 'replace') {
                            this.lineSendText = text;
                        } else {
                            const cur = (this.lineSendText || '').toString();
                            const sep = cur ? (cur.endsWith('\n') ? '' : '\n') : '';
                            this.lineSendText = `${cur}${sep}${text}`;
                        }
                        try {
                            nextTick(() => {
                                try { this.updateLineMessagesPadBottom(); } catch (e) {}
                            });
                        } catch (e) {}
                    },
                    async fetchAutoReplies() {
                        this.autoRepliesLoading = true;
                        try {
                            const params = {};
                            if (this.autoRepliesFilterStatus) params.status = this.autoRepliesFilterStatus;
                            const res = await axios.get(`${ADMIN_API}/ai/auto-replies/`, { params });
                            const items = res.data || [];
                            this.autoReplies = Array.isArray(items) ? items : [];
                            if (this.selectedAutoReply?.id) {
                                const updated = this.autoReplies.find(x => x.id === this.selectedAutoReply.id);
                                if (updated) this.selectAutoReply(updated);
                            }
                        } catch (e) {
                            _showToast('載入 AI 草稿失敗: ' + (e.response?.data?.detail || e.message));
                            this.autoReplies = [];
                            this.selectedAutoReply = null;
                        } finally {
                            this.autoRepliesLoading = false;
                        }
                    },
                    async fetchOpportunities() {
                        this.opportunitiesLoading = true;
                        try {
                            const params = {
                                limit: Number(this.opportunitiesLimit) || 100,
                                offset: Number(this.opportunitiesOffset) || 0,
                                sort_by: this.opportunitiesSortBy,
                                sort_dir: this.opportunitiesSortDir
                            };
                            const s = (this.opportunitiesSearch || '').trim();
                            if (s) params.search = s;
                            const st = (this.opportunitiesStatus || '').trim();
                            if (st) params.status = st;
                            if (this.opportunitiesMinBudget != null && this.opportunitiesMinBudget !== '') params.min_budget = this.opportunitiesMinBudget;
                            if (this.opportunitiesMaxBudget != null && this.opportunitiesMaxBudget !== '') params.max_budget = this.opportunitiesMaxBudget;
                            const res = await axios.get(`${ADMIN_API}/ai/opportunities`, { params });
                            const data = res.data || {};
                            this.opportunities = Array.isArray(data.items) ? data.items : [];
                            this.opportunitiesTotal = Number.isFinite(Number(data.total)) ? Number(data.total) : this.opportunities.length;
                        } catch (e) {
                            _showToast('載入商機清單失敗: ' + (e.response?.data?.detail || e.message));
                            this.opportunities = [];
                            this.opportunitiesTotal = 0;
                        } finally {
                            this.opportunitiesLoading = false;
                        }
                    },
                    async goOpportunitiesPrevPage() {
                        const limit = Number(this.opportunitiesLimit) || 100;
                        const offset = Number(this.opportunitiesOffset) || 0;
                        this.opportunitiesOffset = Math.max(0, offset - limit);
                        await this.fetchOpportunities();
                    },
                    async goOpportunitiesNextPage() {
                        const limit = Number(this.opportunitiesLimit) || 100;
                        const offset = Number(this.opportunitiesOffset) || 0;
                        const total = Number(this.opportunitiesTotal) || 0;
                        if (offset + limit >= total) return;
                        this.opportunitiesOffset = offset + limit;
                        await this.fetchOpportunities();
                    },
                    resetOpportunitiesPaging() {
                        this.opportunitiesOffset = 0;
                    },
                    formatBytes(bytes) {
                        const b = Number(bytes) || 0;
                        if (b <= 0) return '0 B';
                        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
                        const i = Math.min(units.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
                        const v = b / Math.pow(1024, i);
                        return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
                    },
                    async fetchBackupSettings() {
                        if (!this.isSuperAdmin) return;
                        this.backupSettingsLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/backup/settings`);
                            const s = res?.data && typeof res.data === 'object' ? res.data : {};
                            this.backupSettingsForm = { ...this.backupSettingsForm, ...s };
                        } catch (e) {
                            _showToast('載入備份設定失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.backupSettingsLoading = false;
                        }
                    },
                    async saveBackupSettings() {
                        if (!this.isSuperAdmin) return _showToast('需要系統管理員權限');
                        this.backupSettingsSaving = true;
                        try {
                            const s = this.backupSettingsForm || {};
                            const payload = {
                                destination: (s.destination || 'local').toString(),
                                keep_last: Number.isFinite(Number(s.keep_last)) ? Number(s.keep_last) : 20,
                                auto_enabled: !!s.auto_enabled,
                                interval_hours: Number.isFinite(Number(s.interval_hours)) ? Number(s.interval_hours) : 24,
                                nas_path: (s.nas_path || '').toString(),
                                nas_ip: (s.nas_ip || '').toString(),
                                nas_type: (s.nas_type || 'cifs').toString(),
                                nas_share: (s.nas_share || '').toString(),
                                nas_username: (s.nas_username || '').toString(),
                                nas_password: (s.nas_password || '').toString(),
                                gdrive_method: (s.gdrive_method || 'rclone').toString(),
                                gdrive_remote: (s.gdrive_remote || '').toString(),
                                gdrive_folder: (s.gdrive_folder || '').toString(),
                                gdrive_config_path: (s.gdrive_config_path || '').toString()
                            };
                            const res = await axios.post(`${ADMIN_API}/backup/settings`, payload);
                            const saved = res?.data && typeof res.data === 'object' ? res.data : {};
                            this.backupSettingsForm = { ...this.backupSettingsForm, ...saved };
                            _showToast('已儲存');
                        } catch (e) {
                            _showToast('儲存失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.backupSettingsSaving = false;
                        }
                    },
                    async testBackupDestination() {
                        if (!this.isSuperAdmin) return _showToast('需要系統管理員權限');
                        this.backupTestLoading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/backup/test`, {});
                            const ok = !!res?.data?.ok;
                            const msg = (res?.data?.message || '').toString();
                            _showToast(ok ? (msg || 'OK') : (`測試失敗：${msg || 'unknown error'}`));
                        } catch (e) {
                            _showToast('測試失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.backupTestLoading = false;
                        }
                    },
                    async runBackupNow() {
                        if (!this.isSuperAdmin) return _showToast('需要系統管理員權限');
                        if (!confirm('確定要立即執行備份嗎？')) return;
                        this.backupRunLoading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/backup/run`, {});
                            const jobId = (res?.data?.job_id || '').toString();
                            this.backupLastRunJobId = jobId;
                            await this.fetchBackupJobs();
                            this._startBackupPoller(jobId);
                        } catch (e) {
                            _showToast('備份失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.backupRunLoading = false;
                        }
                    },
                    async fetchBackupJobs() {
                        if (!this.isSuperAdmin) return;
                        this.backupJobsLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/backup/jobs`);
                            const jobs = Array.isArray(res?.data?.jobs) ? res.data.jobs : [];
                            this.backupJobs = jobs;
                        } catch (e) {
                            this.backupJobs = [];
                        } finally {
                            this.backupJobsLoading = false;
                        }
                    },
                    async fetchBackupFiles() {
                        if (!this.isSuperAdmin) return;
                        this.backupFilesLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/backup/list`);
                            const items = Array.isArray(res?.data?.items) ? res.data.items : [];
                            this.backupFiles = items;
                        } catch (e) {
                            this.backupFiles = [];
                        } finally {
                            this.backupFilesLoading = false;
                        }
                    },
                    async downloadBackupFile(filename) {
                        if (!this.isSuperAdmin) return _showToast('需要系統管理員權限');
                        const name = (filename || '').toString();
                        if (!name) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/backup/download/${encodeURIComponent(name)}`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/gzip' }));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', name);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        } catch (e) {
                            _showToast('下載失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    openRestoreModal(filename) {
                        this.restoreTarget = filename || '';
                        this.restoreOptions = { restore_db: true, restore_mail: true, restore_resources: false, restart_services: true };
                        this.showRestoreModal = true;
                    },
                    closeRestoreModal() {
                        this.showRestoreModal = false;
                        this.restoreTarget = '';
                    },
                    async runRestore() {
                        if (!this.isSuperAdmin) return _showToast('需要系統管理員權限');
                        const fn = (this.restoreTarget || '').trim();
                        if (!fn) return;
                        this.restoreLoading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/backup/restore/run`, {
                                filename: fn,
                                ...this.restoreOptions
                            });
                            const jobId = (res?.data?.job_id || '').toString();
                            this.restoreJobId = jobId;
                            this.closeRestoreModal();
                            _showToast('還原工作已啟動');
                            await this.fetchRestoreJobs();
                            this._startRestorePoller();
                        } catch (e) {
                            _showToast('還原失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.restoreLoading = false;
                        }
                    },
                    async fetchRestoreJobs() {
                        if (!this.isSuperAdmin) return;
                        this.restoreJobsLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/backup/restore/jobs`);
                            this.restoreJobs = Array.isArray(res?.data?.jobs) ? res.data.jobs : [];
                        } catch (e) {
                            this.restoreJobs = [];
                        } finally {
                            this.restoreJobsLoading = false;
                        }
                    },
                    _startRestorePoller() {
                        if (this.restorePollerTimer) return;
                        const poll = async () => {
                            try { await this.fetchRestoreJobs(); } catch (e) {}
                            const running = (this.restoreJobs || []).some(j => j.status === 'running' || j.status === 'queued');
                            if (running) {
                                this.restorePollerTimer = setTimeout(poll, 2000);
                            } else {
                                this.restorePollerTimer = null;
                            }
                        };
                        this.restorePollerTimer = setTimeout(poll, 2000);
                    },
                    _startBackupPoller(jobId) {
                        if (this.backupPollerTimer) return;
                        const poll = async () => {
                            try { await this.fetchBackupJobs(); } catch (e) {}
                            const running = (this.backupJobs || []).some(j => (j.id === jobId || j.status === 'running') && j.status === 'running');
                            if (running) {
                                this.backupPollerTimer = setTimeout(poll, 2000);
                            } else {
                                this.backupPollerTimer = null;
                                try { await this.fetchBackupFiles(); } catch (e) {}
                            }
                        };
                        this.backupPollerTimer = setTimeout(poll, 2000);
                    },
                    canManageResource(r) {
                        if (this.isSuperAdmin || this.isDomainAdmin) return true;
                        const ownerId = r?.owner_user_id;
                        const meId = this.currentUser?.id;
                        return !!ownerId && !!meId && ownerId === meId;
                    },
                    getResourceUrl(r) {
                        const id = r?.id;
                        return `${ADMIN_API}/resources/${id}/file`;
                    },
                    getResourceUrlById(id) {
                        return `${ADMIN_API}/resources/${id}/file`;
                    },
                    async fetchResources() {
                        this.resourcesLoading = true;
                        try {
                            const params = {};
                            if (this.resourcesTypeFilter) params.type_filter = this.resourcesTypeFilter;
                            const res = await axios.get(`${ADMIN_API}/resources`, { params });
                            const items = res.data || [];
                            this.resources = Array.isArray(items) ? items : [];
                        } catch (e) {
                            _showToast('載入素材庫失敗: ' + (e.response?.data?.detail || e.message));
                            this.resources = [];
                        } finally {
                            this.resourcesLoading = false;
                        }
                    },
                    async ensureComposeResourcePoolLoaded() {
                        if (this.composeResourcePoolLoading) return;
                        if (Array.isArray(this.composeResourcePool) && this.composeResourcePool.length) return;
                        this.composeResourcePoolLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/resources`);
                            const items = res.data || [];
                            this.composeResourcePool = Array.isArray(items) ? items : [];
                        } catch (e) {
                            this.composeResourcePool = [];
                        } finally {
                            this.composeResourcePoolLoading = false;
                        }
                    },
                    async addResourceAttachment(r) {
                        const id = r?.id;
                        if (!id) return;
                        const exists = (this.composeResourceAttachments || []).some(x => x && x.id === id);
                        if (exists) return;
                        this.composeResourceAddingId = id;
                        try {
                            if (!this.composeResourceAttachments) this.composeResourceAttachments = [];
                            this.composeResourceAttachments.push({
                                id,
                                original_filename: (r?.original_filename || `resource-${id}`).toString()
                            });
                            if (this.composeResourceErrors && this.composeResourceErrors[id]) {
                                delete this.composeResourceErrors[id];
                            }
                            if (this.composeResourceErrorMeta && Object.prototype.hasOwnProperty.call(this.composeResourceErrorMeta, id)) {
                                delete this.composeResourceErrorMeta[id];
                            }
                            if (this.composeResourceErrorExpanded && Object.prototype.hasOwnProperty.call(this.composeResourceErrorExpanded, id)) {
                                delete this.composeResourceErrorExpanded[id];
                            }
                            this.scheduleComposeResourcesValidation([id]);
                        } catch (e) {
                            _showToast('加入素材附件失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.composeResourceAddingId = null;
                        }
                    },
                    appendToCompose(text) {
                        const t = (text || '').toString();
                        if (!t.trim()) return;

                        const hasHtml = this.composeForm.html_body && this.composeForm.html_body.toString().trim();
                        if (hasHtml) {
                            const esc = (s) => (s || '').toString()
                                .replace(/&/g, '&amp;')
                                .replace(/</g, '&lt;')
                                .replace(/>/g, '&gt;')
                                .replace(/"/g, '&quot;')
                                .replace(/'/g, '&#39;');
                            const block = esc(t).replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br>');
                            const current = (this.composeForm.html_body || '').toString().trim();
                            this.composeForm.html_body = current ? `${current}<br><br>${block}` : block;
                            this.composeForm.body = this.stripHtml(this.composeForm.html_body || '');
                            this.composeEditorMode = 'rich';
                            return;
                        }

                        const cur = (this.composeForm.body || '').toString();
                        const sep = cur.trim() ? '\n\n' : '';
                        this.composeForm.body = `${cur}${sep}${t}`.trimStart();
                        this.composeEditorMode = 'rich';
                    },
                    async fetchComposeRecommendations() {
                        const ctx = this.getComposeRecommendationInput();
                        const subject = (ctx.subject || '').toString().trim();
                        const body = (ctx.body || '').toString().trim();
                        if (!subject && body.length < 20) {
                            this.composeRecommendedResources = [];
                            this.composeRecommendError = '請先輸入主旨或內文（建議至少 20 字）再推薦';
                            this.composeRecommendLoading = false;
                            this.composeRecommendActiveKey = '';
                            return;
                        }
                        const key = (subject + "\n" + body.slice(0, 2000)).replace(/\s+/g, " ").trim();
                        this.composeRecommendActiveKey = key;
                        this.composeRecommendLoading = true;
                        this.composeRecommendError = '';
                        try {
                            const res = await axios.post(`${ADMIN_API}/resources/recommend`, {
                                subject,
                                body,
                                top_k: 5
                            });
                            const items = res.data?.items || [];
                            if ((this.composeRecommendActiveKey || '') !== key) return;
                            const list = Array.isArray(items) ? items : [];
                            this.composeRecommendedResources = list;
                            if (key) this.composeRecommendLastKey = key;
                            try {
                                const alreadyHasResource = Array.isArray(this.composeResourceAttachments) && this.composeResourceAttachments.length > 0;
                                const canPrompt = !alreadyHasResource
                                    && !!(this.composeForm && this.composeForm.in_reply_to)
                                    && this.showComposeModal
                                    && list.length > 0
                                    && !this.composeApplyLoadingId
                                    && !this.composeResourceAddingId
                                    && !this.composeSnippetLoadingId
                                    && (this.composePromptedRecommendKey || '') !== key;
                                if (canPrompt) {
                                    this.composePromptedRecommendKey = key;
                                    const first = list[0] || {};
                                    const name = (first.original_filename || '').toString().trim() || `resource-${first.id || ''}`;
                                    const ok = confirm(`已找到推薦素材「${name}」。\n是否一鍵套用（加入附件 + 插入摘要）？`);
                                    if (ok) {
                                        await this.applyRecommendedResource(first);
                                    }
                                }
                            } catch (e) {
                            }
                        } catch (e) {
                            if ((this.composeRecommendActiveKey || '') !== key) return;
                            this.composeRecommendedResources = [];
                            this.composeRecommendError = (e.response?.data?.detail || e.message || '推薦失敗').toString();
                        } finally {
                            if ((this.composeRecommendActiveKey || '') === key) this.composeRecommendLoading = false;
                        }
                    },
                    async applyRecommendedResource(item) {
                        if (!item || !item.id) return;
                        this.composeApplyLoadingId = item.id;
                        try {
                            await this.addResourceAttachment(item);
                            await this.insertResourceSnippet(item);
                        } finally {
                            this.composeApplyLoadingId = null;
                        }
                    },
                    async insertResourceSnippet(item) {
                        if (!item) return;
                        const name = (item.original_filename || '').toString().trim() || `resource-${item.id}`;
                        const desc = (item.description || '').toString().trim();
                        const tags = (item.tags || '').toString().trim();
                        const reason = (item.reason || '').toString().trim();

                        this.composeSnippetLoadingId = item.id;
                        try {
                            const rawBody = (this.composeForm.html_body && this.composeForm.html_body.toString().trim())
                                ? this.stripHtml(this.composeForm.html_body)
                                : (this.composeForm.body || '');
                            const res = await axios.post(`${ADMIN_API}/resources/snippet`, {
                                resource_id: item.id,
                                subject: this.composeForm.subject || '',
                                body: rawBody || ''
                            });
                            const snippet = (res.data?.result || '').toString().trim();
                            if (snippet) {
                                this.appendToCompose(snippet);
                                return;
                            }
                        } catch (e) {
                        } finally {
                            this.composeSnippetLoadingId = null;
                        }

                        let fallback = `建議附上附件：${name}`;
                        if (desc) fallback += `\n摘要：${desc}`;
                        else if (tags) fallback += `\n重點：${tags}`;
                        if (reason) fallback += `\n${reason}`;
                        this.appendToCompose(fallback);
                    },
                    async queryComposeKnowledge() {
                        const q = (this.composeKbQueryInput || '').toString().trim();
                        if (!q) return;
                        this.composeKbLoading = true;
                        this.composeKbError = '';
                        this.composeKbResult = '';
                        this.composeKbSources = [];
                        this.kbSourceExpandedIdx = null;
                        try {
                            const res = await axios.post(`${ADMIN_API}/rag/chat`, { query: q });
                            this.composeKbResult = (res.data?.result || '').toString();
                            const sources = res.data?.sources || [];
                            this.composeKbSources = Array.isArray(sources) ? sources : [];
                            if (res.data?.error) this.composeKbError = res.data.error.toString();
                        } catch (e) {
                            this.composeKbError = (e.response?.data?.detail || e.message || '查詢失敗').toString();
                        } finally {
                            this.composeKbLoading = false;
                        }
                    },
                    toggleKbSourceExpand(idx) {
                        this.kbSourceExpandedIdx = this.kbSourceExpandedIdx === idx ? null : idx;
                    },
                    async fetchKbFileBlob(docId) {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${ADMIN_API}/rag/knowledge/${encodeURIComponent(docId)}/file`, {
                            headers: { 'Authorization': `Bearer ${token}` },
                        });
                        if (!res.ok) {
                            const err = await res.json().catch(() => ({}));
                            throw new Error(err.detail || res.statusText);
                        }
                        const blob = await res.blob();
                        return { url: URL.createObjectURL(blob), contentType: res.headers.get('content-type') || blob.type };
                    },
                    async downloadKbFile(docId, sourceName) {
                        if (!docId) return;
                        try {
                            const token = localStorage.getItem('token');
                            const res = await fetch(`${ADMIN_API}/rag/knowledge/${encodeURIComponent(docId)}/download`, {
                                headers: { 'Authorization': `Bearer ${token}` },
                            });
                            if (!res.ok) {
                                const err = await res.json().catch(() => ({}));
                                _showToast('下載失敗: ' + (err.detail || res.statusText), 'error');
                                return;
                            }
                            const blob = await res.blob();
                            const disposition = res.headers.get('content-disposition') || '';
                            const match = disposition.match(/filename="?([^"]+)"?/);
                            const filename = match?.[1] || sourceName || docId;
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                            setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
                        } catch (e) {
                            _showToast('下載失敗: ' + (e.message || ''), 'error');
                        }
                    },
                    async openKbDocFullPreview(source) {
                        const docId = source?.doc_id;
                        if (!docId) return;
                        this.kbDocPreviewModal = {
                            open: true,
                            loading: true,
                            doc_id: docId,
                            source_name: source.source_name || docId,
                            content: '',
                            error: '',
                        };
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/rag/knowledge/${encodeURIComponent(docId)}/content`, {
                                headers: { 'Authorization': `Bearer ${token}` },
                            });
                            this.kbDocPreviewModal.content = res.data?.content || '（無內容）';
                        } catch (e) {
                            this.kbDocPreviewModal.error = (e.response?.data?.detail || e.message || '載入失敗').toString();
                        } finally {
                            this.kbDocPreviewModal.loading = false;
                        }
                    },
                    insertKnowledgeAnswer() {
                        const answer = (this.composeKbResult || '').toString().trim();
                        if (!answer) return;
                        const sources = Array.isArray(this.composeKbSources) ? this.composeKbSources : [];
                        let text = `知識庫回答：\n${answer}`;
                        if (sources.length) {
                            text += `\n\n引用來源：`;
                            for (let i = 0; i < Math.min(sources.length, 8); i++) {
                                const s = sources[i] || {};
                                const name = (s.source_name || '').toString().trim() || `doc-${(s.doc_id || '').toString()}`;
                                text += `\n[${i + 1}] ${name}`;
                            }
                        }
                        this.appendToCompose(text);
                    },
                    scheduleComposeResourcesValidation(resourceIds) {
                        const ids = Array.isArray(resourceIds)
                            ? resourceIds.map(x => Number(x)).filter(x => !isNaN(x) && x > 0)
                            : [];
                        if (!ids.length) return;
                        if (!Array.isArray(this.composeResourceValidateQueue)) this.composeResourceValidateQueue = [];
                        for (const id of ids) {
                            if (!this.composeResourceValidateQueue.includes(id)) {
                                this.composeResourceValidateQueue.push(id);
                            }
                        }
                        if (this.composeResourceValidateTimer) {
                            clearTimeout(this.composeResourceValidateTimer);
                            this.composeResourceValidateTimer = null;
                        }
                        this.composeResourceValidateTimer = setTimeout(async () => {
                            try {
                                const queued = Array.isArray(this.composeResourceValidateQueue) ? this.composeResourceValidateQueue.slice() : [];
                                this.composeResourceValidateQueue = [];
                                await this.validateComposeResources(queued);
                            } finally {
                                this.composeResourceValidateTimer = null;
                            }
                        }, 250);
                    },
                    async validateComposeResources(resourceIds) {
                        const ids = Array.isArray(resourceIds)
                            ? resourceIds.map(x => Number(x)).filter(x => !isNaN(x) && x > 0)
                            : (this.composeResourceAttachments || []).map(r => Number(r && r.id)).filter(x => !isNaN(x) && x > 0);
                        if (!ids.length) return { ok: true, errors: [] };
                        if (!this.composeResourceValidating) this.composeResourceValidating = {};
                        for (const id of ids) {
                            this.composeResourceValidating[id] = true;
                        }
                        try {
                            const res = await axios.post(`${ADMIN_API}/resources/validate`, { resource_ids: ids });
                            const errors = Array.isArray(res.data?.errors) ? res.data.errors : [];
                            if (!this.composeResourceErrors) this.composeResourceErrors = {};
                            if (!this.composeResourceErrorMeta) this.composeResourceErrorMeta = {};
                            if (!this.composeResourceErrorExpanded) this.composeResourceErrorExpanded = {};
                            let shouldRefresh = false;
                            for (const id of ids) {
                                if (Object.prototype.hasOwnProperty.call(this.composeResourceErrors, id)) delete this.composeResourceErrors[id];
                                if (Object.prototype.hasOwnProperty.call(this.composeResourceErrorMeta, id)) delete this.composeResourceErrorMeta[id];
                                if (Object.prototype.hasOwnProperty.call(this.composeResourceErrorExpanded, id)) delete this.composeResourceErrorExpanded[id];
                            }
                            for (const e of errors) {
                                const rid = Number(e && e.resource_id);
                                if (!isNaN(rid) && rid > 0) {
                                    const msg = (e && e.message) ? e.message.toString() : 'Resource error';
                                    this.composeResourceErrors[rid] = msg;
                                    this.composeResourceErrorMeta[rid] = {
                                        code: (e && e.code) ? e.code.toString() : '',
                                        status_code: Number(e && e.status_code),
                                        message: msg
                                    };
                                    if (this.composeResourceErrorExpanded[rid] === undefined) this.composeResourceErrorExpanded[rid] = false;
                                }
                                const sc = Number(e && e.status_code);
                                if (sc === 403 || sc === 404) shouldRefresh = true;
                            }
                            if (shouldRefresh) {
                                this.composeResourceBanner = '素材庫附件權限或檔案狀態可能已變更，已重新整理素材清單';
                                this.composeResourcePool = [];
                                this.ensureComposeResourcePoolLoaded();
                            }
                            return { ok: errors.length === 0, errors };
                        } catch (e) {
                            return { ok: true, errors: [] };
                        } finally {
                            if (this.composeResourceValidating) {
                                for (const id of ids) {
                                    if (Object.prototype.hasOwnProperty.call(this.composeResourceValidating, id)) delete this.composeResourceValidating[id];
                                }
                            }
                        }
                    },
                    removeResourceAttachment(index) {
                        if (!this.composeResourceAttachments) return;
                        const item = this.composeResourceAttachments[index];
                        this.composeResourceAttachments.splice(index, 1);
                        const rid = item && item.id;
                        if (rid && this.composeResourceErrors && this.composeResourceErrors[rid]) {
                            delete this.composeResourceErrors[rid];
                        }
                        if (rid && this.composeResourceErrorMeta && Object.prototype.hasOwnProperty.call(this.composeResourceErrorMeta, rid)) {
                            delete this.composeResourceErrorMeta[rid];
                        }
                        if (rid && this.composeResourceErrorExpanded && Object.prototype.hasOwnProperty.call(this.composeResourceErrorExpanded, rid)) {
                            delete this.composeResourceErrorExpanded[rid];
                        }
                        if (rid && this.composeResourceValidating && Object.prototype.hasOwnProperty.call(this.composeResourceValidating, rid)) {
                            delete this.composeResourceValidating[rid];
                        }
                    },
                    getResourceError(id) {
                        if (!this.composeResourceErrors) return '';
                        const v = this.composeResourceErrors[id];
                        return (v || '').toString();
                    },
                    getResourceErrorSummary(id) {
                        const meta = this.composeResourceErrorMeta ? this.composeResourceErrorMeta[id] : null;
                        if (meta && meta.code) {
                            const code = meta.code.toString();
                            if (code === 'not_authorized') return '沒有權限';
                            if (code === 'resource_not_found') return '素材不存在';
                            if (code === 'resource_file_not_found') return '檔案不存在';
                        }
                        const detail = this.getResourceError(id);
                        if (!detail) return '';
                        const lower = detail.toLowerCase();
                        if (lower.includes('not authorized')) return '沒有權限';
                        if (lower.includes('resource not found')) return '素材不存在';
                        if (lower.includes('resource file not found') || lower.includes('file not found')) return '檔案不存在';
                        if (/^\d+:\s*/.test(detail)) return detail.replace(/^\d+:\s*/, '').slice(0, 50);
                        return detail.slice(0, 50);
                    },
                    toggleResourceError(id) {
                        if (!this.composeResourceErrorExpanded) this.composeResourceErrorExpanded = {};
                        this.composeResourceErrorExpanded[id] = !this.composeResourceErrorExpanded[id];
                    },
                    isResourceErrorExpanded(id) {
                        return !!(this.composeResourceErrorExpanded && this.composeResourceErrorExpanded[id]);
                    },
                    isResourceValidating(id) {
                        if (this.composeResourceValidating && this.composeResourceValidating[id]) return true;
                        if (Array.isArray(this.composeResourceValidateQueue) && this.composeResourceValidateQueue.includes(Number(id))) return true;
                        return false;
                    },
                    removeAndRetryResource(index) {
                        this.removeResourceAttachment(index);
                        this.composeResourceBanner = '';
                        this.sendEmail();
                    },
                    dismissComposeResourceBanner() {
                        this.composeResourceBanner = '';
                    },
                    removeAllErroredResources() {
                        const errors = this.composeResourceErrors || {};
                        const ids = Object.keys(errors)
                            .map(x => Number(x))
                            .filter(x => !isNaN(x) && x > 0);
                        if (ids.length === 0) return;
                        const idSet = new Set(ids);
                        this.composeResourceAttachments = (this.composeResourceAttachments || []).filter(r => !idSet.has(Number(r && r.id)));
                        for (const id of ids) {
                            if (this.composeResourceErrors && this.composeResourceErrors[id]) delete this.composeResourceErrors[id];
                            if (this.composeResourceErrorMeta && Object.prototype.hasOwnProperty.call(this.composeResourceErrorMeta, id)) delete this.composeResourceErrorMeta[id];
                            if (this.composeResourceErrorExpanded && Object.prototype.hasOwnProperty.call(this.composeResourceErrorExpanded, id)) delete this.composeResourceErrorExpanded[id];
                            if (this.composeResourceValidating && Object.prototype.hasOwnProperty.call(this.composeResourceValidating, id)) delete this.composeResourceValidating[id];
                        }
                    },
                    removeAllErroredAndRetry() {
                        this.removeAllErroredResources();
                        this.composeResourceBanner = '';
                        this.sendEmail();
                    },
                    handleResourceFileSelected(event) {
                        const file = (event.target.files && event.target.files[0]) ? event.target.files[0] : null;
                        this.resourceUpload.file = file;
                    },
                    async uploadResource() {
                        if (!this.resourceUpload.file) return;
                        this.resourcesUploading = true;
                        try {
                            const form = new FormData();
                            form.append('file', this.resourceUpload.file);
                            form.append('visibility', this.resourceUpload.visibility || 'private');
                            if (this.resourceUpload.tags) form.append('tags', this.resourceUpload.tags);
                            if (this.resourceUpload.description) form.append('description', this.resourceUpload.description);
                            const res = await axios.post(`${ADMIN_API}/resources/upload`, form, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            const created = res.data;
                            this.resourceUpload.file = null;
                            this.resourceUpload.tags = '';
                            this.resourceUpload.description = '';
                            this.resourceUpload.visibility = 'private';
                            await this.fetchResources();
                            if (created && (created.visibility === 'shared')) {
                                await this.openShareResourceModal(created);
                            }
                        } catch (e) {
                            _showToast('上傳失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.resourcesUploading = false;
                        }
                    },
                    async deleteResource(r) {
                        if (!r?.id) return;
                        if (!confirm(`確定要刪除素材「${r.original_filename}」嗎？`)) return;
                        try {
                            await axios.delete(`${ADMIN_API}/resources/${r.id}`);
                            await this.fetchResources();
                        } catch (e) {
                            _showToast('刪除失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async fetchUsersBrief() {
                        this.usersBriefLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/users/brief`);
                            const users = res.data || [];
                            this.usersBrief = Array.isArray(users) ? users : [];
                        } catch (e) {
                            _showToast('載入使用者清單失敗: ' + (e.response?.data?.detail || e.message));
                            this.usersBrief = [];
                        } finally {
                            this.usersBriefLoading = false;
                        }
                    },
                    async openShareResourceModal(r) {
                        this.shareResourceTarget = r;
                        this.showShareResourceModal = true;
                        if (!this.usersBrief.length) {
                            await this.fetchUsersBrief();
                        }
                        try {
                            const res = await axios.get(`${ADMIN_API}/resources/${r.id}/shares`);
                            const items = Array.isArray(res.data) ? res.data : [];
                            this.shareUserIds = items.map(x => x.user_id);
                        } catch (e) {
                            this.shareUserIds = [];
                        }
                    },
                    closeShareResourceModal() {
                        this.showShareResourceModal = false;
                        this.shareResourceTarget = null;
                        this.shareUserIds = [];
                        this.shareSaving = false;
                    },
                    async saveResourceShares() {
                        if (!this.shareResourceTarget?.id) return;
                        this.shareSaving = true;
                        try {
                            const payload = { user_ids: (this.shareUserIds || []).map(x => Number(x)).filter(x => Number.isFinite(x) && x > 0) };
                            await axios.post(`${ADMIN_API}/resources/${this.shareResourceTarget.id}/share`, payload);
                            this.closeShareResourceModal();
                            await this.fetchResources();
                        } catch (e) {
                            _showToast('儲存授權失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.shareSaving = false;
                        }
                    },
                    openEditResourceModal(r) {
                        this.editResourceTarget = r;
                        const rawVisibility = (r?.visibility || 'domain');
                        const canSetDomain = this.isDomainAdmin || this.isSuperAdmin;
                        const canSetPublic = this.isSuperAdmin;
                        const allowed = new Set(['private', 'shared']);
                        if (canSetDomain) allowed.add('domain');
                        if (canSetPublic) allowed.add('public');
                        const visibility = allowed.has(rawVisibility) ? rawVisibility : 'private';
                        this.editResourceForm = {
                            visibility,
                            tags: (r?.tags || ''),
                            description: (r?.description || '')
                        };
                        this.editSaving = false;
                        this.showEditResourceModal = true;
                    },
                    closeEditResourceModal() {
                        this.showEditResourceModal = false;
                        this.editResourceTarget = null;
                        this.editSaving = false;
                        this.editResourceForm = { visibility: 'private', tags: '', description: '' };
                    },
                    async saveEditResource() {
                        if (!this.editResourceTarget?.id) return;
                        this.editSaving = true;
                        try {
                            const payload = {
                                visibility: (this.editResourceForm.visibility || '').trim(),
                                tags: (this.editResourceForm.tags || '').trim(),
                                description: (this.editResourceForm.description || '').trim()
                            };
                            const res = await axios.patch(`${ADMIN_API}/resources/${this.editResourceTarget.id}`, payload);
                            const updated = res.data;
                            this.closeEditResourceModal();
                            await this.fetchResources();
                            if (updated && updated.visibility === 'shared') {
                                await this.openShareResourceModal(updated);
                            }
                        } catch (e) {
                            _showToast('儲存失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.editSaving = false;
                        }
                    },
                    async fetchNasFiles() {
                        this.nasLoading = true;
                        try {
                            const params = {};
                            if ((this.nasSubdir || '').trim()) params.subdir = this.nasSubdir.trim();
                            const res = await axios.get(`${ADMIN_API}/resources/nas/list`, { params });
                            const items = res.data || [];
                            this.nasFiles = Array.isArray(items) ? items : [];
                        } catch (e) {
                            _showToast('載入 NAS 清單失敗: ' + (e.response?.data?.detail || e.message));
                            this.nasFiles = [];
                        } finally {
                            this.nasLoading = false;
                        }
                    },
                    async importNasFile(path) {
                        if (!path) return;
                        this.nasImportingPath = path;
                        try {
                            await axios.post(`${ADMIN_API}/resources/nas/import`, { path, visibility: 'domain' });
                            await this.fetchResources();
                        } catch (e) {
                            _showToast('NAS 匯入失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.nasImportingPath = '';
                        }
                    },
                    selectAutoReply(reply) {
                        this.selectedAutoReply = reply;
                        this.autoReplyEdit = {
                            status: reply.status || 'pending',
                            draft_subject: reply.ai_draft_subject || '',
                            draft_body: reply.ai_draft_body || ''
                        };
                        if (reply?.contact_id) this.ensureContactLoaded(reply.contact_id);
                    },
                    async regenerateSelectedAutoReply() {
                        if (!this.selectedAutoReply?.id) return;
                        this.autoRepliesLoading = true;
                        try {
                            const id = this.selectedAutoReply.id;
                            const payload = {
                                tone: this.autoReplyRegenerateForm.tone,
                                language: this.autoReplyRegenerateForm.language,
                                length: this.autoReplyRegenerateForm.length,
                                template: this.autoReplyRegenerateForm.template,
                                include_quote: !!this.autoReplyRegenerateForm.include_quote
                            };
                            const candidates = [
                                `${ADMIN_API}/ai/auto-replies/${id}/regenerate`,
                                `${window.location.origin}/ai/auto-replies/${id}/regenerate`,
                            ];

                            let lastErr = null;
                            for (const url of candidates) {
                                try {
                                    const res = await axios.post(url, payload);
                                    const updated = res.data;
                                    const idx = this.autoReplies.findIndex(x => x.id === updated.id);
                                    if (idx !== -1) this.autoReplies.splice(idx, 1, updated);
                                    this.selectAutoReply(updated);
                                    return;
                                } catch (e) {
                                    lastErr = e;
                                    if (e?.response?.status === 404) continue;
                                    break;
                                }
                            }

                            const status = lastErr?.response?.status;
                            const detail = lastErr?.response?.data?.detail || lastErr?.message || 'Unknown error';
                            const url = lastErr?.config?.url;
                            _showToast(`重新生成失敗${status ? ` (${status})` : ''}: ${detail}${url ? ` [${url}]` : ''}`);
                        } finally {
                            this.autoRepliesLoading = false;
                        }
                    },
                    async ensureContactLoaded(contactId) {
                        if (!contactId) return;
                        if (this.contactCache[contactId]) return;
                        if (this.contactCacheLoading[contactId]) return;
                        this.contactCacheLoading[contactId] = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/contacts/${contactId}`);
                            if (res.data) this.contactCache[contactId] = res.data;
                        } catch (e) {
                        } finally {
                            this.contactCacheLoading[contactId] = false;
                        }
                    },
                    formatContactLabel(contactId) {
                        const c = this.contactCache[contactId];
                        if (c?.name && c?.email) return `${c.name} <${c.email}>`;
                        if (contactId) this.ensureContactLoaded(contactId);
                        return contactId ? `Contact #${contactId}` : '';
                    },
                    async saveAutoReplyEdit() {
                        if (!this.selectedAutoReply?.id) return;
                        this.autoRepliesLoading = true;
                        try {
                            const payload = {
                                status: this.autoReplyEdit.status,
                                draft_subject: this.autoReplyEdit.draft_subject,
                                draft_body: this.autoReplyEdit.draft_body
                            };
                            const res = await axios.put(`${ADMIN_API}/ai/auto-replies/${this.selectedAutoReply.id}`, payload);
                            const updated = res.data;
                            const idx = this.autoReplies.findIndex(x => x.id === updated.id);
                            if (idx !== -1) this.autoReplies.splice(idx, 1, updated);
                            this.selectAutoReply(updated);
                        } catch (e) {
                            _showToast('儲存失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.autoRepliesLoading = false;
                        }
                    },
                    async updateAutoReplyStatus(status) {
                        if (!this.selectedAutoReply?.id) return;
                        this.autoReplyEdit.status = status;
                        await this.saveAutoReplyEdit();
                    },
                    async sendSelectedAutoReply() {
                        if (!this.selectedAutoReply?.id) return;
                        if (!await this.ensureMailSession('送出郵件')) return;
                        this.autoRepliesLoading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/auto-replies/${this.selectedAutoReply.id}/send`, null, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const msg = res.data?.message || '已送出';
                            _showToast(msg);
                            await this.fetchAutoReplies();
                        } catch (e) {
                            _showToast('送出失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.autoRepliesLoading = false;
                        }
                    },
                    async applyAutoReplyToCompose() {
                        if (!this.selectedAutoReply?.id) return;
                        const contactId = this.selectedAutoReply.contact_id;
                        await this.ensureContactLoaded(contactId);
                        const contact = this.contactCache[contactId];
                        const to = contact?.email || '';
                        if (!to) return _showToast('無法取得聯絡人 Email');
                        const originalMessageId = (this.selectedAutoReply.original_message_id || '').trim();
                        const originalReferences = (this.selectedAutoReply.original_references || '').trim();
                        const references = originalMessageId
                            ? (originalReferences ? (originalReferences + ' ' + originalMessageId) : originalMessageId)
                            : null;
                        const rawBody = (this.autoReplyEdit.draft_body || this.selectedAutoReply.ai_draft_body || '');
                        const isHtml = /<[a-z][\s\S]*>/i.test(rawBody);
                        this.composeForm = {
                            to,
                            cc: '',
                            bcc: '',
                            subject: this.autoReplyEdit.draft_subject || this.selectedAutoReply.ai_draft_subject || '',
                            body: isHtml ? this.stripHtml(rawBody) : rawBody,
                            html_body: isHtml ? rawBody : '',
                            attachments: [],
                            in_reply_to: originalMessageId || null,
                            references
                        };
                        this.showCcField = false;
                        this.showBccField = false;
                        this.composeEditorMode = 'rich';
                        this.composeAttachmentSources = (this.composeForm.attachments || []).map(() => ({ type: 'upload' }));
                        this.composeResourceSearch = '';
                        this.composeResourceAttachments = [];
                        this.composeResourceErrors = {};
                        this.composeResourceErrorMeta = {};
                        this.composeResourceErrorExpanded = {};
                        this.composeResourceValidating = {};
                        this.composeResourceValidateQueue = [];
                        if (this.composeResourceValidateTimer) clearTimeout(this.composeResourceValidateTimer);
                        this.composeResourceValidateTimer = null;
                        this.composeResourceBanner = '';
                        this.composeRecommendLoading = false;
                        this.composeRecommendedResources = [];
                        this.composeRecommendError = '';
                        this.composeKbQueryInput = '';
                        this.composeKbLoading = false;
                        this.composeKbResult = '';
                        this.composeKbSources = [];
                        this.composeKbError = '';
                        this.showComposeModal = true;
                        this.ensureComposeResourcePoolLoaded();
                    },
                    async openSelectedAutoReplyOriginal() {
                        const messageId = (this.selectedAutoReply?.original_message_id || '').toString().trim();
                        const imapId = this.selectedAutoReply?.original_imap_id;
                        const subject = (this.selectedAutoReply?.original_subject || '').toString().trim();
                        const contactEmail = (this.contactCache[this.selectedAutoReply?.contact_id]?.email || '').toString().trim();
                        if (!messageId && !imapId && !(contactEmail && subject)) return;
                        if (!await this.ensureMailSession('查看原信')) return;
                        const folder = (this.selectedAutoReply?.source_folder || 'INBOX');
                        this.showOriginalMailModal = true;
                        this.originalMailMessage = null;
                        this.originalMailLoading = true;
                        this.originalMailFolder = folder;
                        try {
                            const { original, actualFolder, actualMsgId } = await this.fetchOriginalMail({
                                preferredFolder: folder,
                                messageId,
                                senderEmail: contactEmail,
                                subject,
                                imapId
                            });
                            if (!original) {
                                _showToast('無法載入原始來信');
                                this.showOriginalMailModal = false;
                                return;
                            }
                            this.originalMailFolder = actualFolder;
                            this.originalMailMessage = original;
                            if (this.originalMailMessage) {
                                if ((this.originalMailMessage.id === undefined || this.originalMailMessage.id === null || this.originalMailMessage.id === '') && actualMsgId) {
                                    this.originalMailMessage.id = Number(actualMsgId);
                                }
                                const raw = this.originalMailMessage.body_html || this.originalMailMessage.body_text || '(No Content)';
                                this.originalMailMessage.body = this.sanitizeHtml(raw);
                            }
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                this.showOriginalMailModal = false;
                                this.handleMailAuth401(error, '查看原信');
                                return;
                            }
                            _showToast('無法載入原始來信: ' + (error.response?.data?.detail || error.message));
                            this.showOriginalMailModal = false;
                        } finally {
                            this.originalMailLoading = false;
                        }
                    },
                    async fetchOriginalMail({ preferredFolder, messageId, senderEmail, subject, imapId }) {
                        const folder = (preferredFolder || 'INBOX');
                        const mid = (messageId || '').toString().trim();
                        const sender = (senderEmail || '').toString().trim();
                        const subj = (subject || '').toString().trim();
                        const fallbackId = (imapId === undefined || imapId === null || imapId === '') ? null : Number(imapId);

                        let original = null;
                        let actualFolder = folder;
                        let actualMsgId = Number.isNaN(fallbackId) ? null : fallbackId;

                        if (mid || (sender && subj)) {
                            try {
                                const params = { preferred_folder: folder };
                                if (mid) params.message_id = mid;
                                if (sender) params.sender_email = sender;
                                if (subj) params.subject = subj;
                                const byMid = await axios.get(`${ADMIN_API}/mail/message_by_message_id`, {
                                    params,
                                    headers: { 'X-Mail-Session': this.mailSessionToken }
                                });
                                original = byMid.data || null;
                                actualFolder = (original?.folder || folder);
                                const respId = (original?.id === undefined || original?.id === null || original?.id === '') ? null : Number(original?.id);
                                if (respId && !Number.isNaN(respId)) actualMsgId = respId;
                            } catch (e) {
                                if (!(e.response && (e.response.status === 404 || e.response.status === 400))) throw e;
                            }
                        }

                        if (!original && actualMsgId) {
                            const response = await axios.get(`${ADMIN_API}/mail/message/${Number(actualMsgId)}?folder=${encodeURIComponent(folder)}`, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            original = response.data || null;
                            actualFolder = folder;
                        }

                        if (original && (original.id === undefined || original.id === null || original.id === '') && actualMsgId) {
                            original.id = Number(actualMsgId);
                        }

                        return { original, actualFolder, actualMsgId };
                    },
                    async openOpportunityOriginal(opportunity) {
                        const folder = (opportunity?.source_folder || 'INBOX');
                        if (!await this.ensureMailSession('查看原信')) return;
                        this.showOriginalMailModal = true;
                        this.originalMailMessage = null;
                        this.originalMailLoading = true;
                        this.originalMailFolder = folder;
                        try {
                            const oppMessageId = (opportunity?.message_id || '');
                            const oppSenderEmail = (opportunity?.sender_email || '');
                            const oppSubject = (opportunity?.subject || '');
                            const msgId = opportunity?.imap_id;
                            const { original, actualFolder, actualMsgId } = await this.fetchOriginalMail({
                                preferredFolder: folder,
                                messageId: oppMessageId,
                                senderEmail: oppSenderEmail,
                                subject: oppSubject,
                                imapId: msgId
                            });

                            if (!original) {
                                _showToast('無法載入原始來信');
                                this.showOriginalMailModal = false;
                                return;
                            }

                            this.originalMailFolder = actualFolder;
                            this.originalMailMessage = original;
                            if (this.originalMailMessage) {
                                if ((this.originalMailMessage.id === undefined || this.originalMailMessage.id === null || this.originalMailMessage.id === '') && actualMsgId) {
                                    this.originalMailMessage.id = Number(actualMsgId);
                                }
                                const raw = this.originalMailMessage.body_html || this.originalMailMessage.body_text || '(No Content)';
                                this.originalMailMessage.body = this.sanitizeHtml(raw);
                            }
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                this.showOriginalMailModal = false;
                                this.handleMailAuth401(error, '查看原信');
                                return;
                            }
                            _showToast('無法載入原始來信: ' + (error.response?.data?.detail || error.message));
                            this.showOriginalMailModal = false;
                        } finally {
                            this.originalMailLoading = false;
                        }
                    },
                    async reextractOpportunity(opportunity) {
                        const msgId = opportunity?.imap_id;
                        const folder = (opportunity?.source_folder || 'INBOX');
                        if (!await this.ensureMailSession('重新抽取商機')) return;
                        this.opportunitiesReextractingId = opportunity?.id || null;
                        this.showOpportunityModal = true;
                        this.opportunityLoading = true;
                        this.opportunityResult = null;
                        this.opportunityError = null;
                        this.opportunityEditMode = false;
                        this.opportunitySaving = false;
                        this.resetOpportunityContactPanel();
                        try {
                            const oppMessageId = (opportunity?.message_id || '');
                            const oppSenderEmail = (opportunity?.sender_email || '');
                            const oppSubject = (opportunity?.subject || '');
                            const { original, actualFolder, actualMsgId } = await this.fetchOriginalMail({
                                preferredFolder: folder,
                                messageId: oppMessageId,
                                senderEmail: oppSenderEmail,
                                subject: oppSubject,
                                imapId: msgId
                            });

                            if (!original) throw new Error('無法載入原始來信');

                            const bodyRaw = original.body_html || original.body_text || '';
                            const sender = (original.from || opportunity?.sender_email || '').toString();
                            const safeImapId = (actualMsgId === undefined || actualMsgId === null || actualMsgId === '' || Number.isNaN(Number(actualMsgId))) ? null : Number(actualMsgId);
                            const payload = {
                                subject: (original.subject || opportunity?.subject || '').toString(),
                                body: this.stripHtml(bodyRaw),
                                sender,
                                language: 'Traditional Chinese (繁體中文)',
                                folder: actualFolder,
                                imap_id: safeImapId,
                                message_id: original.message_id || oppMessageId || null,
                                force: true
                            };
                            const res = await axios.post(`${ADMIN_API}/ai/extract/opportunity`, payload);
                            this.opportunityResult = res.data;
                            this.setOpportunityEditFormFromResult(this.opportunityResult);
                            this.initOpportunityContactPanel();
                            if (this.aiSalesTab === 'opportunities') {
                                await this.fetchOpportunities();
                            }
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                this.showOpportunityModal = false;
                                this.opportunityResult = null;
                                this.handleMailAuth401(error, '重新抽取');
                                return;
                            }
                            this.opportunityError = error.response?.data?.detail || error.message || '重新抽取失敗';
                            _showToast('重新抽取失敗: ' + this.opportunityError);
                            this.opportunityResult = null;
                        } finally {
                            this.opportunityLoading = false;
                            this.opportunitiesReextractingId = null;
                        }
                    },
                    async followUpOpportunity(opportunity) {
                        const msgId = opportunity?.imap_id;
                        const folder = (opportunity?.source_folder || 'INBOX');
                        if (!await this.ensureMailSession('跟進郵件')) return;
                        try {
                            const oppMessageId = (opportunity?.message_id || '');
                            const oppSenderEmail = (opportunity?.sender_email || '');
                            const oppSubject = (opportunity?.subject || '');
                            const { original } = await this.fetchOriginalMail({
                                preferredFolder: folder,
                                messageId: oppMessageId,
                                senderEmail: oppSenderEmail,
                                subject: oppSubject,
                                imapId: msgId
                            });

                            if (!original) return _showToast('無法載入原始來信');

                            const to = (opportunity?.sender_email || this.extractEmail(original.from || '') || '').trim();
                            if (!to) return _showToast('無法取得收件者 Email');

                            const subjectRaw = (original.subject || opportunity?.subject || '').toString();
                            const subject = subjectRaw.startsWith('Re:') ? subjectRaw : ('Re: ' + subjectRaw);

                            const summary = (opportunity?.summary || '').toString().trim();
                            const nextStep = (opportunity?.next_step || '').toString().trim();
                            const need = (opportunity?.need || '').toString().trim();
                            const budget = (opportunity?.budget_text || '').toString().trim();
                            const timeline = (opportunity?.timeline || '').toString().trim();
                            const decisionMaker = (opportunity?.decision_maker || '').toString().trim();

                            const lines = [];
                            lines.push('您好，');
                            lines.push('');
                            if (nextStep) lines.push(`下一步：${nextStep}`);
                            if (need) lines.push(`需求：${need}`);
                            if (budget) lines.push(`預算：${budget}`);
                            if (timeline) lines.push(`時程：${timeline}`);
                            if (decisionMaker) lines.push(`決策者：${decisionMaker}`);
                            if (summary) {
                                lines.push('');
                                lines.push(`摘要：${summary}`);
                            }
                            lines.push('');
                            lines.push('---------------- Original Message ----------------');
                            lines.push(`From: ${original.from || ''}`);
                            lines.push(`Date: ${original.date || ''}`);
                            lines.push(`Subject: ${original.subject || ''}`);
                            lines.push('');
                            const raw = original.body_html || original.body_text || '';
                            lines.push(this.stripHtml(raw));

                            const originalMessageId = (original.message_id || '').toString().trim();
                            const oldRefs = (original.references || '').toString().trim();
                            const references = originalMessageId
                                ? (oldRefs ? (oldRefs + ' ' + originalMessageId) : originalMessageId)
                                : null;

                            this.composeForm = {
                                to,
                                cc: '',
                                bcc: '',
                                subject,
                                body: lines.join('\n'),
                                html_body: '',
                                attachments: [],
                                in_reply_to: originalMessageId || null,
                                references
                            };
                            this.showCcField = false;
                            this.showBccField = false;
                            this.composeEditorMode = 'rich';
                            this.composeAttachmentSources = [];
                            this.composeAttachmentPreviews = [];
                            this.composeResourceSearch = '';
                            this.composeResourceAttachments = [];
                            this.composeResourceErrors = {};
                            this.composeResourceErrorMeta = {};
                            this.composeResourceErrorExpanded = {};
                            this.composeResourceValidating = {};
                            this.composeResourceValidateQueue = [];
                            if (this.composeResourceValidateTimer) clearTimeout(this.composeResourceValidateTimer);
                            this.composeResourceValidateTimer = null;
                            this.composeResourceBanner = '';
                            this.showComposeModal = true;
                            this.ensureComposeResourcePoolLoaded();
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                this.handleMailAuth401(error, '建立跟進郵件');
                                return;
                            }
                            _showToast('無法建立跟進郵件: ' + (error.response?.data?.detail || error.message));
                        }
                    },
                    async openOriginalMailByImapId(msgId, folder) {
                        if (!msgId) return;
                        const f = (folder || 'INBOX');
                        if (!await this.ensureMailSession('查看原信')) return;
                        this.showOriginalMailModal = true;
                        this.originalMailMessage = null;
                        this.originalMailLoading = true;
                        this.originalMailFolder = f;
                        try {
                            const response = await axios.get(`${ADMIN_API}/mail/message/${Number(msgId)}?folder=${encodeURIComponent(f)}`, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            this.originalMailMessage = response.data || null;
                            if (this.originalMailMessage) {
                                this.originalMailMessage.id = Number(msgId);
                                const raw = this.originalMailMessage.body_html || this.originalMailMessage.body_text || '(No Content)';
                                this.originalMailMessage.body = this.sanitizeHtml(raw);
                            }
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                this.showOriginalMailModal = false;
                                this.handleMailAuth401(error, '查看原信');
                                return;
                            }
                            _showToast('無法載入原始來信: ' + (error.response?.data?.detail || error.message));
                            this.showOriginalMailModal = false;
                        } finally {
                            this.originalMailLoading = false;
                        }
                    },
                    async downloadOriginalAttachment(filename) {
                        if (!this.originalMailMessage?.id) return;
                        if (!await this.ensureMailSession('下載附件')) return;
                        try {
                            const logId = Number(this.originalMailMessage?.mailAuditLogId) || 0;
                            const response = logId
                                ? await axios.get(`${ADMIN_API}/mail/logs/${logId}/attachments/download`, {
                                    params: { filename },
                                    headers: { 'X-Mail-Session': this.mailSessionToken },
                                    responseType: 'blob'
                                })
                                : await axios.get(`${ADMIN_API}/mail/attachment/${this.originalMailMessage.id}/${encodeURIComponent(filename)}?folder=${encodeURIComponent(this.originalMailFolder || 'INBOX')}`, {
                                    headers: { 'X-Mail-Session': this.mailSessionToken },
                                    responseType: 'blob'
                                });

                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                this.handleMailAuth401(error, '下載附件');
                                return;
                            }
                            _showToast('下載失敗: ' + (error.response?.data?.detail || error.message));
                        }
                    },

                    async generateLeadScore() {
                        if (!this.currentMessage) return _showToast('請先開啟一封郵件再進行業務分析');
                        this.aiLoading = true;
                        this.showLeadScoreModal = true;
                        this.leadScoreResult = null;
                        try {
                            const target = (this.currentFolder === 'Sent') ? this.currentMessage.to : this.currentMessage.from;
                            const response = await axios.post(`${ADMIN_API}/ai/analyze/lead_score`, {
                                subject: this.currentMessage.subject,
                                body: this.stripHtml(this.currentMessage.body),
                                sender: target
                            });
                            this.leadScoreResult = response.data;
                        } catch (error) {
                            _showToast('業務分析失敗: ' + (error.response?.data?.detail || error.message));
                            this.showLeadScoreModal = false;
                        } finally {
                            this.aiLoading = false;
                        }
                    },
                    async extractOpportunityFromCurrentMessage() {
                        if (!this.currentMessage) return;
                        this.showOpportunityModal = true;
                        this.opportunityLoading = true;
                        this.opportunityResult = null;
                        this.opportunityError = null;
                        this.opportunityEditMode = false;
                        this.opportunitySaving = false;
                        this.resetOpportunityContactPanel();
                        try {
                            const target = (this.currentFolder === 'Sent') ? this.currentMessage.to : this.currentMessage.from;
                            const response = await axios.post(`${ADMIN_API}/ai/extract/opportunity`, {
                                subject: this.currentMessage.subject,
                                body: this.stripHtml(this.currentMessage.body),
                                sender: target,
                                language: 'Traditional Chinese (繁體中文)',
                                folder: this.currentFolder,
                                imap_id: this.currentMessage.id,
                                message_id: this.currentMessage.message_id
                            });
                            this.opportunityResult = response.data;
                            this.setOpportunityEditFormFromResult(this.opportunityResult);
                            this.initOpportunityContactPanel();
                        } catch (error) {
                            this.opportunityError = error.response?.data?.detail || error.message || '抽取失敗';
                            _showToast('商機抽取失敗: ' + this.opportunityError);
                            this.opportunityResult = null;
                        } finally {
                            this.opportunityLoading = false;
                        }
                    },
                    prepareFollowup() {
                        if (!this.currentMessage) return;

                        if (this.currentFolder === 'Sent') {
                            this.aiFollowupForm.recipient = this.currentMessage.to;
                        } else {
                            this.aiFollowupForm.recipient = this.currentMessage.from;
                        }

                        const body = this.currentMessage.body_text || this.currentMessage.body_html || this.currentMessage.body || '';
                        this.aiFollowupForm.original_subject = (this.currentMessage.subject || '');
                        this.aiFollowupForm.original_text = this.stripHtml(body);
                        this.aiFollowupForm.tone = 'professional';
                        this.aiFollowupForm.instructions = '';
                        this.aiFollowupForm.language = 'auto';
                        this.aiFollowupForm.length = 'medium';
                        this.aiFollowupForm.template = 'gentle_nudge';
                        this.aiFollowupForm.include_quote = true;
                        this.aiFollowupResult = '';
                        this.aiFollowupSubject = '';
                        this.showAiFollowupModal = true;
                    },
                    async generateFollowup() {
                        this.aiLoading = true;
                        try {
                            const response = await axios.post(`${ADMIN_API}/ai/generate/followup`, {
                                original_text: this.aiFollowupForm.original_text,
                                original_subject: this.aiFollowupForm.original_subject,
                                recipient: this.aiFollowupForm.recipient,
                                tone: this.aiFollowupForm.tone,
                                instructions: this.aiFollowupForm.instructions || '',
                                language: this.aiFollowupForm.language,
                                length: this.aiFollowupForm.length,
                                template: this.aiFollowupForm.template,
                                include_quote: this.aiFollowupForm.include_quote
                            });
                            const data = response.data;
                            if (data && typeof data === 'object') {
                                this.aiFollowupSubject = (data.subject || '').toString();
                                this.aiFollowupResult = (data.body || data.result || '').toString();
                            } else {
                                this.aiFollowupSubject = '';
                                this.aiFollowupResult = (data || '').toString();
                            }
                        } catch (error) {
                            _showToast('生成跟進郵件失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.aiLoading = false;
                        }
                    },
                    useFollowup() {
                        this.composeForm.to = this.aiFollowupForm.recipient;
                        this.composeForm.subject = (this.aiFollowupSubject || ('Follow up: ' + this.currentMessage.subject));
                        const rawBody = (this.aiFollowupResult || '').toString();
                        const isHtml = /<[a-z][\s\S]*>/i.test(rawBody);
                        this.composeForm.html_body = isHtml ? rawBody : '';
                        this.composeForm.body = isHtml ? this.stripHtml(rawBody) : rawBody;
                        this.composeEditorMode = 'rich';
                        // Threading headers
                        this.composeForm.in_reply_to = this.currentMessage.message_id;
                        const oldRefs = this.currentMessage.references || '';
                        this.composeForm.references = oldRefs ? (oldRefs + ' ' + this.currentMessage.message_id) : this.currentMessage.message_id;

                        this.showAiFollowupModal = false;
                        this.composeAttachmentSources = (this.composeForm.attachments || []).map(() => ({ type: 'upload' }));
                        this.composeResourceSearch = '';
                        this.composeResourceAttachments = [];
                        this.composeResourceErrors = {};
                        this.composeResourceErrorMeta = {};
                        this.composeResourceErrorExpanded = {};
                        this.composeResourceValidating = {};
                        this.composeResourceValidateQueue = [];
                        if (this.composeResourceValidateTimer) clearTimeout(this.composeResourceValidateTimer);
                        this.composeResourceValidateTimer = null;
                        this.composeResourceBanner = '';
                        this.showComposeModal = true;
                        this.ensureComposeResourcePoolLoaded();
                    },
                    async smartFileEmail() {
                        if (!this.currentMessage) return;
                        if (!confirm('AI 將分析郵件並自動移動到合適的資料夾。確定要繼續嗎？')) return;

                        this.aiLoading = true;
                        try {
                            // 1. Analyze
                            const response = await axios.post(`${ADMIN_API}/ai/analyze`, {
                                subject: this.currentMessage.subject,
                                body: this.stripHtml(this.currentMessage.body),
                                sender: this.currentMessage.from
                            });

                            let result = response.data.result;
                            if (typeof result === 'string') {
                                try {
                                    const jsonMatch = result.match(/```json\n([\s\S]*?)\n```/) || result.match(/\{[\s\S]*\}/);
                                    if (jsonMatch) result = JSON.parse(jsonMatch[1] || jsonMatch[0]);
                                } catch (e) { }
                            }

                            const category = result.category || 'INBOX';
                            let targetFolder = 'INBOX';

                            // Map category to folder (simple mapping)
                            const folderMap = {
                                'Work': 'Work',
                                'Personal': 'Personal',
                                'Promotion': 'Promotion',
                                'Social': 'Social',
                                'Spam': 'Junk'
                            };

                            if (folderMap[category]) {
                                targetFolder = folderMap[category];
                            } else {
                                targetFolder = category;
                            }

                            // 2. Move
                            await this.moveEmail(targetFolder);

                        } catch (error) {
                            _showToast('智慧歸檔失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.aiLoading = false;
                        }
                    },
                    // Webmail Methods
                    async setMailPassword() {
                        const pwd = (this.mailPasswordInput || '').toString();
                        if (!pwd) return;
                        this.mailPassword = pwd;
                        this.persistMailPasswordToStorage(pwd);
                        try {
                            const ok = await this.ensureMailSession('進入信箱');
                            if (!ok) return;
                            this.mailPasswordInput = '';
                            this.webmailTab = 'mail';
                            await this.fetchMessages(this.currentFolder || 'INBOX');
                        } finally {
                            this.mailPassword = '';
                        }
                    },
                    // --- Mail Signature ---
                    async fetchMailSignature() {
                        this.mailSignature.loading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/mail/signature`);
                            this.mailSignature.enabled = !!res.data.enabled;
                            this.mailSignature.signature_html = res.data.signature_html || '';
                            this.mailSignature.signature_text = res.data.signature_text || '';
                            const logoRes = await axios.get(`${ADMIN_API}/mail/signature/logo`);
                            this.mailSignature.logoUrl = logoRes.data.url || '';
                        } catch (e) {
                            console.error('fetchMailSignature', e);
                        } finally {
                            this.mailSignature.loading = false;
                        }
                    },
                    async saveMailSignature() {
                        this.mailSignature.saving = true;
                        try {
                            await axios.put(`${ADMIN_API}/mail/signature`, {
                                enabled: this.mailSignature.enabled,
                                signature_html: this.mailSignature.signature_html,
                                signature_text: this.mailSignature.signature_text,
                            });
                            window.$toast?.success('簽名已儲存');
                            this.mailSignature.showEditor = false;
                        } catch (e) {
                            _showToast('儲存簽名失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.mailSignature.saving = false;
                        }
                    },
                    async generateSignatureWithAI() {
                        if (!this.sigGenModal.name) return;
                        this.sigGenModal.loading = true;
                        this.sigGenModal.error = '';
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/generate/signature`, {
                                name: this.sigGenModal.name,
                                title: this.sigGenModal.title,
                                company: this.sigGenModal.company,
                                phone: this.sigGenModal.phone,
                                email: this.currentUser?.email || '',
                                website: this.sigGenModal.website,
                                style: this.sigGenModal.style,
                            });
                            this.mailSignature.signature_html = res.data.result;
                            this.sigGenModal.open = false;
                            window.$toast?.success('AI 簽名已生成，請確認後儲存');
                        } catch (e) {
                            this.sigGenModal.error = 'AI 生成失敗: ' + (e.response?.data?.detail || e.message);
                        } finally {
                            this.sigGenModal.loading = false;
                        }
                    },
                    async uploadSignatureLogo(file) {
                        if (!file) return;
                        const fd = new FormData(); fd.append('file', file);
                        try {
                            const res = await axios.post(`${ADMIN_API}/mail/signature/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            this.mailSignature.logoUrl = res.data.url || '';
                            window.$toast?.success('Logo 已上傳');
                        } catch (e) {
                            _showToast('上傳 Logo 失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async deleteSignatureLogo() {
                        try {
                            await axios.delete(`${ADMIN_API}/mail/signature/logo`);
                            this.mailSignature.logoUrl = '';
                            window.$toast?.success('Logo 已移除');
                        } catch (e) {
                            _showToast('移除 Logo 失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async changeUserPassword() {
                        const { current_password, new_password, confirm_password } = this.changePasswordForm;
                        this.changePasswordError = '';
                        this.changePasswordSuccess = '';
                        if (!current_password || !new_password || !confirm_password) {
                            this.changePasswordError = '請填寫所有欄位';
                            return;
                        }
                        if (new_password !== confirm_password) {
                            this.changePasswordError = '兩次輸入的新密碼不一致';
                            return;
                        }
                        if (new_password.length < 8) {
                            this.changePasswordError = '新密碼至少需要 8 個字元';
                            return;
                        }
                        this.changePasswordLoading = true;
                        try {
                            await axios.post(`${ADMIN_API}/me/change-password`, {
                                current_password,
                                new_password,
                            });
                            this.changePasswordSuccess = '密碼已成功更新';
                            this.changePasswordForm = { current_password: '', new_password: '', confirm_password: '' };
                            window.$toast?.success('密碼已成功更新');
                        } catch (e) {
                            this.changePasswordError = e.response?.data?.detail || '修改密碼失敗';
                        } finally {
                            this.changePasswordLoading = false;
                        }
                    },
                    // --- Mail Folders ---
                    async fetchMailFolders() {
                        if (!this.hasValidMailSession) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/mail/folders`, { headers: { 'X-Mail-Session': this.mailSessionToken } });
                            this.mailFolderList = res.data.folders || [];
                        } catch (e) {
                            console.error('fetchMailFolders', e);
                        }
                    },
                    async createMailFolder(name) {
                        if (!name?.trim()) return;
                        try {
                            await axios.post(`${ADMIN_API}/mail/folder`, { name: name.trim() }, { headers: { 'X-Mail-Session': this.mailSessionToken } });
                            window.$toast?.success('資料夾已建立', name.trim());
                            await this.fetchMailFolders();
                        } catch (e) {
                            _showToast('建立資料夾失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async deleteMailFolder(name) {
                        if (!confirm(`確定要刪除資料夾「${name}」？其中的郵件也會一併刪除。`)) return;
                        try {
                            await axios.delete(`${ADMIN_API}/mail/folder`, { data: { name }, headers: { 'X-Mail-Session': this.mailSessionToken } });
                            window.$toast?.success('資料夾已刪除', name);
                            await this.fetchMailFolders();
                        } catch (e) {
                            _showToast('刪除資料夾失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async renameMailFolder(oldName, newName) {
                        if (!newName?.trim() || oldName === newName) return;
                        try {
                            await axios.put(`${ADMIN_API}/mail/folder`, { old_name: oldName, new_name: newName.trim() }, { headers: { 'X-Mail-Session': this.mailSessionToken } });
                            window.$toast?.success('資料夾已重新命名');
                            await this.fetchMailFolders();
                        } catch (e) {
                            _showToast('重新命名失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },

                    async warmupMailSession(password) {
                        if (this.hasValidMailSession) return true;
                        // Try with the login password (ensureMailSession will also try stored password first)
                        const pwd = (password || '').toString();
                        if (!pwd) return false;
                        if (!this.mailPasswordInput) this.mailPasswordInput = pwd;
                        this.mailPassword = pwd;
                        try {
                            const ok = await this.ensureMailSession('登入');
                            if (ok) {
                                this.persistMailPasswordToStorage(pwd);
                                this.mailPasswordInput = '';
                            }
                            return ok;
                        } catch (e) {
                            return false;
                        } finally {
                            this.mailPassword = '';
                        }
                    },
                    async seedWebmailExamples() {
                        if (this.webmailSeedingExamples) return;
                        if (!await this.ensureMailSession('加入範例信件')) return;
                        const folder = (this.currentFolder || 'INBOX').toString() || 'INBOX';
                        this.webmailSeedingExamples = true;
                        try {
                            await axios.post(`${ADMIN_API}/mail/seed_examples`, null, {
                                params: { folder, count: 8 },
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            await this.fetchMessages(folder, 1);
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                this.handleMailAuth401(e, '加入範例信件');
                                return;
                            }
                            _showToast('加入範例信件失敗: ' + (e?.response?.data?.detail || e?.message || 'unknown error'));
                        } finally {
                            this.webmailSeedingExamples = false;
                        }
                    },
                    async openWebmailHome() {
                        await this.switchSidebarView('webmail');
                        if (this.currentView !== 'webmail') return;
                        this.webmailTab = 'mail';
                        this.currentMessage = null;
                        this.webmailLastError = '';
                        if (!this.currentFolder) this.currentFolder = 'INBOX';
                        if (this.hasValidMailSession) {
                            await this.fetchMessages(this.currentFolder || 'INBOX');
                            this.fetchUnreadCounts();
                            return;
                        }
                        if (this.hasStoredMailPassword) {
                            await this.autoEnterWebmail();
                        }
                    },
                    async openWebmailFolder(folder) {
                        await this.switchSidebarView('webmail');
                        if (this.currentView !== 'webmail') return;
                        this.webmailTab = 'mail';
                        this.currentMessage = null;
                        this.mailSearchQuery = '';
                        this.webmailLastError = '';
                        this.currentFolder = folder || 'INBOX';
                        if (this.hasValidMailSession) {
                            await this.fetchMessages(this.currentFolder || 'INBOX');
                            this.fetchUnreadCounts();
                            return;
                        }
                        if (this.hasStoredMailPassword) {
                            await this.autoEnterWebmail();
                        }
                    },
                    async openWebmailCalendar() {
                        await this.switchSidebarView('webmail');
                        if (this.currentView !== 'webmail') return;
                        this.webmailTab = 'calendar';
                        this.currentMessage = null;
                        this.webmailLastError = '';
                        await this.fetchWebmailCalendarMonthEvents();
                    },
                    async autoEnterWebmail() {
                        if (this.currentView !== 'webmail') return;
                        if (this.webmailTab !== 'calendar') this.webmailTab = 'mail';
                        if (this.hasValidMailSession) return;
                        if (!this.hasStoredMailPassword) return;
                        try {
                            const ok = await this.ensureMailSession('進入信箱');
                            if (!ok) return;
                            if (this.webmailTab === 'mail') {
                                await this.fetchMessages(this.currentFolder || 'INBOX');
                                this.fetchUnreadCounts();
                            } else {
                                await this.fetchWebmailCalendarMonthEvents();
                            }
                        } catch (e) {
                        }
                    },
                    switchWebmailTab(tab) {
                        const t = (tab || '').toString();
                        const valid = ['mail', 'calendar', 'settings'];
                        this.webmailTab = valid.includes(t) ? t : 'mail';
                        if (this.webmailTab === 'calendar') {
                            this.fetchWebmailCalendarMonthEvents();
                        }
                    },
                    formatWebmailMonthLabel(value) {
                        const v = (value || '').toString().trim();
                        if (!/^\d{4}-\d{2}$/.test(v)) return v || '—';
                        return v.replace('-', '/');
                    },
                    changeWebmailMonth(delta) {
                        const v = (this.webmailCalendarMonth || '').toString().trim();
                        const m = /^\d{4}-\d{2}$/.test(v) ? v : new Date().toISOString().slice(0, 7);
                        const year = Number(m.slice(0, 4));
                        const month = Number(m.slice(5, 7));
                        const base = new Date(year, month - 1, 1);
                        base.setMonth(base.getMonth() + Number(delta || 0));
                        this.webmailCalendarMonth = base.toISOString().slice(0, 7);
                        this.fetchWebmailCalendarMonthEvents();
                    },
                    async fetchWebmailCalendarMonthEvents() {
                        const v = (this.webmailCalendarMonth || '').toString().trim();
                        const m = /^\d{4}-\d{2}$/.test(v) ? v : new Date().toISOString().slice(0, 7);
                        this.webmailCalendarMonth = m;
                        const year = Number(m.slice(0, 4));
                        const month = Number(m.slice(5, 7));
                        const start = new Date(year, month - 1, 1, 0, 0, 0);
                        const end = new Date(year, month, 1, 0, 0, 0);
                        this.webmailCalendarLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/calendar/events`, {
                                params: { start: start.toISOString(), end: end.toISOString(), limit: 500 }
                            });
                            this.webmailCalendarEvents = Array.isArray(res?.data?.items) ? res.data.items : [];
                            const map = {};
                            for (const ev of this.webmailCalendarEvents) {
                                const s = (ev?.start_at || '').toString();
                                const day = s ? s.slice(0, 10) : '';
                                if (!day) continue;
                                if (!map[day]) map[day] = [];
                                map[day].push(ev);
                            }
                            for (const k of Object.keys(map)) {
                                map[k].sort((a, b) => ((a?.start_at || '').toString()).localeCompare((b?.start_at || '').toString()));
                            }
                            this.webmailCalendarEventMap = map;
                            this.buildWebmailCalendarGrid();
                        } catch (e) {
                            _showToast('載入行事曆失敗: ' + (e.response?.data?.detail || e.message));
                            this.webmailCalendarEvents = [];
                            this.webmailCalendarEventMap = {};
                            this.webmailCalendarGridDays = [];
                        } finally {
                            this.webmailCalendarLoading = false;
                        }
                    },
                    buildWebmailCalendarGrid() {
                        const v = (this.webmailCalendarMonth || '').toString().trim();
                        const m = /^\d{4}-\d{2}$/.test(v) ? v : new Date().toISOString().slice(0, 7);
                        const year = Number(m.slice(0, 4));
                        const month = Number(m.slice(5, 7));
                        const first = new Date(year, month - 1, 1);
                        const last = new Date(year, month, 0);
                        const firstWeekday = (first.getDay() + 6) % 7;
                        const start = new Date(first);
                        start.setDate(first.getDate() - firstWeekday);
                        const grid = [];
                        for (let i = 0; i < 42; i++) {
                            const d = new Date(start);
                            d.setDate(start.getDate() + i);
                            const date = d.toISOString().slice(0, 10);
                            const inMonth = d.getMonth() === (month - 1);
                            const events = Array.isArray(this.webmailCalendarEventMap?.[date]) ? this.webmailCalendarEventMap[date] : [];
                            grid.push({
                                date,
                                day: d.getDate(),
                                inMonth,
                                count: events.length,
                                preview: events.slice(0, 2)
                            });
                        }
                        this.webmailCalendarGridDays = grid;
                    },
                    openWebmailMeetingModal(dateStr = null) {
                        const base = (dateStr || '').toString().trim();
                        const date = /^\d{4}-\d{2}-\d{2}$/.test(base) ? base : new Date().toISOString().slice(0, 10);
                        const start = `${date}T09:00`;
                        const endDate = new Date(`${date}T09:00`);
                        endDate.setMinutes(endDate.getMinutes() + 30);
                        const end = this.normalizeDateTimeLocal(endDate.toISOString());
                        this.webmailMeetingForm = {
                            id: null,
                            title: '',
                            start_time: start,
                            end_time: end,
                            location: '',
                            description: '',
                            attendees: '',
                            invite_message: '',
                            send_invite: true
                        };
                        this.showWebmailMeetingModal = true;
                    },
                    async openWebmailMeetingModalById(eventId) {
                        const id = Number(eventId) || 0;
                        if (!id) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/calendar/events/${id}`);
                            const ev = res?.data || null;
                            if (!ev) {
                                _showToast('載入事件失敗: empty response');
                                return;
                            }
                            let attendees = [];
                            try {
                                const ares = await axios.get(`${ADMIN_API}/calendar/events/${id}/attendees`);
                                attendees = Array.isArray(ares?.data?.items) ? ares.data.items : [];
                            } catch (e) {
                                attendees = [];
                            }
                            this.webmailMeetingForm = {
                                id,
                                title: (ev.title || '').toString(),
                                start_time: this.normalizeDateTimeLocal(ev.start_at),
                                end_time: this.normalizeDateTimeLocal(ev.end_at),
                                location: (ev.location || '').toString(),
                                description: (ev.description || '').toString(),
                                attendees: attendees.map(a => (a?.email || '').toString()).filter(Boolean).join(', '),
                                invite_message: '',
                                send_invite: true
                            };
                            this.showWebmailMeetingModal = true;
                        } catch (e) {
                            _showToast('載入事件失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    closeWebmailMeetingModal() {
                        this.showWebmailMeetingModal = false;
                        this.webmailMeetingSaving = false;
                        this.webmailMeetingInviting = false;
                    },
                    parseWebmailAttendees(value) {
                        const raw = (value || '').toString();
                        const tokens = raw.split(/[,;\n\r\t ]+/).map(s => s.trim()).filter(Boolean);
                        const seen = new Set();
                        const out = [];
                        for (const t of tokens) {
                            const v = t.toLowerCase();
                            if (!v.includes('@')) continue;
                            if (seen.has(v)) continue;
                            seen.add(v);
                            out.push(v);
                        }
                        return out;
                    },
                    async saveWebmailMeeting() {
                        const eventId = Number(this.webmailMeetingForm?.id) || 0;
                        const title = (this.webmailMeetingForm?.title || '').toString().trim();
                        const startTime = this.normalizeDateTimeLocal(this.webmailMeetingForm?.start_time);
                        if (!title || !startTime) {
                            _showToast('標題與開始時間必填');
                            return;
                        }
                        const endTime = this.normalizeDateTimeLocal(this.webmailMeetingForm?.end_time) || null;
                        this.webmailMeetingSaving = true;
                        try {
                            const payload = {
                                title,
                                start_time: startTime,
                                end_time: endTime,
                                location: (this.webmailMeetingForm?.location || '').toString().trim() || null,
                                description: (this.webmailMeetingForm?.description || '').toString().trim() || null
                            };
                            let savedId = eventId;
                            if (savedId) {
                                await axios.put(`${ADMIN_API}/calendar/events/${savedId}`, payload);
                            } else {
                                const created = await axios.post(`${ADMIN_API}/calendar/events`, { ...payload, source_type: 'manual' });
                                savedId = Number(created?.data?.id) || 0;
                            }
                            const attendees = this.parseWebmailAttendees(this.webmailMeetingForm?.attendees);
                            const shouldInvite = !!this.webmailMeetingForm?.send_invite;
                            this.webmailMeetingSaving = false;
                            if (shouldInvite && savedId && attendees.length) {
                                this.webmailMeetingInviting = true;
                                try {
                                    if (!await this.ensureMailSession('寄送會議邀請')) return;
                                    await axios.post(`${ADMIN_API}/calendar/events/${savedId}/invite`, {
                                        attendees,
                                        message: (this.webmailMeetingForm?.invite_message || '').toString().trim() || null
                                    }, {
                                        headers: { 'X-Mail-Session': this.mailSessionToken }
                                    });
                                } catch (e) {
                                    _showToast('已建立事件，但寄送邀請失敗: ' + (e.response?.data?.detail || e.message));
                                } finally {
                                    this.webmailMeetingInviting = false;
                                }
                            }
                            this.closeWebmailMeetingModal();
                            this.fetchWebmailCalendarMonthEvents();
                        } catch (e) {
                            _showToast('建立會議失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.webmailMeetingSaving = false;
                        }
                    },
                    async deleteWebmailCalendarEvent(eventId) {
                        const id = Number(eventId) || 0;
                        if (!id) return;
                        const ok = confirm(`確定要刪除事件 #${id} ?`);
                        if (!ok) return;
                        try {
                            await axios.delete(`${ADMIN_API}/calendar/events/${id}`);
                            this.fetchWebmailCalendarMonthEvents();
                        } catch (e) {
                            _showToast('刪除失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async deleteWebmailMeetingFromModal() {
                        const id = Number(this.webmailMeetingForm?.id) || 0;
                        if (!id) return;
                        const ok = confirm(`確定要刪除事件 #${id} ?`);
                        if (!ok) return;
                        try {
                            await axios.delete(`${ADMIN_API}/calendar/events/${id}`);
                            this.closeWebmailMeetingModal();
                            this.fetchWebmailCalendarMonthEvents();
                        } catch (e) {
                            _showToast('刪除失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async fetchMessages(folder, page = 1, _retry = false) {
                        this.currentFolder = folder;
                        this.mailPage = page;
                        this.selectedMessageIds = [];
                        this.selectAllMessages = false;
                        const offset = (page - 1) * this.mailLimit;

                        this.loadingMessages = true;
                        this.currentMessage = null;
                        try {
                            if (!await this.ensureMailSession('載入郵件')) return;
                            let url = `${ADMIN_API}/mail/messages?folder=${folder}&limit=${this.mailLimit}&offset=${offset}`;
                            if (this.mailSearchQuery) {
                                url += `&search=${encodeURIComponent(this.mailSearchQuery)}`;
                            }

                            const response = await axios.get(url, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });

                            const raw = response.data?.messages ? response.data.messages : response.data;
                            const items = Array.isArray(raw) ? raw : [];
                            this.messages = items.filter(x => x && typeof x === 'object' && x.id !== undefined && x.id !== null);
                            this.mailTotal = Number(response.data?.total) || this.messages.length;
                        } catch (error) {
                            window.$toast?.error('載入 messages 失敗', error?.response?.data?.detail || error?.message || '');
                            console.error('Failed to fetch messages', error);
                            if (error.response && error.response.status === 401) {
                                const canRetry = this.handleMailAuth401(error, '載入郵件');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('載入郵件');
                                        if (ok) return await this.fetchMessages(folder, page, true);
                                    } catch (e) {
                                    }
                                }
                                return;
                            }
                            const status = error?.response?.status;
                            const detail = error?.response?.data?.detail || error?.message;
                            const msg = `載入郵件失敗${status ? `（${status}）` : ''}: ${detail || 'unknown error'}`;
                            this.webmailLastError = msg;
                            _showToast(msg);
                            this.messages = [];
                            this.mailTotal = 0;
                        } finally {
                            this.loadingMessages = false;
                        }
                    },
                    async openMessage(msgId, _retry = false) {
                        this.loadingContent = true;
                        const msgIndex = this.messages.findIndex(m => m && m.id === msgId);

                        try {
                            if (!await this.ensureMailSession('查看郵件')) return;
                            const response = await axios.get(`${ADMIN_API}/mail/message/${msgId}?folder=${this.currentFolder}`, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const data = response ? response.data : null;
                            if (!data) {
                                throw new Error('Mail not found');
                            }
                            this.currentMessage = data;
                            if (this.currentMessage && typeof this.currentMessage === 'object') {
                                this.currentMessage.id = msgId;
                            }
                            try {
                                if (msgIndex !== -1 && this.messages[msgIndex]) {
                                    this.messages[msgIndex].is_read = true;
                                }
                                if (this.currentMessage) {
                                    this.currentMessage.is_read = true;
                                }
                                await axios.post(`${ADMIN_API}/mail/message/seen`, {
                                    msg_id: msgId,
                                    folder: (this.currentFolder || 'INBOX').toString(),
                                    seen: true,
                                }, {
                                    headers: { 'X-Mail-Session': this.mailSessionToken }
                                });
                            } catch (e) {
                            }
                            const bodyHtml = (this.currentMessage.body_html ?? '').toString();
                            const bodyText = (this.currentMessage.body_text ?? '').toString();
                            let body = '';
                            if (bodyHtml) {
                                const sanitized = this.sanitizeHtml(bodyHtml);
                                const visible = (this.stripHtml(sanitized) || '').trim();
                                const hasRenderable = (visible.length > 0) || /<(img|table|tr|td|div|p|br|span)\b/i.test(sanitized);
                                if (hasRenderable) body = sanitized;
                            }
                            if (!body) {
                                const rawText = (bodyText || '').toString().trim();
                                const fallback = rawText || '（此郵件沒有文字內容，可能僅包含附件或使用了不支援的格式）';
                                body = this.sanitizeHtml(fallback);
                            }
                            this.currentMessage.body = body;
                        } catch (error) {
                            window.$toast?.error('載入 message content 失敗', error?.response?.data?.detail || error?.message || '');
                            console.error('Failed to fetch message content', error);
                            if (error.response && error.response.status === 401) {
                                const canRetry = this.handleMailAuth401(error, '查看郵件');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('查看郵件');
                                        if (ok) return await this.openMessage(msgId, true);
                                    } catch (e) {
                                    }
                                }
                                return;
                            }
                            const status = error?.response?.status;
                            const detail = error?.response?.data?.detail || error?.message;
                            const msg = `載入郵件內容失敗${status ? `（${status}）` : ''}: ${detail || 'unknown error'}`;
                            this.webmailLastError = msg;
                            _showToast(msg);
                        } finally {
                            this.loadingContent = false;
                        }
                    },
                    openComposeModal() {
                        this.closeRecipientPicker();
                        this.composeForm = { to: '', cc: '', bcc: '', subject: '', body: '', html_body: '', attachments: [], in_reply_to: null, references: null };
                        this.showCcField = false;
                        this.showBccField = false;
                        this.composeEditorMode = 'rich';
                        this.composeAttachmentSources = [];
                        this.composeAttachmentPreviews = [];
                        this.composeResourceSearch = '';
                        this.composeResourceAttachments = [];
                        this.composeResourceErrors = {};
                        this.composeResourceErrorMeta = {};
                        this.composeResourceErrorExpanded = {};
                        this.composeResourceValidating = {};
                        this.composeResourceValidateQueue = [];
                        if (this.composeResourceValidateTimer) clearTimeout(this.composeResourceValidateTimer);
                        this.composeResourceValidateTimer = null;
                        this.composeResourceBanner = '';
                        this.composeRecommendLoading = false;
                        this.composeRecommendedResources = [];
                        this.composeRecommendError = '';
                        this.composeKbQueryInput = '';
                        this.composeKbLoading = false;
                        this.composeKbResult = '';
                        this.composeKbSources = [];
                        this.composeKbError = '';
                        this.ensureComposeResourcePoolLoaded();
                        this.composeAiPanelOpen = false;
                        this.composeAiPanelCollapsed = false;
                        this.composeKbPanelOpen = false;
                        this.composeKbPanelCollapsed = false;
                        this.composeAttachPanelCollapsed = false;
                        this.kbSourceExpandedIdx = null;
                        this.kbDocPreviewModal = { open: false, loading: false, doc_id: null, source_name: '', content: '', error: '' };
                        this.composeMaximized = false;
                        this.composeIsDraggingFile = false;
                        this.composeDraftRestored = false;
                        // Restore draft from localStorage
                        try {
                            const saved = localStorage.getItem('compose_draft');
                            if (saved) {
                                const draft = JSON.parse(saved);
                                if (draft && (draft.subject || draft.body || draft.to)) {
                                    this.composeForm = { ...this.composeForm, ...draft, attachments: [], in_reply_to: null, references: null };
                                    if (draft.cc) this.showCcField = true;
                                    if (draft.bcc) this.showBccField = true;
                                    this.composeDraftRestored = true;
                                }
                            }
                        } catch (e) {}
                        this.showComposeModal = true;
                        this.ensureComposeResourcePoolLoaded();
                    },

                    saveComposeDraft() {
                        try {
                            const { to, cc, bcc, subject, body, html_body } = this.composeForm;
                            if (!subject && !body && !html_body && !to) {
                                localStorage.removeItem('compose_draft');
                                return;
                            }
                            localStorage.setItem('compose_draft', JSON.stringify({ to, cc, bcc, subject, body, html_body }));
                        } catch (e) {}
                    },

                    clearComposeDraft() {
                        try { localStorage.removeItem('compose_draft'); } catch (e) {}
                    },

                    closeComposeWithConfirm() {
                        const hasContent = !!(
                            (this.composeForm.subject && this.composeForm.subject.trim()) ||
                            (this.composeForm.body && this.composeForm.body.trim()) ||
                            (this.composeForm.html_body && this.composeForm.html_body.trim()) ||
                            (this.composeForm.to && this.composeForm.to.trim())
                        );
                        if (hasContent) {
                            const choice = confirm('郵件尚未送出。\n\n確定要關閉嗎？（內容已自動儲存為草稿）');
                            if (!choice) return;
                            this.saveComposeDraft();
                        } else {
                            this.clearComposeDraft();
                        }
                        this.showComposeModal = false;
                    },

                    discardComposeDraft() {
                        this.clearComposeDraft();
                        this.composeDraftRestored = false;
                        this.composeForm = { to: '', cc: '', bcc: '', subject: '', body: '', html_body: '', attachments: [], in_reply_to: null, references: null };
                        this.showCcField = false;
                        this.showBccField = false;
                    },
                    openRecipientPicker(target = 'to') {
                        if (!this.canAccess('contacts')) {
                            _showToast('沒有權限使用通訊錄');
                            return;
                        }
                        const t = (target || 'to').toString().toLowerCase();
                        this.recipientPickerTarget = (t === 'cc' || t === 'bcc') ? t : 'to';
                        this.showRecipientPickerModal = true;
                        this.recipientPickerLoading = false;
                        this.recipientPickerQuery = '';
                        this.recipientPickerResults = [];
                        this.recipientPickerError = '';
                        nextTick(() => {
                            this.searchRecipientContacts();
                        });
                    },
                    closeRecipientPicker() {
                        this.showRecipientPickerModal = false;
                        this.recipientPickerLoading = false;
                        this.recipientPickerQuery = '';
                        this.recipientPickerResults = [];
                        this.recipientPickerError = '';
                        this.recipientPickerSelected = [];
                    },
                    async searchRecipientContacts() {
                        if (!this.canAccess('contacts')) return;
                        this.recipientPickerError = '';
                        this.recipientPickerResults = [];
                        const q = (this.recipientPickerQuery || '').toString().trim();
                        this.recipientPickerLoading = true;
                        try {
                            const params = { limit: 20, skip: 0, sort_by: 'last_interaction_at', sort_dir: 'desc' };
                            if (q) params.search = q;
                            const res = await axios.get(`${ADMIN_API}/contacts`, { params });
                            const items = Array.isArray(res?.data?.items) ? res.data.items : (Array.isArray(res?.data) ? res.data : []);
                            this.recipientPickerResults = items;
                            if (items.length === 0) {
                                this.recipientPickerError = q ? '找不到符合的聯絡人' : '目前沒有可用的聯絡人';
                            }
                        } catch (e) {
                            this.recipientPickerError = (e.response?.data?.detail || e.message || '搜尋失敗');
                        } finally {
                            this.recipientPickerLoading = false;
                        }
                    },
                    pickRecipientFromContacts(c) {
                        const email = (c?.email || '').toString().trim();
                        if (!email) return;
                        const name = (c?.name || '').toString().trim();
                        const token = name ? `${name} <${email}>` : email;
                        const target = (this.recipientPickerTarget || 'to').toString().toLowerCase();
                        const key = (target === 'cc' || target === 'bcc') ? target : 'to';
                        this.mergeRecipientTokens(key, [token]);
                        this.closeRecipientPicker();
                        nextTick(() => {
                            try {
                                const el = document.getElementById(key === 'to' ? 'compose-to-input' : (key === 'cc' ? 'compose-cc-input' : 'compose-bcc-input'));
                                if (el) el.focus();
                            } catch (e) {
                            }
                        });
                    },
                    addSelectedRecipients() {
                        const target = (this.recipientPickerTarget || 'to').toString().toLowerCase();
                        const key = (target === 'cc' || target === 'bcc') ? target : 'to';
                        const tokens = (this.recipientPickerSelected || []).map(x => (x || '').toString().trim()).filter(Boolean);
                        if (tokens.length > 0) {
                            this.mergeRecipientTokens(key, tokens);
                        }
                        this.closeRecipientPicker();
                        nextTick(() => {
                            try {
                                const el = document.getElementById(key === 'to' ? 'compose-to-input' : (key === 'cc' ? 'compose-cc-input' : 'compose-bcc-input'));
                                if (el) el.focus();
                            } catch (e) {
                            }
                        });
                    },
                    recipientsList(target) {
                        const key = (target === 'cc' || target === 'bcc') ? target : 'to';
                        const cur = (this.composeForm?.[key] || '').toString().trim();
                        if (!cur) return [];
                        return cur.split(/[,;]+/).map(x => x.trim()).filter(Boolean);
                    },
                    isInvalidRecipientToken(token) {
                        const t = (token || '').toString().trim();
                        if (!t) return false;
                        const arr = Array.isArray(this.composeInvalidRecipientTokens) ? this.composeInvalidRecipientTokens : [];
                        return arr.includes(t);
                    },
                    onRecipientKeydown(e, target) {
                        const k = (e && e.key) ? e.key.toString() : '';
                        if (k === ',' || k === ';') {
                            try {
                                e.preventDefault();
                            } catch (err) {
                            }
                            this.commitRecipient(target);
                        }
                    },
                    removeRecipient(target, idx) {
                        const key = (target === 'cc' || target === 'bcc') ? target : 'to';
                        const list = this.recipientsList(key);
                        if (idx < 0 || idx >= list.length) return;
                        list.splice(idx, 1);
                        this.composeForm[key] = list.join(', ');
                        this.composeInvalidRecipientTokens = [];
                    },
                    removeInvalidRecipients() {
                        const invalid = Array.isArray(this.composeInvalidRecipientTokens) ? this.composeInvalidRecipientTokens : [];
                        if (!invalid.length) return;
                        const fields = ['to', 'cc', 'bcc'];
                        for (const f of fields) {
                            const toks = this.recipientsList(f);
                            if (!toks.length) continue;
                            const kept = toks.filter(t => !invalid.includes((t || '').toString().trim()));
                            this.composeForm[f] = kept.join(', ');
                        }
                        this.composeInvalidRecipientTokens = [];
                    },
                    isValidEmail(email) {
                        const s = (email || '').toString().trim();
                        if (!s) return false;
                        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
                    },
                    commitRecipient(target) {
                        const key = (target === 'cc' || target === 'bcc') ? target : 'to';
                        const inputKey = key === 'to' ? 'composeToInput' : (key === 'cc' ? 'composeCcInput' : 'composeBccInput');
                        const raw = (this[inputKey] || '').toString().trim();
                        if (!raw) return;
                        this.composeInvalidRecipientTokens = [];
                        const tokens = raw.split(/[,;]+/).map(x => x.trim()).filter(Boolean);
                        if (tokens.length === 0) return;
                        const valid = [];
                        const invalid = [];
                        for (const t of tokens) {
                            const email = this.extractEmail(t);
                            if (email && this.isValidEmail(email)) {
                                valid.push(t);
                            } else {
                                invalid.push(t);
                            }
                        }
                        if (valid.length > 0) this.mergeRecipientTokens(key, valid);
                        this[inputKey] = invalid.join(', ');
                    },
                    mergeRecipientTokens(key, tokens) {
                        const curTokens = this.recipientsList(key);
                        const curEmails = curTokens.map(x => this.extractEmail(x).toLowerCase()).filter(Boolean);
                        const add = [];
                        for (const t of (tokens || [])) {
                            const email = this.extractEmail(t);
                            if (!email) continue;
                            if (!this.isValidEmail(email)) continue;
                            const emailLc = email.toLowerCase();
                            if (!curEmails.includes(emailLc)) {
                                add.push(t);
                                curEmails.push(emailLc);
                            }
                        }
                        if (add.length === 0) return;
                        const next = curTokens.concat(add).join(', ');
                        this.composeForm[key] = next;
                        this.composeInvalidRecipientTokens = [];
                    },
                    findInvalidRecipients() {
                        const invalid = [];
                        const fields = ['to', 'cc', 'bcc'];
                        for (const f of fields) {
                            const toks = this.recipientsList(f);
                            for (const t of toks) {
                                const email = this.extractEmail(t);
                                if (!email || !this.isValidEmail(email)) invalid.push(`${f.toUpperCase()}: ${t}`);
                            }
                        }
                        return invalid;
                    },
                    findDuplicateRecipientEmails() {
                        const seen = {};
                        const dups = [];
                        const fields = ['to', 'cc', 'bcc'];
                        for (const f of fields) {
                            const toks = this.recipientsList(f);
                            for (const t of toks) {
                                const email = this.extractEmail(t);
                                if (!email || !this.isValidEmail(email)) continue;
                                const lc = email.toLowerCase();
                                const prev = seen[lc];
                                if (prev && prev !== f) {
                                    if (!dups.includes(lc)) dups.push(lc);
                                } else if (!prev) {
                                    seen[lc] = f;
                                }
                            }
                        }
                        return dups;
                    },
                    replyEmail() {
                        if (!this.currentMessage) return;
                        this.closeRecipientPicker();
                        this.composeForm.to = this.extractEmail(this.currentMessage.from);
                        this.composeForm.cc = '';
                        this.composeForm.bcc = '';
                        this.showCcField = false;
                        this.showBccField = false;
                        this.composeForm.subject = this.currentMessage.subject.startsWith('Re:') ? this.currentMessage.subject : 'Re: ' + this.currentMessage.subject;
                        this.composeForm.html_body = '';
                        this.composeForm.body = `\n\n\n---------------- Original Message ----------------\nFrom: ${this.currentMessage.from}\nDate: ${this.currentMessage.date}\nSubject: ${this.currentMessage.subject}\n\n${this.stripHtml(this.currentMessage.body)}`;
                        this.composeEditorMode = 'rich';

                        // Set threading headers
                        this.composeForm.in_reply_to = this.currentMessage.message_id;
                        const oldRefs = this.currentMessage.references || '';
                        this.composeForm.references = oldRefs ? (oldRefs + ' ' + this.currentMessage.message_id) : this.currentMessage.message_id;

                        this.composeAttachmentSources = [];
                        this.composeAttachmentPreviews = [];
                        this.composeResourceSearch = '';
                        this.composeResourceAttachments = [];
                        this.composeResourceErrors = {};
                        this.composeResourceErrorMeta = {};
                        this.composeResourceErrorExpanded = {};
                        this.composeResourceValidating = {};
                        this.composeResourceValidateQueue = [];
                        if (this.composeResourceValidateTimer) clearTimeout(this.composeResourceValidateTimer);
                        this.composeResourceValidateTimer = null;
                        this.composeResourceBanner = '';
                        this.composeRecommendLoading = false;
                        this.composeRecommendedResources = [];
                        this.composeRecommendError = '';
                        this.composeKbQueryInput = '';
                        this.composeKbLoading = false;
                        this.composeKbResult = '';
                        this.composeKbSources = [];
                        this.composeKbError = '';
                        this.showComposeModal = true;
                        this.ensureComposeResourcePoolLoaded();
                    },
                    async forwardEmail() {
                        if (!this.currentMessage) return;
                        this.closeRecipientPicker();
                        if (!await this.ensureMailSession('轉寄郵件')) return;

                        // Fetch original attachments
                        const attachments = [];
                        if (this.currentMessage.attachments && this.currentMessage.attachments.length > 0) {
                            const originalLoading = this.loadingContent;
                            this.loadingContent = true;
                            try {
                                for (const att of this.currentMessage.attachments) {
                                    const response = await axios.get(`${ADMIN_API}/mail/attachment/${this.currentMessage.id}/${encodeURIComponent(att.filename)}?folder=${this.currentFolder}`, {
                                        headers: { 'X-Mail-Session': this.mailSessionToken },
                                        responseType: 'blob'
                                    });
                                    const file = new File([response.data], att.filename, { type: att.content_type });
                                    attachments.push(file);
                                }
                            } catch (e) {
                                window.$toast?.error('載入 attachments for forwarding 失敗', e?.response?.data?.detail || e?.message || '');
                                console.error("Failed to fetch attachments for forwarding", e);
                                _showToast("無法讀取原始附件，轉寄郵件將不包含附件。");
                            } finally {
                                this.loadingContent = originalLoading;
                            }
                        }

                        this.composeForm = {
                            to: '',
                            cc: '',
                            bcc: '',
                            subject: this.currentMessage.subject.startsWith('Fwd:') ? this.currentMessage.subject : 'Fwd: ' + this.currentMessage.subject,
                            body: `\n\n\n---------------- Forwarded Message ----------------\nFrom: ${this.currentMessage.from}\nDate: ${this.currentMessage.date}\nSubject: ${this.currentMessage.subject}\n\n${this.stripHtml(this.currentMessage.body)}`,
                            html_body: '',
                            attachments: attachments,
                            in_reply_to: null,
                            references: null
                        };
                        this.showCcField = false;
                        this.showBccField = false;
                        this.composeEditorMode = 'rich';
                        this.composeAttachmentSources = attachments.map(() => ({ type: 'upload' }));
                        this.composeAttachmentPreviews = attachments.map(f =>
                            (f instanceof File || f instanceof Blob) && f.type?.startsWith('image/')
                                ? URL.createObjectURL(f)
                                : null
                        );
                        this.composeResourceSearch = '';
                        this.composeResourceAttachments = [];
                        this.composeResourceErrors = {};
                        this.composeResourceErrorMeta = {};
                        this.composeResourceErrorExpanded = {};
                        this.composeResourceValidating = {};
                        this.composeResourceValidateQueue = [];
                        if (this.composeResourceValidateTimer) clearTimeout(this.composeResourceValidateTimer);
                        this.composeResourceValidateTimer = null;
                        this.composeResourceBanner = '';
                        this.showComposeModal = true;
                        this.ensureComposeResourcePoolLoaded();
                    },
                    extractEmail(text) {
                        const raw = (text ?? '').toString().trim();
                        if (!raw) return '';
                        const match = raw.match(/<([^>]+)>/);
                        if (match && match[1]) return match[1].trim();
                        const emailMatch = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
                        if (emailMatch && emailMatch[0]) return emailMatch[0].trim();
                        return raw;
                    },
                    extractDomain(text) {
                        const raw = (text ?? '').toString().trim();
                        if (!raw) return '';
                        const emailOrDomain = this.extractEmail(raw);
                        const v = (emailOrDomain || '').toString().trim();
                        if (!v) return '';
                        if (v.startsWith('@')) return v.slice(1).trim();
                        if (v.includes('@')) return v.split('@').pop().trim();
                        return v;
                    },
                    senderMapDomainValueFromText(text) {
                        const dom = (this.extractDomain(text) || '').toString().trim().toLowerCase();
                        if (!dom || !dom.includes('.') || dom.includes(' ')) return '';
                        return '@' + dom;
                    },
                    stripHtml(html) {
                        let tmp = document.createElement("DIV");
                        tmp.innerHTML = html;
                        return tmp.textContent || tmp.innerText || "";
                    },
                    sanitizeHtml(html) {
                        try {
                            const raw = (html ?? '').toString();
                            const cache = this.__sanitizeCache || (this.__sanitizeCache = { input: null, output: null });
                            if (cache.input === raw && typeof cache.output === 'string') return cache.output;
                            const hasTags = /<[a-z][\s\S]*>/i.test(raw);
                            const escapeOnly = (text) => {
                                const div = document.createElement('div');
                                div.textContent = (text ?? '').toString();
                                return div.innerHTML;
                            };
                            if (raw.length > 200000) {
                                const head = raw.slice(0, 200000);
                                const out = `<pre style="white-space:pre-wrap;margin:0;">${escapeOnly(head)}\n...(${raw.length - 200000} chars omitted)</pre>`;
                                cache.input = raw;
                                cache.output = out;
                                return out;
                            }
                            if (!hasTags) {
                                const escaped = escapeOnly(raw);
                                const out = `<pre style="white-space:pre-wrap;margin:0;">${escaped}</pre>`;
                                cache.input = raw;
                                cache.output = out;
                                return out;
                            }

                            const doc = new DOMParser().parseFromString(raw, 'text/html');
                            const blocked = ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'base', 'form', 'input', 'button'];
                            blocked.forEach(tag => doc.querySelectorAll(tag).forEach(el => el.remove()));
                            doc.querySelectorAll('*').forEach(el => {
                                Array.from(el.attributes || []).forEach(attr => {
                                    const name = (attr.name || '').toLowerCase();
                                    const val = (attr.value || '').trim().toLowerCase();
                                    if (name.startsWith('on')) el.removeAttribute(attr.name);
                                    if ((name === 'href' || name === 'src') && (val.startsWith('javascript:') || val.startsWith('vbscript:'))) {
                                        el.removeAttribute(attr.name);
                                    }
                                });
                            });
                            const out = doc.body ? doc.body.innerHTML : '';
                            cache.input = raw;
                            cache.output = out;
                            return out;
                        } catch (e) {
                            const div = document.createElement('div');
                            div.textContent = (html ?? '').toString();
                            return `<pre style="white-space:pre-wrap;margin:0;">${div.innerHTML}</pre>`;
                        }
                    },
                    rspamdBadgeForMessage(msg) {
                        const folder = this.currentFolder || 'INBOX';
                        const id = msg && msg.id != null ? msg.id : null;
                        if (!id) return null;
                        const key = `${folder}:${id}`;
                        const cached = (this.rspamdScanCache && typeof this.rspamdScanCache === 'object') ? this.rspamdScanCache[key] : null;
                        const root = cached?.result?.response;
                        if (!root || typeof root !== 'object') return null;

                        const action = (root.action || root.default_action || '').toString() || '';
                        const score = (root.score ?? root.metric_score ?? root.total_score);
                        const required = (root.required_score ?? root.metric_action_score ?? root.action_score);

                        const scoreNum = (typeof score === 'number') ? score : Number(score);
                        const requiredNum = (typeof required === 'number') ? required : Number(required);
                        const scoreDisplay = (Number.isFinite(scoreNum) && Number.isFinite(requiredNum))
                            ? `${scoreNum.toFixed(2)} / ${requiredNum.toFixed(2)}`
                            : (Number.isFinite(scoreNum) ? scoreNum.toFixed(2) : '-');

                        const a = action.toLowerCase();
                        let label = 'RSP';
                        if (a.includes('reject')) label = 'REJ';
                        else if (a.includes('add')) label = 'HDR';
                        else if (a.includes('grey')) label = 'GRY';
                        else if (a.includes('no') || a.includes('accept') || a.includes('pass')) label = 'OK';

                        const reject = Number(this.rspamdActions?.reject);
                        const addHeader = Number(this.rspamdActions?.add_header);

                        let cls = 'bg-slate-50 text-slate-800 border-slate-200';
                        if (Number.isFinite(scoreNum) && Number.isFinite(reject) && scoreNum >= reject) {
                            cls = 'bg-red-50 text-red-800 border-red-200';
                        } else if (Number.isFinite(scoreNum) && Number.isFinite(addHeader) && scoreNum >= addHeader) {
                            cls = 'bg-amber-50 text-amber-800 border-amber-200';
                        } else if (Number.isFinite(scoreNum)) {
                            cls = 'bg-emerald-50 text-emerald-800 border-emerald-200';
                        }

                        return { text: `${label} ${scoreDisplay}`, cls };
                    },
                    async deleteEmail() {
                        if (!confirm('確定要刪除這封郵件嗎？')) return;
                        try {
                            if (!await this.ensureMailSession('刪除郵件')) return;
                            await axios.post(`${ADMIN_API}/mail/delete`, {
                                msg_id: this.currentMessage.id,
                                folder: this.currentFolder
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            _showToast('郵件已刪除');
                            this.currentMessage = null;
                            this.fetchMessages(this.currentFolder);
                        } catch (error) {
                            _showToast('刪除失敗: ' + (error.response?.data?.detail || error.message));
                        }
                    },
                    async moveEmail(targetFolder, opts) {
                        try {
                            if (!await this.ensureMailSession('移動郵件')) return;
                            await axios.post(`${ADMIN_API}/mail/move`, {
                                msg_id: this.currentMessage.id,
                                source_folder: this.currentFolder,
                                target_folder: targetFolder
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const silent = !!(opts && typeof opts === 'object' && opts.silent);
                            if (!silent) _showToast('郵件已移動');
                            this.currentMessage = null;
                            this.fetchMessages(this.currentFolder);
                            return true;
                        } catch (error) {
                            _showToast('移動失敗: ' + (error.response?.data?.detail || error.message));
                            return false;
                        }
                    },
                    async attachKbFileToCompose(docId, sourceName) {
                        if (!docId) return;
                        try {
                            // Check not already attached
                            const already = (this.composeForm.attachments || []).some(
                                f => f._kbDocId === docId
                            );
                            if (already) {
                                _showToast(`「${sourceName}」已在附件中`, 'info');
                                return;
                            }
                            _showToast('載入檔案中...', 'info');
                            const token = localStorage.getItem('token');
                            const res = await fetch(
                                `${ADMIN_API}/rag/knowledge/${encodeURIComponent(docId)}/file`,
                                { headers: { 'Authorization': `Bearer ${token}` } }
                            );
                            if (!res.ok) {
                                const err = await res.json().catch(() => ({}));
                                _showToast('載入失敗: ' + (err.detail || res.statusText), 'error');
                                return;
                            }
                            const blob = await res.blob();
                            const fileName = sourceName || `document_${docId.slice(0, 8)}`;
                            const file = new File([blob], fileName, { type: blob.type });
                            // Tag so we can deduplicate
                            Object.defineProperty(file, '_kbDocId', { value: docId, writable: false });
                            if (!this.composeForm.attachments) this.composeForm.attachments = [];
                            if (!this.composeAttachmentPreviews) this.composeAttachmentPreviews = [];
                            if (!this.composeAttachmentSources) this.composeAttachmentSources = [];
                            this.composeForm.attachments.push(file);
                            this.composeAttachmentPreviews.push(null);
                            this.composeAttachmentSources.push({ type: 'kb', docId });
                            _showToast(`📎 「${fileName}」已加為附件`, 'success');
                        } catch (e) {
                            _showToast('加為附件失敗: ' + e.message, 'error');
                        }
                    },

                    handleAttachmentUpload(event) {
                        const files = event.dataTransfer?.files || event.target?.files;
                        if (!files || !files.length) return;

                        if (!this.composeForm.attachments) this.composeForm.attachments = [];
                        if (!this.composeAttachmentSources) this.composeAttachmentSources = [];
                        if (!this.composeAttachmentPreviews) this.composeAttachmentPreviews = [];

                        for (let i = 0; i < files.length; i++) {
                            this.composeForm.attachments.push(files[i]);
                            this.composeAttachmentSources.push({ type: 'upload' });
                            const preview = files[i].type.startsWith('image/') ? URL.createObjectURL(files[i]) : null;
                            this.composeAttachmentPreviews.push(preview);
                        }
                        if (event.target) event.target.value = '';
                    },

                    removeAttachment(index) {
                        if (this.composeAttachmentPreviews?.[index]) {
                            URL.revokeObjectURL(this.composeAttachmentPreviews[index]);
                        }
                        if (Array.isArray(this.composeAttachmentPreviews) && this.composeAttachmentPreviews.length > index) {
                            this.composeAttachmentPreviews.splice(index, 1);
                        }
                        this.composeForm.attachments.splice(index, 1);
                        if (this.composeAttachmentSources && this.composeAttachmentSources.length > index) {
                            this.composeAttachmentSources.splice(index, 1);
                        }
                    },

                    async downloadAttachment(filename, _retry = false) {
                        try {
                            if (!await this.ensureMailSession('下載附件')) return;
                            const response = await axios.get(`${ADMIN_API}/mail/attachment/${this.currentMessage.id}/${encodeURIComponent(filename)}?folder=${this.currentFolder}`, {
                                headers: { 'X-Mail-Session': this.mailSessionToken },
                                responseType: 'blob'
                            });

                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                const canRetry = this.handleMailAuth401(error, '下載附件');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('下載附件');
                                        if (ok) return await this.downloadAttachment(filename, true);
                                    } catch (e) {
                                    }
                                }
                                return;
                            }
                            _showToast('下載失敗: ' + (error.response?.data?.detail || error.message));
                        }
                    },

                    invoiceAttachmentKey(att) {
                        const id = Number(this.currentMessage?.id || 0);
                        const folder = (this.currentFolder || 'INBOX').toString();
                        const name = (att?.filename || '').toString();
                        return `${folder}::${id}::${name}`;
                    },
                    isInvoiceAttachment(att) {
                        const name = (att?.filename || '').toString().toLowerCase().trim();
                        const ctype = (att?.content_type || '').toString().toLowerCase().trim();
                        if (name.endsWith('.pdf')) return true;
                        if (name.endsWith('.png')) return true;
                        if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return true;
                        if (name.endsWith('.webp')) return true;
                        if (ctype === 'application/pdf') return true;
                        if (ctype === 'image/jpeg' || ctype === 'image/jpg') return true;
                        if (ctype === 'image/png') return true;
                        if (ctype === 'image/webp') return true;
                        return false;
                    },
                    invoiceCandidates() {
                        const atts = Array.isArray(this.currentMessage?.attachments) ? this.currentMessage.attachments : [];
                        return atts.filter(a => this.isInvoiceAttachment(a));
                    },
                    invoiceImportedStorageKey() {
                        try {
                            const id = Number(this.currentUser?.id || 0) || 0;
                            if (id > 0) return `invoiceImportedAttachmentKeys:uid:${id}`;
                            const u = (this.currentUser?.username || this.currentUser?.email || '').toString().trim();
                            if (u) return `invoiceImportedAttachmentKeys:user:${u}`;
                        } catch (e) {}
                        return 'invoiceImportedAttachmentKeys';
                    },
                    loadInvoiceImportedAttachmentKeys(strictNamespaced = false) {
                        try {
                            const keysToTry = [];
                            const hasUser = !!(this.currentUser && (Number(this.currentUser?.id || 0) > 0 || this.currentUser?.username || this.currentUser?.email));
                            if (hasUser) keysToTry.push(this.invoiceImportedStorageKey());
                            if (!strictNamespaced) keysToTry.push('invoiceImportedAttachmentKeys');
                            let loaded = null;
                            for (const k of keysToTry) {
                                try {
                                    const raw = (localStorage.getItem(k) || '').trim();
                                    if (!raw) continue;
                                    const parsed = JSON.parse(raw);
                                    if (parsed && typeof parsed === 'object') {
                                        loaded = parsed;
                                        break;
                                    }
                                } catch (e0) {
                                }
                            }
                            this.invoiceImportedAttachmentKeys = loaded && typeof loaded === 'object' ? loaded : {};
                            try {
                                if (hasUser && loaded && keysToTry[0] && keysToTry[0] !== 'invoiceImportedAttachmentKeys') {
                                    localStorage.setItem(keysToTry[0], JSON.stringify(this.invoiceImportedAttachmentKeys));
                                }
                            } catch (e1) {}
                        } catch (e) {
                            this.invoiceImportedAttachmentKeys = {};
                        }
                    },
                    invoiceImportedInfo(att) {
                        try {
                            const key = typeof att === 'string' ? att : this.invoiceAttachmentKey(att);
                            const map = this.invoiceImportedAttachmentKeys && typeof this.invoiceImportedAttachmentKeys === 'object' ? this.invoiceImportedAttachmentKeys : {};
                            const info = map[key];
                            if (!info || typeof info !== 'object') return null;
                            return info;
                        } catch (e) {
                            return null;
                        }
                    },
                    invoiceRememberImported(entry) {
                        try {
                            const folder = (entry?.folder || this.currentFolder || 'INBOX').toString();
                            const msgId = Number(entry?.msg_id || entry?.id || this.currentMessage?.id || 0) || 0;
                            const filename = (entry?.filename || '').toString();
                            if (!folder || !msgId || !filename) return;
                            const key = `${folder}::${msgId}::${filename}`;
                            const invId = Number(entry?.invoice_id || 0) || 0;
                            const dup = !!entry?.duplicate;
                            const map = this.invoiceImportedAttachmentKeys && typeof this.invoiceImportedAttachmentKeys === 'object' ? this.invoiceImportedAttachmentKeys : {};
                            map[key] = { at: Date.now(), invoice_id: invId || null, duplicate: dup };
                            const entries = Object.entries(map).map(([k, v]) => ({ k, v }));
                            entries.sort((a, b) => (Number(b?.v?.at || 0) - Number(a?.v?.at || 0)));
                            const keep = entries.slice(0, 500);
                            const next = {};
                            for (const it of keep) next[it.k] = it.v;
                            this.invoiceImportedAttachmentKeys = next;
                            try {
                                localStorage.setItem(this.invoiceImportedStorageKey(), JSON.stringify(next));
                            } catch (e) {}
                        } catch (e) {}
                    },
                    async openInvoiceFromImportedAttachment(att) {
                        try {
                            const info = this.invoiceImportedInfo(att);
                            const id = Number(info?.invoice_id || 0) || 0;
                            if (!id) {
                                _showToast('此附件已辨識，但未記錄發票編號');
                                return;
                            }
                            await this.openInvoiceDriveView();
                            await this.invoiceDriveOpenDetail(id);
                        } catch (e) {
                            _showToast('開啟失敗');
                        }
                    },
                    async importInvoiceFromAttachment(att, _retry = false, ev = null, markReadOverride = null) {
                        let markRead = false;
                        try {
                            if (!this.currentMessage?.id) return;
                            if (!await this.ensureMailSession('辨識發票')) return;
                            markRead = (markReadOverride === null || markReadOverride === undefined) ? !!(ev && ev.shiftKey) : !!markReadOverride;
                            if (markRead) {
                                const ok = confirm('辨識完成後將此信標記為已讀，確定要繼續嗎？');
                                if (!ok) return;
                            }
                            const key = this.invoiceAttachmentKey(att);
                            this.invoiceImportingAttachmentKey = key;
                            const res = await axios.post(`${ADMIN_API}/mail/attachment/invoice_import`, {
                                msg_id: this.currentMessage.id,
                                filename: att?.filename,
                                folder: this.currentFolder,
                                mark_read: markRead,
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const invoiceId = Number(res?.data?.invoice_id || 0);
                            try {
                                this.invoiceRememberImported({
                                    folder: (this.currentFolder || 'INBOX').toString(),
                                    msg_id: Number(this.currentMessage.id || 0),
                                    filename: (att?.filename || '').toString(),
                                    invoice_id: invoiceId || null,
                                    duplicate: !!res?.data?.duplicate
                                });
                            } catch (e) {}
                            const markedReadIds = Array.isArray(res?.data?.marked_read_msg_ids) ? res.data.marked_read_msg_ids : [];
                            if (markRead && markedReadIds.length) {
                                const set = new Set(markedReadIds.map(x => Number(x) || 0).filter(x => x > 0));
                                for (const m of this.safeArr(this.messages)) {
                                    const mid = Number(m?.id) || 0;
                                    if (mid && set.has(mid)) m.is_read = true;
                                }
                                try {
                                    const curId = Number(this.currentMessage?.id) || 0;
                                    if (curId && set.has(curId) && this.currentMessage) this.currentMessage.is_read = true;
                                } catch (e) {}
                            }
                            if (invoiceId) {
                                await this.openInvoiceDriveView();
                                await this.invoiceDriveOpenDetail(invoiceId);
                            } else {
                                _showToast('已送出辨識，但未取得發票編號');
                            }
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                const canRetry = this.handleMailAuth401(error, '辨識發票');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('辨識發票');
                                        if (ok) return await this.importInvoiceFromAttachment(att, true, ev, markRead);
                                    } catch (e) {
                                    }
                                }
                                return;
                            }
                            _showToast('辨識失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            const key = this.invoiceAttachmentKey(att);
                            if (this.invoiceImportingAttachmentKey === key) {
                                this.invoiceImportingAttachmentKey = '';
                            }
                        }
                    },
                    async importAllInvoiceAttachments(_retry = false, ev = null, markReadOverride = null) {
                        let markRead = false;
                        try {
                            if (!this.currentMessage?.id) return;
                            if (!await this.ensureMailSession('辨識發票')) return;
                            const atts = this.invoiceCandidates();
                            if (!atts.length) return;
                            markRead = (markReadOverride === null || markReadOverride === undefined) ? !!(ev && ev.shiftKey) : !!markReadOverride;
                            const ok = confirm(`將辨識 ${atts.length} 個可能的發票附件${markRead ? '（辨識後標記原信已讀）' : ''}，確定要繼續嗎？`);
                            if (!ok) return;
                            this.invoiceImportingAllAttachments = true;
                            this.invoiceImportingAttachmentKey = '__batch__';
                            const res = await axios.post(`${ADMIN_API}/mail/message/invoice_import_all`, {
                                msg_id: this.currentMessage.id,
                                folder: this.currentFolder,
                                max_imports: atts.length,
                                mark_read: markRead
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const imported = Array.isArray(res?.data?.imported) ? res.data.imported : [];
                            try {
                                for (const it of imported) {
                                    this.invoiceRememberImported(it);
                                }
                            } catch (e) {}
                            const importedCount = Number(res?.data?.imported_count || imported.length || 0);
                            const dup = Number(res?.data?.duplicates || 0);
                            const skippedLarge = Number(res?.data?.skipped_too_large || 0);
                            const skippedUnsupported = Number(res?.data?.skipped_unsupported || 0);
                            const errors = Array.isArray(res?.data?.errors) ? res.data.errors : [];
                            const checkedAttachments = Number(res?.data?.checked_attachments || 0);
                            const scannedMessages = Number(res?.data?.scanned_messages || 0);
                            const lastInvoiceId = Number(res?.data?.last_invoice_id || imported.slice(-1)[0]?.invoice_id || 0);
                            const markedReadIds = Array.isArray(res?.data?.marked_read_msg_ids) ? res.data.marked_read_msg_ids : [];
                            if (markRead && markedReadIds.length) {
                                const set = new Set(markedReadIds.map(x => Number(x) || 0).filter(x => x > 0));
                                for (const m of this.safeArr(this.messages)) {
                                    const mid = Number(m?.id) || 0;
                                    if (mid && set.has(mid)) m.is_read = true;
                                }
                                try {
                                    const curId = Number(this.currentMessage?.id) || 0;
                                    if (curId && set.has(curId) && this.currentMessage) this.currentMessage.is_read = true;
                                } catch (e) {}
                            }
                            if (lastInvoiceId) {
                                await this.openInvoiceDriveView();
                                await this.invoiceDriveOpenDetail(lastInvoiceId);
                            }
                            const parts = [];
                            const newCount = Math.max(0, importedCount - dup);
                            parts.push(`新增 ${newCount} 筆`);
                            if (dup) parts.push(`重複 ${dup} 筆`);
                            if (scannedMessages) parts.push(`掃描 ${scannedMessages} 封`);
                            if (checkedAttachments) parts.push(`檢查 ${checkedAttachments} 個附件`);
                            if (skippedUnsupported) parts.push(`不支援 ${skippedUnsupported} 筆`);
                            if (skippedLarge) parts.push(`過大略過 ${skippedLarge} 筆`);
                            if (markedReadIds.length) parts.push(`已讀 ${markedReadIds.length} 封`);
                            if (errors.length) parts.push(`錯誤 ${errors.length} 筆`);
                            _showToast(parts.join(' / '));
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                const canRetry = this.handleMailAuth401(error, '辨識發票');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('辨識發票');
                                        if (ok) return await this.importAllInvoiceAttachments(true, ev, markRead);
                                    } catch (e) {
                                    }
                                }
                                return;
                            }
                            _showToast('辨識失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            if (this.invoiceImportingAttachmentKey === '__batch__') {
                                this.invoiceImportingAttachmentKey = '';
                            }
                            this.invoiceImportingAllAttachments = false;
                        }
                    },

                    async scanImportInvoicesInCurrentFolder(_retry = false, ev = null, markReadOverride = null) {
                        let markRead = false;
                        try {
                            if (!await this.ensureMailSession('掃描發票')) return;
                            const folder = (this.currentFolder || 'INBOX').toString();
                            markRead = (markReadOverride === null || markReadOverride === undefined) ? !!(ev && ev.shiftKey) : !!markReadOverride;
                            const ok = confirm(`將掃描「${folder}」最近 50 封郵件，並自動匯入 PDF/圖片發票附件${markRead ? '（匯入後標記已讀）' : ''}（重複檔案會自動略過），確定要繼續嗎？`);
                            if (!ok) return;
                            this.webmailInvoiceScanLoading = true;
                            const res = await axios.post(`${ADMIN_API}/mail/invoices/scan_import`, {
                                folder,
                                limit: 50,
                                unseen_only: false,
                                max_imports: 30,
                                mark_read: markRead
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const imported = Array.isArray(res?.data?.imported) ? res.data.imported : [];
                            try {
                                for (const it of imported) {
                                    this.invoiceRememberImported(it);
                                }
                            } catch (e) {}
                            const importedCount = Number(res?.data?.imported_count || imported.length || 0);
                            const dup = Number(res?.data?.duplicates || 0);
                            const skippedUnsupported = Number(res?.data?.skipped_unsupported || 0);
                            const skippedLarge = Number(res?.data?.skipped_too_large || 0);
                            const errors = Array.isArray(res?.data?.errors) ? res.data.errors : [];
                            const checkedAttachments = Number(res?.data?.checked_attachments || 0);
                            const scannedMessages = Number(res?.data?.scanned_messages || 0);
                            const lastInvoiceId = Number(res?.data?.last_invoice_id || imported.slice(-1)[0]?.invoice_id || 0);
                            const markedReadIds = Array.isArray(res?.data?.marked_read_msg_ids) ? res.data.marked_read_msg_ids : [];
                            if (markRead && markedReadIds.length) {
                                const set = new Set(markedReadIds.map(x => Number(x) || 0).filter(x => x > 0));
                                for (const m of this.safeArr(this.messages)) {
                                    const mid = Number(m?.id) || 0;
                                    if (mid && set.has(mid)) m.is_read = true;
                                }
                                try {
                                    const curId = Number(this.currentMessage?.id) || 0;
                                    if (curId && set.has(curId) && this.currentMessage) this.currentMessage.is_read = true;
                                } catch (e) {}
                            }
                            if (lastInvoiceId) {
                                await this.openInvoiceDriveView();
                                await this.invoiceDriveOpenDetail(lastInvoiceId);
                            }
                            const parts = [];
                            const newCount = Math.max(0, importedCount - dup);
                            parts.push(`新增 ${newCount} 筆`);
                            if (dup) parts.push(`重複 ${dup} 筆`);
                            if (scannedMessages) parts.push(`掃描 ${scannedMessages} 封`);
                            if (checkedAttachments) parts.push(`檢查 ${checkedAttachments} 個附件`);
                            if (skippedUnsupported) parts.push(`不支援 ${skippedUnsupported} 筆`);
                            if (skippedLarge) parts.push(`過大略過 ${skippedLarge} 筆`);
                            if (errors.length) parts.push(`錯誤 ${errors.length} 筆`);
                            _showToast(parts.join(' / '));
                        } catch (error) {
                            if (error.response && error.response.status === 401) {
                                const canRetry = this.handleMailAuth401(error, '掃描發票');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('掃描發票');
                                        if (ok) return await this.scanImportInvoicesInCurrentFolder(true, ev, markRead);
                                    } catch (e) {
                                    }
                                }
                                return;
                            }
                            _showToast('掃描失敗: ' + (error.response?.data?.detail || error.message));
                        } finally {
                            this.webmailInvoiceScanLoading = false;
                        }
                    },
                    enableAutoInvoiceScan() {
                        try {
                            if (this.webmailInvoiceScanTimer) {
                                clearInterval(this.webmailInvoiceScanTimer);
                                this.webmailInvoiceScanTimer = null;
                            }
                        } catch (e) {}
                        try {
                            localStorage.setItem('webmailAutoScanInvoices', '1');
                        } catch (e) {}
                        this.webmailAutoScanInvoices = true;
                        this.webmailAutoScanLastImportedCount = 0;
                        this.webmailAutoScanLastAt = 0;
                        this.webmailAutoScanLastInvoiceId = 0;
                        try {
                            this.runAutoInvoiceScanOnce();
                        } catch (e) {}
                        this.webmailInvoiceScanTimer = setInterval(() => {
                            try {
                                this.runAutoInvoiceScanOnce();
                            } catch (e) {}
                        }, 5 * 60 * 1000);
                    },
                    disableAutoInvoiceScan() {
                        try {
                            if (this.webmailInvoiceScanTimer) {
                                clearInterval(this.webmailInvoiceScanTimer);
                                this.webmailInvoiceScanTimer = null;
                            }
                        } catch (e) {}
                        try {
                            localStorage.setItem('webmailAutoScanInvoices', '0');
                        } catch (e) {}
                        this.webmailAutoScanInvoices = false;
                        this.webmailAutoScanLastImportedCount = 0;
                        this.webmailAutoScanLastAt = 0;
                        this.webmailAutoScanLastInvoiceId = 0;
                    },
                    async runAutoInvoiceScanOnce(_retry = false) {
                        try {
                            if (!this.webmailAutoScanInvoices) return;
                            if (this.currentView !== 'webmail') return;
                            if (this.webmailTab !== 'mail') return;
                            try {
                                if (document && document.visibilityState && document.visibilityState !== 'visible') return;
                            } catch (e) {}
                            if (this.webmailInvoiceScanLoading) return;
                            if (this.webmailInvoiceAutoScanLoading) return;
                            this.webmailInvoiceAutoScanLoading = true;
                            if (!this.hasValidMailSession) {
                                if (!this.hasStoredMailPassword) {
                                    this.disableAutoInvoiceScan();
                                    return;
                                }
                                const ok = await this.ensureMailSession('自動掃描發票');
                                if (!ok) {
                                    this.disableAutoInvoiceScan();
                                    return;
                                }
                            }
                            try {
                                const res = await axios.post(`${ADMIN_API}/mail/invoices/scan_import`, {
                                    folder: (this.currentFolder || 'INBOX').toString(),
                                    limit: 30,
                                    unseen_only: true,
                                    max_imports: 10,
                                    mark_read: !!this.webmailAutoScanMarkRead
                                }, {
                                    headers: { 'X-Mail-Session': this.mailSessionToken }
                                });
                                try {
                                    const imported = Array.isArray(res?.data?.imported) ? res.data.imported : [];
                                    for (const it of imported) {
                                        this.invoiceRememberImported(it);
                                    }
                                } catch (e) {}
                                const importedCount = Number(res?.data?.imported_count || 0);
                                const dup = Number(res?.data?.duplicates || 0);
                                const newCount = Math.max(0, importedCount - dup);
                                const lastInvoiceId = Number(res?.data?.last_invoice_id || 0);
                                const markedReadIds = Array.isArray(res?.data?.marked_read_msg_ids) ? res.data.marked_read_msg_ids : [];
                                if (this.webmailAutoScanMarkRead && markedReadIds.length) {
                                    const set = new Set(markedReadIds.map(x => Number(x) || 0).filter(x => x > 0));
                                    for (const m of this.safeArr(this.messages)) {
                                        const mid = Number(m?.id) || 0;
                                        if (mid && set.has(mid)) m.is_read = true;
                                    }
                                    try {
                                        const curId = Number(this.currentMessage?.id) || 0;
                                        if (curId && set.has(curId) && this.currentMessage) this.currentMessage.is_read = true;
                                    } catch (e) {}
                                }
                                this.webmailAutoScanLastAt = Date.now();
                                this.webmailAutoScanLastImportedCount = newCount > 0 ? newCount : 0;
                                this.webmailAutoScanLastInvoiceId = lastInvoiceId > 0 ? lastInvoiceId : 0;
                            } catch (e) {
                                if (e?.response?.status === 401 && !_retry && this.hasStoredMailPassword) {
                                    try {
                                        this.resetMailSessionOnly();
                                        const ok = await this.ensureMailSession('自動掃描發票');
                                        if (ok) return await this.runAutoInvoiceScanOnce(true);
                                    } catch (ee) {}
                                }
                            }
                        } catch (e) {
                        } finally {
                            this.webmailInvoiceAutoScanLoading = false;
                        }
                    },
                    toggleAutoInvoiceScan(ev = null) {
                        try {
                            if (ev && (ev.ctrlKey || ev.metaKey) && this.webmailAutoScanLastInvoiceId) {
                                const id = Number(this.webmailAutoScanLastInvoiceId || 0);
                                if (id > 0) {
                                    this.openInvoiceDriveView().then(() => {
                                        this.invoiceDriveOpenDetail(id);
                                    });
                                    return;
                                }
                            }
                            if (ev && ev.shiftKey) {
                                this.webmailAutoScanMarkRead = !this.webmailAutoScanMarkRead;
                                try {
                                    localStorage.setItem('webmailAutoScanMarkRead', this.webmailAutoScanMarkRead ? '1' : '0');
                                } catch (e) {}
                                return;
                            }
                        } catch (e) {}
                        if (this.webmailAutoScanInvoices) {
                            this.disableAutoInvoiceScan();
                        } else {
                            this.enableAutoInvoiceScan();
                        }
                    },

                    toggleSecureSendPanel() {
                        if (!this.secureSendEnabled) {
                            this.secureSendEnabled = true;
                            this.showSecureSendPanel = true;
                        } else {
                            this.showSecureSendPanel = !this.showSecureSendPanel;
                            if (!this.showSecureSendPanel) {
                                this.secureSendEnabled = false;
                            }
                        }
                    },

                    async sendSecureMail() {
                        // 安全傳送：呼叫 /secure/send API
                        this.sendingEmail = true;
                        this.secureSendResult = null;
                        try {
                            const toList = Array.isArray(this.composeForm?.to)
                                ? this.composeForm.to
                                : (this.composeForm?.to || '').split(',').map(s => s.trim()).filter(Boolean);
                            if (!toList.length) {
                                alert('請填寫收件人');
                                return;
                            }
                            if (!this.secureSendOptions.mfa_password?.trim()) {
                                alert('安全傳送需要設定密碼，請在安全傳送設定中填寫查閱密碼，再透過電話或 LINE 告知收件人');
                                return;
                            }
                            // 逐一傳送給每位收件人
                            const results = [];
                            for (const to of toList) {
                                const payload = {
                                    to,
                                    subject: (this.composeForm?.subject || '').trim() || '（無主旨）',
                                    body: this.composeForm?.body || this.composeForm?.html_body || '',
                                    mfa_type: this.secureSendOptions.mfa_type,
                                    mfa_password: this.secureSendOptions.mfa_type === 'password'
                                        ? this.secureSendOptions.mfa_password : undefined,
                                    expires_in_hours: Number(this.secureSendOptions.expires_in_hours) || 24,
                                    max_open_count: Number(this.secureSendOptions.max_open_count) || 1,
                                    burn_after_read: !!this.secureSendOptions.burn_after_read,
                                    burn_delay_seconds: Number(this.secureSendOptions.burn_delay_seconds) || 30,
                                    no_download: !!this.secureSendOptions.no_download,
                                    no_forward: true,
                                    watermark: !!this.secureSendOptions.watermark,
                                };
                                const res = await axios.post(`${ADMIN_API}/secure/send`, payload, {
                                    headers: { 'X-Mail-Session': this.mailSessionToken || '' },
                                });
                                results.push({ to, ok: true, portal_url: res.data.portal_url, expires_at: res.data.expires_at });
                            }
                            this.secureSendResult = results;
                            // 先顯示 toast（在 modal 關閉前，避免被遮住）
                            _showToast('安全傳送成功！連結已存入左側「安全信件」頁面，可隨時調閱。', 'success');
                            // 關閉撰寫視窗、清草稿
                            this.clearComposeDraft && this.clearComposeDraft();
                            this.showComposeModal = false;
                        } catch (e) {
                            alert(`安全傳送失敗：${e?.message || e}`);
                        } finally {
                            this.sendingEmail = false;
                        }
                    },

                    async sendEmail(_retry = false) {
                        // 如果啟用安全傳送，改走安全門戶流程
                        if (this.secureSendEnabled) {
                            if (!await this.ensureMailSession('安全傳送')) return;
                            return await this.sendSecureMail();
                        }
                        // 強制從 DOM 同步 richEditor 最新內容（避免最後一次輸入未觸發 @input）
                        if (this.composeEditorMode === 'rich') {
                            const _richEl = typeof document !== 'undefined' && document.getElementById('richEditor');
                            if (_richEl) {
                                this.composeForm.html_body = _richEl.innerHTML;
                                this.composeForm.body = _richEl.innerText;
                            }
                        }
                        if (!await this.ensureMailSession('送出郵件')) return;
                        this.sendingEmail = true;
                        try {

                            if ((this.composeToInput || '').toString().trim()) this.commitRecipient('to');
                            if ((this.composeCcInput || '').toString().trim()) this.commitRecipient('cc');
                            if ((this.composeBccInput || '').toString().trim()) this.commitRecipient('bcc');
                            const pendingInvalid = [];
                            if ((this.composeToInput || '').toString().trim()) pendingInvalid.push(`TO: ${this.composeToInput}`);
                            if ((this.composeCcInput || '').toString().trim()) pendingInvalid.push(`CC: ${this.composeCcInput}`);
                            if ((this.composeBccInput || '').toString().trim()) pendingInvalid.push(`BCC: ${this.composeBccInput}`);
                            const invalid = this.findInvalidRecipients().concat(pendingInvalid);
                            if (invalid.length > 0) {
                                _showToast(`以下收件者格式不正確：\n\n${invalid.join('\n')}`);
                                return;
                            }
                            if (!(this.composeForm.to || '').toString().trim()) {
                                _showToast('請至少填寫一位收件者');
                                return;
                            }
                            const dupEmails = this.findDuplicateRecipientEmails();
                            if (dupEmails.length > 0) {
                                const ok = confirm(`偵測到重複收件者（可能同時存在於 To/Cc/Bcc）：\n\n${dupEmails.join('\n')}\n\n仍要寄出嗎？`);
                                if (!ok) return;
                            }
                            // D4: PII 敏感資訊偵測
                            {
                                const _piiBody = ((this.composeForm.html_body || '').replace(/<[^>]+>/g,' ') + ' ' + (this.composeForm.body || '')).trim();
                                const _piiSubject = (this.composeForm.subject || '').trim();
                                const _piiText = _piiSubject + ' ' + _piiBody;
                                const _piiPatterns = [
                                    { label: '台灣身分證字號', re: /\b[A-Z][12]\d{8}\b/g },
                                    { label: '信用卡號碼（16碼）', re: /\b\d{4}[- ]\d{4}[- ]\d{4}[- ]\d{4}\b/g },
                                    { label: '台灣手機號碼', re: /\b09\d{2}[-\s]?\d{3}[-\s]?\d{3}\b/g },
                                    { label: '台灣銀行帳號（14碼）', re: /\b\d{14}\b/g },
                                    { label: '護照號碼', re: /\b[A-Z]{2}\d{7}\b/g },
                                    { label: '明文密碼欄位', re: /密碼\s*[:：]\s*\S{4,}/g },
                                ];
                                const _piiFound = [];
                                for (const p of _piiPatterns) {
                                    if (p.re.test(_piiText)) _piiFound.push(p.label);
                                }
                                if (_piiFound.length > 0) {
                                    const ok = confirm(`⚠️ 敏感資訊警告\n\n郵件內容可能包含以下敏感資料：\n• ${_piiFound.join('\n• ')}\n\n請確認這些資訊的傳送是必要且安全的。\n建議使用「安全傳送」功能加密傳輸。\n\n仍要繼續寄出嗎？`);
                                    if (!ok) return;
                                }
                            }
                            for (let attempt = 0; attempt < 2; attempt++) {
                                try {
                                    // PGP encrypt hook (injected by WebmailView)
                                    let _subject = this.composeForm.subject;
                                    let _body = this.composeForm.body;
                                    let _html_body = this.composeForm.html_body;
                                    if (typeof window.__pgpBeforeSend === 'function') {
                                        try {
                                            const pgpResult = await window.__pgpBeforeSend(_subject, _body || _html_body || '');
                                            if (pgpResult.pgp_encrypted) {
                                                _subject = pgpResult.subject;
                                                _body = pgpResult.body;
                                                _html_body = '';
                                            }
                                        } catch (pgpErr) {
                                            throw pgpErr;
                                        }
                                    }
                                    const formData = new FormData();
                                    formData.append('to', this.composeForm.to);
                                    if (this.composeForm.cc && this.composeForm.cc.toString().trim()) {
                                        formData.append('cc', this.composeForm.cc.toString());
                                    }
                                    if (this.composeForm.bcc && this.composeForm.bcc.toString().trim()) {
                                        formData.append('bcc', this.composeForm.bcc.toString());
                                    }
                                    formData.append('subject', _subject);
                                    formData.append('body', _body);
                                    if (_html_body && _html_body.toString().trim()) {
                                        formData.append('html_body', _html_body.toString());
                                    }

                                    if (this.composeForm.in_reply_to) {
                                        formData.append('in_reply_to', this.composeForm.in_reply_to);
                                    }
                                    if (this.composeForm.references) {
                                        formData.append('references', this.composeForm.references);
                                    }

                                    if (this.composeForm.attachments) {
                                        this.composeForm.attachments.forEach(file => {
                                            formData.append('files', file);
                                        });
                                    }

                                    await axios.post(`${ADMIN_API}/mail/send`, formData, {
                                        headers: {
                                            'X-Mail-Session': this.mailSessionToken
                                        }
                                    });
                                    _showToast('郵件已發送！');
                                    this.clearComposeDraft();
                                    this.composeInvalidRecipientTokens = [];
                                    this.showComposeModal = false;
                                    this.composeForm = { to: '', cc: '', bcc: '', subject: '', body: '', html_body: '', attachments: [], in_reply_to: null, references: null };
                                    this.composeToInput = '';
                                    this.composeCcInput = '';
                                    this.composeBccInput = '';
                                    this.showCcField = false;
                                    this.showBccField = false;
                                    this.composeEditorMode = 'rich';
                                    this.composeAttachmentSources = [];
                                    this.composeResourceAttachments = [];
                                    this.composeResourceErrors = {};
                                    this.composeResourceErrorMeta = {};
                                    this.composeResourceErrorExpanded = {};
                                    this.composeResourceValidating = {};
                                    this.composeResourceValidateQueue = [];
                                    if (this.composeResourceValidateTimer) clearTimeout(this.composeResourceValidateTimer);
                                    this.composeResourceValidateTimer = null;
                                    this.composeResourceSearch = '';
                                    this.composeResourceBanner = '';
                                    this.composeAiReplySources = [];
                                    this.composePromptedRecommendKey = '';
                                    if (this.currentFolder === 'Sent') {
                                        this.fetchMessages('Sent');
                                    }
                                    return;
                                } catch (error) {
                                    window.$toast?.error('send email 失敗', error?.response?.data?.detail || error?.message || '');
                                    console.error('Failed to send email', error);
                                    if (error.response && error.response.status === 401) {
                                        this.handleMailAuth401(error, '送出郵件');
                                        return;
                                    }
                                    if (error.response && error.response.status === 422) {
                                        const d = error.response?.data?.detail;
                                        if (d && d.type === 'recipient_errors' && Array.isArray(d.invalid)) {
                                            const inv = d.invalid.map(x => (x || '').toString().trim()).filter(Boolean);
                                            this.composeInvalidRecipientTokens = inv;
                                            _showToast(`以下收件者無效，請修正後再試：\n\n${inv.join('\n')}`);
                                            return;
                                        }
                                    }
                                    const rawDetail = error.response?.data?.detail;
                                    let handledResourceErrors = false;
                                    if (rawDetail && typeof rawDetail === 'object') {
                                        const type = (rawDetail.type || '').toString();
                                        const errors = rawDetail.errors;
                                        if (type === 'resource_errors' && Array.isArray(errors)) {
                                            if (!this.composeResourceErrors) this.composeResourceErrors = {};
                                            if (!this.composeResourceErrorMeta) this.composeResourceErrorMeta = {};
                                            if (!this.composeResourceErrorExpanded) this.composeResourceErrorExpanded = {};
                                            let shouldRefresh = false;
                                            for (const e of errors) {
                                                const rid = Number(e && e.resource_id);
                                                if (!isNaN(rid) && rid > 0) {
                                                    const msg = (e && e.message) ? e.message.toString() : 'Resource error';
                                                    this.composeResourceErrors[rid] = msg;
                                                    this.composeResourceErrorMeta[rid] = {
                                                        code: (e && e.code) ? e.code.toString() : '',
                                                        status_code: Number(e && e.status_code),
                                                        message: msg
                                                    };
                                                    if (this.composeResourceErrorExpanded[rid] === undefined) this.composeResourceErrorExpanded[rid] = false;
                                                }
                                                const sc = Number(e && e.status_code);
                                                if (sc === 403 || sc === 404) shouldRefresh = true;
                                            }
                                            if (shouldRefresh) {
                                                this.composeResourceBanner = '素材庫附件權限或檔案狀態可能已變更，已重新整理素材清單';
                                                this.composeResourcePool = [];
                                                this.ensureComposeResourcePoolLoaded();
                                            }
                                            handledResourceErrors = true;
                                        }
                                    }
                                    const detail = (typeof rawDetail === 'string' ? rawDetail : (error.message || '')).toString();
                                    if (!handledResourceErrors) {
                                        const m = detail.match(/resource[^:]*:\s*(\d+)/i);
                                        if (m && m[1]) {
                                            const rid = Number(m[1]);
                                            if (!isNaN(rid)) {
                                                if (!this.composeResourceErrors) this.composeResourceErrors = {};
                                                this.composeResourceErrors[rid] = detail;
                                                if (!this.composeResourceErrorMeta) this.composeResourceErrorMeta = {};
                                                this.composeResourceErrorMeta[rid] = { code: '', status_code: Number(error.response && error.response.status), message: detail };
                                                if (!this.composeResourceErrorExpanded) this.composeResourceErrorExpanded = {};
                                                if (this.composeResourceErrorExpanded[rid] === undefined) this.composeResourceErrorExpanded[rid] = false;
                                                if (error.response && (error.response.status === 403 || error.response.status === 404)) {
                                                    this.composeResourceBanner = '素材庫附件權限或檔案狀態可能已變更，已重新整理素材清單';
                                                    this.composeResourcePool = [];
                                                    this.ensureComposeResourcePoolLoaded();
                                                }
                                            }
                                        }
                                    }
                                    if (handledResourceErrors && attempt === 0) {
                                        const count = this.composeResourceErrors ? Object.keys(this.composeResourceErrors).length : 0;
                                        const ok = confirm(`有 ${count || 0} 個素材附件無法加入。要移除失敗素材並繼續寄出嗎？`);
                                        if (ok) {
                                            this.removeAllErroredResources();
                                            this.composeResourceBanner = `已移除 ${count || 0} 個失敗素材附件，將繼續寄出`;
                                            continue;
                                        }
                                    }
                                    _showToast('發送失敗: ' + (handledResourceErrors ? '素材庫附件有問題，請檢查紅字提示' : detail));
                                    return;
                                }
                            }
                            _showToast('發送失敗: 素材庫附件仍有問題，請檢查紅字提示');
                        } finally {
                            this.sendingEmail = false;
                        }
                    },

                    async optimizeSubject() {
                        if (!this.composeForm.subject && !this.composeForm.body) {
                            _showToast('請先輸入主旨或內容以進行分析。');
                            return;
                        }
                        this.optimizingSubject = true;
                        this.showSubjectOptimizerModal = true;
                        this.subjectVariations = [];

                        try {
                            const response = await axios.post(`${ADMIN_API}/ai/generate/subjects`, {
                                subject: this.composeForm.subject || '',
                                body: this.stripHtml(this.composeForm.body || ''),
                                target_audience: 'General',
                                tone: 'Engaging'
                            });

                            this.subjectVariations = response.data.variations || [];
                        } catch (error) {
                            _showToast('主旨優化失敗: ' + (error.response?.data?.detail || error.message));
                            this.showSubjectOptimizerModal = false;
                        } finally {
                            this.optimizingSubject = false;
                        }
                    },

                    applySubject(newSubject) {
                        this.composeForm.subject = newSubject;
                        this.showSubjectOptimizerModal = false;
                    },

                    // Dashboard Visualization
                    async initDashboard() {
                        if (this.isSuperAdmin || this.isDomainAdmin) {
                            await this.fetchTrafficStats();
                            await this.fetchSpamStats();
                            await this.fetchServiceStatus();
                        }
                    },

                    async fetchTrafficStats() {
                        try {
                            const response = await axios.get(`${ADMIN_API}/stats/traffic?days=7`);
                            const data = response.data;
                            const rows = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
                            this.renderTrafficChart(rows);
                        } catch (error) {
                            window.$toast?.error('載入 traffic stats 失敗', error?.response?.data?.detail || error?.message || '');
                            console.error('Failed to fetch traffic stats', error);
                        }
                    },

                    async fetchSpamStats() {
                        try {
                            const response = await axios.get(`${ADMIN_API}/stats/spam?days=30`);
                            this.spamStats = response.data || {};
                            this.renderSpamChart(this.spamStats);
                        } catch (error) {
                            window.$toast?.error('載入 spam stats 失敗', error?.response?.data?.detail || error?.message || '');
                            console.error('Failed to fetch spam stats', error);
                            this.spamStats = {};
                            if (this.spamChartInstance) {
                                this.spamChartInstance.destroy();
                                this.spamChartInstance = null;
                            }
                        }
                    },

                    renderTrafficChart(data) {
                        const ctx = document.getElementById('trafficChart');
                        if (!ctx) return;
                        const rows = Array.isArray(data) ? data : [];

                        if (this.trafficChartInstance) {
                            this.trafficChartInstance.destroy();
                        }

                        this.trafficChartInstance = new Chart(ctx, {
                            type: 'line',
                            data: {
                                labels: rows.map(d => d.date),
                                datasets: [
                                    {
                                        label: '接收 (Received)',
                                        data: rows.map(d => d.received),
                                        borderColor: 'rgb(59, 130, 246)',
                                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                        fill: true,
                                        tension: 0.4
                                    },
                                    {
                                        label: '垃圾信 (Spam)',
                                        data: rows.map(d => d.spam),
                                        borderColor: 'rgb(239, 68, 68)',
                                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                        fill: true,
                                        tension: 0.4
                                    }
                                ]
                            },
                            options: {
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { position: 'top' }
                                },
                                scales: {
                                    y: { beginAtZero: true, ticks: { precision: 0 } }
                                }
                            }
                        });
                    },

                    renderSpamChart(stats) {
                        const ctx = document.getElementById('spamChart');
                        if (!ctx) return;

                        if (this.spamChartInstance) {
                            this.spamChartInstance.destroy();
                        }

                        const spamCount = stats.total || 0;
                        // We don't have total emails count in stats yet, let's assume a ratio or just show spam vs non-spam if available
                        // For now, let's just show a simple donut chart representing the score distribution or just visual candy
                        // Actually, let's fetch total emails to show ratio

                        this.spamChartInstance = new Chart(ctx, {
                            type: 'doughnut',
                            data: {
                                labels: ['垃圾信', '正常信'],
                                datasets: [{
                                    data: [spamCount, Math.max(0, (spamCount * 4))], // Fake ratio if no total, but let's try to get real
                                    backgroundColor: ['rgb(239, 68, 68)', 'rgb(34, 197, 94)']
                                }]
                            },
                            options: {
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { position: 'bottom' },
                                    title: { display: true, text: '攔截比例 (示意)' }
                                }
                            }
                        });
                    },

                    toggleLogStream() {
                        if (this.logWs) {
                            this.disconnectLogWs();
                        } else {
                            this.connectLogWs();
                        }
                    },

                    connectLogWs() {
                        if (this.logWs) return;
                        if (!this.isSuperAdmin) {
                            this.realtimeLogs = ['需要系統管理員權限'];
                            return;
                        }

                        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                        const token = (localStorage.getItem('token') || '').toString();
                        if (!token) {
                            this.realtimeLogs = ['Not logged in. Please login again.'];
                            return;
                        }
                        const url = `${protocol}//${window.location.host}/ws/logs/${this.logService}?token=${encodeURIComponent(token)}`;

                        this.realtimeLogs = [`Connecting to ${this.logService}...`];

                        this.logWs = new WebSocket(url);

                        this.logWs.onopen = () => {
                            this.realtimeLogs.push('Connected.');
                        };

                        this.logWs.onmessage = (event) => {
                            this.realtimeLogs.push(event.data);
                            // Auto scroll
                            nextTick(() => {
                                const container = this.$refs.logContainer;
                                if (container) {
                                    container.scrollTop = container.scrollHeight;
                                }
                            });
                            // Limit lines
                            if (this.realtimeLogs.length > 200) {
                                this.realtimeLogs.shift();
                            }
                        };

                        this.logWs.onclose = () => {
                            this.realtimeLogs.push('Connection closed.');
                            this.logWs = null;
                        };

                        this.logWs.onerror = (error) => {
                            console.error('WebSocket error:', error);
                            this.realtimeLogs.push('Connection error.');
                            this.logWs = null;
                        };
                    },

                    disconnectLogWs() {
                        if (this.logWs) {
                            this.logWs.close();
                            this.logWs = null;
                        }
                    },

                    clearRealtimeLogs() {
                        this.realtimeLogs = [];
                    },

                    async fetchLoginDomains() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/public/login-domains`);
                            const list = Array.isArray(res?.data?.domains) ? res.data.domains : [];
                            this.loginDomains = list.filter(d => typeof d === 'string' && d.trim()).map(d => d.trim());
                        } catch (e) {
                            this.loginDomains = [];
                        }
                    },

                    async login() {
                        this.loginError = '';
                        try {
                            const formData = new FormData();
                            formData.append('username', this.loginForm.username);
                            formData.append('password', this.loginForm.password);

                            const res = await axios.post(`${ADMIN_API}/token`, formData);
                            // TOTP two-step login
                            if (res.data.totp_required) {
                                this.totpPartialToken = res.data.partial_token;
                                this.totpCode = '';
                                this.totpError = '';
                                this.showTotpModal = true;
                                return;
                            }
                            this.token = res.data.access_token;
                            localStorage.setItem('token', this.token);
                            _scheduleSessionWarning(this.token);

                            await this.fetchCurrentUser();
                            this.refreshData();
                            await this.warmupMailSession(this.loginForm.password);
                            this.loginForm = { username: '', password: '' };
                            await this.openWebmailFolder('INBOX');
                        } catch (e) {
                            this.loginError = '登入失敗: ' + (e.response?.data?.detail || e.message);
                        }
                    },

                    async totpLogin() {
                        this.totpError = '';
                        this.totpLoading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/token/totp`, {
                                partial_token: this.totpPartialToken,
                                code: this.totpCode.trim(),
                            });
                            this.showTotpModal = false;
                            this.token = res.data.access_token;
                            localStorage.setItem('token', this.token);
                            _scheduleSessionWarning(this.token);
                            await this.fetchCurrentUser();
                            this.refreshData();
                            await this.warmupMailSession(this.loginForm.password);
                            this.loginForm = { username: '', password: '' };
                            this.totpPartialToken = '';
                            this.totpCode = '';
                            await this.openWebmailFolder('INBOX');
                        } catch (e) {
                            this.totpError = e.response?.data?.detail || '驗證碼錯誤，請重試';
                        } finally {
                            this.totpLoading = false;
                        }
                    },
                    logout() {
                        _clearSessionTimers();
                        this.token = null;
                        this.currentUser = null;
                        localStorage.removeItem('token');
                        this.resetMailAuth();
                        this.loginForm = { username: '', password: '' };
                        this.invoiceImportedAttachmentKeys = {};
                    },
                    async fetchCurrentUser() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/users/me`);
                            this.currentUser = res.data;
                            if (this.currentUser && this.currentUser.domain_id && !this.isSuperAdmin) {
                                if (!this.newUser.domain_id) this.newUser.domain_id = this.currentUser.domain_id;
                                if (!this.newGroup.domain_id) this.newGroup.domain_id = this.currentUser.domain_id;
                                if (!this.newDepartment.domain_id) this.newDepartment.domain_id = this.currentUser.domain_id;
                            }
                            if (!this.canAccess(this.currentView)) {
                                this.currentView = 'webmail';
                            }
                            this.loadInvoiceImportedAttachmentKeys(true);
                        } catch (e) {
                            window.$toast?.error('載入 user 失敗', e?.response?.data?.detail || e?.message || '');
                            console.error('Failed to fetch user', e);
                            this.logout();
                        }
                    },
                    refreshData() {
                        if (this.isSuperAdmin || this.isDomainAdmin) {
                            this.fetchDomains();
                            this.fetchUsers();
                            this.fetchGroups();
                        }
                        if (this.isSuperAdmin && this.canAccess('logs')) {
                            this.fetchLogs();
                        }
                        this.fetchRagStats();
                    },

                    async openContactsView() {
                        if (!this.setCurrentView('contacts')) return;
                        this.contactsTab = 'contacts';
                        await this.initContactsView();
                    },
                    async initContactsView() {
                        const tasks = [this.fetchContactGroups(), this.fetchContactTags()];
                        if (this.isSuperAdmin || this.isDomainAdmin) tasks.push(this.fetchUsers());
                        await Promise.all(tasks);
                        this.contactsPage = 1;
                        await this.fetchContacts(1);
                    },
                    resetContactFilters() {
                        this.contactFilters = { search: '', tag: '', group_id: null, sort_by: 'name', sort_dir: 'asc' };
                        this.contactsPage = 1;
                        this.fetchContacts(1);
                    },
                    canEditContact(c) {
                        if (!c) return false;
                        if (this.isSuperAdmin || this.isDomainAdmin) return true;
                        return !!c.user_id && this.currentUser && c.user_id === this.currentUser.id;
                    },
                    async fetchContacts(page = null) {
                        if (!this.token) return;
                        if (page !== null) this.contactsPage = page;
                        this.contactsLoading = true;
                        try {
                            const params = { limit: this.contactsLimit, skip: (this.contactsPage - 1) * this.contactsLimit };
                            if (this.contactFilters.search) params.search = this.contactFilters.search;
                            if (this.contactFilters.tag) params.tag = this.contactFilters.tag;
                            if (this.contactFilters.group_id) params.group_id = this.contactFilters.group_id;
                            if (this.contactFilters.sort_by) params.sort_by = this.contactFilters.sort_by;
                            if (this.contactFilters.sort_dir) params.sort_dir = this.contactFilters.sort_dir;
                            const res = await axios.get(`${ADMIN_API}/contacts`, { params });
                            const data = res.data || {};
                            if (Array.isArray(data)) {
                                this.contacts = data;
                                this.contactsTotal = data.length;
                            } else {
                                this.contacts = data.items || [];
                                this.contactsTotal = data.total || 0;
                            }
                        } catch (e) {
                            _showToast('載入聯絡人失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.contactsLoading = false;
                        }
                    },
                    async fetchContactTags() {
                        if (!this.token) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/contacts/tags`);
                            this.contactTags = res.data || [];
                        } catch (e) {
                            this.contactTags = [];
                        }
                    },
                    async fetchContactGroups() {
                        if (!this.token) return;
                        this.contactGroupsLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/contacts/groups`);
                            this.contactGroups = res.data || [];
                        } catch (e) {
                            this.contactGroups = [];
                        } finally {
                            this.contactGroupsLoading = false;
                        }
                    },
                    async openLeadHistory(c) {
                        if (!c || !c.id) return;
                        this.leadHistoryContact = c;
                        this.showLeadHistoryModal = true;
                        this.leadHistoryLoading = true;
                        this.leadHistoryEvents = [];
                        try {
                            const res = await axios.get(`${ADMIN_API}/contacts/${c.id}/lead_score_events`);
                            this.leadHistoryEvents = res.data || [];
                        } catch (e) {
                            _showToast('載入 Lead Score 歷史失敗: ' + (e.response?.data?.detail || e.message));
                            this.leadHistoryEvents = [];
                        } finally {
                            this.leadHistoryLoading = false;
                        }
                    },
                    async openLeadHistoryFromMessage() {
                        const target = (this.currentFolder === 'Sent') ? this.currentMessage?.to : this.currentMessage?.from;
                        const email = (target || '').match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] || '';
                        const key = email.trim().toLowerCase();
                        if (!key) {
                            _showToast('無法取得對象 Email');
                            return;
                        }
                        let c = (this.contacts || []).find(x => ((x?.email || '').trim().toLowerCase() === key));
                        if (!c) {
                            try {
                                const res = await axios.get(`${ADMIN_API}/contacts`, { params: { search: key, limit: 1, sort_by: 'created_at', sort_dir: 'desc' } });
                                const list = res.data || [];
                                c = Array.isArray(list) && list.length > 0 ? list[0] : null;
                            } catch (e) {
                                c = null;
                            }
                        }
                        if (!c || !c.id) {
                            _showToast('此 Email 不在聯絡人名單中');
                            return;
                        }
                        await this.openLeadHistory(c);
                    },
                    openCreateContact() {
                        this.editingContactId = null;
                        this.contactForm = { name: '', email: '', phone: '', notes: '', tags: '', group_id: null, user_id: null };
                        this.showContactModal = true;
                    },
                    openEditContact(c) {
                        this.editingContactId = c.id;
                        this.contactForm = {
                            name: c.name || '',
                            email: c.email || '',
                            phone: c.phone || '',
                            notes: c.notes || '',
                            tags: c.tags || '',
                            group_id: c.group_id ?? null,
                            user_id: c.user_id ?? null
                        };
                        this.showContactModal = true;
                    },
                    closeContactModal() {
                        this.showContactModal = false;
                        this.contactSaving = false;
                        this.editingContactId = null;
                    },
                    async saveContact() {
                        if (!this.contactForm.name || !this.contactForm.email) {
                            _showToast('請填寫姓名與 Email');
                            return;
                        }
                        this.contactSaving = true;
                        try {
                            const payload = {
                                name: this.contactForm.name,
                                email: this.contactForm.email,
                                phone: this.contactForm.phone || null,
                                notes: this.contactForm.notes || null,
                                tags: this.contactForm.tags || null
                            };
                            if (this.isSuperAdmin || this.isDomainAdmin) {
                                payload.group_id = this.contactForm.group_id ?? null;
                                payload.user_id = this.contactForm.user_id ?? null;
                            }
                            if (this.editingContactId) {
                                await axios.put(`${ADMIN_API}/contacts/${this.editingContactId}`, payload);
                                _showToast('聯絡人已更新');
                            } else {
                                await axios.post(`${ADMIN_API}/contacts`, payload);
                                _showToast('聯絡人已新增');
                            }
                            this.closeContactModal();
                            this.contactsPage = 1;
                            await Promise.all([this.fetchContactTags(), this.fetchContacts(1)]);
                        } catch (e) {
                            const msg = '儲存失敗: ' + (e.response?.data?.detail || e.message);
                            window.alert(msg);
                        } finally {
                            this.contactSaving = false;
                        }
                    },
                    async contactQuickFollowup() {
                        const recipient = (this.contactForm.email || '').toString().trim();
                        if (!recipient) {
                            _showToast('請先填寫 Email');
                            return;
                        }
                        const name = (this.contactForm.name || '').toString().trim();
                        const notes = (this.contactForm.notes || '').toString().trim();
                        const originalText = notes || (name ? `聯絡人：${name}（${recipient}）` : `聯絡人：${recipient}`);
                        const payload = {
                            original_text: originalText,
                            recipient,
                            tone: 'professional',
                            instructions: '禮貌且具體，提出明確下一步與時間，簡短友善。',
                            original_subject: null,
                            language: 'Traditional Chinese (繁體中文)',
                            length: 'medium',
                            template: 'gentle_nudge',
                            include_quote: false
                        };
                        this.contactFollowupGenerating = true;
                        try {
                            let data = null;
                            for (const b of [ADMIN_API, window.location.origin, ADMIN_API_FALLBACK_8000]) {
                                try {
                                    const res = await axios.post(`${b}/ai/generate/followup`, payload);
                                    data = res?.data || null;
                                    if (data) break;
                                } catch (e) {
                                    const sc = Number(e?.response?.status || 0);
                                    if (sc === 404) continue;
                                    throw e;
                                }
                            }
                            if (!data) {
                                throw new Error('無法生成跟進草稿');
                            }
                            const subject = (data?.subject || '').toString().trim() || '跟進';
                            const body = (data?.body || data?.result || '').toString();
                            this.composeForm = { to: recipient, subject, body, attachments: [], in_reply_to: null, references: null };
                            this.showContactModal = false;
                            await this.switchSidebarView('webmail');
                            this.webmailTab = 'mail';
                            this.showComposeModal = true;
                        } catch (e) {
                            _showToast('生成跟進草稿失敗: ' + (e?.response?.data?.detail || e?.message || ''));
                        } finally {
                            this.contactFollowupGenerating = false;
                        }
                    },
                    async deleteContact(c) {
                        if (!c || !c.id) return;
                        if (!confirm(`確定要刪除聯絡人 ${c.email} 嗎？`)) return;
                        try {
                            await axios.delete(`${ADMIN_API}/contacts/${c.id}`);
                            await Promise.all([this.fetchContactTags(), this.fetchContacts()]);
                        } catch (e) {
                            _showToast('刪除失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    parseCsvToContacts(csvText) {
                        const lines = csvText.split(/\r?\n/).filter(l => l.trim());
                        if (lines.length < 2) return [];
                        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z0-9_]/g, ''));
                        const nameIdx   = headers.findIndex(h => ['name','姓名','名稱'].includes(h));
                        const emailIdx  = headers.findIndex(h => ['email','mail','電子郵件'].includes(h));
                        const phoneIdx  = headers.findIndex(h => ['phone','tel','電話'].includes(h));
                        const notesIdx  = headers.findIndex(h => ['notes','note','備註','備注'].includes(h));
                        const tagsIdx   = headers.findIndex(h => ['tags','tag','標籤'].includes(h));
                        if (emailIdx === -1) return null; // email column required
                        return lines.slice(1).map(line => {
                            const cols = line.match(/(?:"[^"]*"|[^,]*)(?:,|$)/g)?.map(c => c.replace(/,$/, '').replace(/^"|"$/g, '').trim()) || [];
                            const email = cols[emailIdx] || '';
                            if (!email || !email.includes('@')) return null;
                            return {
                                name:  (nameIdx  >= 0 ? cols[nameIdx]  : '') || email.split('@')[0],
                                email,
                                phone: phoneIdx >= 0 ? (cols[phoneIdx] || '') : '',
                                notes: notesIdx >= 0 ? (cols[notesIdx] || '') : '',
                                tags:  tagsIdx  >= 0 ? (cols[tagsIdx]  || '') : '',
                            };
                        }).filter(Boolean);
                    },
                    async importContactsFromCsv(contacts) {
                        if (!contacts || contacts.length === 0) return;
                        this.contactImportLoading = true;
                        this.contactImportResult = null;
                        try {
                            const res = await axios.post(`${ADMIN_API}/contacts/import`, contacts);
                            this.contactImportResult = res.data;
                            window.$toast?.success(`匯入完成：${res.data.imported || 0} 筆成功，${res.data.skipped || 0} 筆略過`);
                            this.showContactImportModal = false;
                            this.contactImportPreview = [];
                            await this.fetchContacts();
                        } catch (e) {
                            _showToast('匯入失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.contactImportLoading = false;
                        }
                    },
                    openCreateContactGroup() {
                        this.editingContactGroupId = null;
                        this.contactGroupForm = { name: '', description: '' };
                        this.showContactGroupModal = true;
                    },
                    openEditContactGroup(g) {
                        this.editingContactGroupId = g.id;
                        this.contactGroupForm = { name: g.name || '', description: g.description || '' };
                        this.showContactGroupModal = true;
                    },
                    closeContactGroupModal() {
                        this.showContactGroupModal = false;
                        this.contactGroupSaving = false;
                        this.editingContactGroupId = null;
                    },
                    async saveContactGroup() {
                        if (!this.contactGroupForm.name) {
                            _showToast('請填寫群組名稱');
                            return;
                        }
                        this.contactGroupSaving = true;
                        try {
                            const payload = {
                                name: this.contactGroupForm.name,
                                description: this.contactGroupForm.description || null
                            };
                            if (this.editingContactGroupId) {
                                await axios.put(`${ADMIN_API}/contacts/groups/${this.editingContactGroupId}`, payload);
                            } else {
                                await axios.post(`${ADMIN_API}/contacts/groups`, payload);
                            }
                            this.closeContactGroupModal();
                            await Promise.all([this.fetchContactGroups(), this.fetchContacts()]);
                        } catch (e) {
                            _showToast('儲存群組失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.contactGroupSaving = false;
                        }
                    },
                    async deleteContactGroup(g) {
                        if (!g || !g.id) return;
                        if (!confirm(`確定要刪除群組「${g.name}」嗎？`)) return;
                        try {
                            await axios.delete(`${ADMIN_API}/contacts/groups/${g.id}`);
                            await Promise.all([this.fetchContactGroups(), this.fetchContacts()]);
                        } catch (e) {
                            _showToast('刪除群組失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },

                    async openQuotesView() {
                        if (!this.setCurrentView('quotes')) return;
                        await this.initQuotesView();
                    },
                    async initQuotesView() {
                        await Promise.all([
                            this.fetchQuoteMgmtProducts(),
                            this.fetchQuoteMgmtPricebooks(),
                            this.fetchQuoteMgmtQuotes()
                        ]);
                    },
                    openQuoteMgmtPricebookModal() {
                        this.showQuoteMgmtPricebookModal = true;
                        this.quoteMgmtSelectedPricebookId = null;
                        this.quoteMgmtPricebookItems = [];
                        this.quoteMgmtPricebookSearch = '';
                        this.fetchQuoteMgmtPricebooks();
                        this.fetchQuoteMgmtProducts();
                    },
                    closeQuoteMgmtPricebookModal() {
                        this.showQuoteMgmtPricebookModal = false;
                        this.quoteMgmtSelectedPricebookId = null;
                        this.quoteMgmtPricebookItems = [];
                        this.quoteMgmtPricebookSearch = '';
                    },
                    resetQuoteMgmtFilters() {
                        this.quoteMgmtFilters = { status: '', search: '' };
                        this.fetchQuoteMgmtQuotes();
                    },
                    async fetchQuoteMgmtPricebooks(opts) {
                        if (!this.token) return;
                        this.quoteMgmtPricebookLoading = true;
                        try {
                            const params = { active: true };
                            const cur = (opts && typeof opts === 'object') ? (opts.currency ?? null) : null;
                            const curNorm = (cur || '').toString().trim().toUpperCase();
                            if (curNorm) params.currency = curNorm;
                            const res = await axios.get(`${ADMIN_API}/pricebooks`, { params });
                            let list = Array.isArray(res.data) ? res.data : [];
                            if (curNorm && list.length === 0) {
                                const resAll = await axios.get(`${ADMIN_API}/pricebooks`, { params: { active: true } });
                                list = Array.isArray(resAll.data) ? resAll.data : [];
                            }
                            this.quoteMgmtPricebooks = list;
                        } catch (e) {
                            this.quoteMgmtPricebooks = [];
                        } finally {
                            this.quoteMgmtPricebookLoading = false;
                        }
                    },
                    async onQuoteMgmtPricebookSelected() {
                        this.quoteMgmtPricebookItems = [];
                        await this.fetchQuoteMgmtPricebookItems();
                    },
                    async fetchQuoteMgmtPricebookItems() {
                        if (!this.token) return;
                        const pbId = this.quoteMgmtSelectedPricebookId;
                        if (!pbId) {
                            this.quoteMgmtPricebookItems = [];
                            return;
                        }
                        this.quoteMgmtPricebookLoading = true;
                        try {
                            const params = { limit: 200, offset: 0 };
                            const s = (this.quoteMgmtPricebookSearch || '').trim();
                            if (s) params.search = s;
                            const res = await axios.get(`${ADMIN_API}/pricebooks/${pbId}/items`, { params });
                            this.quoteMgmtPricebookItems = Array.isArray(res.data) ? res.data : [];
                        } catch (e) {
                            this.quoteMgmtPricebookItems = [];
                        } finally {
                            this.quoteMgmtPricebookLoading = false;
                        }
                    },
                    async createQuoteMgmtPricebook() {
                        if (!this.token) return;
                        const name = (this.quoteMgmtNewPricebookForm?.name || '').trim();
                        if (!name) return;
                        this.quoteMgmtPricebookSaving = true;
                        try {
                            const payload = {
                                name,
                                currency: (this.quoteMgmtNewPricebookForm?.currency || 'TWD').trim() || 'TWD',
                                tax_mode: (this.quoteMgmtNewPricebookForm?.tax_mode || 'exclusive').trim() || 'exclusive'
                            };
                            const res = await axios.post(`${ADMIN_API}/pricebooks`, payload);
                            await this.fetchQuoteMgmtPricebooks();
                            const id = res?.data?.id ?? null;
                            this.quoteMgmtSelectedPricebookId = id;
                            if (id) await this.fetchQuoteMgmtPricebookItems();
                        } catch (e) {
                            _showToast('建立價目表失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quoteMgmtPricebookSaving = false;
                        }
                    },
                    async onQuoteMgmtPricebookProductChanged() {
                        if (!this.token) return;
                        const pid = this.quoteMgmtNewPricebookItemForm?.product_id ?? null;
                        this.quoteMgmtNewPricebookItemForm.product_spec_id = null;
                        if (!pid) {
                            this.quoteMgmtPricebookProductSpecs = [];
                            return;
                        }
                        try {
                            const res = await axios.get(`${ADMIN_API}/products/${pid}/specs`);
                            this.quoteMgmtPricebookProductSpecs = Array.isArray(res.data) ? res.data : [];
                        } catch (e) {
                            this.quoteMgmtPricebookProductSpecs = [];
                        }
                    },
                    async createQuoteMgmtPricebookItem() {
                        if (!this.token) return;
                        const pbId = this.quoteMgmtSelectedPricebookId;
                        if (!pbId) return;
                        const pid = this.quoteMgmtNewPricebookItemForm?.product_id ?? null;
                        if (!pid) return;
                        this.quoteMgmtPricebookSaving = true;
                        try {
                            const payload = {
                                product_id: pid,
                                product_spec_id: this.quoteMgmtNewPricebookItemForm?.product_spec_id ?? null,
                                unit_price: Number(this.quoteMgmtNewPricebookItemForm?.unit_price ?? 0) || 0,
                                min_qty: Number(this.quoteMgmtNewPricebookItemForm?.min_qty ?? 1) || 1
                            };
                            await axios.post(`${ADMIN_API}/pricebooks/${pbId}/items`, payload);
                            await this.fetchQuoteMgmtPricebookItems();
                        } catch (e) {
                            _showToast('加入價目表品項失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quoteMgmtPricebookSaving = false;
                        }
                    },
                    async fetchQuoteMgmtProducts() {
                        if (!this.token) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/products`);
                            this.quoteMgmtProducts = Array.isArray(res.data) ? res.data : [];
                        } catch (e) {
                            this.quoteMgmtProducts = [];
                        }
                    },
                    async fetchQuoteMgmtProductSpecs(productId) {
                        if (!this.token) return;
                        if (!productId) {
                            this.quoteMgmtProductSpecs = [];
                            return;
                        }
                        try {
                            const res = await axios.get(`${ADMIN_API}/products/${productId}/specs`);
                            this.quoteMgmtProductSpecs = Array.isArray(res.data) ? res.data : [];
                        } catch (e) {
                            this.quoteMgmtProductSpecs = [];
                        }
                    },
                    quoteMgmtProductLabelById(productId) {
                        const pid = productId ?? null;
                        if (!pid) return '-';
                        const p = (this.quoteMgmtProducts || []).find(x => x.id === pid);
                        if (!p) return String(pid);
                        const sku = (p.sku || '').trim();
                        const name = (p.name || '').trim();
                        if (sku && name) return `${sku} - ${name}`;
                        return sku || name || String(pid);
                    },
                    async fetchQuoteMgmtQuotes() {
                        if (!this.token) return;
                        this.quoteMgmtLoading = true;
                        try {
                            const params = {};
                            if (this.quoteMgmtFilters.status) params.status = this.quoteMgmtFilters.status;
                            if (this.quoteMgmtFilters.search) params.search = this.quoteMgmtFilters.search;
                            const res = await axios.get(`${ADMIN_API}/quotes`, { params });
                            this.quoteMgmtQuotes = Array.isArray(res.data) ? res.data : [];
                        } catch (e) {
                            const status = e?.response?.status;
                            const detail = e.response?.data?.detail || e.message;
                            if (status === 404) {
                                if (await this.handleAdminApiNotFound(e)) {
                                    this.quoteMgmtQuotes = [];
                                    return;
                                }
                            }
                            _showToast('載入報價失敗: ' + detail);
                            this.quoteMgmtQuotes = [];
                        } finally {
                            this.quoteMgmtLoading = false;
                        }
                    },
                    openCreateQuoteMgmtModal() {
                        this.quoteMgmtCreateForm = { customer_name: '', contact_id: null, tax_mode: 'exclusive', tax_rate: 0 };
                        this.quoteMgmtContactSearch = '';
                        this.quoteMgmtContactResults = [];
                        this.showQuoteMgmtCreateModal = true;
                        this.initQuoteMgmtCreateModalPosition();
                    },
                    closeCreateQuoteMgmtModal() {
                        this.showQuoteMgmtCreateModal = false;
                        this.quoteMgmtSaving = false;
                        this.stopDragQuoteMgmtCreateModal();
                    },
                    _getPointerXY(e) {
                        const t = e?.touches?.[0] || e?.changedTouches?.[0];
                        if (t) return { x: t.clientX, y: t.clientY };
                        return { x: e?.clientX ?? 0, y: e?.clientY ?? 0 };
                    },
                    _getSidebarWidth() {
                        const vw = window.innerWidth || 0;
                        if (vw < 768) return 0;
                        try {
                            const el = document.querySelector('aside');
                            const rect = el && typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null;
                            const w = rect?.width ?? 0;
                            if (w > 0) return w;
                        } catch (e) {
                        }
                        return 256;
                    },
                    quoteMgmtModalMaxWidth(preferredPx) {
                        const sidebar = this._getSidebarWidth() || 0;
                        const pad = 12;
                        const maxW = Math.max(240, (window.innerWidth || 0) - sidebar - pad * 2);
                        const pref = Number(preferredPx || 0);
                        const w = pref > 0 ? Math.min(pref, maxW) : maxW;
                        return `${Math.max(240, Math.round(w))}px`;
                    },
                    _clampModalPos(pos, rect) {
                        const pad = 12;
                        const vw = window.innerWidth || 0;
                        const vh = window.innerHeight || 0;
                        const w = rect?.width ?? 0;
                        const h = rect?.height ?? 0;
                        const minX = (this._getSidebarWidth() || 0) + pad;
                        const maxX = Math.max(minX, vw - w - pad);
                        const maxY = Math.max(pad, vh - h - pad);
                        return {
                            x: Math.min(Math.max(pos.x, minX), maxX),
                            y: Math.min(Math.max(pos.y, pad), maxY)
                        };
                    },
                    initQuoteMgmtCreateModalPosition() {
                        nextTick(() => {
                            const el = this.$refs.quoteMgmtCreateModal;
                            if (!el || typeof el.getBoundingClientRect !== 'function') return;
                            const rect = el.getBoundingClientRect();
                            const vw = window.innerWidth || 0;
                            const vh = window.innerHeight || 0;
                            const sidebar = this._getSidebarWidth() || 0;
                            const availW = Math.max(0, vw - sidebar);
                            const x = Math.max(sidebar + 12, Math.round(sidebar + (availW - rect.width) / 2));
                            const y = Math.max(12, Math.round((vh - rect.height) / 2));
                            this.quoteMgmtCreateModalPos = this._clampModalPos({ x, y }, rect);
                        });
                    },
                    initQuoteMgmtEditorModalPosition() {
                        nextTick(() => {
                            const el = this.$refs.quoteMgmtEditorModal;
                            if (!el || typeof el.getBoundingClientRect !== 'function') return;
                            const rect = el.getBoundingClientRect();
                            const vw = window.innerWidth || 0;
                            const vh = window.innerHeight || 0;
                            const sidebar = this._getSidebarWidth() || 0;
                            const availW = Math.max(0, vw - sidebar);
                            const x = Math.max(sidebar + 12, Math.round(sidebar + (availW - rect.width) / 2));
                            const y = Math.max(12, Math.round((vh - rect.height) / 2));
                            this.quoteMgmtEditorModalPos = this._clampModalPos({ x, y }, rect);
                        });
                    },
                    startDragQuoteMgmtCreateModal(e) {
                        if (e?.type === 'mousedown' && e.button !== 0) return;
                        try {
                            const t = e?.target;
                            if (t && typeof t.closest === 'function' && t.closest('input,select,textarea,button,a,[contenteditable="true"]')) return;
                        } catch (err) {
                        }
                        const el = this.$refs.quoteMgmtCreateModal;
                        if (!el || typeof el.getBoundingClientRect !== 'function') return;
                        const p = this._getPointerXY(e);
                        this.quoteMgmtCreateModalDragging = true;
                        this.quoteMgmtCreateModalDragOffset = {
                            x: p.x - (this.quoteMgmtCreateModalPos?.x ?? 0),
                            y: p.y - (this.quoteMgmtCreateModalPos?.y ?? 0)
                        };
                        const move = (ev) => this.onDragQuoteMgmtCreateModal(ev);
                        const up = () => this.stopDragQuoteMgmtCreateModal();
                        const cancel = () => this.stopDragQuoteMgmtCreateModal();
                        const esc = (ev) => {
                            if ((ev?.key || '').toString().toLowerCase() === 'escape') this.stopDragQuoteMgmtCreateModal();
                        };
                        this._quoteMgmtCreateModalMove = move;
                        this._quoteMgmtCreateModalUp = up;
                        this._quoteMgmtCreateModalCancel = cancel;
                        this._quoteMgmtCreateModalEsc = esc;
                        window.addEventListener('mousemove', move);
                        window.addEventListener('mouseup', up);
                        window.addEventListener('touchmove', move, { passive: false });
                        window.addEventListener('touchend', up);
                        window.addEventListener('touchcancel', up);
                        window.addEventListener('blur', cancel);
                        document.addEventListener('visibilitychange', cancel);
                        window.addEventListener('keydown', esc);
                    },
                    startDragQuoteMgmtEditorModal(e) {
                        if (e?.type === 'mousedown' && e.button !== 0) return;
                        try {
                            const t = e?.target;
                            if (t && typeof t.closest === 'function' && t.closest('input,select,textarea,button,a,[contenteditable="true"]')) return;
                        } catch (err) {
                        }
                        const el = this.$refs.quoteMgmtEditorModal;
                        if (!el || typeof el.getBoundingClientRect !== 'function') return;
                        const p = this._getPointerXY(e);
                        this.quoteMgmtEditorModalDragging = true;
                        this.quoteMgmtEditorModalDragOffset = {
                            x: p.x - (this.quoteMgmtEditorModalPos?.x ?? 0),
                            y: p.y - (this.quoteMgmtEditorModalPos?.y ?? 0)
                        };
                        const move = (ev) => this.onDragQuoteMgmtEditorModal(ev);
                        const up = () => this.stopDragQuoteMgmtEditorModal();
                        const cancel = () => this.stopDragQuoteMgmtEditorModal();
                        const esc = (ev) => {
                            if ((ev?.key || '').toString().toLowerCase() === 'escape') this.stopDragQuoteMgmtEditorModal();
                        };
                        this._quoteMgmtEditorModalMove = move;
                        this._quoteMgmtEditorModalUp = up;
                        this._quoteMgmtEditorModalCancel = cancel;
                        this._quoteMgmtEditorModalEsc = esc;
                        window.addEventListener('mousemove', move);
                        window.addEventListener('mouseup', up);
                        window.addEventListener('touchmove', move, { passive: false });
                        window.addEventListener('touchend', up);
                        window.addEventListener('touchcancel', up);
                        window.addEventListener('blur', cancel);
                        document.addEventListener('visibilitychange', cancel);
                        window.addEventListener('keydown', esc);
                    },
                    onDragQuoteMgmtCreateModal(e) {
                        if (!this.quoteMgmtCreateModalDragging) return;
                        if (e?.type && e.type.toString().startsWith('touch')) e?.preventDefault?.();
                        const el = this.$refs.quoteMgmtCreateModal;
                        if (!el || typeof el.getBoundingClientRect !== 'function') return;
                        const rect = el.getBoundingClientRect();
                        const p = this._getPointerXY(e);
                        const next = {
                            x: p.x - (this.quoteMgmtCreateModalDragOffset?.x ?? 0),
                            y: p.y - (this.quoteMgmtCreateModalDragOffset?.y ?? 0)
                        };
                        this.quoteMgmtCreateModalPos = this._clampModalPos(next, rect);
                    },
                    onDragQuoteMgmtEditorModal(e) {
                        if (!this.quoteMgmtEditorModalDragging) return;
                        if (e?.type && e.type.toString().startsWith('touch')) e?.preventDefault?.();
                        const el = this.$refs.quoteMgmtEditorModal;
                        if (!el || typeof el.getBoundingClientRect !== 'function') return;
                        const rect = el.getBoundingClientRect();
                        const p = this._getPointerXY(e);
                        const next = {
                            x: p.x - (this.quoteMgmtEditorModalDragOffset?.x ?? 0),
                            y: p.y - (this.quoteMgmtEditorModalDragOffset?.y ?? 0)
                        };
                        this.quoteMgmtEditorModalPos = this._clampModalPos(next, rect);
                    },
                    stopDragQuoteMgmtCreateModal() {
                        this.quoteMgmtCreateModalDragging = false;
                        const move = this._quoteMgmtCreateModalMove;
                        const up = this._quoteMgmtCreateModalUp;
                        const cancel = this._quoteMgmtCreateModalCancel;
                        const esc = this._quoteMgmtCreateModalEsc;
                        if (move) {
                            window.removeEventListener('mousemove', move);
                            window.removeEventListener('touchmove', move);
                        }
                        if (up) {
                            window.removeEventListener('mouseup', up);
                            window.removeEventListener('touchend', up);
                            window.removeEventListener('touchcancel', up);
                        }
                        if (cancel) {
                            window.removeEventListener('blur', cancel);
                            document.removeEventListener('visibilitychange', cancel);
                        }
                        if (esc) window.removeEventListener('keydown', esc);
                        this._quoteMgmtCreateModalMove = null;
                        this._quoteMgmtCreateModalUp = null;
                        this._quoteMgmtCreateModalCancel = null;
                        this._quoteMgmtCreateModalEsc = null;
                    },
                    stopDragQuoteMgmtEditorModal() {
                        this.quoteMgmtEditorModalDragging = false;
                        const move = this._quoteMgmtEditorModalMove;
                        const up = this._quoteMgmtEditorModalUp;
                        const cancel = this._quoteMgmtEditorModalCancel;
                        const esc = this._quoteMgmtEditorModalEsc;
                        if (move) {
                            window.removeEventListener('mousemove', move);
                            window.removeEventListener('touchmove', move);
                        }
                        if (up) {
                            window.removeEventListener('mouseup', up);
                            window.removeEventListener('touchend', up);
                            window.removeEventListener('touchcancel', up);
                        }
                        if (cancel) {
                            window.removeEventListener('blur', cancel);
                            document.removeEventListener('visibilitychange', cancel);
                        }
                        if (esc) window.removeEventListener('keydown', esc);
                        this._quoteMgmtEditorModalMove = null;
                        this._quoteMgmtEditorModalUp = null;
                        this._quoteMgmtEditorModalCancel = null;
                        this._quoteMgmtEditorModalEsc = null;
                    },
                    async searchQuoteMgmtContacts() {
                        if (!this.token) return;
                        const q = (this.quoteMgmtContactSearch || '').trim();
                        if (!q) {
                            this.quoteMgmtContactResults = [];
                            return;
                        }
                        try {
                            const res = await axios.get(`${ADMIN_API}/contacts`, {
                                params: { search: q, limit: 20, sort_by: 'created_at', sort_dir: 'desc' }
                            });
                            this.quoteMgmtContactResults = Array.isArray(res.data) ? res.data : [];
                        } catch (e) {
                            this.quoteMgmtContactResults = [];
                        }
                    },
                    async createQuoteMgmt() {
                        if (!this.token) return;
                        const customerName = (this.quoteMgmtCreateForm.customer_name || '').trim();
                        const contactId = this.quoteMgmtCreateForm.contact_id ?? null;
                        if (!customerName && !contactId) {
                            _showToast('請輸入客戶名稱或選擇聯絡人');
                            return;
                        }
                        this.quoteMgmtSaving = true;
                        try {
                            const payload = {
                                customer_name: customerName || null,
                                contact_id: contactId,
                                tax_mode: this.quoteMgmtCreateForm.tax_mode,
                                tax_rate: this.quoteMgmtCreateForm.tax_rate
                            };
                            const res = await axios.post(`${ADMIN_API}/quotes`, payload);
                            const qt = res.data;
                            this.closeCreateQuoteMgmtModal();
                            await this.openQuoteMgmtEditor(qt);
                            await this.fetchQuoteMgmtQuotes();
                        } catch (e) {
                            _showToast('建立報價失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quoteMgmtSaving = false;
                        }
                    },
                    async openQuoteMgmtEditor(q) {
                        if (!q || !q.id) return;
                        this.showQuoteMgmtEditorModal = true;
                        this.initQuoteMgmtEditorModalPosition();
                        await this.loadQuoteMgmtQuote(q.id);
                        this.initQuoteMgmtEditorModalPosition();
                    },
                    closeQuoteMgmtEditor() {
                        this.showQuoteMgmtEditorModal = false;
                        this.quoteMgmtSelectedQuote = null;
                        this.quoteMgmtProductSpecs = [];
                        this.quoteMgmtRenderPayload = '';
                        this.quoteMgmtAISourceText = '';
                        this.quoteMgmtAIFile = null;
                        this.quoteMgmtAIExtracting = false;
                        this.quoteMgmtAIDraft = null;
                        this.stopDragQuoteMgmtEditorModal();
                    },
                    async refreshQuoteMgmtSelected() {
                        const id = this.quoteMgmtSelectedQuote?.id;
                        if (!id) return;
                        await this.loadQuoteMgmtQuote(id);
                    },
                    async loadQuoteMgmtQuote(id) {
                        if (!this.token) return;
                        this.quoteMgmtEditorLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/quotes/${id}`);
                            this.quoteMgmtSelectedQuote = res.data || null;
                            const qt = this.quoteMgmtSelectedQuote;
                            await this.fetchQuoteMgmtPricebooks({ currency: qt?.currency || null });
                            this.quoteMgmtEditForm = {
                                status: qt?.status || 'draft',
                                customer_name: qt?.customer_name || '',
                                contact_id: qt?.contact_id ?? null,
                                tax_mode: qt?.tax_mode || 'exclusive',
                                tax_rate: qt?.tax_rate ?? 0,
                                notes: qt?.notes || '',
                                terms: qt?.terms || ''
                            };
                            const pbRaw = this.quoteMgmtAIPricingPricebookId;
                            const pbId = pbRaw === '' || pbRaw === null || pbRaw === undefined ? null : Number(pbRaw);
                            if (pbId) {
                                const allowList = Array.isArray(this.quoteMgmtPricebooksForAI) ? this.quoteMgmtPricebooksForAI : [];
                                const ok = allowList.some(x => Number(x?.id || 0) === pbId);
                                if (!ok) this.quoteMgmtAIPricingPricebookId = '';
                            }
                            this.quoteMgmtNewItem = { product_id: null, product_spec_id: null, product_label: '', title: '', spec_text: '', qty: 1, unit: '', unit_price: 0, discount_type: 'none', discount_value: 0, remark: '' };
                            this.quoteMgmtProductSpecs = [];
                            this.quoteMgmtRenderPayload = '';
                            this.quoteMgmtAISourceText = '';
                            this.quoteMgmtAIFile = null;
                            this.quoteMgmtAIExtracting = false;
                            this.quoteMgmtAIDraft = null;
                        } catch (e) {
                            _showToast('載入報價失敗: ' + (e.response?.data?.detail || e.message));
                            this.quoteMgmtSelectedQuote = null;
                        } finally {
                            this.quoteMgmtEditorLoading = false;
                        }
                    },
                    onQuoteMgmtAIFileSelected(e) {
                        const f = e?.target?.files?.[0] || null;
                        this.quoteMgmtAIFile = f || null;
                    },
                    async quoteMgmtTryRestartService(serviceKey) {
                        const adminApiRoot = (ADMIN_API || '').toString().replace(/\/api\/?$/, '');
                        const candidates = [
                            `${ADMIN_API}/service/restart/${serviceKey}`,
                            `${adminApiRoot}/service/restart/${serviceKey}`,
                            `${ADMIN_API}/server/service/restart/${serviceKey}`,
                            `${ADMIN_API}/admin/service/restart/${serviceKey}`
                        ];
                        let lastErr = null;
                        for (const url of candidates) {
                            try {
                                await axios.post(url);
                                return true;
                            } catch (e) {
                                lastErr = e;
                                const status = e?.response?.status;
                                if (status && status !== 404) throw e;
                            }
                        }
                        const detail = lastErr?.response?.data?.detail || lastErr?.message || 'Not Found';
                        throw new Error(detail);
                    },
                    async runQuoteMgmtAIDraftFromText() {
                        const text = (this.quoteMgmtAISourceText || '').trim();
                        if (!text) return;
                        this.quoteMgmtAIExtracting = true;
                        try {
                            const callOnce = async () => {
                                return await axios.post(`${ADMIN_API}/ai/quote/draft_from_text`, {
                                    text,
                                    language: 'Traditional Chinese (繁體中文)'
                                });
                            };
                            let res;
                            try {
                                res = await callOnce();
                            } catch (err) {
                                const status = err?.response?.status;
                                const detail = err?.response?.data?.detail || err?.message;
                                const detailLower = (detail || '').toString().toLowerCase();
                                const shouldPromptRestart = (status === 404 && detailLower.includes('not found')) || (status === 503 && detailLower.includes('ai service')) || status === 504;
                                if (shouldPromptRestart) {
                                    const ok = confirm(status === 404
                                        ? 'AI 服務尚未更新或尚未重啟（回傳 404 Not Found）。\n\n要嘗試重啟 AI Service 嗎？（需要系統管理員權限）'
                                        : (status === 504
                                            ? 'AI 服務逾時（回傳 504 Timeout）。\n\n要嘗試重啟 AI Service 嗎？（需要系統管理員權限）'
                                            : 'AI 服務目前無法連線（回傳 503 Service Unavailable）。\n\n要嘗試重啟 AI Service 嗎？（需要系統管理員權限）'));
                                    if (ok) {
                                        try {
                                            await this.quoteMgmtTryRestartService('ai_service');
                                            await new Promise(r => setTimeout(r, 2500));
                                            res = await callOnce();
                                        } catch (e2) {
                                            const msg = (e2?.response?.data?.detail || e2?.message || '').toString();
                                            if (msg.toLowerCase().includes('not found')) {
                                                _showToast('重啟 AI Service 失敗: Not Found\n\n此系統目前無法透過 UI 重啟服務（可能 admin_api 尚未更新或未提供重啟端點）。\n請在主機上手動執行：\n- docker compose restart ai_service\n- docker compose restart admin_api');
                                            } else {
                                                _showToast('重啟 AI Service 失敗: ' + msg);
                                            }
                                            throw err;
                                        }
                                    } else {
                                        throw err;
                                    }
                                } else {
                                    throw err;
                                }
                            }
                            this.quoteMgmtAIDraft = res?.data || null;
                        } catch (e) {
                            _showToast('AI 解析失敗: ' + (e.response?.data?.detail || e.message));
                            this.quoteMgmtAIDraft = null;
                        } finally {
                            this.quoteMgmtAIExtracting = false;
                        }
                    },
                    async runQuoteMgmtAIDraftFromFile() {
                        if (!this.quoteMgmtAIFile) return;
                        this.quoteMgmtAIExtracting = true;
                        try {
                            const callOnce = async () => {
                                const fd = new FormData();
                                fd.append('file', this.quoteMgmtAIFile);
                                fd.append('language', 'Traditional Chinese (繁體中文)');
                                return await axios.post(`${ADMIN_API}/ai/quote/draft_from_file`, fd, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                });
                            };
                            let res;
                            try {
                                res = await callOnce();
                            } catch (err) {
                                const status = err?.response?.status;
                                const detail = err?.response?.data?.detail || err?.message;
                                const detailLower = (detail || '').toString().toLowerCase();
                                const shouldPromptRestart = (status === 404 && detailLower.includes('not found')) || (status === 503 && detailLower.includes('ai service')) || status === 504;
                                if (shouldPromptRestart) {
                                    const ok = confirm(status === 404
                                        ? 'AI 服務尚未更新或尚未重啟（回傳 404 Not Found）。\n\n要嘗試重啟 AI Service 嗎？（需要系統管理員權限）'
                                        : (status === 504
                                            ? 'AI 服務逾時（回傳 504 Timeout）。\n\n要嘗試重啟 AI Service 嗎？（需要系統管理員權限）'
                                            : 'AI 服務目前無法連線（回傳 503 Service Unavailable）。\n\n要嘗試重啟 AI Service 嗎？（需要系統管理員權限）'));
                                    if (ok) {
                                        try {
                                            await this.quoteMgmtTryRestartService('ai_service');
                                            await new Promise(r => setTimeout(r, 2500));
                                            res = await callOnce();
                                        } catch (e2) {
                                            const msg = (e2?.response?.data?.detail || e2?.message || '').toString();
                                            if (msg.toLowerCase().includes('not found')) {
                                                _showToast('重啟 AI Service 失敗: Not Found\n\n此系統目前無法透過 UI 重啟服務（可能 admin_api 尚未更新或未提供重啟端點）。\n請在主機上手動執行：\n- docker compose restart ai_service\n- docker compose restart admin_api');
                                            } else {
                                                _showToast('重啟 AI Service 失敗: ' + msg);
                                            }
                                            throw err;
                                        }
                                    } else {
                                        throw err;
                                    }
                                } else {
                                    throw err;
                                }
                            }
                            this.quoteMgmtAIDraft = res?.data || null;
                        } catch (e) {
                            _showToast('AI 解析失敗: ' + (e.response?.data?.detail || e.message));
                            this.quoteMgmtAIDraft = null;
                        } finally {
                            this.quoteMgmtAIExtracting = false;
                        }
                    },
                    async applyQuoteMgmtAIDraftToQuote() {
                        const qid = this.quoteMgmtSelectedQuote?.id;
                        const draft = this.quoteMgmtAIDraft;
                        if (!qid || !draft || !Array.isArray(draft.items) || draft.items.length === 0) return;
                        this.quoteMgmtSaving = true;
                        try {
                            const pbRaw = this.quoteMgmtAIPricingPricebookId;
                            const pbId = pbRaw === '' || pbRaw === null || pbRaw === undefined ? null : Number(pbRaw);
                            const req = {
                                customer_name: draft.customer_name || null,
                                notes: draft.notes || null,
                                terms: draft.terms || null,
                                items: Array.isArray(draft.items) ? draft.items : [],
                                pricebook_id: pbId,
                                prefer_pricebook: !!this.quoteMgmtAIPreferPricebook
                            };
                            let usedNew = false;
                            try {
                                const res = await axios.post(`${ADMIN_API}/quotes/${qid}/apply_ai_draft`, req);
                                const data = res?.data || {};
                                usedNew = true;
                                const warn = Array.isArray(data.warnings) ? data.warnings : [];
                                const addedCount = Number(data.added_count ?? 0) || 0;
                                const usedPbId = data.pricebook_id === null || data.pricebook_id === undefined ? null : Number(data.pricebook_id || 0);
                                let pbName = '';
                                if (usedPbId && Array.isArray(this.quoteMgmtPricebooks)) {
                                    const hit = this.quoteMgmtPricebooks.find(x => Number(x?.id || 0) === usedPbId);
                                    pbName = (hit?.name || '').toString().trim();
                                }
                                const pbText = pbName ? `（價目表：${pbName}）` : (usedPbId ? `（價目表 #${usedPbId}）` : '（未使用價目表）');
                                const lines = [`已加入 ${addedCount} 筆明細 ${pbText}`];
                                if (warn.length) {
                                    lines.push('', '提醒：', '- ' + warn.join('\n- '));
                                }
                                _showToast(lines.join('\n'));
                            } catch (err) {
                                const status = err?.response?.status;
                                const detail = (err?.response?.data?.detail || '').toString();
                                if (status === 404 && detail === 'Not Found') {
                                } else {
                                    throw err;
                                }
                            }
                            if (!usedNew) {
                                for (const it of draft.items) {
                                    if (!it || !it.title) continue;
                                    const payload = {
                                        product_id: null,
                                        product_spec_id: null,
                                        title: (it.title || '').toString().trim() || null,
                                        spec_text: (it.spec_text || '').toString().trim() || null,
                                        qty: Number(it.qty ?? 1) || 1,
                                        unit: (it.unit || '').toString().trim() || null,
                                        unit_price: it.unit_price === null || it.unit_price === undefined ? 0 : Number(it.unit_price || 0),
                                        discount_type: (it.discount_type || 'none').toString().toLowerCase(),
                                        discount_value: Number(it.discount_value || 0),
                                        remark: (it.remark || '').toString().trim() || null
                                    };
                                    await axios.post(`${ADMIN_API}/quotes/${qid}/items`, payload);
                                }
                                if (!this.quoteMgmtEditForm.customer_name && draft.customer_name) {
                                    this.quoteMgmtEditForm.customer_name = draft.customer_name;
                                }
                                if (!this.quoteMgmtEditForm.notes && draft.notes) {
                                    this.quoteMgmtEditForm.notes = draft.notes;
                                }
                                if (!this.quoteMgmtEditForm.terms && draft.terms) {
                                    this.quoteMgmtEditForm.terms = draft.terms;
                                }
                                await this.saveQuoteMgmt();
                            }
                            await this.loadQuoteMgmtQuote(qid);
                            await this.fetchQuoteMgmtQuotes();
                        } catch (e) {
                            _showToast('套用草稿失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quoteMgmtSaving = false;
                        }
                    },
                    async saveQuoteMgmt() {
                        const id = this.quoteMgmtSelectedQuote?.id;
                        if (!id) return;
                        this.quoteMgmtSaving = true;
                        try {
                            const payload = {
                                status: this.quoteMgmtEditForm.status,
                                customer_name: (this.quoteMgmtEditForm.customer_name || '').trim(),
                                contact_id: this.quoteMgmtEditForm.contact_id ?? null,
                                tax_mode: this.quoteMgmtEditForm.tax_mode,
                                tax_rate: this.quoteMgmtEditForm.tax_rate,
                                notes: this.quoteMgmtEditForm.notes || null,
                                terms: this.quoteMgmtEditForm.terms || null
                            };
                            await axios.put(`${ADMIN_API}/quotes/${id}`, payload);
                            await this.loadQuoteMgmtQuote(id);
                            await this.fetchQuoteMgmtQuotes();
                        } catch (e) {
                            _showToast('儲存報價失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quoteMgmtSaving = false;
                        }
                    },
                    quoteMgmtCalcLineTotal(qty, unitPrice, discountType, discountValue) {
                        const q = Number(qty || 0);
                        const p = Number(unitPrice || 0);
                        const raw = q * p;
                        const t = (discountType || 'none').toString().toLowerCase();
                        const v = Number(discountValue || 0);
                        if (!raw) return 0;
                        if (t === 'percent') return Math.max(0, raw * (1 - v / 100));
                        if (t === 'amount') return Math.max(0, raw - v);
                        return raw;
                    },
                    formatQuoteMgmtProductLabel(p) {
                        if (!p) return '';
                        const name = (p.name || '').toString().trim();
                        const sku = (p.sku || '').toString().trim();
                        if (name && sku) return `${name} (${sku})`;
                        return name || sku || '';
                    },
                    clearQuoteMgmtProductSelection() {
                        this.quoteMgmtNewItem.product_id = null;
                        this.quoteMgmtNewItem.product_spec_id = null;
                        this.quoteMgmtNewItem.product_label = '';
                        this.quoteMgmtProductSpecs = [];
                    },
                    async onQuoteMgmtProductLabelInput() {
                        const text = (this.quoteMgmtNewItem.product_label || '').trim();
                        if (!text) {
                            this.clearQuoteMgmtProductSelection();
                            return;
                        }
                        const products = this.quoteMgmtProducts || [];
                        const norm = (s) => (s || '').toString().trim().toLowerCase();
                        let match = products.find(p => norm(this.formatQuoteMgmtProductLabel(p)) === norm(text));
                        if (!match) {
                            match = products.find(p => norm(p.name) === norm(text) || norm(p.sku) === norm(text));
                        }
                        if (match) {
                            if (this.quoteMgmtNewItem.product_id !== match.id) {
                                this.quoteMgmtNewItem.product_id = match.id;
                                await this.onQuoteMgmtProductChanged();
                            }
                            return;
                        }
                        if (this.quoteMgmtNewItem.product_id) {
                            this.quoteMgmtNewItem.product_id = null;
                            this.quoteMgmtNewItem.product_spec_id = null;
                            this.quoteMgmtProductSpecs = [];
                        }
                    },
                    async onQuoteMgmtProductLabelChanged() {
                        const text = (this.quoteMgmtNewItem.product_label || '').trim();
                        if (!text) {
                            this.clearQuoteMgmtProductSelection();
                            return;
                        }
                        const products = this.quoteMgmtProducts || [];
                        const norm = (s) => (s || '').toString().trim().toLowerCase();
                        let match = products.find(p => norm(this.formatQuoteMgmtProductLabel(p)) === norm(text));
                        if (!match) {
                            match = products.find(p => norm(p.name) === norm(text) || norm(p.sku) === norm(text));
                        }
                        if (match) {
                            this.quoteMgmtNewItem.product_id = match.id;
                            await this.onQuoteMgmtProductChanged();
                            return;
                        }
                        this.quoteMgmtNewItem.product_id = null;
                        this.quoteMgmtNewItem.product_spec_id = null;
                        this.quoteMgmtProductSpecs = [];
                        if (!this.quoteMgmtNewItem.title) {
                            this.quoteMgmtNewItem.title = text;
                        }
                    },
                    async onQuoteMgmtProductChanged() {
                        const pid = this.quoteMgmtNewItem.product_id;
                        this.quoteMgmtNewItem.product_spec_id = null;
                        await this.fetchQuoteMgmtProductSpecs(pid);
                        if (pid) {
                            const p = (this.quoteMgmtProducts || []).find(x => x.id === pid);
                            if (p) {
                                const label = this.formatQuoteMgmtProductLabel(p);
                                if (label) this.quoteMgmtNewItem.product_label = label;
                            }
                            if (p && !this.quoteMgmtNewItem.title) {
                                this.quoteMgmtNewItem.title = p.sku ? `${p.name} (${p.sku})` : (p.name || '');
                            }
                        }
                    },
                    onQuoteMgmtSpecChanged() {
                        const sid = this.quoteMgmtNewItem.product_spec_id;
                        if (!sid) return;
                        const s = (this.quoteMgmtProductSpecs || []).find(x => x.id === sid);
                        if (s && !this.quoteMgmtNewItem.spec_text) {
                            this.quoteMgmtNewItem.spec_text = `${s.spec_name}: ${s.spec_value}`;
                        }
                    },
                    async addQuoteMgmtItem() {
                        const qid = this.quoteMgmtSelectedQuote?.id;
                        if (!qid) return;
                        const title = (this.quoteMgmtNewItem.title || '').trim();
                        if (!title && !this.quoteMgmtNewItem.product_id) {
                            _showToast('請選擇產品或輸入品項名稱');
                            return;
                        }
                        this.quoteMgmtSaving = true;
                        try {
                            const payload = {
                                product_id: this.quoteMgmtNewItem.product_id ?? null,
                                product_spec_id: this.quoteMgmtNewItem.product_spec_id ?? null,
                                title: title || null,
                                spec_text: (this.quoteMgmtNewItem.spec_text || '').trim() || null,
                                qty: this.quoteMgmtNewItem.qty,
                                unit: (this.quoteMgmtNewItem.unit || '').trim() || null,
                                unit_price: this.quoteMgmtNewItem.unit_price,
                                discount_type: this.quoteMgmtNewItem.discount_type,
                                discount_value: this.quoteMgmtNewItem.discount_value,
                                remark: (this.quoteMgmtNewItem.remark || '').trim() || null
                            };
                            await axios.post(`${ADMIN_API}/quotes/${qid}/items`, payload);
                            await this.loadQuoteMgmtQuote(qid);
                            await this.fetchQuoteMgmtQuotes();
                        } catch (e) {
                            _showToast('新增明細失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quoteMgmtSaving = false;
                        }
                    },
                    async updateQuoteMgmtItem(it) {
                        const qid = this.quoteMgmtSelectedQuote?.id;
                        if (!qid || !it || !it.id) return;
                        this.quoteMgmtSaving = true;
                        try {
                            const payload = {
                                title: it.title,
                                spec_text: it.spec_text,
                                qty: it.qty,
                                unit: it.unit,
                                unit_price: it.unit_price,
                                discount_type: it.discount_type,
                                discount_value: it.discount_value,
                                remark: it.remark
                            };
                            await axios.put(`${ADMIN_API}/quotes/${qid}/items/${it.id}`, payload);
                            await this.loadQuoteMgmtQuote(qid);
                            await this.fetchQuoteMgmtQuotes();
                        } catch (e) {
                            _showToast('更新明細失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quoteMgmtSaving = false;
                        }
                    },
                    async deleteQuoteMgmtItem(it) {
                        const qid = this.quoteMgmtSelectedQuote?.id;
                        if (!qid || !it || !it.id) return;
                        if (!confirm('確定要刪除此明細嗎？')) return;
                        this.quoteMgmtSaving = true;
                        try {
                            await axios.delete(`${ADMIN_API}/quotes/${qid}/items/${it.id}`);
                            await this.loadQuoteMgmtQuote(qid);
                            await this.fetchQuoteMgmtQuotes();
                        } catch (e) {
                            _showToast('刪除明細失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quoteMgmtSaving = false;
                        }
                    },
                    async openQuoteMgmtRenderPayload() {
                        const qid = this.quoteMgmtSelectedQuote?.id;
                        if (!qid) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/quotes/${qid}/render`);
                            this.quoteMgmtRenderPayload = JSON.stringify(res.data || {}, null, 2);
                        } catch (e) {
                            _showToast('取得 Render Payload 失敗: ' + (e.response?.data?.detail || e.message));
                            this.quoteMgmtRenderPayload = '';
                        }
                    },
                    async openQuoteMgmtHtmlPreview() {
                        const q = this.quoteMgmtSelectedQuote;
                        const qid = q?.id;
                        if (!qid) return;
                        this.quoteMgmtHtmlLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/quotes/${qid}/html`, { responseType: 'text' });
                            const html = (res.data ?? '').toString();
                            const w = window.open('', '_blank');
                            if (!w) {
                                _showToast('瀏覽器已阻擋彈出視窗，請允許後重試。');
                                return;
                            }
                            w.document.open();
                            w.document.write(html);
                            w.document.close();
                        } catch (e) {
                            _showToast('預覽失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quoteMgmtHtmlLoading = false;
                        }
                    },
                    async downloadQuoteMgmtPdf() {
                        const q = this.quoteMgmtSelectedQuote;
                        const qid = q?.id;
                        if (!qid) return;
                        this.quoteMgmtPdfLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/quotes/${qid}/pdf`, { responseType: 'blob' });
                            const quoteNo = (q?.quote_no ?? 'quote').toString().replaceAll('/', '-').replaceAll('\\', '-');
                            const filename = `${quoteNo || 'quote'}.pdf`;
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        } catch (e) {
                            _showToast('下載失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quoteMgmtPdfLoading = false;
                        }
                    },

                    async downloadQuoteMgmtInvoicePdf() {
                        const q = this.quoteMgmtSelectedQuote;
                        const qid = q?.id;
                        if (!qid) return;
                        this.quoteMgmtInvoicePdfLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/quotes/${qid}/invoice_pdf`, { responseType: 'blob' });
                            const quoteNo = (q?.quote_no ?? 'invoice').toString().replaceAll('/', '-').replaceAll('\\', '-');
                            const filename = `INV-${quoteNo || 'invoice'}.pdf`;
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        } catch (e) {
                            _showToast('發票 PDF 下載失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quoteMgmtInvoicePdfLoading = false;
                        }
                    },

                    // --- Domains ---
                    async fetchDomains() {
                        if (!this.token) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/domains`);
                            this.domains = res.data;
                            const firstDomainId = (this.domains && this.domains.length > 0) ? this.domains[0].id : null;
                            if (!this.newUser.domain_id && firstDomainId) this.newUser.domain_id = firstDomainId;
                            if (!this.newGroup.domain_id && firstDomainId) this.newGroup.domain_id = firstDomainId;
                            if (!this.newDepartment.domain_id && firstDomainId) this.newDepartment.domain_id = firstDomainId;
                        } catch (e) { console.error(e); }
                    },
                    getDomainName(domainId) {
                        const d = (this.domains || []).find(x => x.id === domainId);
                        return d ? d.name : '';
                    },
                    normalizePermissionKeys(keys) {
                        const arr = Array.isArray(keys) ? keys : [];
                        return arr.map(x => (x ?? '').toString().trim()).filter(x => x);
                    },
                    permissionsToJson(keys) {
                        const list = this.normalizePermissionKeys(keys);
                        return list.length > 0 ? JSON.stringify(list) : null;
                    },
                    parsePermissionKeys(raw) {
                        if (!raw) return [];
                        if (Array.isArray(raw)) return this.normalizePermissionKeys(raw);
                        try {
                            const v = JSON.parse((raw ?? '').toString());
                            if (Array.isArray(v)) return this.normalizePermissionKeys(v);
                            if (v && typeof v === 'object') {
                                const arr = Array.isArray(v.permissions) ? v.permissions : (Array.isArray(v.views) ? v.views : []);
                                return this.normalizePermissionKeys(arr);
                            }
                        } catch (e) {
                        }
                        return [];
                    },
                    formatDepartmentPermissions(dept) {
                        const keys = this.parsePermissionKeys(dept?.permissions);
                        if (!keys || keys.length === 0) return [];
                        const map = {};
                        for (const opt of (this.departmentPermissionOptions || [])) {
                            if (opt && opt.key) map[opt.key] = opt.label || opt.key;
                        }
                        return keys.map(k => map[k] || k);
                    },
                    async addDomain() {
                        try {
                            const payload = { ...this.newDomain };
                            if (payload.max_quota) payload.max_quota = payload.max_quota * 1024 * 1024;

                            await axios.post(`${ADMIN_API}/domains`, payload);
                            this.newDomain = { name: '', max_users: 0, max_aliases: 0, max_quota: 0, audit_email: '' };
                            this.fetchDomains();
                            _showToast('網域已新增！');
                        } catch (e) { _showToast('新增網域時發生錯誤: ' + (e.response?.data?.detail || e.message)); }
                    },
                    startEditDomain(domain) {
                        this.editingDomain = domain;
                        this.editDomainForm = {
                            id: domain.id,
                            name: domain.name,
                            max_users: domain.max_users,
                            max_aliases: domain.max_aliases,
                            max_quota: domain.max_quota ? Math.round(domain.max_quota / 1024 / 1024) : 0,
                            audit_email: domain.audit_email,
                            active: domain.active
                        };
                    },
                    async updateDomain() {
                        try {
                            const payload = { ...this.editDomainForm };
                            if (payload.max_quota) payload.max_quota = payload.max_quota * 1024 * 1024;

                            await axios.put(`${ADMIN_API}/domains/${this.editingDomain.id}`, payload);
                            this.editingDomain = null;
                            this.fetchDomains();
                            _showToast('網域已更新！');
                        } catch (e) { _showToast('更新網域時發生錯誤: ' + (e.response?.data?.detail || e.message)); }
                    },
                    async deleteDomain(domainId) {
                        if (!confirm('確定要刪除此網域嗎？所有相關的使用者和群組也會被刪除！')) return;
                        try {
                            await axios.delete(`${ADMIN_API}/domains/${domainId}`);
                            this.fetchDomains();
                            _showToast('網域已刪除！');
                        } catch (e) { _showToast('刪除網域時發生錯誤: ' + (e.response?.data?.detail || e.message)); }
                    },

                    // --- Departments ---
                    async fetchDepartments(domainId) {
                        if (!this.token) return;
                        if (!this.isSuperAdmin && !this.isDomainAdmin) return;
                        try {
                            let did = domainId;
                            if (this.isSuperAdmin) {
                                if (!did && this.domains && this.domains.length > 0) did = this.domains[0].id;
                            }
                            const config = did ? { params: { domain_id: did } } : undefined;
                            const res = await axios.get(`${ADMIN_API}/departments`, config);
                            this.departments = res.data;
                        } catch (e) {
                            console.warn('fetchDepartments:', e?.response?.status, e?.message);
                        }
                    },
                    async addDepartment() {
                        try {
                            const did = this.isSuperAdmin ? this.newDepartment.domain_id : this.currentUser?.domain_id;
                            if (!did) {
                                _showToast('請選擇網域');
                                return;
                            }
                            if (!this.newDepartment.name || !this.newDepartment.name.trim()) {
                                _showToast('請輸入部門名稱');
                                return;
                            }
                            const payload = {
                                domain_id: did,
                                name: this.newDepartment.name.trim(),
                                description: this.newDepartment.description || null,
                                permissions: this.permissionsToJson(this.newDepartment.permission_keys)
                            };
                            await axios.post(`${ADMIN_API}/departments`, payload);
                            this.newDepartment.name = '';
                            this.newDepartment.description = '';
                            this.newDepartment.permission_keys = [];
                            await this.fetchDepartments(did);
                            _showToast('部門已建立！');
                        } catch (e) {
                            _showToast('建立部門時發生錯誤: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    startEditDepartment(dept) {
                        this.editingDepartment = dept;
                        this.editDepartmentForm = {
                            id: dept.id,
                            domain_id: dept.domain_id,
                            name: dept.name,
                            description: dept.description || '',
                            permission_keys: this.parsePermissionKeys(dept.permissions)
                        };
                    },
                    async updateDepartment() {
                        try {
                            const payload = {
                                name: this.editDepartmentForm.name,
                                description: this.editDepartmentForm.description,
                                permissions: this.permissionsToJson(this.editDepartmentForm.permission_keys)
                            };
                            await axios.put(`${ADMIN_API}/departments/${this.editingDepartment.id}`, payload);
                            const did = this.editDepartmentForm.domain_id || (this.isSuperAdmin ? this.newDepartment.domain_id : this.currentUser?.domain_id);
                            this.editingDepartment = null;
                            await this.fetchDepartments(did);
                            _showToast('部門已更新！');
                        } catch (e) {
                            _showToast('更新部門時發生錯誤: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async deleteDepartment(deptId) {
                        if (!confirm('確定要刪除此部門嗎？')) return;
                        try {
                            await axios.delete(`${ADMIN_API}/departments/${deptId}`);
                            const did = this.isSuperAdmin ? (this.newDepartment.domain_id || this.domains?.[0]?.id) : this.currentUser?.domain_id;
                            await this.fetchDepartments(did);
                        } catch (e) {
                            _showToast('刪除部門時發生錯誤: ' + (e.response?.data?.detail || e.message));
                        }
                    },

                    // --- Users ---
                    async fetchUsers() {
                        if (!this.token) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/users`);
                            this.users = res.data;
                        } catch (e) { console.error(e); }
                    },
                    async addUser() {
                        try {
                            if (!this.newUser.domain_id) {
                                _showToast('請選擇網域');
                                return;
                            }
                            const payload = { ...this.newUser };
                            if (payload.quota) payload.quota = payload.quota * 1024 * 1024;

                            await axios.post(`${ADMIN_API}/users`, payload);
                            this.newUser = { username: '', password: '', domain_id: this.newUser.domain_id, role: 'user', quota: 0, company_name: '', department_id: null, phone_ext: '' };
                            this.fetchUsers();
                            _showToast('使用者已建立！');
                        } catch (e) {
                            _showToast('建立使用者時發生錯誤: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async deleteUser(userId) {
                        if (!confirm('確定要刪除此使用者嗎？')) return;
                        try {
                            await axios.delete(`${ADMIN_API}/users/${userId}`);
                            this.fetchUsers();
                        } catch (e) { _showToast('刪除使用者時發生錯誤: ' + (e.response?.data?.detail || e.message)); }
                    },
                    startEditUser(user) {
                        this.editingUser = user;
                        const username = user.email.split('@')[0];
                        this.editUserForm = {
                            ...user,
                            username: username,
                            password: '', // Clear password field
                            quota: user.quota ? Math.round(user.quota / 1024 / 1024) : 0 // Convert Bytes to MB
                        };
                        this.fetchDepartments(this.editUserForm.domain_id);
                    },
                    async updateUser() {
                        try {
                            const payload = { ...this.editUserForm };
                            if (!payload.password) delete payload.password;
                            if (payload.quota) payload.quota = payload.quota * 1024 * 1024; // Convert MB to Bytes

                            await axios.put(`${ADMIN_API}/users/${this.editingUser.id}`, payload);
                            this.editingUser = null;
                            this.fetchUsers();
                            _showToast('使用者已更新！');
                        } catch (e) { _showToast('更新使用者時發生錯誤: ' + (e.response?.data?.detail || e.message)); }
                    },

                    // --- Groups ---
                    async fetchGroups() {
                        if (!this.token) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/groups`);
                            this.groups = res.data;
                        } catch (e) { console.error(e); }
                    },
                    async addGroup() {
                        try {
                            if (!this.newGroup.name || !this.newGroup.domain_id || this.newGroup.members.length === 0) {
                                _showToast('請填寫完整資訊並選擇至少一位成員');
                                return;
                            }
                            await axios.post(`${ADMIN_API}/groups`, this.newGroup);
                            this.newGroup = { name: '', domain_id: this.newGroup.domain_id, members: [] };
                            this.fetchGroups();
                            _showToast('群組已建立！');
                        } catch (e) {
                            _showToast('建立群組時發生錯誤: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    startEditGroup(group) {
                        this.editingGroup = JSON.parse(JSON.stringify(group));
                    },
                    async updateGroup() {
                        try {
                            await axios.put(`${ADMIN_API}/groups/${this.editingGroup.email}`, { members: this.editingGroup.members });
                            this.editingGroup = null;
                            this.fetchGroups();
                            _showToast('群組已更新！');
                        } catch (e) {
                            _showToast('更新群組時發生錯誤: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async deleteGroup(email) {
                        if (!confirm(`確定要刪除群組 ${email} 嗎？`)) return;
                        try {
                            await axios.delete(`${ADMIN_API}/groups/${email}`);
                            this.fetchGroups();
                        } catch (e) { _showToast('刪除群組時發生錯誤: ' + (e.response?.data?.detail || e.message)); }
                    },

                    // --- Let's Encrypt ---
                    async fetchLeCerts() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/ssl/letsencrypt/list`);
                            this.letsEncryptCerts = res.data;
                        } catch (e) { console.error(e); }
                    },
                    async requestLeCert() {
                        if (!this.newLeCert.domain || !this.newLeCert.email) {
                            _showToast('請填寫網域和 Email');
                            return;
                        }
                        try {
                            _showToast('正在申請憑證，請稍候... (可能需要 10-30 秒)');
                            await axios.post(`${ADMIN_API}/ssl/letsencrypt/request`, this.newLeCert);
                            _showToast('憑證申請成功！');
                            this.newLeCert.domain = '';
                            this.fetchLeCerts();
                        } catch (e) {
                            _showToast('申請失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async applyLeCert(domain) {
                        if (!confirm(`確定要將 ${domain} 的憑證套用到 Postfix 和 Dovecot 嗎？服務將會重啟。`)) return;
                        try {
                            await axios.post(`${ADMIN_API}/ssl/letsencrypt/apply`, { domain });
                            _showToast('憑證已套用！服務正在重啟...');
                            setTimeout(() => this.fetchServiceStatus(), 2000);
                        } catch (e) {
                            _showToast('套用失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async deleteLeCert(domain) {
                        if (!confirm(`確定要刪除 ${domain} 的憑證嗎？此操作無法復原。`)) return;
                        try {
                            await axios.delete(`${ADMIN_API}/ssl/letsencrypt/${encodeURIComponent(domain)}`);
                            _showToast(`憑證 ${domain} 已刪除`);
                            await this.fetchLeCerts();
                            this.sslInfo = null;
                            this.fetchSslInfo();
                        } catch (e) {
                            _showToast('刪除失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },

                    // --- Security Filter ---
                    async handleAdminApiNotFound(e) {
                        const status = e?.response?.status;
                        const detail = e?.response?.data?.detail;
                        if (status !== 404) return false;
                        if (!(detail === 'Not Found' || /not found/i.test(String(detail || '')))) return false;

                        if (!this.isSuperAdmin) {
                            _showToast('後端 API 似乎尚未載入此功能（404）。\n\n請聯絡系統管理員重啟 admin_api 或重新部署後端服務。');
                            return true;
                        }

                        const ok = confirm('後端 API 似乎尚未載入此功能（404）。可能 admin_api 尚未重啟套用新版本。\n\n要嘗試重啟 admin_api 嗎？（可能會暫時斷線，稍後請重新整理）');
                        if (!ok) return true;
                        try {
                            await axios.post(`${ADMIN_API}/service/restart/admin_api`);
                        } catch (restartErr) {
                            _showToast('重啟失敗: ' + (restartErr?.response?.data?.detail || restartErr?.message || 'unknown error'));
                            _showToast('如果你不是用 docker 執行，請手動重啟後端服務（uvicorn / docker-compose）。');
                            return true;
                        }
                        _showToast('已送出重啟指令，3 秒後自動重新整理。');
                        setTimeout(() => window.location.reload(), 3000);
                        return true;
                    },
                    handleRspamd503(e) {
                        const status = e?.response?.status;
                        if (status !== 503) return false;
                        const detail = e?.response?.data?.detail;
                        _showToast('Rspamd 服務目前不可用：' + (detail || 'Service Unavailable') + '\n\n請確認 Rspamd controller 已啟動，且已設定 RSPAMD_CONTROLLER_PASSWORD / RSPAMD_CONTROLLER_HOST / RSPAMD_CONTROLLER_PORT。');
                        return true;
                    },
                    async openSecurityQuarantine(mode) {
                        try {
                            await this.openQuarantineModal(mode);
                        } catch (e) {
                            _showToast('開啟隔離區失敗: ' + (e?.response?.data?.detail || e?.message || 'unknown error'));
                        }
                    },
                    quarantineBasePath() {
                        return (this.quarantineMode === 'phishing') ? 'phishing' : 'spam';
                    },
                    quarantineDefaultFolder() {
                        return (this.quarantineMode === 'phishing') ? 'Phishing' : 'Spam';
                    },
                    async openQuarantineModal(mode) {
                        this.quarantineMode = (mode === 'phishing') ? 'phishing' : 'spam';
                        this.quarantineFolders = [];
                        this.quarantineFolder = this.quarantineDefaultFolder();
                        this.quarantineItems = [];
                        this.quarantineTotal = 0;
                        this.quarantineOffset = 0;
                        this.quarantineSearch = '';
                        this.quarantineError = '';
                        this.quarantineActionLoadingId = null;
                        this.quarantineShowAnalysis = (this.quarantineMode === 'phishing');
                        this.quarantineCacheInfo = null;
                        this.quarantineSelectedIds = [];
                        this.quarantineBatch = { running: false, action: '', done: 0, total: 0, errors: [] };
                        this.showQuarantineModal = true;
                        this.quarantineLoading = true;
                        this.quarantineError = '載入中...';
                        try {
                            if (!await this.ensureMailSession('開啟隔離區')) {
                                this.quarantineError = (this.webmailLastError || '信箱工作階段尚未建立或已過期，無法載入隔離區。請重新登入後再試一次。').toString();
                                return;
                            }
                            this.quarantineError = '';
                            await this.fetchQuarantineFolders();
                            if (!Array.isArray(this.quarantineFolders) || this.quarantineFolders.length === 0) {
                                const label = (this.quarantineMode === 'phishing') ? 'AI 詐騙隔離區' : '垃圾郵件隔離區';
                                this.quarantineError = `${label} 找不到對應的隔離資料夾。\n\n請確認：\n1) 信箱 IMAP 內已建立資料夾（建議：Spam / Junk / Quarantine 或 Phishing）\n2) 郵件過濾規則/服務已將命中的郵件移入該資料夾`;
                                return;
                            }
                            if (this.quarantineMode === 'phishing') {
                                try {
                                    await this.fetchPhishingQuarantineCache();
                                } catch (e) {
                                }
                            }
                            await this.fetchQuarantineList(true);
                        } finally {
                            this.quarantineLoading = false;
                        }
                    },
                    closeQuarantineModal() {
                        this.showQuarantineModal = false;
                        this.quarantineError = '';
                        this.quarantineActionLoadingId = null;
                        this.quarantineSelectedIds = [];
                        this.quarantineBatch = { running: false, action: '', done: 0, total: 0, errors: [] };
                    },
                    async fetchQuarantineFolders(_retry = false) {
                        if (!await this.ensureMailSession('載入隔離區資料夾')) return;
                        const base = this.quarantineBasePath();
                        try {
                            const res = await axios.get(`${ADMIN_API}/${base}/quarantine/folders`, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const folders = (res?.data?.folders || []);
                            this.quarantineFolders = Array.isArray(folders) ? folders : [];
                            if (this.quarantineFolders.length) {
                                if (!this.quarantineFolders.includes(this.quarantineFolder)) {
                                    this.quarantineFolder = this.quarantineFolders[0];
                                }
                            }
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                const canRetry = this.handleMailAuth401(e, '載入隔離區資料夾');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('載入隔離區資料夾');
                                        if (ok) return await this.fetchQuarantineFolders(true);
                                    } catch (err) {
                                    }
                                }
                                return;
                            }
                            this.quarantineFolders = [];
                            this.quarantineFolder = this.quarantineDefaultFolder();
                            this.quarantineError = (e?.response?.data?.detail || e?.message || '載入隔離區資料夾失敗').toString();
                        }
                    },
                    async fetchQuarantineList(resetOffset = false, _retry = false) {
                        if (!this.showQuarantineModal) return;
                        if (!await this.ensureMailSession('載入隔離信件')) return;
                        const base = this.quarantineBasePath();
                        if (resetOffset) this.quarantineOffset = 0;
                        this.quarantineLoading = true;
                        this.quarantineError = '';
                        try {
                            const params = {
                                folder: this.quarantineFolder || this.quarantineDefaultFolder(),
                                limit: this.quarantineLimit,
                                offset: this.quarantineOffset
                            };
                            if (base === 'phishing') {
                                if (this.quarantineShowAnalysis) {
                                    params.include_scores = true;
                                    params.include_details = true;
                                    params.label_threshold = Math.max(0, Math.min(Number(this.quarantineScanPhishingThreshold || 0.7), 1));
                                }
                            } else {
                                if (this.quarantineShowAnalysis) {
                                    params.include_details = true;
                                }
                            }
                            const s = (this.quarantineSearch || '').trim();
                            if (s) params.search = s;
                            const res = await axios.get(`${ADMIN_API}/${base}/quarantine/list`, {
                                params,
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            this.quarantineItems = Array.isArray(res?.data?.items) ? res.data.items : [];
                            this.quarantineTotal = Number(res?.data?.total) || 0;
                            this.quarantineSelectedIds = [];
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                const canRetry = this.handleMailAuth401(e, '載入隔離信件');
                                if (canRetry && !_retry) {
                                    try {
                                        const ok = await this.ensureMailSession('載入隔離信件');
                                        if (ok) return await this.fetchQuarantineList(resetOffset, true);
                                    } catch (err) {
                                    }
                                }
                                return;
                            }
                            this.quarantineItems = [];
                            this.quarantineTotal = 0;
                            this.quarantineError = (e?.response?.data?.detail || e?.message || '載入失敗').toString();
                        } finally {
                            this.quarantineLoading = false;
                        }
                    },
                    clearQuarantineSelection() {
                        this.quarantineSelectedIds = [];
                    },
                    isQuarantineSelected(id) {
                        const v = Number(id) || 0;
                        if (!v) return false;
                        return Array.isArray(this.quarantineSelectedIds) && this.quarantineSelectedIds.includes(v);
                    },
                    toggleQuarantineSelect(id) {
                        const v = Number(id) || 0;
                        if (!v) return;
                        const cur = Array.isArray(this.quarantineSelectedIds) ? [...this.quarantineSelectedIds] : [];
                        const idx = cur.indexOf(v);
                        if (idx >= 0) cur.splice(idx, 1);
                        else cur.push(v);
                        this.quarantineSelectedIds = cur;
                    },
                    isQuarantineAllPageSelected() {
                        const items = Array.isArray(this.quarantineItems) ? this.quarantineItems : [];
                        if (!items.length) return false;
                        const selected = new Set((Array.isArray(this.quarantineSelectedIds) ? this.quarantineSelectedIds : []).map(x => Number(x) || 0).filter(Boolean));
                        if (!selected.size) return false;
                        for (const m of items) {
                            const id = Number(m?.id) || 0;
                            if (!id) continue;
                            if (!selected.has(id)) return false;
                        }
                        return true;
                    },
                    toggleQuarantineSelectAllPage() {
                        const items = Array.isArray(this.quarantineItems) ? this.quarantineItems : [];
                        const pageIds = items.map(m => Number(m?.id) || 0).filter(Boolean);
                        if (!pageIds.length) return;
                        const cur = Array.isArray(this.quarantineSelectedIds) ? [...this.quarantineSelectedIds] : [];
                        const set = new Set(cur.map(x => Number(x) || 0).filter(Boolean));
                        const allSelected = this.isQuarantineAllPageSelected();
                        if (allSelected) {
                            for (const id of pageIds) set.delete(id);
                        } else {
                            for (const id of pageIds) set.add(id);
                        }
                        this.quarantineSelectedIds = Array.from(set);
                    },
                    async batchReleaseQuarantineSelected(action) {
                        if (this.quarantineBatch && this.quarantineBatch.running) return;
                        const ids = (Array.isArray(this.quarantineSelectedIds) ? this.quarantineSelectedIds : []).map(x => Number(x) || 0).filter(Boolean);
                        if (!ids.length) return;
                        const base = this.quarantineBasePath();
                        const folder = this.quarantineFolder || this.quarantineDefaultFolder();
                        const isPhishing = this.quarantineMode === 'phishing';

                        const act = (action || '').toString().trim();
                        if (act === 'allowlist_domain' && !this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        let promptText = '';
                        let endpoint = '';
                        let payload = {};
                        if (act === 'delete') {
                            promptText = `確定要刪除已選 ${ids.length} 封隔離信件嗎？`;
                            endpoint = `${ADMIN_API}/${base}/quarantine/bulk_delete`;
                            payload = { ids, folder };
                        } else if (act === 'allowlist') {
                            promptText = isPhishing ? `確定要釋放已選 ${ids.length} 封到收件匣，並加入寄件者信任清單嗎？` : `確定要釋放已選 ${ids.length} 封到收件匣，並加入寄件者允許清單嗎？`;
                            endpoint = isPhishing ? `${ADMIN_API}/phishing/quarantine/bulk_release_not_phishing` : `${ADMIN_API}/spam/quarantine/bulk_release_not_spam`;
                            payload = { ids, folder, target: 'INBOX' };
                        } else if (act === 'allowlist_domain') {
                            promptText = isPhishing ? `確定要釋放已選 ${ids.length} 封到收件匣，並加入寄件網域信任清單嗎？` : `確定要釋放已選 ${ids.length} 封到收件匣，並加入寄件網域允許清單嗎？`;
                            endpoint = isPhishing ? `${ADMIN_API}/phishing/quarantine/bulk_release_not_phishing_domain` : `${ADMIN_API}/spam/quarantine/bulk_release_not_spam_domain`;
                            payload = { ids, folder, target: 'INBOX' };
                        } else {
                            promptText = `確定要釋放已選 ${ids.length} 封隔離信件到收件匣嗎？`;
                            endpoint = `${ADMIN_API}/${base}/quarantine/bulk_release`;
                            payload = { ids, folder, target: 'INBOX' };
                        }
                        if (!confirm(promptText)) return;
                        if (!await this.ensureMailSession('批次處理隔離信件')) return;

                        const errors = [];
                        this.quarantineBatch = { running: true, action: act || 'release', done: 0, total: ids.length, errors };
                        try {
                            const res = await axios.post(endpoint, payload, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const ok = Array.isArray(res?.data?.ok) ? res.data.ok : [];
                            const failed = Array.isArray(res?.data?.failed) ? res.data.failed : [];
                            for (const f of failed) {
                                const id = Number(f?.id) || 0;
                                const msg = (f?.detail || 'failed').toString();
                                errors.push({ id, message: msg });
                            }
                            this.quarantineBatch = { ...this.quarantineBatch, done: ids.length, errors };

                            if (act === 'allowlist') {
                                const updated = !!(res?.data?.allowlist_updated);
                                _showToast(`批次處理完成：成功 ${ok.length}，失敗 ${failed.length}；${isPhishing ? '信任名單' : '允許名單'}更新：${updated ? '是' : '否'}`);
                            } else if (act === 'allowlist_domain') {
                                const updated = !!(res?.data?.allowlist_updated);
                                const domains = Array.isArray(res?.data?.allowlist_domains) ? res.data.allowlist_domains : [];
                                _showToast(`批次處理完成：成功 ${ok.length}，失敗 ${failed.length}；${isPhishing ? '信任名單' : '允許名單'}（網域）更新：${updated ? '是' : '否'}（網域 ${domains.length} 筆）`);
                            } else if (failed.length) {
                                const head = errors.slice(0, 5).map(x => `${x.id}: ${x.message}`).join('\n');
                                _showToast(`批次處理完成：成功 ${ok.length}，失敗 ${failed.length}\n\n${head}${failed.length > 5 ? '\n...(更多略)' : ''}`);
                            } else {
                                _showToast(`批次處理完成：成功 ${ok.length} 封`);
                            }
                        } finally {
                            this.quarantineBatch = { ...this.quarantineBatch, running: false, errors };
                            this.quarantineSelectedIds = [];
                            await this.fetchQuarantineList(false);
                        }
                    },
                    async fetchPhishingQuarantineCache() {
                        if (this.quarantineMode !== 'phishing') return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/phishing/quarantine/cache`);
                            const data = res?.data || null;
                            this.quarantineCacheInfo = data && typeof data === 'object' ? data : null;
                        } catch (e) {
                            this.quarantineCacheInfo = null;
                        }
                    },
                    async clearPhishingQuarantineCache() {
                        if (this.quarantineMode !== 'phishing') return;
                        if (!confirm('確定要清空 AI 分析快取嗎？')) return;
                        try {
                            const res = await axios.post(`${ADMIN_API}/phishing/quarantine/cache/clear`);
                            const cleared = Number(res?.data?.cleared) || 0;
                            await this.fetchPhishingQuarantineCache();
                            _showToast(`已清空快取 ${cleared} 筆`);
                        } catch (e) {
                            _showToast('清空快取失敗: ' + (e?.response?.data?.detail || e?.message || 'unknown error'));
                        }
                    },
                    async scanQuarantineSourceFolder() {
                        if (this.quarantineScanning) return;
                        if (!await this.ensureMailSession('AI 掃描')) return;
                        const isPhishing = this.quarantineMode === 'phishing';
                        const endpoint = isPhishing ? `${ADMIN_API}/ai/security/phishing/scan_folder` : `${ADMIN_API}/ai/security/spam/scan_folder`;
                        const folder = (this.quarantineScanFolder || 'INBOX').toString().trim() || 'INBOX';
                        const target = (this.quarantineFolder || this.quarantineDefaultFolder()).toString().trim() || this.quarantineDefaultFolder();
                        const limit = Math.max(1, Math.min(Number(this.quarantineScanLimit || 50), 500));
                        const unseen_only = !!this.quarantineScanUnseenOnly;
                        const threshold = isPhishing
                            ? Math.max(0, Math.min(Number(this.quarantineScanPhishingThreshold || 0.7), 1))
                            : Math.max(0, Number(this.quarantineScanSpamThreshold || 5));
                        this.quarantineScanning = true;
                        try {
                            const res = await axios.post(endpoint, null, {
                                params: { folder, target, limit, threshold, unseen_only },
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const data = res?.data || {};
                            const scanned = Number(data.scanned) || 0;
                            const quarantined = Number(data.quarantined) || 0;
                            const errCount = Array.isArray(data.errors) ? data.errors.length : 0;
                            await this.fetchQuarantineFolders();
                            await this.fetchQuarantineList(true);
                            _showToast(`AI 掃描完成：掃描 ${scanned} 封，隔離 ${quarantined} 封${errCount ? `，錯誤 ${errCount} 筆` : ''}`);
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                this.handleMailAuth401(e, 'AI 掃描');
                                return;
                            }
                            _showToast('AI 掃描失敗: ' + (e?.response?.data?.detail || e?.message || 'unknown error'));
                        } finally {
                            this.quarantineScanning = false;
                        }
                    },
                    quarantinePrevPage() {
                        if (this.quarantineLoading) return;
                        const next = Math.max(0, Number(this.quarantineOffset || 0) - Number(this.quarantineLimit || 50));
                        if (next === this.quarantineOffset) return;
                        this.quarantineOffset = next;
                        this.fetchQuarantineList(false);
                    },
                    quarantineNextPage() {
                        if (this.quarantineLoading) return;
                        const total = Number(this.quarantineTotal || 0);
                        const offset = Number(this.quarantineOffset || 0);
                        const limit = Number(this.quarantineLimit || 50);
                        if ((offset + limit) >= total) return;
                        this.quarantineOffset = offset + limit;
                        this.fetchQuarantineList(false);
                    },
                    quarantineReasonText(m) {
                        const msg = m || {};
                        const summary = (msg.evidence_summary || '').toString().trim();
                        if (summary) return summary;
                        if (this.quarantineMode === 'phishing') {
                            const parts = [];
                            const score = Number(msg.phishing_score);
                            if (isFinite(score)) parts.push(`分數 ${score.toFixed(2)}`);
                            const suspicious = Number(msg.phishing_links_suspicious);
                            const total = Number(msg.phishing_links_total);
                            if (isFinite(suspicious) && isFinite(total) && total > 0) parts.push(`可疑連結 ${Math.max(0, suspicious)}/${Math.max(0, total)}`);
                            const reason = (msg.reason || msg.phishing_reason || '').toString().trim();
                            if (reason) parts.push(reason);
                            const flags = Array.isArray(msg.phishing_links_flags) ? msg.phishing_links_flags.filter(x => x).slice(0, 3).join('、') : '';
                            if (flags) parts.push(`特徵：${flags}`);
                            return parts.join('｜');
                        }
                        const parts = [];
                        const score = Number(msg.spam_score);
                        if (isFinite(score)) parts.push(`分數 ${score.toFixed(1)}`);
                        const action = (msg.spam_action || '').toString().trim();
                        if (action) parts.push(`action ${action}`);
                        const reason = (msg.reason || msg.spam_reason || '').toString().trim();
                        if (reason) parts.push(reason);
                        return parts.join('｜');
                    },
                    async seedQuarantineExamples() {
                        if (this.quarantineSeedingExamples) return;
                        if (!await this.ensureMailSession('加入隔離區範例信件')) return;
                        const base = this.quarantineBasePath();
                        const folder = (this.quarantineFolder || this.quarantineDefaultFolder()).toString() || this.quarantineDefaultFolder();
                        this.quarantineSeedingExamples = true;
                        try {
                            await axios.post(`${ADMIN_API}/${base}/quarantine/seed_examples`, null, {
                                params: { folder, count: 5 },
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            await this.fetchQuarantineFolders();
                            await this.fetchQuarantineList(true);
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                this.handleMailAuth401(e, '加入隔離區範例信件');
                                return;
                            }
                            _showToast('加入隔離區範例信件失敗: ' + (e?.response?.data?.detail || e?.message || 'unknown error'));
                        } finally {
                            this.quarantineSeedingExamples = false;
                        }
                    },
                    quarantineMapTitle() {
                        const isPhishing = this.quarantineMode === 'phishing';
                        const kind = (this.quarantineMapKind || 'allow').toString();
                        if (kind === 'block') return isPhishing ? '詐騙封鎖名單管理' : '封鎖名單管理';
                        return isPhishing ? '詐騙信任名單管理' : '允許名單管理';
                    },
                    quarantineMapEndpoint(kind) {
                        const base = this.quarantineBasePath();
                        const k = (kind || 'allow').toString();
                        if (k === 'block') return `${ADMIN_API}/${base}/rspamd/maps/from_block`;
                        return `${ADMIN_API}/${base}/rspamd/maps/from_allow`;
                    },
                    quarantineMapItemsFromText() {
                        const lines = (this.quarantineMapText || '').toString().split('\n').map(l => l.trim()).filter(Boolean);
                        const out = [];
                        const seen = new Set();
                        for (const raw of lines) {
                            const v = raw.toLowerCase();
                            if (!v) continue;
                            if (seen.has(v)) continue;
                            seen.add(v);
                            out.push(v);
                            if (out.length >= 1000) break;
                        }
                        return out;
                    },
                    quarantineSelectedSenders() {
                        const selected = new Set((Array.isArray(this.quarantineSelectedIds) ? this.quarantineSelectedIds : []).map(x => Number(x) || 0).filter(Boolean));
                        if (!selected.size) return [];
                        const items = Array.isArray(this.quarantineItems) ? this.quarantineItems : [];
                        const out = [];
                        const seen = new Set();
                        for (const m of items) {
                            const id = Number(m?.id) || 0;
                            if (!id || !selected.has(id)) continue;
                            const sender = (this.extractEmail(m?.from || '') || '').toString().trim().toLowerCase();
                            if (!sender) continue;
                            if (seen.has(sender)) continue;
                            seen.add(sender);
                            out.push(sender);
                            if (out.length >= 1000) break;
                        }
                        return out;
                    },
                    quarantineMapLabel(kind) {
                        const isPhishing = this.quarantineMode === 'phishing';
                        const k = (kind === 'block') ? 'block' : 'allow';
                        if (k === 'block') return '封鎖名單';
                        return isPhishing ? '信任名單' : '允許名單';
                    },
                    quarantineNormalizeSenderOrDomain(raw) {
                        const v = (raw ?? '').toString().trim().toLowerCase();
                        if (!v) return '';
                        if (v.startsWith('@')) return v;
                        if (v.includes('@')) return v;
                        return '@' + v;
                    },
                    quarantineSenderFromItem(m) {
                        return (this.extractEmail(m?.from || '') || '').toString().trim().toLowerCase();
                    },
                    async quarantineUpdateMapWithSenders(kind, senders) {
                        const k = (kind === 'block') ? 'block' : 'allow';
                        const endpoint = this.quarantineMapEndpoint(k);
                        const curRes = await axios.get(endpoint);
                        const cur = Array.isArray(curRes?.data?.items) ? curRes.data.items : [];
                        const beforeSet = new Set(cur.map(x => this.quarantineNormalizeSenderOrDomain(x)).filter(Boolean));
                        const merged = [];
                        const seen = new Set();
                        for (const raw of [...cur, ...(Array.isArray(senders) ? senders : [])]) {
                            const v = this.quarantineNormalizeSenderOrDomain(raw);
                            if (!v) continue;
                            if (seen.has(v)) continue;
                            seen.add(v);
                            merged.push(v);
                            if (merged.length >= 1000) break;
                        }
                        const res = await axios.post(endpoint, merged);
                        const afterItems = Array.isArray(res?.data?.items) ? res.data.items : merged;
                        const afterSet = new Set(afterItems.map(x => this.quarantineNormalizeSenderOrDomain(x)).filter(Boolean));
                        return { beforeSet, afterItems, afterSet };
                    },
                    async quarantineAddSelectedToMap(kind) {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        const senders = this.quarantineSelectedSenders();
                        if (!senders.length) {
                            await this.openQuarantineMapModal(kind);
                            return;
                        }
                        const k = (kind === 'block') ? 'block' : 'allow';
                        const label = this.quarantineMapLabel(k);
                        if (!confirm(`確定要將已選 ${senders.length} 個寄件者加入「${label}」嗎？`)) return;
                        this.quarantineMapLoading = true;
                        try {
                            const { beforeSet, afterSet } = await this.quarantineUpdateMapWithSenders(k, senders);
                            let added = 0;
                            let existed = 0;
                            let skipped = 0;
                            for (const s of senders) {
                                const norm = this.quarantineNormalizeSenderOrDomain(s);
                                if (!norm) continue;
                                if (beforeSet.has(norm)) {
                                    existed += 1;
                                    continue;
                                }
                                if (afterSet.has(norm)) {
                                    added += 1;
                                } else {
                                    skipped += 1;
                                }
                            }
                            _showToast(`已加入 ${added}/${senders.length} 個寄件者到「${label}」（已存在 ${existed}，未加入 ${skipped}）`);
                        } catch (e) {
                            const detail = e?.response?.data?.detail;
                            _showToast('更新失敗: ' + (detail || e?.message || 'unknown error'));
                        } finally {
                            this.quarantineMapLoading = false;
                        }
                    },
                    quarantineSelectedSenderDomains() {
                        const senders = this.quarantineSelectedSenders();
                        if (!senders.length) return [];
                        const out = [];
                        const seen = new Set();
                        for (const s of senders) {
                            const at = s.lastIndexOf('@');
                            if (at < 0) continue;
                            const dom = (s.slice(at + 1) || '').toString().trim().toLowerCase();
                            if (!dom) continue;
                            if (seen.has(dom)) continue;
                            seen.add(dom);
                            out.push(dom);
                            if (out.length >= 1000) break;
                        }
                        return out;
                    },
                    async quarantineAddSelectedDomainsToMap(kind) {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        const domains = this.quarantineSelectedSenderDomains();
                        if (!domains.length) {
                            await this.openQuarantineMapModal(kind);
                            return;
                        }
                        const k = (kind === 'block') ? 'block' : 'allow';
                        const label = this.quarantineMapLabel(k);
                        if (!confirm(`確定要將已選 ${domains.length} 個寄件網域加入「${label}」嗎？`)) return;
                        this.quarantineMapLoading = true;
                        try {
                            const { beforeSet, afterSet } = await this.quarantineUpdateMapWithSenders(k, domains);
                            let added = 0;
                            let existed = 0;
                            let skipped = 0;
                            for (const d of domains) {
                                const norm = this.quarantineNormalizeSenderOrDomain(d);
                                if (!norm) continue;
                                if (beforeSet.has(norm)) {
                                    existed += 1;
                                    continue;
                                }
                                if (afterSet.has(norm)) {
                                    added += 1;
                                } else {
                                    skipped += 1;
                                }
                            }
                            _showToast(`已加入 ${added}/${domains.length} 個寄件網域到「${label}」（已存在 ${existed}，未加入 ${skipped}）`);
                        } catch (e) {
                            const detail = e?.response?.data?.detail;
                            _showToast('更新失敗: ' + (detail || e?.message || 'unknown error'));
                        } finally {
                            this.quarantineMapLoading = false;
                        }
                    },
                    async quarantineAddMessageToMap(m, kind) {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        const sender = this.quarantineSenderFromItem(m);
                        if (!sender) {
                            _showToast('找不到寄件者');
                            return;
                        }
                        const label = this.quarantineMapLabel(kind);
                        if (!confirm(`確定要將寄件者 ${sender} 加入「${label}」嗎？`)) return;
                        this.quarantineMapLoading = true;
                        try {
                            const { beforeSet, afterSet } = await this.quarantineUpdateMapWithSenders(kind, [sender]);
                            const norm = this.quarantineNormalizeSenderOrDomain(sender);
                            if (!norm) {
                                _showToast('找不到寄件者');
                            } else if (beforeSet.has(norm)) {
                                _showToast(`寄件者已存在於「${label}」`);
                            } else if (afterSet.has(norm)) {
                                _showToast(`已加入 1 個寄件者到「${label}」`);
                            } else {
                                _showToast(`未加入寄件者到「${label}」`);
                            }
                        } catch (e) {
                            const detail = e?.response?.data?.detail;
                            _showToast('更新失敗: ' + (detail || e?.message || 'unknown error'));
                        } finally {
                            this.quarantineMapLoading = false;
                        }
                    },
                    async quarantineAddMessageDomainToMap(m, kind) {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        const sender = this.quarantineSenderFromItem(m);
                        if (!sender) {
                            _showToast('找不到寄件者');
                            return;
                        }
                        const at = sender.lastIndexOf('@');
                        if (at < 0) {
                            _showToast('找不到寄件網域');
                            return;
                        }
                        const domain = (sender.slice(at + 1) || '').toString().trim().toLowerCase();
                        if (!domain) {
                            _showToast('找不到寄件網域');
                            return;
                        }
                        const label = this.quarantineMapLabel(kind);
                        if (!confirm(`確定要將寄件網域 @${domain} 加入「${label}」嗎？`)) return;
                        this.quarantineMapLoading = true;
                        try {
                            const { beforeSet, afterSet } = await this.quarantineUpdateMapWithSenders(kind, [domain]);
                            const norm = this.quarantineNormalizeSenderOrDomain(domain);
                            if (!norm) {
                                _showToast('找不到寄件網域');
                            } else if (beforeSet.has(norm)) {
                                _showToast(`寄件網域已存在於「${label}」`);
                            } else if (afterSet.has(norm)) {
                                _showToast(`已加入 1 個寄件網域到「${label}」`);
                            } else {
                                _showToast(`未加入寄件網域到「${label}」`);
                            }
                        } catch (e) {
                            const detail = e?.response?.data?.detail;
                            _showToast('更新失敗: ' + (detail || e?.message || 'unknown error'));
                        } finally {
                            this.quarantineMapLoading = false;
                        }
                    },
                    async openQuarantineMapModal(kind) {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        this.quarantineMapKind = (kind === 'block') ? 'block' : 'allow';
                        this.quarantineMapText = '';
                        this.quarantineMapError = '';
                        this.showQuarantineMapModal = true;
                        this.quarantineMapLoading = true;
                        try {
                            const res = await axios.get(this.quarantineMapEndpoint(this.quarantineMapKind));
                            const items = (res?.data?.items || []);
                            this.quarantineMapText = Array.isArray(items) ? items.join('\n') : '';
                        } catch (e) {
                            const detail = e?.response?.data?.detail;
                            this.quarantineMapError = (detail || e?.message || '載入失敗').toString();
                        } finally {
                            this.quarantineMapLoading = false;
                        }
                    },
                    closeQuarantineMapModal() {
                        this.showQuarantineMapModal = false;
                        this.quarantineMapLoading = false;
                        this.quarantineMapError = '';
                    },
                    async saveQuarantineMapModal() {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        this.quarantineMapLoading = true;
                        this.quarantineMapError = '';
                        try {
                            const items = this.quarantineMapItemsFromText();
                            const res = await axios.post(this.quarantineMapEndpoint(this.quarantineMapKind), items);
                            const cleaned = (res?.data?.items || []);
                            this.quarantineMapText = Array.isArray(cleaned) ? cleaned.join('\n') : items.join('\n');
                            _showToast('名單已更新');
                        } catch (e) {
                            const detail = e?.response?.data?.detail;
                            this.quarantineMapError = (detail || e?.message || '儲存失敗').toString();
                            _showToast('更新失敗: ' + this.quarantineMapError);
                        } finally {
                            this.quarantineMapLoading = false;
                        }
                    },
                    async openQuarantineMessage(msg) {
                        const id = Number(msg?.id) || 0;
                        if (!id) return;
                        if (!await this.ensureMailSession('查看隔離信件')) return;
                        const base = this.quarantineBasePath();
                        const folder = this.quarantineFolder || this.quarantineDefaultFolder();
                        this.showOriginalMailModal = true;
                        this.originalMailMessage = null;
                        this.originalMailLoading = true;
                        this.originalMailFolder = folder;
                        try {
                            const res = await axios.get(`${ADMIN_API}/${base}/quarantine/open`, {
                                params: { id, folder },
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const original = res?.data || null;
                            if (!original) {
                                this.showOriginalMailModal = false;
                                return;
                            }
                            this.originalMailMessage = original;
                            if (this.originalMailMessage) {
                                if ((this.originalMailMessage.id === undefined || this.originalMailMessage.id === null || this.originalMailMessage.id === '') && id) {
                                    this.originalMailMessage.id = id;
                                }
                                const raw = this.originalMailMessage.body_html || this.originalMailMessage.body_text || '(No Content)';
                                this.originalMailMessage.body = this.sanitizeHtml(raw);
                            }
                            try {
                                await axios.post(`${ADMIN_API}/${base}/quarantine/seen`, null, {
                                    params: { id, folder, seen: true },
                                    headers: { 'X-Mail-Session': this.mailSessionToken }
                                });
                            } catch (e) {
                            }
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                this.showOriginalMailModal = false;
                                this.handleMailAuth401(e, '查看隔離信件');
                                return;
                            }
                            _showToast('無法載入隔離信件: ' + (e.response?.data?.detail || e.message));
                            this.showOriginalMailModal = false;
                        } finally {
                            this.originalMailLoading = false;
                        }
                    },
                    async releaseQuarantineMessage(msg) {
                        const id = Number(msg?.id) || 0;
                        if (!id) return;
                        if (!confirm('確定要將此封信件釋放到收件匣嗎？')) return;
                        if (!await this.ensureMailSession('釋放隔離信件')) return;
                        const base = this.quarantineBasePath();
                        const folder = this.quarantineFolder || this.quarantineDefaultFolder();
                        this.quarantineActionLoadingId = id;
                        try {
                            await axios.post(`${ADMIN_API}/${base}/quarantine/release`, null, {
                                params: { id, folder, target: 'INBOX' },
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            await this.fetchQuarantineList(false);
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                this.handleMailAuth401(e, '釋放隔離信件');
                                return;
                            }
                            _showToast('釋放失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quarantineActionLoadingId = null;
                        }
                    },
                    async releaseQuarantineAllowlistMessage(msg) {
                        const id = Number(msg?.id) || 0;
                        if (!id) return;
                        const isPhishing = this.quarantineMode === 'phishing';
                        const promptText = isPhishing ? '確定要釋放到收件匣，並加入寄件者信任清單嗎？' : '確定要釋放到收件匣，並加入寄件者允許清單嗎？';
                        if (!confirm(promptText)) return;
                        if (!await this.ensureMailSession('釋放並加入允許')) return;
                        const folder = this.quarantineFolder || this.quarantineDefaultFolder();
                        this.quarantineActionLoadingId = id;
                        try {
                            const url = isPhishing ? `${ADMIN_API}/phishing/quarantine/release_not_phishing` : `${ADMIN_API}/spam/quarantine/release_not_spam`;
                            await axios.post(url, null, {
                                params: { id, folder, target: 'INBOX' },
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            await this.fetchQuarantineList(false);
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                this.handleMailAuth401(e, '釋放並加入允許');
                                return;
                            }
                            _showToast('操作失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quarantineActionLoadingId = null;
                        }
                    },
                    async releaseQuarantineAllowlistDomainMessage(msg) {
                        const id = Number(msg?.id) || 0;
                        if (!id) return;
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        const isPhishing = this.quarantineMode === 'phishing';
                        const promptText = isPhishing ? '確定要釋放到收件匣，並加入寄件網域信任清單嗎？' : '確定要釋放到收件匣，並加入寄件網域允許清單嗎？';
                        if (!confirm(promptText)) return;
                        if (!await this.ensureMailSession('釋放並加入允許網域')) return;
                        const folder = this.quarantineFolder || this.quarantineDefaultFolder();
                        this.quarantineActionLoadingId = id;
                        try {
                            const url = isPhishing ? `${ADMIN_API}/phishing/quarantine/release_not_phishing_domain` : `${ADMIN_API}/spam/quarantine/release_not_spam_domain`;
                            const res = await axios.post(url, null, {
                                params: { id, folder, target: 'INBOX' },
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const label = isPhishing ? '信任名單' : '允許名單';
                            const updated = !!(res?.data?.allowlist_updated);
                            const domain = (res?.data?.allowlist_domain || '').toString().trim().toLowerCase();
                            if (!domain) {
                                _showToast('已釋放，但找不到寄件網域可加入');
                            } else if (updated) {
                                _showToast(`已釋放並加入寄件網域 @${domain} 到「${label}」`);
                            } else {
                                _showToast(`已釋放並加入寄件網域 @${domain}（已存在於「${label}」）`);
                            }
                            await this.fetchQuarantineList(false);
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                this.handleMailAuth401(e, '釋放並加入允許網域');
                                return;
                            }
                            _showToast('操作失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quarantineActionLoadingId = null;
                        }
                    },
                    async releaseNotSpamMessage(msg) {
                        const id = Number(msg?.id) || 0;
                        if (!id) return;
                        if (this.quarantineMode === 'phishing') return;
                        if (!confirm('確定要釋放到收件匣，並加入寄件者允許清單嗎？')) return;
                        if (!await this.ensureMailSession('釋放並加入允許')) return;
                        const folder = this.quarantineFolder || this.quarantineDefaultFolder();
                        this.quarantineActionLoadingId = id;
                        try {
                            await axios.post(`${ADMIN_API}/spam/quarantine/release_not_spam`, null, {
                                params: { id, folder, target: 'INBOX' },
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            await this.fetchQuarantineList(false);
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                this.handleMailAuth401(e, '釋放並加入允許');
                                return;
                            }
                            _showToast('操作失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quarantineActionLoadingId = null;
                        }
                    },
                    async deleteQuarantineMessage(msg) {
                        const id = Number(msg?.id) || 0;
                        if (!id) return;
                        if (!confirm('確定要刪除此封隔離信件嗎？')) return;
                        if (!await this.ensureMailSession('刪除隔離信件')) return;
                        const base = this.quarantineBasePath();
                        const folder = this.quarantineFolder || this.quarantineDefaultFolder();
                        this.quarantineActionLoadingId = id;
                        try {
                            await axios.post(`${ADMIN_API}/${base}/quarantine/delete`, null, {
                                params: { id, folder },
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            await this.fetchQuarantineList(false);
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                this.handleMailAuth401(e, '刪除隔離信件');
                                return;
                            }
                            _showToast('刪除失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quarantineActionLoadingId = null;
                        }
                    },
                    async downloadQuarantineEml(msg) {
                        const id = Number(msg?.id) || 0;
                        if (!id) return;
                        if (!await this.ensureMailSession('下載隔離信件')) return;
                        const base = this.quarantineBasePath();
                        const folder = this.quarantineFolder || this.quarantineDefaultFolder();
                        this.quarantineActionLoadingId = id;
                        try {
                            const res = await axios.get(`${ADMIN_API}/${base}/quarantine/download`, {
                                params: { id, folder },
                                headers: { 'X-Mail-Session': this.mailSessionToken },
                                responseType: 'blob'
                            });
                            let filename = `message-${id}.eml`;
                            const cd = (res?.headers?.['content-disposition'] || res?.headers?.['Content-Disposition'] || '').toString();
                            const m = /filename\\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i.exec(cd);
                            const f = decodeURIComponent((m?.[1] || m?.[2] || '').toString());
                            if (f) filename = f;

                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        } catch (e) {
                            if (e?.response?.status === 401) {
                                this.handleMailAuth401(e, '下載隔離信件');
                                return;
                            }
                            _showToast('下載失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.quarantineActionLoadingId = null;
                        }
                    },
                    async fetchRspamdActions() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/security/rspamd/actions`);
                            this.rspamdActions = res.data;
                        } catch (e) {
                            if (e && e.response && (e.response.status === 403 || e.response.status === 404)) {
                                try {
                                    const res = await axios.get(`${ADMIN_API}/spam/actions`);
                                    if (res && res.data) this.rspamdActions = res.data;
                                    return;
                                } catch (e2) {
                                    window.$toast?.error('載入 rspamd actions 失敗', e2?.response?.data?.detail || e2?.message || '');
                                    console.error('Failed to fetch rspamd actions', e2);
                                }
                            }
                            if (await this.handleAdminApiNotFound(e)) return;
                            console.error('Failed to fetch rspamd actions', e);
                        }
                    },
                    async fetchSenderMaps() {
                        this.senderMapsLoading = true;
                        try {
                            const [allowRes, blockRes] = await Promise.all([
                                axios.get(`${ADMIN_API}/security/rspamd/maps/from_allow`),
                                axios.get(`${ADMIN_API}/security/rspamd/maps/from_block`)
                            ]);
                            this.senderAllowlistText = (allowRes.data && Array.isArray(allowRes.data.items)) ? allowRes.data.items.join('\n') : '';
                            this.senderBlocklistText = (blockRes.data && Array.isArray(blockRes.data.items)) ? blockRes.data.items.join('\n') : '';
                            this.senderMapsLoaded = true;
                        } catch (e) {
                            if (e && e.response && (e.response.status === 403 || e.response.status === 404)) {
                                try {
                                    const [allowRes, blockRes] = await Promise.all([
                                        axios.get(`${ADMIN_API}/spam/rspamd/maps/from_allow`),
                                        axios.get(`${ADMIN_API}/spam/rspamd/maps/from_block`)
                                    ]);
                                    this.senderAllowlistText = (allowRes.data && Array.isArray(allowRes.data.items)) ? allowRes.data.items.join('\n') : '';
                                    this.senderBlocklistText = (blockRes.data && Array.isArray(blockRes.data.items)) ? blockRes.data.items.join('\n') : '';
                                    this.senderMapsLoaded = true;
                                    return;
                                } catch (e2) {
                                    window.$toast?.error('載入 sender maps (fallback) 失敗', e2?.response?.data?.detail || e2?.message || '');
                                    console.error('Failed to fetch sender maps (fallback)', e2);
                                }
                            }
                            if (await this.handleAdminApiNotFound(e)) {
                                this.senderMapsLoaded = false;
                                return;
                            }
                            console.error('Failed to fetch sender maps', e);
                            this.senderMapsLoaded = false;
                        } finally {
                            this.senderMapsLoading = false;
                        }
                    },
                    async updateSenderAllowlist() {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        this.senderMapsLoading = true;
                        try {
                            const items = this.senderAllowlistText.split('\n').map(l => l.trim()).filter(l => l);
                            const res = await axios.post(`${ADMIN_API}/security/rspamd/maps/from_allow`, items);
                            if (res.data && Array.isArray(res.data.items)) {
                                this.senderAllowlistText = res.data.items.join('\n');
                            }
                            _showToast('白名單已更新');
                        } catch (e) {
                            _showToast('更新失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.senderMapsLoading = false;
                        }
                    },
                    async updateSenderBlocklist() {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        this.senderMapsLoading = true;
                        try {
                            const items = this.senderBlocklistText.split('\n').map(l => l.trim()).filter(l => l);
                            const res = await axios.post(`${ADMIN_API}/security/rspamd/maps/from_block`, items);
                            if (res.data && Array.isArray(res.data.items)) {
                                this.senderBlocklistText = res.data.items.join('\n');
                            }
                            _showToast('黑名單已更新');
                        } catch (e) {
                            _showToast('更新失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.senderMapsLoading = false;
                        }
                    },
                    async restartRspamd() {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限才能重啟 Rspamd');
                            return;
                        }
                        if (!confirm('確定要重啟 Rspamd 套用設定嗎？')) return;
                        try {
                            await axios.post(`${ADMIN_API}/service/restart/rspamd`);
                            _showToast('Rspamd 已重啟');
                            setTimeout(() => this.fetchServiceStatus(), 1500);
                        } catch (e) {
                            _showToast('重啟失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async loadRspamdEmlFromCurrentMessage(quiet = false) {
                        if (!this.currentMessage || !this.currentMessage.id) return;
                        if (!await this.ensureMailSession('載入 EML')) return;
                        const key = `${this.currentFolder || 'INBOX'}:${this.currentMessage.id}`;
                        if (this.rspamdLearnEmlLoadedKey === key && (this.rspamdLearnEml || '').toString().trim()) return;
                        this.rspamdLearnLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/mail/message_raw/${this.currentMessage.id}`, {
                                params: { folder: this.currentFolder || 'INBOX' },
                                headers: { 'X-Mail-Session': this.mailSessionToken },
                                responseType: 'arraybuffer'
                            });
                            const bytes = new Uint8Array(res.data || []);
                            const eml = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
                            this.rspamdLearnEml = eml;
                            this.rspamdLearnEmlLoadedKey = key;
                            if (!quiet) _showToast('已載入 EML');
                        } catch (e) {
                            if (!quiet) _showToast('載入 EML 失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.rspamdLearnLoading = false;
                        }
                    },
                    async learnRspamd(kind) {
                        const eml = (this.rspamdLearnEml || '').toString();
                        if (!eml.trim()) {
                            _showToast('請先貼上 EML 內容');
                            return;
                        }
                        this.rspamdLearnLoading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/security/rspamd/learn`, { kind, eml });
                            this.rspamdLearnResult = res.data || null;
                            this.rspamdLearnResultKey = '';
                            const statusCode = this.rspamdLearnResult?.result?.status_code;
                            const resp = this.rspamdLearnResult?.result?.response;
                            if (statusCode === 200) {
                                if (resp && resp.skipped) {
                                    _showToast('這封信已訓練過，已略過');
                                } else {
                                    _showToast('訓練完成');
                                }
                                await this.maybeMoveAfterRspamdTrain(kind);
                            } else if (statusCode) {
                                const errText = (resp && (resp.error || resp.text || resp.message)) ? String(resp.error || resp.text || resp.message) : '';
                                _showToast(`訓練回應（${statusCode}）：${errText || '請查看結果內容'}`);
                            } else {
                                _showToast('訓練完成');
                                await this.maybeMoveAfterRspamdTrain(kind);
                            }
                        } catch (e) {
                            this.rspamdLearnResult = null;
                            this.rspamdLearnResultKey = '';
                            const detail = e?.response?.data?.detail;
                            if (this.handleRspamd503(e)) return;
                            _showToast('訓練失敗: ' + (detail || e.message));
                        } finally {
                            this.rspamdLearnLoading = false;
                        }
                    },
                    async learnRspamdFromCurrentMessage(kind) {
                        if (!this.currentMessage || !this.currentMessage.id) return;
                        if (!await this.ensureMailSession('Rspamd 訓練')) return;
                        const key = `${this.currentFolder || 'INBOX'}:${this.currentMessage.id}`;
                        if (kind === 'spam') {
                            if (!confirm('確定要回報這封信為垃圾信嗎？')) return;
                        } else if (kind === 'ham') {
                            if (!confirm('確定要回報這封信為正常信嗎？')) return;
                        } else {
                            if (!confirm('確定要用這封信進行訓練嗎？')) return;
                        }
                        this.loadingContent = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/security/rspamd/learn_from_mail`, {
                                kind,
                                folder: this.currentFolder || 'INBOX',
                                msg_id: this.currentMessage.id
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            this.rspamdLearnResult = res.data || null;
                            this.rspamdLearnResultKey = key;
                            this.rspamdLearnCache[key] = this.rspamdLearnResult;
                            const statusCode = this.rspamdLearnResult?.result?.status_code;
                            const resp = this.rspamdLearnResult?.result?.response;
                            if (statusCode === 200) {
                                if (resp && resp.skipped) {
                                    _showToast('這封信已訓練過，已略過');
                                } else {
                                    _showToast('訓練完成');
                                }
                            } else if (statusCode) {
                                const errText = (resp && (resp.error || resp.text || resp.message)) ? String(resp.error || resp.text || resp.message) : '';
                                _showToast(`訓練回應（${statusCode}）：${errText || '請查看結果內容'}`);
                            } else {
                                _showToast('訓練完成');
                            }
                        } catch (e) {
                            const status = e?.response?.status;
                            const detail = e?.response?.data?.detail;
                            if (this.handleRspamd503(e)) return;
                            if (status === 404 && (detail === 'Not Found' || /not found/i.test(String(detail || '')))) {
                                try {
                                    const rawRes = await axios.get(`${ADMIN_API}/mail/message_raw/${this.currentMessage.id}`, {
                                        params: { folder: this.currentFolder || 'INBOX' },
                                        headers: { 'X-Mail-Session': this.mailSessionToken },
                                        responseType: 'arraybuffer'
                                    });
                                    const bytes = new Uint8Array(rawRes.data || []);
                                    const eml = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
                                    const learnRes = await axios.post(`${ADMIN_API}/security/rspamd/learn`, { kind, eml });
                                    this.rspamdLearnResult = learnRes.data || null;
                                    this.rspamdLearnResultKey = key;
                                    this.rspamdLearnCache[key] = this.rspamdLearnResult;
                                    const statusCode = this.rspamdLearnResult?.result?.status_code;
                                    const resp = this.rspamdLearnResult?.result?.response;
                                    if (statusCode === 200) {
                                        if (resp && resp.skipped) {
                                            _showToast('這封信已訓練過，已略過（使用原始 EML）');
                                        } else {
                                            _showToast('訓練完成（使用原始 EML）');
                                        }
                                        await this.maybeMoveAfterRspamdTrain(kind);
                                    } else if (statusCode) {
                                        const errText = (resp && (resp.error || resp.text || resp.message)) ? String(resp.error || resp.text || resp.message) : '';
                                        _showToast(`訓練回應（${statusCode}）：${errText || '請查看結果內容'}`);
                                    } else {
                                        _showToast('訓練完成（使用原始 EML）');
                                        await this.maybeMoveAfterRspamdTrain(kind);
                                    }
                                    return;
                                } catch (fallbackErr) {
                                }
                                if (await this.handleAdminApiNotFound(e)) return;
                            }
                            _showToast('訓練失敗: ' + (detail || e.message));
                        } finally {
                            this.loadingContent = false;
                        }
                    },
                    async scanRspamdByEml() {
                        const eml = (this.rspamdLearnEml || '').toString();
                        if (!eml.trim()) {
                            _showToast('請先貼上 EML 內容');
                            return;
                        }
                        this.rspamdScanLoading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/security/rspamd/scan`, { eml });
                            this.rspamdScanResult = res.data || null;
                            this.rspamdScanResultKey = '';
                            const statusCode = this.rspamdScanResult?.result?.status_code;
                            if (statusCode && statusCode !== 200) {
                                const resp = this.rspamdScanResult?.result?.response;
                                const errText = (resp && (resp.error || resp.text || resp.message)) ? String(resp.error || resp.text || resp.message) : '';
                                _showToast(`掃描回應（${statusCode}）：${errText || '請查看結果內容'}`);
                                return;
                            }
                            const d = this.rspamdScanDetails;
                            if (d && d.scoreDisplay) {
                                _showToast(`掃描完成：${d.action || ''}（${d.scoreDisplay}）`);
                            } else {
                                _showToast('掃描完成');
                            }
                        } catch (e) {
                            this.rspamdScanResult = null;
                            this.rspamdScanResultKey = '';
                            const detail = e?.response?.data?.detail;
                            if (this.handleRspamd503(e)) return;
                            _showToast('掃描失敗: ' + (detail || e.message));
                        } finally {
                            this.rspamdScanLoading = false;
                        }
                    },
                    async scanRspamdFromCurrentMessage() {
                        if (!this.currentMessage || !this.currentMessage.id) return;
                        if (!await this.ensureMailSession('Rspamd 掃描')) return;
                        const key = `${this.currentFolder || 'INBOX'}:${this.currentMessage.id}`;
                        this.loadingContent = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/security/rspamd/scan_from_mail`, {
                                folder: this.currentFolder || 'INBOX',
                                msg_id: this.currentMessage.id
                            }, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            this.rspamdScanResult = res.data || null;
                            this.rspamdScanResultKey = key;
                            this.rspamdScanCache[key] = this.rspamdScanResult;
                            const statusCode = this.rspamdScanResult?.result?.status_code;
                            if (statusCode && statusCode !== 200) {
                                const resp = this.rspamdScanResult?.result?.response;
                                const errText = (resp && (resp.error || resp.text || resp.message)) ? String(resp.error || resp.text || resp.message) : '';
                                _showToast(`掃描回應（${statusCode}）：${errText || '請查看結果內容'}`);
                                return;
                            }
                            const d = this.rspamdScanDetails;
                            if (d && d.scoreDisplay) {
                                _showToast(`掃描完成：${d.action || ''}（${d.scoreDisplay}）`);
                            } else {
                                _showToast('掃描完成');
                            }
                        } catch (e) {
                            this.rspamdScanResult = null;
                            const status = e?.response?.status;
                            const detail = e?.response?.data?.detail;
                            if (this.handleRspamd503(e)) return;
                            if (status === 404 && (detail === 'Not Found' || /not found/i.test(String(detail || '')))) {
                                try {
                                    const rawRes = await axios.get(`${ADMIN_API}/mail/message_raw/${this.currentMessage.id}`, {
                                        params: { folder: this.currentFolder || 'INBOX' },
                                        headers: { 'X-Mail-Session': this.mailSessionToken },
                                        responseType: 'arraybuffer'
                                    });
                                    const bytes = new Uint8Array(rawRes.data || []);
                                    const eml = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
                                    const scanRes = await axios.post(`${ADMIN_API}/security/rspamd/scan`, { eml });
                                    this.rspamdScanResult = scanRes.data || null;
                                    this.rspamdScanResultKey = key;
                                    this.rspamdScanCache[key] = this.rspamdScanResult;
                                    const statusCode = this.rspamdScanResult?.result?.status_code;
                                    if (statusCode && statusCode !== 200) {
                                        const resp = this.rspamdScanResult?.result?.response;
                                        const errText = (resp && (resp.error || resp.text || resp.message)) ? String(resp.error || resp.text || resp.message) : '';
                                        _showToast(`掃描回應（${statusCode}）：${errText || '請查看結果內容'}`);
                                        return;
                                    }
                                    const d = this.rspamdScanDetails;
                                    if (d && d.scoreDisplay) {
                                        _showToast(`掃描完成：${d.action || ''}（${d.scoreDisplay}）`);
                                    } else {
                                        _showToast('掃描完成');
                                    }
                                    return;
                                } catch (fallbackErr) {
                                }
                                if (await this.handleAdminApiNotFound(e)) return;
                            }
                            _showToast('掃描失敗: ' + (detail || e.message));
                        } finally {
                            this.loadingContent = false;
                        }
                    },
                    async maybeMoveAfterRspamdTrain(kind) {
                        if (!this.currentMessage || !this.currentMessage.id) return;
                        const folder = this.currentFolder || 'INBOX';
                        if (kind === 'spam' && folder !== 'Junk') {
                            const ok = confirm('已回報垃圾信。要將此信移到「垃圾信」嗎？');
                            if (ok) await this.moveEmail('Junk', { silent: true });
                            return;
                        }
                        if (kind === 'ham' && folder !== 'INBOX') {
                            const ok = confirm('已回報正常信。要將此信移到「收件匣」嗎？');
                            if (ok) await this.moveEmail('INBOX', { silent: true });
                            return;
                        }
                    },
                    extractRspamdScanDetailsFromResult(scanResult) {
                        const root = scanResult?.result?.response;
                        if (!root || typeof root !== 'object') return null;

                        const action = (root.action || root.default_action || '').toString() || null;
                        const score = (root.score ?? root.metric_score ?? root.total_score);
                        const required = (root.required_score ?? root.metric_action_score ?? root.action_score);

                        const symbolsObj = (root.symbols && typeof root.symbols === 'object') ? root.symbols : null;
                        const symbols = symbolsObj ? Object.keys(symbolsObj).map(name => {
                            const v = symbolsObj[name] || {};
                            const scoreValue = (v.score ?? v.weight ?? 0);
                            return {
                                name,
                                score: typeof scoreValue === 'number' ? scoreValue : Number(scoreValue || 0),
                                description: (v.description || v.name || '').toString() || ''
                            };
                        }).sort((a, b) => Math.abs(b.score) - Math.abs(a.score)).slice(0, 15) : [];

                        const scoreNum = (typeof score === 'number') ? score : Number(score);
                        const requiredNum = (typeof required === 'number') ? required : Number(required);
                        const scoreDisplay = (Number.isFinite(scoreNum) && Number.isFinite(requiredNum))
                            ? `${scoreNum.toFixed(2)} / ${requiredNum.toFixed(2)}`
                            : (Number.isFinite(scoreNum) ? scoreNum.toFixed(2) : '-');

                        return { action, score: scoreNum, required: requiredNum, scoreDisplay, symbols };
                    },
                    async fetchRspamdStats() {
                        this.rspamdStatsLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/security/rspamd/stats`);
                            this.rspamdStats = res.data || null;
                        } catch (e) {
                            if (e && e.response && (e.response.status === 403 || e.response.status === 404)) {
                                try {
                                    const res = await axios.get(`${ADMIN_API}/spam/rspamd/stats`);
                                    this.rspamdStats = res.data || null;
                                    return;
                                } catch (e2) {
                                    this.rspamdStats = null;
                                }
                            }
                            if (this.handleRspamd503(e)) {
                                this.rspamdStats = null;
                                return;
                            }
                            if (await this.handleAdminApiNotFound(e)) {
                                this.rspamdStats = null;
                                return;
                            }
                            this.rspamdStats = null;
                        } finally {
                            this.rspamdStatsLoading = false;
                        }
                    },
                    async updateRspamdActions() {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        try {
                            const res = await axios.post(`${ADMIN_API}/security/rspamd/actions`, this.rspamdActions);
                            if (res.data && res.data.actions) {
                                this.rspamdActions = { ...this.rspamdActions, ...res.data.actions };
                            }
                            await this.fetchRspamdActions();
                            _showToast('Rspamd 設定已更新');
                        } catch (e) {
                            _showToast('更新失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async fetchGreylistWhitelist() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/security/greylist/whitelist`);
                            this.greylistWhitelistText = res.data.join('\n');
                        } catch (e) {
                            const status = e?.response?.status;
                            if (status === 403 || status === 404) {
                                try {
                                    const res = await axios.get(`${ADMIN_API}/spam/greylist`);
                                    this.greylistWhitelistText = Array.isArray(res.data) ? res.data.join('\n') : '';
                                    return;
                                } catch (e2) {
                                    window.$toast?.error('載入 greylist whitelist 失敗', e2?.response?.data?.detail || e2?.message || '');
                                    console.error('Failed to fetch greylist whitelist', e2);
                                }
                            }
                            if (await this.handleAdminApiNotFound(e)) return;
                            console.error('Failed to fetch greylist whitelist', e);
                        }
                    },
                    async fetchSecurityMailSummary() {
                        this.securityMailSummaryLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/security/mail/summary`, {
                                params: { days: this.securityMailDays || 30, spam_threshold: this.securityMailSpamThreshold || 10 }
                            });
                            this.securityMailSummary = res.data || null;
                        } catch (e) {
                            const status = e?.response?.status;
                            if (status === 403 || status === 404) {
                                try {
                                    const res = await axios.get(`${ADMIN_API}/spam/mail/summary`, {
                                        params: { days: this.securityMailDays || 30, spam_threshold: this.securityMailSpamThreshold || 10 }
                                    });
                                    this.securityMailSummary = res.data || null;
                                    return;
                                } catch (e2) {
                                    this.securityMailSummary = null;
                                }
                            }
                            if (await this.handleAdminApiNotFound(e)) {
                                this.securityMailSummary = null;
                                return;
                            }
                            this.securityMailSummary = null;
                        } finally {
                            this.securityMailSummaryLoading = false;
                        }
                    },
                    async fetchSecurityMailTrend() {
                        this.securityMailTrendLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/security/mail/trend`, {
                                params: { days: this.securityMailDays || 30, spam_threshold: this.securityMailSpamThreshold || 10 }
                            });
                            this.securityMailTrend = (res.data && Array.isArray(res.data.trend)) ? res.data.trend : [];
                            nextTick(() => {
                                this.renderSecurityMailTrendChart();
                            });
                        } catch (e) {
                            const status = e?.response?.status;
                            if (status === 403 || status === 404) {
                                try {
                                    const res = await axios.get(`${ADMIN_API}/spam/mail/trend`, {
                                        params: { days: this.securityMailDays || 30, spam_threshold: this.securityMailSpamThreshold || 10 }
                                    });
                                    this.securityMailTrend = (res.data && Array.isArray(res.data.trend)) ? res.data.trend : [];
                                    nextTick(() => {
                                        this.renderSecurityMailTrendChart();
                                    });
                                    return;
                                } catch (e2) {
                                }
                            }
                            if (await this.handleAdminApiNotFound(e)) {
                                this.securityMailTrend = [];
                                if (this.securityMailTrendChartInstance) {
                                    this.securityMailTrendChartInstance.destroy();
                                    this.securityMailTrendChartInstance = null;
                                }
                                return;
                            }
                            this.securityMailTrend = [];
                            if (this.securityMailTrendChartInstance) {
                                this.securityMailTrendChartInstance.destroy();
                                this.securityMailTrendChartInstance = null;
                            }
                        } finally {
                            this.securityMailTrendLoading = false;
                        }
                    },
                    renderSecurityMailTrendChart() {
                        const canvas = document.getElementById('securityMailTrendChart');
                        if (!canvas) return;
                        if (this.securityMailTrendChartInstance) {
                            this.securityMailTrendChartInstance.destroy();
                            this.securityMailTrendChartInstance = null;
                        }
                        const dataRows = Array.isArray(this.securityMailTrend) ? this.securityMailTrend : [];
                        const labels = dataRows.map(r => (r && r.date) ? String(r.date) : '');
                        const totals = dataRows.map(r => Number((r && r.total) || 0));
                        const highs = dataRows.map(r => Number((r && r.high_spam_count) || 0));

                        this.securityMailTrendChartInstance = new Chart(canvas, {
                            type: 'line',
                            data: {
                                labels,
                                datasets: [
                                    {
                                        label: '總量',
                                        data: totals,
                                        borderColor: 'rgb(59, 130, 246)',
                                        backgroundColor: 'rgba(59, 130, 246, 0.12)',
                                        fill: true,
                                        tension: 0.35
                                    },
                                    {
                                        label: '高垃圾分',
                                        data: highs,
                                        borderColor: 'rgb(239, 68, 68)',
                                        backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                        fill: true,
                                        tension: 0.35
                                    }
                                ]
                            },
                            options: {
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { position: 'top' }
                                },
                                scales: {
                                    y: { beginAtZero: true, ticks: { precision: 0 } }
                                }
                            }
                        });
                    },
                    async refreshSecurityMailAudit() {
                        await Promise.all([this.fetchSecurityMailSummary(), this.fetchSecurityMailTrend()]);
                    },
                    async openSecurityHighSpamDetails() {
                        this.showSecurityHighSpamModal = true;
                        await this.fetchSecurityHighSpamDetails();
                    },
                    async ensureSenderMapsLoaded() {
                        if (!this.isSuperAdmin) return false;
                        if (this.senderMapsLoaded) return true;
                        await this.fetchSenderMaps();
                        return !!this.senderMapsLoaded;
                    },
                    async quickAllowSender(sender) {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        if (this.senderMapsLoading) return;
                        await this.ensureSenderMapsLoaded();
                        const s = (sender || '').toString().trim();
                        if (!s) return;
                        if (!confirm(`要將寄件者加入白名單嗎？\n\n${s}`)) return;
                        const current = this.senderAllowlistText.split('\n').map(l => l.trim()).filter(l => l);
                        if (!current.includes(s)) current.push(s);
                        this.senderAllowlistText = current.join('\n');
                        const blocked = this.senderBlocklistText.split('\n').map(l => l.trim()).filter(l => l && l !== s);
                        this.senderBlocklistText = blocked.join('\n');
                        await this.updateSenderAllowlist();
                    },
                    async quickBlockSender(sender) {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        if (this.senderMapsLoading) return;
                        await this.ensureSenderMapsLoaded();
                        const s = (sender || '').toString().trim();
                        if (!s) return;
                        if (!confirm(`要將寄件者加入黑名單嗎？\n\n${s}`)) return;
                        const current = this.senderBlocklistText.split('\n').map(l => l.trim()).filter(l => l);
                        if (!current.includes(s)) current.push(s);
                        this.senderBlocklistText = current.join('\n');
                        const allowed = this.senderAllowlistText.split('\n').map(l => l.trim()).filter(l => l && l !== s);
                        this.senderAllowlistText = allowed.join('\n');
                        await this.updateSenderBlocklist();
                    },
                    async quickAllowDomainFromText(text) {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        if (this.senderMapsLoading) return;
                        await this.ensureSenderMapsLoaded();
                        const v = this.senderMapDomainValueFromText(text);
                        if (!v) return;
                        if (!confirm(`要將網域加入白名單嗎？\n\n${v}`)) return;
                        const current = this.senderAllowlistText.split('\n').map(l => l.trim()).filter(l => l);
                        if (!current.includes(v)) current.push(v);
                        this.senderAllowlistText = current.join('\n');
                        const blocked = this.senderBlocklistText.split('\n').map(l => l.trim()).filter(l => l && l !== v);
                        this.senderBlocklistText = blocked.join('\n');
                        await this.updateSenderAllowlist();
                    },
                    async quickBlockDomainFromText(text) {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        if (this.senderMapsLoading) return;
                        await this.ensureSenderMapsLoaded();
                        const v = this.senderMapDomainValueFromText(text);
                        if (!v) return;
                        if (!confirm(`要將網域加入黑名單嗎？\n\n${v}`)) return;
                        const current = this.senderBlocklistText.split('\n').map(l => l.trim()).filter(l => l);
                        if (!current.includes(v)) current.push(v);
                        this.senderBlocklistText = current.join('\n');
                        const allowed = this.senderAllowlistText.split('\n').map(l => l.trim()).filter(l => l && l !== v);
                        this.senderAllowlistText = allowed.join('\n');
                        await this.updateSenderBlocklist();
                    },
                    closeSecurityHighSpamDetails() {
                        this.showSecurityHighSpamModal = false;
                        this.securityHighSpamLogs = [];
                        this.securityHighSpamLoading = false;
                        this.securityHighSpamFilter = { sender: '', status: '' };
                    },
                    async fetchSecurityHighSpamDetails() {
                        const days = (this.securityMailSummary && this.securityMailSummary.days) ? this.securityMailSummary.days : (this.securityMailDays || 30);
                        const minSpam = (this.securityMailSummary && this.securityMailSummary.spam_threshold != null) ? this.securityMailSummary.spam_threshold : (this.securityMailSpamThreshold || 10);
                        this.securityHighSpamLoading = true;
                        try {
                            const params = {
                                days,
                                min_spam_score: minSpam,
                                sender: (this.securityHighSpamFilter.sender || '').toString().trim() || undefined,
                                status: (this.securityHighSpamFilter.status || '').toString().trim() || undefined,
                                skip: 0,
                                limit: 200
                            };
                            const res = await axios.get(`${ADMIN_API}/security/mail/logs`, { params });
                            this.securityHighSpamLogs = Array.isArray(res.data) ? res.data : [];
                        } catch (e) {
                            const status = e?.response?.status;
                            if (status === 403 || status === 404) {
                                try {
                                    const params = {
                                        days,
                                        min_spam_score: minSpam,
                                        sender: (this.securityHighSpamFilter.sender || '').toString().trim() || undefined,
                                        status: (this.securityHighSpamFilter.status || '').toString().trim() || undefined,
                                        skip: 0,
                                        limit: 200
                                    };
                                    const res = await axios.get(`${ADMIN_API}/spam/mail/logs`, { params });
                                    this.securityHighSpamLogs = Array.isArray(res.data) ? res.data : [];
                                    return;
                                } catch (e2) {
                                    this.securityHighSpamLogs = [];
                                }
                            }
                            if (await this.handleAdminApiNotFound(e)) {
                                this.securityHighSpamLogs = [];
                                return;
                            }
                            this.securityHighSpamLogs = [];
                        } finally {
                            this.securityHighSpamLoading = false;
                        }
                    },
                    async openMailFromSecurityLog(row) {
                        if (!row) return;
                        const sender = this.extractEmail(row.sender || '');
                        if (!sender) {
                            _showToast('無法解析寄件者');
                            return;
                        }
                        const targetDate = row.timestamp ? new Date(row.timestamp) : null;
                        this.securityHighSpamOpenLoadingId = row.id;
                        try {
                            if (!await this.ensureMailSession('開啟郵件')) return;

                            const folders = ['Junk', 'INBOX', 'Trash', 'Archive', 'Sent'];
                            const wantSubject = (row.subject || '').toString().trim();
                            let best = null;

                            for (const folder of folders) {
                                try {
                                    const res = await axios.get(`${ADMIN_API}/mail/messages`, {
                                        params: { folder, limit: 50, offset: 0, search: sender },
                                        headers: { 'X-Mail-Session': this.mailSessionToken }
                                    });
                                    const msgs = (res.data && Array.isArray(res.data.messages)) ? res.data.messages : (Array.isArray(res.data) ? res.data : []);
                                    for (const m of (msgs || [])) {
                                        const fromEmail = this.extractEmail(m.from || '');
                                        if (!fromEmail || fromEmail.toLowerCase() !== sender.toLowerCase()) continue;

                                        let diffMs = 0;
                                        if (targetDate && !isNaN(targetDate.getTime())) {
                                            const md = m.date ? new Date(m.date) : null;
                                            if (md && !isNaN(md.getTime())) {
                                                diffMs = Math.abs(md.getTime() - targetDate.getTime());
                                            } else {
                                                diffMs = 10 * 365 * 24 * 60 * 60 * 1000;
                                            }
                                        }

                                        let score = diffMs;
                                        const subj = (m.subject || '').toString();
                                        if (wantSubject && subj && subj.toLowerCase().includes(wantSubject.toLowerCase())) score -= 6 * 60 * 60 * 1000;
                                        if (!best || score < best.score) {
                                            best = {
                                                folder,
                                                id: m.id,
                                                subject: subj,
                                                date: m.date,
                                                score
                                            };
                                        }
                                    }
                                } catch (e) {
                                }
                            }

                            if (!best || !best.id || !best.folder) {
                                const ok = confirm('找不到對應郵件。\n\n要切到郵件並用寄件者做搜尋（先在 INBOX）嗎？\n你也可以再手動改到 Junk / Trash。');
                                if (!ok) return;
                                this.currentView = 'webmail';
                                this.webmailTab = 'mail';
                                this.closeSecurityHighSpamDetails();
                                this.currentFolder = 'INBOX';
                                this.mailSearchQuery = sender;
                                await this.fetchMessages('INBOX', 1);
                                return;
                            }

                            const ok = confirm(`找到可能的郵件：\n\n資料夾：${best.folder}\n時間：${best.date || ''}\n主旨：${best.subject || ''}\n\n要切到郵件並開啟嗎？`);
                            if (!ok) return;

                            this.currentView = 'webmail';
                            this.webmailTab = 'mail';
                            this.closeSecurityHighSpamDetails();
                            this.currentFolder = best.folder;
                            this.mailSearchQuery = sender;
                            await this.fetchMessages(best.folder, 1);
                            await this.openMessage(best.id);
                        } finally {
                            if (this.securityHighSpamOpenLoadingId === row.id) this.securityHighSpamOpenLoadingId = null;
                        }
                    },
                    async updateGreylistWhitelist() {
                        if (!this.isSuperAdmin) {
                            _showToast('需要系統管理員權限');
                            return;
                        }
                        try {
                            const whitelist = this.greylistWhitelistText.split('\n').map(l => l.trim()).filter(l => l);
                            const res = await axios.post(`${ADMIN_API}/security/greylist/whitelist`, whitelist);
                            if (res.data && Array.isArray(res.data.whitelist)) {
                                this.greylistWhitelistText = res.data.whitelist.join('\n');
                            }
                            await this.fetchGreylistWhitelist();
                            _showToast('白名單已更新');
                        } catch (e) {
                            _showToast('更新失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },

                    // --- Logs ---
                    async fetchLogs() {
                        if (!this.token) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/logs/mail`);
                            this.logs = res.data;
                        } catch (e) { console.error(e); }
                    },
                    extractEmailDomain(address) {
                        const v = (address || '').toString().trim();
                        const at = v.lastIndexOf('@');
                        if (at <= 0) return '';
                        const d = v.slice(at + 1).trim().toLowerCase();
                        if (!d) return '';
                        return d.replace(/[>\s].*$/, '');
                    },
                    mailAuditDomainLabel(row) {
                        const s = this.extractEmailDomain(row?.sender);
                        const r = this.extractEmailDomain(row?.recipient);
                        if (s && r && s !== r) return `${s} → ${r}`;
                        return s || r || '-';
                    },
                    mailAuditStatusClass(status) {
                        const s = (status || '').toString().trim().toLowerCase();
                        if (!s) return 'bg-gray-500';
                        if (s.includes('sent') || s.includes('deliver')) return 'bg-green-500';
                        if (s.includes('bounce') || s.includes('reject') || s.includes('fail') || s.includes('error')) return 'bg-red-500';
                        if (s.includes('defer') || s.includes('queue') || s.includes('pending') || s.includes('retry')) return 'bg-yellow-500';
                        return 'bg-gray-500';
                    },
                    exportMailAuditCsv() {
                        const rows = this.safeArr(this.mailAuditRecords);
                        if (!rows.length) return;
                        const headers = this.isSuperAdmin
                            ? ['網域', '日期時間', '主旨', '寄件者', '收件者', '垃圾郵件分數', '狀態', '方向', '內容']
                            : ['日期時間', '主旨', '寄件者', '收件者', '垃圾郵件分數', '狀態', '方向', '內容'];
                        const escapeCsv = (v) => {
                            const s = (v ?? '').toString();
                            if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
                                return `"${s.replace(/"/g, '""')}"`;
                            }
                            return s;
                        };
                        const lines = [];
                        lines.push(headers.map(escapeCsv).join(','));
                        for (const row of rows) {
                            const directionLabel = row.direction === 'in' ? '進站' : (row.direction === 'out' ? '出站' : '');
                            const archiveLabel = row.has_archive ? '已保存' : '未保存';
                            const values = this.isSuperAdmin
                                ? [
                                    this.mailAuditDomainLabel(row),
                                    row.timestamp ? new Date(row.timestamp).toLocaleString() : '',
                                    row.subject || '',
                                    row.sender || '',
                                    row.recipient || '',
                                    (row.spam_score ?? '').toString(),
                                    row.status || '',
                                    directionLabel,
                                    archiveLabel
                                ]
                                : [
                                    row.timestamp ? new Date(row.timestamp).toLocaleString() : '',
                                    row.subject || '',
                                    row.sender || '',
                                    row.recipient || '',
                                    (row.spam_score ?? '').toString(),
                                    row.status || '',
                                    directionLabel,
                                    archiveLabel
                                ];
                            lines.push(values.map(escapeCsv).join(','));
                        }
                        const csv = '\ufeff' + lines.join('\r\n');
                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        const ymd = new Date().toISOString().slice(0, 10);
                        link.href = url;
                        link.setAttribute('download', `mail_audit_${ymd}.csv`);
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                    },
                    async exportMailAuditCsvAll() {
                        if (!this.token) return;
                        const maxRows = 5000;
                        const pageLimit = 500;
                        this.mailAuditLoading = true;
                        this.mailAuditError = '';
                        try {
                            const baseParams = {
                                skip: 0,
                                limit: pageLimit,
                                include_total: true,
                            };
                            const q = (this.mailAuditQuery || '').toString().trim();
                            if (q) baseParams.search = q;
                            const since = (this.mailAuditSince || '').toString().trim();
                            const until = (this.mailAuditUntil || '').toString().trim();
                            if (since) baseParams.since = since;
                            if (until) baseParams.until = until;
                            if (this.mailAuditMinScore !== null && this.mailAuditMinScore !== '' && this.mailAuditMinScore !== undefined) {
                                baseParams.min_spam_score = Number(this.mailAuditMinScore);
                            }
                            if (this.mailAuditMaxScore !== null && this.mailAuditMaxScore !== '' && this.mailAuditMaxScore !== undefined) {
                                baseParams.max_spam_score = Number(this.mailAuditMaxScore);
                            }
                            const dir = (this.mailAuditDirection || '').toString().trim().toLowerCase();
                            if (this.isDomainAdmin && dir && dir !== 'all') baseParams.direction = dir;
                            const dom = (this.mailAuditDomain || '').toString().trim();
                            if (this.isSuperAdmin && dom) baseParams.domain = dom;
                            const ha = (this.mailAuditHasArchive || '').toString().trim().toLowerCase();
                            if (ha === 'yes') baseParams.has_archive = true;
                            if (ha === 'no') baseParams.has_archive = false;

                            let allRows = [];
                            let total = 0;
                            let skip = 0;
                            while (true) {
                                const params = { ...baseParams, skip };
                                const res = await axios.get(`${ADMIN_API}/logs/mail`, { params });
                                const data = res.data || {};
                                const items = Array.isArray(data.items) ? data.items : [];
                                total = Number(data.total) || total || 0;
                                allRows = allRows.concat(items);
                                if (!items.length) break;
                                if (total && allRows.length >= total) break;
                                if (allRows.length >= maxRows) break;
                                skip += pageLimit;
                            }
                            if (!allRows.length) return;
                            if (total && total > maxRows) {
                                const ok = confirm(`符合條件共有 ${total} 筆，為避免瀏覽器卡頓，將只匯出前 ${maxRows} 筆。要繼續嗎？`);
                                if (!ok) return;
                                allRows = allRows.slice(0, maxRows);
                            }

                            const headers = this.isSuperAdmin
                                ? ['網域', '日期時間', '主旨', '寄件者', '收件者', '垃圾郵件分數', '狀態', '方向', '內容']
                                : ['日期時間', '主旨', '寄件者', '收件者', '垃圾郵件分數', '狀態', '方向', '內容'];
                            const escapeCsv = (v) => {
                                const s = (v ?? '').toString();
                                if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
                                    return `"${s.replace(/"/g, '""')}"`;
                                }
                                return s;
                            };
                            const lines = [];
                            lines.push(headers.map(escapeCsv).join(','));
                            for (const row of allRows) {
                                const directionLabel = row.direction === 'in' ? '進站' : (row.direction === 'out' ? '出站' : '');
                                const archiveLabel = row.has_archive ? '已保存' : '未保存';
                                const values = this.isSuperAdmin
                                    ? [
                                        this.mailAuditDomainLabel(row),
                                        row.timestamp ? new Date(row.timestamp).toLocaleString() : '',
                                        row.subject || '',
                                        row.sender || '',
                                        row.recipient || '',
                                        (row.spam_score ?? '').toString(),
                                        row.status || '',
                                        directionLabel,
                                        archiveLabel
                                    ]
                                    : [
                                        row.timestamp ? new Date(row.timestamp).toLocaleString() : '',
                                        row.subject || '',
                                        row.sender || '',
                                        row.recipient || '',
                                        (row.spam_score ?? '').toString(),
                                        row.status || '',
                                        directionLabel,
                                        archiveLabel
                                    ];
                                lines.push(values.map(escapeCsv).join(','));
                            }
                            const csv = '\ufeff' + lines.join('\r\n');
                            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            const ymd = new Date().toISOString().slice(0, 10);
                            link.href = url;
                            link.setAttribute('download', `mail_audit_all_${ymd}.csv`);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        } catch (e) {
                            this.mailAuditError = '匯出失敗: ' + (e.response?.data?.detail || e.message);
                        } finally {
                            this.mailAuditLoading = false;
                        }
                    },
                    scheduleMailAuditSearch() {
                        if (this.mailAuditQueryTimer) clearTimeout(this.mailAuditQueryTimer);
                        this.mailAuditQueryTimer = setTimeout(() => {
                            this.resetMailAuditAndFetch();
                        }, 400);
                    },
                    async resetMailAuditAndFetch() {
                        this.mailAuditSkip = 0;
                        await this.fetchMailAuditRecords();
                    },
                    async mailAuditPrevPage() {
                        const limit = Math.max(1, Number(this.mailAuditLimit) || 50);
                        this.mailAuditSkip = Math.max(0, (Number(this.mailAuditSkip) || 0) - limit);
                        await this.fetchMailAuditRecords();
                    },
                    async mailAuditNextPage() {
                        const limit = Math.max(1, Number(this.mailAuditLimit) || 50);
                        const next = (Number(this.mailAuditSkip) || 0) + limit;
                        const total = Number(this.mailAuditTotal) || 0;
                        if (total && next >= total) return;
                        this.mailAuditSkip = next;
                        await this.fetchMailAuditRecords();
                    },
                    scheduleSecurityAuditSearch() {
                        if (this.securityAuditQueryTimer) clearTimeout(this.securityAuditQueryTimer);
                        this.securityAuditQueryTimer = setTimeout(() => {
                            this.resetSecurityAuditAndFetch();
                        }, 400);
                    },
                    async resetSecurityAuditAndFetch() {
                        this.securityAuditSkip = 0;
                        await this.fetchSecurityAuditLogs();
                    },
                    async securityAuditPrevPage() {
                        const limit = Math.max(1, Number(this.securityAuditLimit) || 50);
                        this.securityAuditSkip = Math.max(0, (Number(this.securityAuditSkip) || 0) - limit);
                        await this.fetchSecurityAuditLogs();
                    },
                    async securityAuditNextPage() {
                        const limit = Math.max(1, Number(this.securityAuditLimit) || 50);
                        const next = (Number(this.securityAuditSkip) || 0) + limit;
                        const total = Number(this.securityAuditTotal) || 0;
                        if (total && next >= total) return;
                        this.securityAuditSkip = next;
                        await this.fetchSecurityAuditLogs();
                    },
                    securityAuditDetailText(row) {
                        const raw = row?.detail;
                        if (raw === null || raw === undefined) return '-';
                        if (typeof raw === 'object') {
                            try {
                                return JSON.stringify(raw);
                            } catch (e) {
                                return String(raw);
                            }
                        }
                        const s0 = String(raw).trim();
                        if (!s0) return '-';
                        if (s0.startsWith('{') || s0.startsWith('[')) {
                            try {
                                const obj = JSON.parse(s0);
                                if (Array.isArray(obj)) {
                                    return `Array(${obj.length})`;
                                }
                                if (obj && typeof obj === 'object') {
                                    const keys = Object.keys(obj);
                                    const parts = [];
                                    for (const k of keys.slice(0, 8)) {
                                        const v = obj[k];
                                        if (v === null || v === undefined) continue;
                                        if (typeof v === 'object') {
                                            if (Array.isArray(v)) parts.push(`${k}=[${v.length}]`);
                                            else parts.push(`${k}={...}`);
                                        } else {
                                            const text = String(v);
                                            parts.push(`${k}=${text.length > 120 ? text.slice(0, 120) + '…' : text}`);
                                        }
                                    }
                                    return parts.length ? parts.join('\n') : s0;
                                }
                                return String(obj);
                            } catch (e) {
                            }
                        }
                        return s0.length > 500 ? s0.slice(0, 500) + '…' : s0;
                    },
                    async fetchSecurityAuditLogs() {
                        if (!this.token) return;
                        this.securityAuditLoading = true;
                        this.securityAuditError = '';
                        try {
                            const params = {
                                skip: Math.max(0, Number(this.securityAuditSkip) || 0),
                                limit: Math.max(1, Math.min(Number(this.securityAuditLimit) || 50, 500)),
                                include_total: true,
                            };
                            const q = (this.securityAuditQuery || '').toString().trim();
                            if (q) params.search = q;
                            const since = (this.securityAuditSince || '').toString().trim();
                            const until = (this.securityAuditUntil || '').toString().trim();
                            if (since) params.since = since;
                            if (until) params.until = until;
                            const act = (this.securityAuditAction || '').toString().trim().toLowerCase();
                            if (act) params.action = act;
                            const dom = (this.securityAuditDomain || '').toString().trim();
                            if (this.isSuperAdmin && dom) params.domain = dom;

                            const res = await axios.get(`${ADMIN_API}/security/audit/logs`, { params });
                            const data = res?.data || {};
                            const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
                            this.securityAuditTotal = Number(data?.total) || 0;
                            this.securityAuditRecords = items;
                        } catch (e) {
                            this.securityAuditRecords = [];
                            this.securityAuditTotal = 0;
                            this.securityAuditError = '載入失敗: ' + (e?.response?.data?.detail || e?.message || 'unknown error');
                        } finally {
                            this.securityAuditLoading = false;
                        }
                    },
                    async openMailAuditDetail(row) {
                        const id = Number(row?.id) || 0;
                        if (!id) return;
                        this.showOriginalMailModal = true;
                        this.originalMailMessage = null;
                        this.originalMailLoading = true;
                        this.originalMailFolder = '';
                        try {
                            const res = await axios.get(`${ADMIN_API}/mail/logs/${id}`);
                            const payload = res.data || {};
                            const log = payload.log || {};
                            const archive = payload.archive || null;
                            const hasArchive = !!(archive && (archive.body_html || archive.body_text));
                            const raw = hasArchive ? (archive.body_html || archive.body_text) : '(尚未保存內容)';
                            this.originalMailMessage = {
                                id,
                                mailAuditLogId: id,
                                subject: log.subject || '(無主旨)',
                                from: log.sender || '',
                                to: log.recipient || '',
                                date: log.timestamp ? new Date(log.timestamp).toLocaleString() : '',
                                attachments: [],
                                body: this.sanitizeHtml(raw),
                                body_html: archive ? (archive.body_html || null) : null,
                                body_text: archive ? (archive.body_text || null) : null,
                                status: log.status || '',
                                spam_score: log.spam_score !== undefined ? log.spam_score : null,
                                direction: log.direction || '',
                                queue_id: log.queue_id || '',
                                message_id: log.message_id || '',
                                has_archive: hasArchive,
                            };
                        } catch (e) {
                            _showToast('無法載入稽核內容: ' + (e.response?.data?.detail || e.message));
                            this.showOriginalMailModal = false;
                        } finally {
                            this.originalMailLoading = false;
                        }
                    },
                    async fetchMailAuditArchive(logId) {
                        const id = Number(logId) || 0;
                        if (!id) return;
                        if (!(await this.ensureMailSession('補抓內容'))) return;
                        this.originalMailLoading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/mail/logs/${id}/archive`, {}, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const archive = res.data?.archive || null;
                            const raw = archive ? (archive.body_html || archive.body_text) : '';
                            if (!raw) {
                                _showToast('無法取得內容');
                                return;
                            }
                            if (this.originalMailMessage) {
                                this.originalMailMessage.body_html = archive.body_html || null;
                                this.originalMailMessage.body_text = archive.body_text || null;
                                this.originalMailMessage.body = this.sanitizeHtml(raw);
                                this.originalMailMessage.has_archive = true;
                            }
                            const idx = this.safeArr(this.mailAuditRecords).findIndex(r => (Number(r?.id) || 0) === id);
                            if (idx >= 0) this.mailAuditRecords[idx].has_archive = true;
                        } catch (e) {
                            const st = e?.response?.status;
                            if (st === 401) {
                                this.handleMailAuth401('補抓內容');
                                return;
                            }
                            _showToast('補抓失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.originalMailLoading = false;
                        }
                    },
                    async downloadMailAuditEml(logId) {
                        const id = Number(logId) || 0;
                        if (!id) return;
                        if (!(await this.ensureMailSession('下載 EML'))) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/mail/logs/${id}/download`, {
                                responseType: 'blob',
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const blob = new Blob([res.data], { type: 'message/rfc822' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `mail-log-${id}.eml`);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        } catch (e) {
                            const st = e?.response?.status;
                            if (st === 401) {
                                this.handleMailAuth401('下載 EML');
                                return;
                            }
                            _showToast('下載失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async fetchMailAuditAttachments(logId) {
                        const id = Number(logId) || 0;
                        if (!id) return;
                        if (!(await this.ensureMailSession('載入附件'))) return;
                        this.originalMailLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/mail/logs/${id}/attachments`, {
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const attachments = this.safeArr(res?.data?.attachments).filter(a => a && a.filename);
                            if (this.originalMailMessage) {
                                this.originalMailMessage.attachments = attachments;
                                this.$forceUpdate();
                            }
                        } catch (e) {
                            const st = e?.response?.status;
                            if (st === 401) {
                                this.handleMailAuth401('載入附件');
                                return;
                            }
                            _showToast('載入附件失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.originalMailLoading = false;
                        }
                    },
                    toggleMailAuditSelected(row, ev) {
                        const id = Number(row?.id) || 0;
                        if (!id) return;
                        if (!this.mailAuditSelectedIds || typeof this.mailAuditSelectedIds !== 'object') {
                            this.mailAuditSelectedIds = {};
                        }
                        const checked = !!ev?.target?.checked;
                        if (checked) {
                            this.mailAuditSelectedIds[id] = true;
                        } else {
                            delete this.mailAuditSelectedIds[id];
                        }
                    },
                    toggleMailAuditSelectAllCurrentPage(ev) {
                        const checked = !!ev?.target?.checked;
                        if (!this.mailAuditSelectedIds || typeof this.mailAuditSelectedIds !== 'object') {
                            this.mailAuditSelectedIds = {};
                        }
                        const rows = this.safeArr(this.mailAuditRecords);
                        const ids = rows.map(r => Number(r?.id) || 0).filter(id => id > 0);
                        if (checked) {
                            for (const id of ids) {
                                this.mailAuditSelectedIds[id] = true;
                            }
                        } else {
                            for (const id of ids) {
                                if (this.mailAuditSelectedIds[id]) delete this.mailAuditSelectedIds[id];
                            }
                        }
                    },
                    async downloadMailAuditEmlSelectedZip() {
                        const m = this.mailAuditSelectedIds && typeof this.mailAuditSelectedIds === 'object' ? this.mailAuditSelectedIds : {};
                        const ids = Object.keys(m).map(x => Number(x) || 0).filter(x => x > 0 && m[x]);
                        if (ids.length === 0) return;
                        if (!(await this.ensureMailSession('下載選取 EML'))) return;

                        this.mailAuditBulkDownloadLoading = true;
                        this.mailAuditError = '';
                        try {
                            const res = await axios.post(`${ADMIN_API}/mail/logs/download/bulk`, { ids }, {
                                responseType: 'blob',
                                headers: { 'X-Mail-Session': this.mailSessionToken }
                            });
                            const blob = new Blob([res.data], { type: 'application/zip' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            const ts = new Date().toISOString().replace(/[:\-]/g, '').replace('T', '_').slice(0, 15);
                            link.href = url;
                            link.setAttribute('download', `mail-logs-${ts}.zip`);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            this.mailAuditSelectedIds = {};
                        } catch (e) {
                            const st = e?.response?.status;
                            if (st === 401) {
                                this.handleMailAuth401('下載選取 EML');
                                return;
                            }
                            this.mailAuditError = '下載失敗: ' + (e.response?.data?.detail || e.message);
                        } finally {
                            this.mailAuditBulkDownloadLoading = false;
                        }
                    },
                    async batchFetchMailAuditArchiveCurrentPage() {
                        const targets = this.safeArr(this.mailAuditRecords)
                            .filter(r => r && r.id && !r.has_archive)
                            .map(r => Number(r?.id) || 0)
                            .filter(id => id > 0);
                        if (targets.length === 0) return;
                        if (!(await this.ensureMailSession('補抓本頁未保存'))) return;

                        this.mailAuditBatchArchiveLoading = true;
                        this.mailAuditBatchArchiveTotal = targets.length;
                        this.mailAuditBatchArchiveDone = 0;
                        this.mailAuditBatchArchiveFail = 0;
                        this.mailAuditError = '';

                        const headers = { 'X-Mail-Session': this.mailSessionToken };
                        const chunkSize = 100;

                        try {
                            for (let i = 0; i < targets.length; i += chunkSize) {
                                const chunk = targets.slice(i, i + chunkSize);
                                try {
                                    const res = await axios.post(`${ADMIN_API}/mail/logs/archive/bulk`, { ids: chunk }, { headers });
                                    const okIds = this.safeArr(res?.data?.ok).map(x => Number(x) || 0).filter(x => x > 0);
                                    const existingIds = this.safeArr(res?.data?.existing).map(x => Number(x) || 0).filter(x => x > 0);
                                    const failed = this.safeArr(res?.data?.failed);
                                    this.mailAuditBatchArchiveFail += failed.length;
                                    this.mailAuditBatchArchiveDone += okIds.length + existingIds.length + failed.length;

                                    const successSet = new Set([...okIds, ...existingIds]);
                                    if (successSet.size) {
                                        for (let k = 0; k < this.safeArr(this.mailAuditRecords).length; k += 1) {
                                            const rid = Number(this.mailAuditRecords[k]?.id) || 0;
                                            if (rid && successSet.has(rid)) this.mailAuditRecords[k].has_archive = true;
                                        }
                                    }
                                } catch (e) {
                                    const st = e?.response?.status;
                                    if (st === 401) {
                                        this.handleMailAuth401('補抓本頁未保存');
                                        return;
                                    }
                                    this.mailAuditBatchArchiveFail += chunk.length;
                                    this.mailAuditBatchArchiveDone += chunk.length;
                                }
                            }
                            const ok = Math.max(0, (this.mailAuditBatchArchiveTotal || 0) - (this.mailAuditBatchArchiveFail || 0));
                            this.mailAuditError = `補抓完成：成功 ${ok}，失敗 ${this.mailAuditBatchArchiveFail || 0}`;
                        } finally {
                            this.mailAuditBatchArchiveLoading = false;
                        }
                    },
                    async batchFetchMailAuditArchiveAllFiltered() {
                        if (!this.token) return;
                        if (!(await this.ensureMailSession('補抓全部未保存'))) return;

                        const maxRows = 5000;
                        const pageLimit = 500;
                        this.mailAuditBatchAllArchiveLoading = true;
                        this.mailAuditBatchAllArchiveTotal = 0;
                        this.mailAuditBatchAllArchiveDone = 0;
                        this.mailAuditBatchAllArchiveFail = 0;
                        this.mailAuditError = '';

                        const headers = { 'X-Mail-Session': this.mailSessionToken };

                        try {
                            const baseParams = {
                                skip: 0,
                                limit: pageLimit,
                                include_total: true,
                                has_archive: false,
                            };
                            const q = (this.mailAuditQuery || '').toString().trim();
                            if (q) baseParams.search = q;
                            const since = (this.mailAuditSince || '').toString().trim();
                            const until = (this.mailAuditUntil || '').toString().trim();
                            if (since) baseParams.since = since;
                            if (until) baseParams.until = until;
                            if (this.mailAuditMinScore !== null && this.mailAuditMinScore !== '' && this.mailAuditMinScore !== undefined) {
                                baseParams.min_spam_score = Number(this.mailAuditMinScore);
                            }
                            if (this.mailAuditMaxScore !== null && this.mailAuditMaxScore !== '' && this.mailAuditMaxScore !== undefined) {
                                baseParams.max_spam_score = Number(this.mailAuditMaxScore);
                            }
                            const dir = (this.mailAuditDirection || '').toString().trim().toLowerCase();
                            if (this.isDomainAdmin && dir && dir !== 'all') baseParams.direction = dir;
                            const dom = (this.mailAuditDomain || '').toString().trim();
                            if (this.isSuperAdmin && dom) baseParams.domain = dom;

                            let ids = [];
                            let total = 0;
                            let skip = 0;
                            while (true) {
                                const params = { ...baseParams, skip };
                                const res = await axios.get(`${ADMIN_API}/logs/mail`, { params });
                                const data = res.data || {};
                                const items = Array.isArray(data.items) ? data.items : [];
                                total = Number(data.total) || total || 0;
                                for (const it of items) {
                                    const id = Number(it?.id) || 0;
                                    if (id) ids.push(id);
                                }
                                if (!items.length) break;
                                if (total && ids.length >= total) break;
                                if (ids.length >= maxRows) break;
                                skip += pageLimit;
                            }

                            if (!ids.length) {
                                this.mailAuditError = '沒有需要補抓的未保存內容';
                                return;
                            }
                            if (total && total > maxRows) {
                                const ok = confirm(`符合條件共有 ${total} 筆未保存內容，為避免系統負載過高，將只補抓前 ${maxRows} 筆。要繼續嗎？`);
                                if (!ok) return;
                                ids = ids.slice(0, maxRows);
                            }

                            this.mailAuditBatchAllArchiveTotal = ids.length;
                            const chunkSize = 100;
                            for (let i = 0; i < ids.length; i += chunkSize) {
                                const chunk = ids.slice(i, i + chunkSize);
                                try {
                                    const res = await axios.post(`${ADMIN_API}/mail/logs/archive/bulk`, { ids: chunk }, { headers });
                                    const okIds = this.safeArr(res?.data?.ok).map(x => Number(x) || 0).filter(x => x > 0);
                                    const existingIds = this.safeArr(res?.data?.existing).map(x => Number(x) || 0).filter(x => x > 0);
                                    const failed = this.safeArr(res?.data?.failed);
                                    this.mailAuditBatchAllArchiveFail += failed.length;
                                    this.mailAuditBatchAllArchiveDone += okIds.length + existingIds.length + failed.length;

                                    const successSet = new Set([...okIds, ...existingIds]);
                                    if (successSet.size) {
                                        for (let k = 0; k < this.safeArr(this.mailAuditRecords).length; k += 1) {
                                            const rid = Number(this.mailAuditRecords[k]?.id) || 0;
                                            if (rid && successSet.has(rid)) this.mailAuditRecords[k].has_archive = true;
                                        }
                                    }
                                } catch (e) {
                                    const st = e?.response?.status;
                                    if (st === 401) {
                                        this.handleMailAuth401('補抓全部未保存');
                                        return;
                                    }
                                    this.mailAuditBatchAllArchiveFail += chunk.length;
                                    this.mailAuditBatchAllArchiveDone += chunk.length;
                                }
                            }

                            const ok = Math.max(0, (this.mailAuditBatchAllArchiveTotal || 0) - (this.mailAuditBatchAllArchiveFail || 0));
                            this.mailAuditError = `補抓全部完成：成功 ${ok}，失敗 ${this.mailAuditBatchAllArchiveFail || 0}`;
                        } catch (e) {
                            this.mailAuditError = '補抓全部失敗: ' + (e.response?.data?.detail || e.message);
                        } finally {
                            this.mailAuditBatchAllArchiveLoading = false;
                        }
                    },
                    async fetchMailAuditRecords() {
                        if (!this.token) return;
                        this.mailAuditLoading = true;
                        this.mailAuditError = '';
                        try {
                            const params = {
                                skip: Math.max(0, Number(this.mailAuditSkip) || 0),
                                limit: Math.max(1, Math.min(500, Number(this.mailAuditLimit) || 50)),
                                include_total: true,
                            };
                            const q = (this.mailAuditQuery || '').toString().trim();
                            if (q) params.search = q;
                            const since = (this.mailAuditSince || '').toString().trim();
                            const until = (this.mailAuditUntil || '').toString().trim();
                            if (since) params.since = since;
                            if (until) params.until = until;
                            if (this.mailAuditMinScore !== null && this.mailAuditMinScore !== '' && this.mailAuditMinScore !== undefined) {
                                params.min_spam_score = Number(this.mailAuditMinScore);
                            }
                            if (this.mailAuditMaxScore !== null && this.mailAuditMaxScore !== '' && this.mailAuditMaxScore !== undefined) {
                                params.max_spam_score = Number(this.mailAuditMaxScore);
                            }
                            const dom = (this.mailAuditDomain || '').toString().trim();
                            if (this.isSuperAdmin && dom) params.domain = dom;
                            const ha = (this.mailAuditHasArchive || '').toString().trim().toLowerCase();
                            if (ha === 'yes') params.has_archive = true;
                            if (ha === 'no') params.has_archive = false;
                            const dir = (this.mailAuditDirection || '').toString().trim().toLowerCase();
                            if (this.isDomainAdmin && dir && dir !== 'all') params.direction = dir;
                            const res = await axios.get(`${ADMIN_API}/logs/mail`, { params });
                            const data = res.data;
                            if (Array.isArray(data)) {
                                this.mailAuditRecords = data;
                                this.mailAuditTotal = data.length;
                            } else if (data && typeof data === 'object' && Array.isArray(data.items)) {
                                this.mailAuditRecords = data.items;
                                this.mailAuditTotal = Number(data.total) || 0;
                            } else {
                                this.mailAuditRecords = [];
                                this.mailAuditTotal = 0;
                            }
                            this.mailAuditSelectedIds = {};
                        } catch (e) {
                            this.mailAuditRecords = [];
                            this.mailAuditTotal = 0;
                            this.mailAuditSelectedIds = {};
                            this.mailAuditError = '載入失敗: ' + (e.response?.data?.detail || e.message);
                        } finally {
                            this.mailAuditLoading = false;
                        }
                    },

                    // --- AI Service ---
                    async fetchAiSettings() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/settings/ai`);
                            const rows = Array.isArray(res?.data)
                                ? res.data
                                : (Array.isArray(res?.data?.items) ? res.data.items : (Array.isArray(res?.data?.settings) ? res.data.settings : []));
                            this.aiSettings = rows;

                            // Populate form
                            (Array.isArray(this.aiSettings) ? this.aiSettings : []).forEach(s => {
                                if (!s || typeof s !== 'object') return;
                                if (s.setting_key === 'google_api_key') this.aiSettingsForm.google_api_key = s.setting_value;
                                if (s.setting_key === 'image_provider') this.aiSettingsForm.image_provider = s.setting_value;
                                if (s.setting_key === 'image_api_key') this.aiSettingsForm.image_api_key = s.setting_value;
                                if (s.setting_key === 'spam_threshold') this.aiSettingsForm.spam_threshold = parseFloat(s.setting_value || '0') || 0;
                                if (s.setting_key === 'use_ai_analysis') this.aiSettingsForm.use_ai_analysis = s.setting_value === 'true';
                                if (s.setting_key === 'ai_provider') this.aiSettingsForm.ai_provider = s.setting_value;
                                if (s.setting_key === 'gateway_base_url') this.aiSettingsForm.gateway_base_url = s.setting_value;
                                if (s.setting_key === 'gateway_api_key') this.aiSettingsForm.gateway_api_key = s.setting_value;
                                if (s.setting_key === 'gateway_model') this.aiSettingsForm.gateway_model = s.setting_value;
                                if (s.setting_key === 'ollama_base_url') this.aiSettingsForm.ollama_base_url = s.setting_value;
                                if (s.setting_key === 'ollama_model') this.aiSettingsForm.ollama_model = s.setting_value;
                                if (s.setting_key === 'lmstudio_base_url') this.aiSettingsForm.lmstudio_base_url = s.setting_value;
                                if (s.setting_key === 'lmstudio_model') this.aiSettingsForm.lmstudio_model = s.setting_value;
                                if (s.setting_key === 'vision_provider') this.aiSettingsForm.vision_provider = s.setting_value;
                                if (s.setting_key === 'vision_base_url') this.aiSettingsForm.vision_base_url = s.setting_value;
                                if (s.setting_key === 'vision_model') this.aiSettingsForm.vision_model = s.setting_value;
                                if (s.setting_key === 'security_auto_enabled') this.aiSettingsForm.security_auto_enabled = s.setting_value === 'true';
                                if (s.setting_key === 'security_auto_interval_seconds') this.aiSettingsForm.security_auto_interval_seconds = parseInt(s.setting_value || '60', 10) || 60;
                                if (s.setting_key === 'security_auto_folder') this.aiSettingsForm.security_auto_folder = s.setting_value || 'INBOX';
                                if (s.setting_key === 'security_auto_limit') this.aiSettingsForm.security_auto_limit = parseInt(s.setting_value || '30', 10) || 30;
                                if (s.setting_key === 'security_auto_unseen_only') this.aiSettingsForm.security_auto_unseen_only = s.setting_value === 'true';
                                if (s.setting_key === 'security_auto_phishing_enabled') this.aiSettingsForm.security_auto_phishing_enabled = s.setting_value === 'true';
                                if (s.setting_key === 'security_auto_phishing_threshold') this.aiSettingsForm.security_auto_phishing_threshold = parseFloat(s.setting_value || '0.7');
                                if (s.setting_key === 'security_auto_phishing_target') this.aiSettingsForm.security_auto_phishing_target = s.setting_value || 'Phishing';
                                if (s.setting_key === 'security_auto_spam_enabled') this.aiSettingsForm.security_auto_spam_enabled = s.setting_value === 'true';
                                if (s.setting_key === 'security_auto_spam_threshold') this.aiSettingsForm.security_auto_spam_threshold = parseFloat(s.setting_value || '5.0');
                                if (s.setting_key === 'security_auto_spam_target') this.aiSettingsForm.security_auto_spam_target = s.setting_value || 'Spam';
                                if (s.setting_key === 'cbi_crawl_enabled') this.aiSettingsForm.cbi_crawl_enabled = s.setting_value === 'true';
                                if (s.setting_key === 'cbi_crawl_max_pages') this.aiSettingsForm.cbi_crawl_max_pages = parseInt(s.setting_value || '5', 10) || 5;
                                if (s.setting_key === 'cbi_crawl_timeout_seconds') this.aiSettingsForm.cbi_crawl_timeout_seconds = parseInt(s.setting_value || '8', 10) || 8;
                                if (s.setting_key === 'cbi_crawl_allowed_paths') this.aiSettingsForm.cbi_crawl_allowed_paths = s.setting_value || '';
                                if (s.setting_key === 'cbi_crawl_user_agent') this.aiSettingsForm.cbi_crawl_user_agent = s.setting_value || '';
                            });

                            // 設定載入完成後自動撈模型清單（不等結果，背景執行）
                            const _p = this.aiSettingsForm.ai_provider;
                            if (_p === 'ollama') this.fetchAiModels('ollama').catch(() => {});
                            else if (_p === 'lmstudio') this.fetchAiModels('lmstudio').catch(() => {});
                            else if (_p === 'gateway') this.fetchAiModels('gateway').catch(() => {});

                            const _vp = this.aiSettingsForm.vision_provider;
                            if ((_vp === 'ollama' || _vp === 'lmstudio' || _vp === 'gateway') && _vp !== _p) {
                                this.fetchAiModels(_vp, this.aiSettingsForm.vision_base_url).catch(() => {});
                            }

                        } catch (e) {
                            window.$toast?.error('載入 AI settings 失敗', e?.response?.data?.detail || e?.message || '');
                            console.error('Failed to fetch AI settings', e);
                        }
                    },

                    async fetchAiModels(provider, baseUrlOverride) {
                        try {
                            let baseUrl = baseUrlOverride || '';
                            if (!baseUrl) {
                                if (provider === 'gateway') baseUrl = this.aiSettingsForm.gateway_base_url;
                                if (provider === 'ollama') baseUrl = this.aiSettingsForm.ollama_base_url;
                                if (provider === 'lmstudio') baseUrl = this.aiSettingsForm.lmstudio_base_url;
                            }

                            const res = await axios.get(`${ADMIN_API}/ai/models`, {
                                params: { provider, base_url: baseUrl }
                            });
                            const models = res.data.models || [];
                            this.availableModels = models;
                            if (provider === 'ollama') this.ollamaModels = models;
                            else if (provider === 'lmstudio') this.lmstudioModels = models;
                            else if (provider === 'gateway') this.gatewayModels = models;

                            if (models.length > 0) {
                                if (provider === 'gateway' && !this.aiSettingsForm.gateway_model) {
                                    this.aiSettingsForm.gateway_model = models[0];
                                }
                                if (provider === 'ollama' && !this.aiSettingsForm.ollama_model) {
                                    this.aiSettingsForm.ollama_model = models[0];
                                }
                                if (provider === 'lmstudio' && !this.aiSettingsForm.lmstudio_model) {
                                    this.aiSettingsForm.lmstudio_model = models[0];
                                }
                                if (provider === this.aiSettingsForm.vision_provider && !this.aiSettingsForm.vision_model) {
                                    this.aiSettingsForm.vision_model = models[0];
                                }
                            }
                            _showToast('已偵測到 ' + models.length + ' 個模型');
                        } catch (e) {
                            _showToast('無法偵測模型: ' + (e.response?.data?.detail || e.message));
                            if (provider === 'ollama') this.ollamaModels = [];
                            else if (provider === 'lmstudio') this.lmstudioModels = [];
                            else if (provider === 'gateway') this.gatewayModels = [];
                            this.availableModels = [];
                        }
                    },

                    async saveAiSetting(key, value) {
                        try {
                            await axios.post(`${ADMIN_API}/settings/ai`, {
                                setting_key: key,
                                setting_value: String(value)
                            });
                            _showToast('設定已儲存');
                            this.editingAiSetting = null;
                            this.fetchAiSettings();
                            this.showAiFollowupModal = false;
                            this.showLeadScoreModal = false;
                        } catch (e) {
                            _showToast('儲存失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },

                    async saveAiSettingsBatch(items, successMessage) {
                        const rows = Array.isArray(items) ? items : [];
                        if (rows.length === 0) return;
                        try {
                            for (const row of rows) {
                                const key = (row?.key || '').toString();
                                if (!key) continue;
                                await axios.post(`${ADMIN_API}/settings/ai`, {
                                    setting_key: key,
                                    setting_value: String(row?.value ?? '')
                                });
                            }
                            _showToast((successMessage || '設定已儲存').toString());
                            await this.fetchAiSettings();
                        } catch (e) {
                            _showToast('儲存失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },

                    async saveSecurityAutoSettings() {
                        const f = this.aiSettingsForm || {};
                        const clampInt = (v, d, minV, maxV) => {
                            let n = d;
                            try { n = parseInt(v, 10); } catch (e) { n = d; }
                            if (!isFinite(n)) n = d;
                            if (n < minV) n = minV;
                            if (n > maxV) n = maxV;
                            return n;
                        };
                        const clampFloat = (v, d, minV, maxV) => {
                            let n = d;
                            try { n = parseFloat(v); } catch (e) { n = d; }
                            if (!isFinite(n)) n = d;
                            if (n < minV) n = minV;
                            if (n > maxV) n = maxV;
                            return n;
                        };

                        const payload = [
                            { key: 'security_auto_enabled', value: !!f.security_auto_enabled },
                            { key: 'security_auto_interval_seconds', value: clampInt(f.security_auto_interval_seconds, 60, 10, 3600) },
                            { key: 'security_auto_folder', value: (f.security_auto_folder || 'INBOX').toString().trim() || 'INBOX' },
                            { key: 'security_auto_limit', value: clampInt(f.security_auto_limit, 30, 1, 500) },
                            { key: 'security_auto_unseen_only', value: !!f.security_auto_unseen_only },
                            { key: 'security_auto_phishing_enabled', value: !!f.security_auto_phishing_enabled },
                            { key: 'security_auto_phishing_threshold', value: clampFloat(f.security_auto_phishing_threshold, 0.7, 0.0, 1.0) },
                            { key: 'security_auto_phishing_target', value: (f.security_auto_phishing_target || 'Phishing').toString().trim() || 'Phishing' },
                            { key: 'security_auto_spam_enabled', value: !!f.security_auto_spam_enabled },
                            { key: 'security_auto_spam_threshold', value: clampFloat(f.security_auto_spam_threshold, 5.0, 0.0, 100.0) },
                            { key: 'security_auto_spam_target', value: (f.security_auto_spam_target || 'Spam').toString().trim() || 'Spam' },
                        ];
                        await this.saveAiSettingsBatch(payload, '自動隔離設定已儲存');
                    },

                    async testAiSpam() {
                        this.aiLoading = true;
                        this.aiResult = null;
                        try {
                            const res = await axios.post(`${AI_API}/analyze`, {
                                subject: this.aiTest.subject,
                                body: this.aiTest.body,
                                sender: this.aiTest.sender
                            });
                            this.aiResult = {
                                type: 'spam',
                                data: res.data
                            };
                        } catch (e) {
                            _showToast('分析失敗: ' + e.message);
                        } finally {
                            this.aiLoading = false;
                        }
                    },

                    async generateSummary() {
                        if (!this.aiTest.body) return _showToast('請輸入郵件內文');
                        this.aiLoading = true;
                        this.aiResult = null;
                        try {
                            const res = await axios.post(`${AI_API}/generate/summary`, {
                                text: this.aiTest.body,
                                images: this.aiTest.images
                            });
                            this.aiResult = {
                                type: 'summary',
                                title: '郵件摘要 (Summary)',
                                data: res.data,
                                error: res.data.error
                            };
                        } catch (e) {
                            this.aiResult = { type: 'error', error: e.message };
                        } finally {
                            this.aiLoading = false;
                        }
                    },

                    async generateReply() {
                        if (!this.aiTest.body) return _showToast('請輸入郵件內文');
                        this.aiLoading = true;
                        this.aiResult = null;
                        try {
                            const res = await axios.post(`${AI_API}/generate/reply`, {
                                text: this.aiTest.body,
                                tone: this.aiTest.tone || 'professional',
                                images: this.aiTest.images
                            });
                            this.aiResult = {
                                type: 'reply',
                                title: '建議回覆 (Suggested Reply)',
                                data: res.data,
                                error: res.data.error
                            };
                        } catch (e) {
                            this.aiResult = { type: 'error', error: e.message };
                        } finally {
                            this.aiLoading = false;
                        }
                    },

                    async analyzeSentiment() {
                        if (!this.aiTest.body) return _showToast('請輸入郵件內文');
                        this.aiLoading = true;
                        this.aiResult = null;
                        try {
                            const res = await axios.post(`${AI_API}/analyze/sentiment`, {
                                text: this.aiTest.body,
                                images: this.aiTest.images
                            });
                            this.aiResult = {
                                type: 'sentiment',
                                title: '情緒分析 (Sentiment Analysis)',
                                data: res.data,
                                error: res.data.error
                            };
                        } catch (e) {
                            this.aiResult = { type: 'error', error: e.message };
                        } finally {
                            this.aiLoading = false;
                        }
                    },

                    async generateMarketingContent() {
                        this.aiMarketingState.loading = true;
                        this.aiMarketingState.result = null;
                        this.aiMarketingState.step = 'generate';
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/edm`, {
                                product_name: this.aiMarketingState.form.product_name,
                                key_features: this.aiMarketingState.form.key_features.split('\n').filter(f => f.trim()),
                                target_audience: this.aiMarketingState.form.target_audience,
                                tone: this.aiMarketingState.form.tone
                            });

                            let result = res.data.result;
                            this.aiMarketingState.fullResult = null;

                            // Try to parse JSON from the result string
                            const _cleanHtml = (html) => {
                                if (typeof html !== 'string') return html;
                                // Replace literal \n / \t from AI double-escaping with real whitespace
                                let h = html.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
                                // If no HTML tags, convert markdown/plain-text to basic HTML
                                if (!/<[a-z][\s\S]*>/i.test(h)) {
                                    h = h
                                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                        .replace(/^#{1,2}\s+(.+)$/gm, '<h2 style="font-size:20px;color:#1a1a2e;margin:1.2em 0 0.4em;">$1</h2>')
                                        .replace(/^[-*]\s+(.+)$/gm, '<li style="margin:0.4em 0;">$1</li>')
                                        .replace(/(<li[\s\S]*?<\/li>)/g, '<ul style="padding-left:1.2em;">$1</ul>')
                                        .replace(/\n{2,}/g, '</p><p style="margin:1em 0;line-height:1.7;">')
                                        .replace(/\n/g, '<br>');
                                    h = `<div style="font-family:Arial,sans-serif;font-size:16px;color:#333;max-width:600px;margin:0 auto;padding:24px;line-height:1.7;"><p style="margin:1em 0;line-height:1.7;">${h}</p></div>`;
                                }
                                return h;
                            };
                            if (typeof result === 'string') {
                                try {
                                    const jsonMatch = result.match(/```json\n([\s\S]*?)\n```/) || result.match(/\{[\s\S]*\}/);
                                    if (jsonMatch) {
                                        const parsed = JSON.parse(jsonMatch[1] || jsonMatch[0]);
                                        this.aiMarketingState.fullResult = parsed;
                                        this.aiMarketingState.result = _cleanHtml(parsed.body_html || result);
                                    } else {
                                        this.aiMarketingState.result = _cleanHtml(result);
                                    }
                                } catch (e) {
                                    this.aiMarketingState.result = _cleanHtml(result);
                                }
                            } else {
                                this.aiMarketingState.result = result;
                            }
                        } catch (e) {
                            _showToast('生成失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.aiMarketingState.loading = false;
                        }
                    },

                    useMarketingContent() {
                        const subject = this.aiMarketingState.fullResult ? (this.aiMarketingState.fullResult.subject || '') : '';
                        const rawBody = this.aiMarketingState.fullResult
                            ? (this.aiMarketingState.fullResult.body_html || this.aiMarketingState.result || '')
                            : (this.aiMarketingState.result || '');

                        this.composeForm.subject = subject;
                        if (/<[a-z][\s\S]*>/i.test(rawBody)) {
                            this.composeForm.html_body = rawBody;
                            this.composeForm.body = this.stripHtml(rawBody);
                        } else {
                            this.composeForm.html_body = '';
                            this.composeForm.body = rawBody;
                        }
                        this.composeEditorMode = (this.composeForm.html_body && this.composeForm.html_body.toString().trim()) ? 'html_preview' : 'rich';

                        this.showAiMarketingModal = false;
                        this.composeAttachmentSources = (this.composeForm.attachments || []).map(() => ({ type: 'upload' }));
                        this.composeResourceSearch = '';
                        this.composeResourceAttachments = [];
                        this.composeResourceErrors = {};
                        this.composeResourceErrorMeta = {};
                        this.composeResourceErrorExpanded = {};
                        this.composeResourceValidating = {};
                        this.composeResourceValidateQueue = [];
                        if (this.composeResourceValidateTimer) clearTimeout(this.composeResourceValidateTimer);
                        this.composeResourceValidateTimer = null;
                        this.composeResourceBanner = '';
                        this.showComposeModal = true;
                        this.ensureComposeResourcePoolLoaded();
                    },

                    stopCampaignProgressPolling() {
                        if (this.campaignProgressTimer) {
                            clearInterval(this.campaignProgressTimer);
                        }
                        this.campaignProgressTimer = null;
                    },
                    async fetchCampaignDetails(campaignId) {
                        const id = Number(campaignId || 0);
                        if (!id) return;
                        this.campaignDetails.loading = true;
                        this.campaignDetails.error = '';
                        this.campaignDetails.data = null;
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/campaign/${id}`, {
                                params: { _ts: Date.now() }
                            });
                            this.campaignDetails.data = res?.data || null;
                        } catch (e) {
                            this.campaignDetails.error = (e.response?.data?.detail || e.message || '載入失敗').toString();
                        } finally {
                            this.campaignDetails.loading = false;
                        }
                    },
                    async openCampaignDetails(campaignId) {
                        const id = Number(campaignId || 0);
                        if (!id) return;
                        this.showCampaignDetailsModal = true;
                        await this.fetchCampaignDetails(id);
                    },
                    closeCampaignDetails() {
                        this.showCampaignDetailsModal = false;
                        this.campaignDetails = { loading: false, error: '', data: null };
                    },
                    async fetchCampaignList(reset = false) {
                        if (reset) {
                            this.campaignList.offset = 0;
                            this.campaignList.items = [];
                        }
                        this.campaignList.loading = true;
                        this.campaignList.error = '';
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/campaigns`, {
                                params: { skip: this.campaignList.offset, limit: this.campaignList.limit, _ts: Date.now() }
                            });
                            const items = Array.isArray(res?.data) ? res.data : [];
                            if (this.campaignList.offset === 0) {
                                this.campaignList.items = items;
                            } else {
                                this.campaignList.items = this.safeArr(this.campaignList.items).concat(items);
                            }
                            this.campaignList.offset = this.campaignList.offset + items.length;
                        } catch (e) {
                            this.campaignList.error = (e.response?.data?.detail || e.message || '載入失敗').toString();
                        } finally {
                            this.campaignList.loading = false;
                        }
                    },
                    async openCampaignList(statusFilter) {
                        if (statusFilter) this.campaignList.status = statusFilter;
                        this.showCampaignListModal = true;
                        await this.fetchCampaignList(true);
                    },
                    async openScheduledCampaignList() {
                        this.campaignList.status = 'scheduled';
                        this.showCampaignListModal = true;
                        await this.fetchCampaignList(true);
                    },
                    closeCampaignList() {
                        this.showCampaignListModal = false;
                        this.campaignList = { loading: false, error: '', items: [], offset: 0, limit: 20, q: '', status: 'all' };
                    },
                    filteredCampaignListItems() {
                        const q = (this.campaignList?.q || '').toString().trim().toLowerCase();
                        const st = (this.campaignList?.status || 'all').toString().trim().toLowerCase();
                        const items = this.safeArr(this.campaignList?.items);
                        return items.filter((c) => {
                            const okStatus = (st === 'all') ? true : ((c?.status || '').toString().toLowerCase() === st);
                            if (!okStatus) return false;
                            if (!q) return true;
                            const hay = [
                                (c?.id ?? '').toString(),
                                (c?.subject || '').toString(),
                                (c?.sender || '').toString()
                            ].join(' ').toLowerCase();
                            return hay.includes(q);
                        });
                    },
                    async retryCampaignFailedRecipients(campaignId) {
                        const id = Number(campaignId || 0);
                        if (!id) return;
                        if (!confirm(`要重試寄送失敗收件人嗎？campaign_id=${id}`)) return;
                        this.campaignOps.retryLoading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/campaign/${id}/retry_failed`, {});
                            const status = res?.data?.status || '';
                            if (status === 'queued') {
                                this.startCampaignProgressPolling(id);
                                if (this.showCampaignDetailsModal) {
                                    await this.fetchCampaignDetails(id);
                                }
                                _showToast(`已加入重試佇列：失敗筆數=${res?.data?.failed_count || 0}`);
                            } else if (status === 'no_failed') {
                                _showToast('目前沒有失敗收件人可重試');
                            } else {
                                _showToast(`重試回應：${JSON.stringify(res?.data || {}, null, 2)}`);
                            }
                        } catch (e) {
                            _showToast('重試失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.campaignOps.retryLoading = false;
                        }
                    },
                    async downloadCampaignFailuresCsv(campaignId) {
                        const id = Number(campaignId || 0);
                        if (!id) return;
                        this.campaignOps.downloadLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/campaign/${id}/failures.csv`, {
                                responseType: 'blob',
                                params: { _ts: Date.now() }
                            });
                            const blob = res?.data instanceof Blob ? res.data : new Blob([res?.data || ''], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `campaign_${id}_failures.csv`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            try { URL.revokeObjectURL(url); } catch (e) {}
                        } catch (e) {
                            _showToast('下載失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.campaignOps.downloadLoading = false;
                        }
                    },
                    async downloadCampaignRecipientsCsv(campaignId, status = 'all') {
                        const id = Number(campaignId || 0);
                        if (!id) return;
                        const st = (status || 'all').toString().trim().toLowerCase();
                        this.campaignOps.downloadLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/campaign/${id}/recipients.csv`, {
                                responseType: 'blob',
                                params: { status: st, _ts: Date.now() }
                            });
                            const blob = res?.data instanceof Blob ? res.data : new Blob([res?.data || ''], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `campaign_${id}_recipients_${st}.csv`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            try { URL.revokeObjectURL(url); } catch (e) {}
                        } catch (e) {
                            _showToast('下載失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.campaignOps.downloadLoading = false;
                        }
                    },
                    async fetchCampaignProgress(campaignId) {
                        const id = Number(campaignId || 0);
                        if (!id) return;
                        this.campaignProgress.loading = true;
                        this.campaignProgress.error = '';
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/campaign/${id}/progress`, {
                                params: { _ts: Date.now() }
                            });
                            const data = res?.data || {};
                            this.campaignProgress = {
                                ...this.campaignProgress,
                                ...data,
                                loading: false,
                                error: ''
                            };
                            if (data?.progress?.finished) {
                                this.stopCampaignProgressPolling();
                            }
                        } catch (e) {
                            this.campaignProgress.loading = false;
                            this.campaignProgress.error = (e.response?.data?.detail || e.message || '載入失敗').toString();
                        }
                    },
                    startCampaignProgressPolling(campaignId) {
                        const id = Number(campaignId || 0);
                        if (!id) return;
                        this.stopCampaignProgressPolling();
                        this.campaignProgress = {
                            campaign_id: id,
                            loading: false,
                            error: '',
                            status: '',
                            total_recipients: 0,
                            counts: { pending: 0, sent: 0, failed: 0, moved: 0, other: 0 },
                            progress: { processed: 0, pending: 0, percent: 0, finished: false },
                            failures: [],
                            server_time: null
                        };
                        this.fetchCampaignProgress(id);
                        this.campaignProgressTimer = setInterval(() => {
                            if (!this.showAiMarketingModal) {
                                this.stopCampaignProgressPolling();
                                return;
                            }
                            if (this.campaignProgress?.progress?.finished) {
                                this.stopCampaignProgressPolling();
                                return;
                            }
                            this.fetchCampaignProgress(id);
                        }, 2000);
                    },

                    async fetchWarmupStatus(senderEmail) {
                        this.warmupLoading = true;
                        try {
                            const params = senderEmail ? { sender_email: senderEmail } : {};
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/warmup/status`, { params });
                            this.warmupStatus = res.data;
                        } catch (e) {
                            _showToast('預熱狀態載入失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.warmupLoading = false;
                        }
                    },
                    async startWarmup(senderEmail) {
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/warmup/start`, { sender_email: senderEmail });
                            this.warmupStatus = res.data;
                            _showToast('IP 預熱已啟動');
                        } catch (e) {
                            _showToast('啟動失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async disableWarmup(senderEmail) {
                        try {
                            await axios.post(`${ADMIN_API}/ai/marketing/warmup/disable`, { sender_email: senderEmail });
                            this.warmupStatus = { enabled: false };
                            _showToast('IP 預熱已停用');
                        } catch (e) {
                            _showToast('停用失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async submitRemarket(campaignId) {
                        const id = Number(campaignId || 0);
                        if (!id) return;
                        if (!this.remarketForm.subject.trim()) return _showToast('請輸入再行銷主旨');
                        this.remarketLoading = true;
                        this.remarketResult = null;
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/campaign/${id}/remarket`, {
                                subject: this.remarketForm.subject.trim(),
                                delay_days: Number(this.remarketForm.delay_days) || 0,
                            });
                            this.remarketResult = res.data;
                            const msg = res.data.scheduled_at
                                ? `已排程再行銷，${res.data.recipient_count} 位未開信者，${new Date(res.data.scheduled_at).toLocaleString()} 發送`
                                : `再行銷已啟動，${res.data.recipient_count} 位未開信者`;
                            _showToast(msg);
                        } catch (e) {
                            _showToast('再行銷失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.remarketLoading = false;
                        }
                    },

                    async cancelScheduledCampaign(campaignId) {
                        const id = Number(campaignId || 0);
                        if (!id) return;
                        if (!confirm(`確定取消此排程活動？(ID=${id})`)) return;
                        try {
                            await axios.delete(`${ADMIN_API}/ai/marketing/campaign/${id}/schedule`);
                            _showToast(`排程活動 #${id} 已取消`);
                            await this.fetchCampaignList(true);
                        } catch (e) {
                            _showToast('取消失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },

                    async fetchUnreadCounts() {
                        if (!this.mailSessionToken) return;
                        try {
                            const res = await axios.get(`${ADMIN_API}/mail/unread_counts`, {
                                headers: {
                                    'X-Mail-Session': this.mailSessionToken,
                                }
                            });
                            this.folderUnreadCounts = res.data || {};
                        } catch (_e) {
                            // 靜默失敗，不影響主流程
                        }
                    },

                    async sendCampaign() {
                        if (!this.campaignForm.recipients) return _showToast('請輸入收件人');

                        this.campaignSending = true;
                        this.campaignResult = null;

                        try {
                            if (!await this.ensureMailSession('發送行銷活動')) {
                                const msg = (this.webmailLastError || '信箱工作階段尚未建立或已過期，無法發送行銷活動。請重新登入後再試一次。').toString();
                                _showToast(msg);
                                return;
                            }
                            const lines = this.campaignForm.recipients.split('\n').filter(l => l.trim());
                            if (lines.length === 0) throw new Error('無效的收件人列表');

                            const recipients = lines.map(line => {
                                const parts = line.split(',');
                                const email = parts[0].trim();
                                const name = parts[1] ? parts[1].trim() : '';
                                return { email, name };
                            });

                            const baseSubject = this.aiMarketingState.fullResult ? this.aiMarketingState.fullResult.subject : '行銷郵件';
                            const bodyHtml = this.aiMarketingState.fullResult ? this.aiMarketingState.fullResult.body_html : this.aiMarketingState.result;
                            const scheduledAt = (this.aiMarketingState.scheduleEnabled && this.aiMarketingState.scheduledAt) ? this.aiMarketingState.scheduledAt : null;

                            if (this.aiMarketingState.abTestEnabled && this.aiMarketingState.subjectB && this.aiMarketingState.subjectB.trim()) {
                                // A/B test: split recipients 50/50
                                const half = Math.ceil(recipients.length / 2);
                                const groupA = recipients.slice(0, half);
                                const groupB = recipients.slice(half);
                                const payloadA = { subject: baseSubject, body_html: bodyHtml, recipients: groupA, ...(scheduledAt ? { scheduled_at: scheduledAt } : {}) };
                                const payloadB = { subject: this.aiMarketingState.subjectB.trim(), body_html: bodyHtml, recipients: groupB, ...(scheduledAt ? { scheduled_at: scheduledAt } : {}) };
                                const [resA, resB] = await Promise.all([
                                    axios.post(`${ADMIN_API}/ai/marketing/send_campaign`, payloadA, { headers: { 'X-Mail-Session': this.mailSessionToken } }),
                                    axios.post(`${ADMIN_API}/ai/marketing/send_campaign`, payloadB, { headers: { 'X-Mail-Session': this.mailSessionToken } }),
                                ]);
                                const idA = resA?.data?.campaign_id;
                                const idB = resB?.data?.campaign_id;
                                this.campaignResult = { campaign_id: idA, campaign_id_b: idB, recipient_count: recipients.length, ab_test: true };
                                if (idA) this.startCampaignProgressPolling(idA);
                                _showToast(`A/B 測試已啟動！\nA 組（主旨：${baseSubject}）：${groupA.length} 人\nB 組（主旨：${this.aiMarketingState.subjectB}）：${groupB.length} 人`);
                            } else {
                                const payload = { subject: baseSubject, body_html: bodyHtml, recipients, ...(scheduledAt ? { scheduled_at: scheduledAt } : {}) };
                                const res = await axios.post(`${ADMIN_API}/ai/marketing/send_campaign`, payload, {
                                    headers: { 'X-Mail-Session': this.mailSessionToken }
                                });
                                const campaignId = res?.data?.campaign_id;
                                const count = res?.data?.recipient_count;
                                this.campaignResult = { campaign_id: campaignId, recipient_count: (count || recipients.length) };
                                if (campaignId) this.startCampaignProgressPolling(campaignId);
                                const scheduleMsg = scheduledAt ? `\n排程時間：${new Date(scheduledAt).toLocaleString()}` : '';
                                _showToast(`已啟動行銷活動（背景處理）：campaign_id=${campaignId || '—'}，收件人=${count || recipients.length}${scheduleMsg}`);
                            }
                        } catch (e) {
                            _showToast('發送失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.campaignSending = false;
                        }
                    },

                    async checkEdmQuality() {
                        const subject = this.aiMarketingState.fullResult?.subject || '';
                        const body_html = this.aiMarketingState.fullResult?.body_html || this.aiMarketingState.result || '';
                        if (!body_html) return _showToast('請先生成 EDM 內容');
                        this.edmQualityLoading = true;
                        this.edmQualityResult = null;
                        this.edmQualityError = '';
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/edm_quality`, { subject, body_html });
                            const raw = res.data?.result || '';
                            // Parse structured result
                            const parse = (key) => {
                                const m = raw.match(new RegExp(key + ':\\s*(\\d+)'));
                                return m ? parseInt(m[1]) : null;
                            };
                            const suggestMatch = raw.match(/SUGGESTIONS:\s*([\s\S]+)/);
                            this.edmQualityResult = {
                                raw,
                                subject_score: parse('SUBJECT_SCORE'),
                                cta_score: parse('CTA_SCORE'),
                                spam_risk: parse('SPAM_RISK'),
                                personal_score: parse('PERSONAL_SCORE'),
                                overall: parse('OVERALL'),
                                suggestions: suggestMatch ? suggestMatch[1].trim() : '',
                            };
                        } catch (e) {
                            this.edmQualityError = e.response?.data?.detail || e.message;
                        } finally {
                            this.edmQualityLoading = false;
                        }
                    },

                    async translateEdm() {
                        const subject = this.aiMarketingState.fullResult?.subject || '';
                        const body_html = this.aiMarketingState.fullResult?.body_html || this.aiMarketingState.result || '';
                        if (!body_html) return _showToast('請先生成 EDM 內容');
                        this.edmTranslateLoading = true;
                        this.edmTranslateResult = null;
                        this.edmTranslateError = '';
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/edm_translate`, {
                                subject,
                                body_html,
                                target_language: this.edmTranslateLang,
                            });
                            const raw = res.data?.result || '';
                            try {
                                this.edmTranslateResult = JSON.parse(raw);
                            } catch {
                                this.edmTranslateResult = { subject: '', body_html: raw };
                            }
                        } catch (e) {
                            this.edmTranslateError = e.response?.data?.detail || e.message;
                        } finally {
                            this.edmTranslateLoading = false;
                        }
                    },

                    async generateAbSubject() {
                        const subject = this.aiMarketingState.fullResult?.subject || '';
                        if (!subject.trim()) return _showToast('請先生成 EDM（需要有主旨 A）');
                        this.aiMarketingState.abSubjectGenerating = true;
                        this.aiMarketingState.abSubjectError = '';
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/generate_ab_subject`, {
                                subject,
                                context: this.aiMarketingState.fullResult?.body_html ? '' : '',
                            });
                            const result = (res.data?.result || '').trim();
                            if (result) {
                                this.aiMarketingState.subjectB = result;
                                _showToast('AI 已產生主旨 B，可依需求修改');
                            } else {
                                this.aiMarketingState.abSubjectError = '未能產生主旨，請稍後再試';
                            }
                        } catch (e) {
                            this.aiMarketingState.abSubjectError = e.response?.data?.detail || e.message;
                        } finally {
                            this.aiMarketingState.abSubjectGenerating = false;
                        }
                    },

                    async fetchSendTimeStats() {
                        this.sendTimeLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/send_time_stats`);
                            this.sendTimeStats = res.data;
                        } catch (e) {
                            _showToast('載入時段分析失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.sendTimeLoading = false;
                        }
                    },
                    async checkListHealth() {
                        if (!this.campaignForm.recipients) return _showToast('請先輸入收件人');
                        this.listHealthLoading = true;
                        this.listHealth = null;
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/list_health`, {
                                recipients: this.campaignForm.recipients,
                            });
                            this.listHealth = res.data;
                        } catch (e) {
                            _showToast('健康度檢查失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.listHealthLoading = false;
                        }
                    },
                    async fetchWebhookConfig() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/webhook/config`);
                            this.webhookConfig = { ...this.webhookConfig, ...res.data };
                        } catch (e) {
                            _showToast('Webhook 設定載入失敗');
                        }
                    },
                    async saveWebhookConfig() {
                        this.webhookSaving = true;
                        try {
                            await axios.post(`${ADMIN_API}/ai/marketing/webhook/config`, {
                                webhook_url: this.webhookConfig.webhook_url,
                                enabled: this.webhookConfig.enabled,
                                events: this.webhookConfig.events,
                            });
                            _showToast('Webhook 設定已儲存');
                        } catch (e) {
                            _showToast('儲存失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.webhookSaving = false;
                        }
                    },
                    async generateSubscribeFormEmbed() {
                        this.subscribeFormLoading = true;
                        this.subscribeFormEmbed = null;
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/subscribe_form/embed`, {
                                params: {
                                    title: this.subscribeFormConfig.title,
                                    button_text: this.subscribeFormConfig.button_text,
                                    accent_color: this.subscribeFormConfig.accent_color,
                                },
                            });
                            this.subscribeFormEmbed = res.data;
                        } catch (e) {
                            _showToast('產生失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.subscribeFormLoading = false;
                        }
                    },
                    async submitAdvancedSegment() {
                        if (!this.advancedSegmentForm.name.trim()) return _showToast('請輸入分群名稱');
                        this.advancedSegmentLoading = true;
                        this.advancedSegmentResult = null;
                        try {
                            const payload = { ...this.advancedSegmentForm };
                            if (payload.campaign_id) payload.campaign_id = Number(payload.campaign_id);
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/segments/advanced`, payload);
                            this.advancedSegmentResult = res.data;
                            if (res.data.recipients_csv) {
                                this.campaignForm.recipients = res.data.recipients_csv;
                                _showToast(`已匯入 ${res.data.count} 位收件人至收件人列表`);
                            }
                        } catch (e) {
                            _showToast('分群失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.advancedSegmentLoading = false;
                        }
                    },
                    openInboxPreview() {
                        const html = this.aiMarketingState.fullResult?.body_html || this.aiMarketingState.result || '';
                        if (!html) return _showToast('請先生成 EDM 內容');
                        this.inboxPreviewHtml = html;
                        this.showInboxPreviewModal = true;
                    },
                    async runSpamCheck() {
                        const html = this.aiMarketingState.fullResult?.body_html || this.aiMarketingState.result || '';
                        if (!html) return _showToast('請先生成 EDM 內容');
                        this.spamCheck = { loading: true, result: null, error: '' };
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/spam_check`, {
                                subject: this.aiMarketingState.fullResult?.subject || this.aiMarketingState.form?.product_name || '',
                                body_html: html,
                            });
                            this.spamCheck = { loading: false, result: res.data, error: '' };
                        } catch (e) {
                            this.spamCheck = { loading: false, result: null, error: e.response?.data?.detail || e.message };
                        }
                    },
                    async downloadCampaignReportPdf(campaignId) {
                        const id = Number(campaignId || 0);
                        if (!id) return;
                        this.campaignPdfLoading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/campaign/${id}/report.pdf`, { responseType: 'blob' });
                            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `campaign_${id}_report.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                        } catch (e) {
                            _showToast('PDF 匯出失敗: ' + e.message);
                        } finally {
                            this.campaignPdfLoading = false;
                        }
                    },
                    importCsvToRecipients(csvText) {
                        csvText = csvText.replace(/^﻿/, ''); // strip UTF-8 BOM
                        const parseCols = (line) => {
                            const cols = []; let cur = '', inQ = false;
                            for (const ch of line) {
                                if (ch === '"') { inQ = !inQ; }
                                else if (ch === ',' && !inQ) { cols.push(cur.trim()); cur = ''; }
                                else { cur += ch; }
                            }
                            cols.push(cur.trim());
                            return cols.map(c => c.replace(/^"|"$/g, '').trim());
                        };
                        const lines = csvText.split(/\r?\n/).filter(l => l.trim());
                        if (!lines.length) return _showToast('CSV 為空');
                        const firstCols = parseCols(lines[0]).map(c => c.toLowerCase());
                        const isHeader = firstCols.some(c => ['email','mail','電子郵件','name','姓名','名稱'].includes(c));
                        let emailIdx = 0, nameIdx = 1, dataLines = lines;
                        if (isHeader) {
                            emailIdx = firstCols.findIndex(h => ['email','mail','電子郵件'].includes(h));
                            nameIdx  = firstCols.findIndex(h => ['name','姓名','名稱'].includes(h));
                            if (emailIdx === -1) emailIdx = 0;
                            if (nameIdx  === -1) nameIdx  = emailIdx === 0 ? 1 : 0;
                            dataLines = lines.slice(1);
                        }
                        const rows = dataLines.map(l => {
                            const cols = parseCols(l);
                            const email = (cols[emailIdx] || '').trim();
                            const name  = (cols[nameIdx]  || '').trim();
                            if (!email || !email.includes('@')) return null;
                            return name ? `${email}, ${name}` : email;
                        }).filter(Boolean);
                        if (!rows.length) return _showToast('未找到有效 Email 欄位');
                        this.campaignForm.recipients = rows.join('\n');
                        _showToast(`已匯入 ${rows.length} 位收件人`);
                    },

                    async previewFillVariables() {
                        const bodyHtml = this.aiMarketingState.fullResult?.body_html || this.aiMarketingState.result || '';
                        if (!bodyHtml) return _showToast('請先生成 EDM 內容');
                        const lines = (this.campaignForm.recipients || '').split('\n').filter(l => l.trim());
                        if (!lines.length) return _showToast('請先輸入收件人列表');
                        const emails = lines.map(l => l.split(',')[0].trim()).filter(Boolean);
                        this.fillVarsLoading = true;
                        this.fillVarsResult = null;
                        this.fillVarsError = '';
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/contacts/fill_variables`, {
                                recipients: emails,
                                body_html: bodyHtml,
                            });
                            this.fillVarsResult = res.data;
                            if (!res.data.variable_names?.length) {
                                _showToast('模板中無自訂變數（{{name}}/{{email}} 以外）');
                            }
                        } catch (e) {
                            this.fillVarsError = e.response?.data?.detail || e.message;
                        } finally {
                            this.fillVarsLoading = false;
                        }
                    },

                    openSupportChat() {
                        if (!this.supportChatSessionId) {
                            this.supportChatSessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
                        }
                        this.supportChatOpen = true;
                    },

                    closeSupportChat() {
                        this.supportChatOpen = false;
                    },

                    clearSupportChat() {
                        this.supportChatMessages = [];
                        this.supportChatSessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
                    },

                    async sendSupportMessage(text) {
                        if (!text?.trim() || this.supportChatLoading) return;
                        const userMsg = { role: 'user', content: text.trim() };
                        this.supportChatMessages = [...this.supportChatMessages, userMsg];
                        this.supportChatLoading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/support/chat`, {
                                messages: this.supportChatMessages,
                                session_id: this.supportChatSessionId,
                            });
                            this.supportChatMessages = [
                                ...this.supportChatMessages,
                                { role: 'assistant', content: res.data.answer },
                            ];
                        } catch (e) {
                            this.supportChatMessages = [
                                ...this.supportChatMessages,
                                { role: 'assistant', content: '⚠️ 暫時無法連線，請稍後再試。' },
                            ];
                        } finally {
                            this.supportChatLoading = false;
                        }
                    },

                    async redesignEdm() {
                        const bodyHtml = this.aiMarketingState.fullResult?.body_html || this.aiMarketingState.result || '';
                        const subject = this.aiMarketingState.fullResult?.subject || '';
                        if (!bodyHtml) return _showToast('請先生成 EDM 內容再重構');
                        this.edmRedesignLoading = true;
                        this.edmRedesignResult = null;
                        this.edmRedesignError = '';
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/edm_redesign`, {
                                subject,
                                body_html: bodyHtml,
                                style: this.edmRedesignStyle,
                            });
                            this.edmRedesignResult = res.data;
                        } catch (e) {
                            this.edmRedesignError = e.response?.data?.detail || e.message;
                        } finally {
                            this.edmRedesignLoading = false;
                        }
                    },

                    applyEdmRedesign() {
                        if (!this.edmRedesignResult?.html) return;
                        if (this.aiMarketingState.fullResult) {
                            this.aiMarketingState.fullResult.body_html = this.edmRedesignResult.html;
                        } else {
                            this.aiMarketingState.result = this.edmRedesignResult.html;
                        }
                        this.edmRedesignResult = null;
                        _showToast('已套用重構版型');
                    },

                    async sendTestCampaign() {
                        const testEmail = (this.currentUser?.email || '').toString().trim();
                        if (!testEmail) return _showToast('無法取得目前使用者 Email，請重新登入');
                        const subject = this.aiMarketingState.fullResult?.subject || '行銷郵件（測試）';
                        const body_html = this.aiMarketingState.fullResult?.body_html || this.aiMarketingState.result || '';
                        if (!body_html) return _showToast('請先生成 EDM 內容再測試寄送');
                        if (!confirm(`將發送一封測試信到 ${testEmail}，確定嗎？`)) return;
                        this.campaignTestSending = true;
                        try {
                            if (!await this.ensureMailSession('測試寄送')) {
                                _showToast(this.webmailLastError || '信箱工作階段尚未建立，請先登入信箱');
                                return;
                            }
                            await axios.post(`${ADMIN_API}/ai/marketing/send_campaign`, {
                                subject: `[測試] ${subject}`,
                                body_html,
                                recipients: [{ email: testEmail, name: this.currentUser?.name || '' }]
                            }, { headers: { 'X-Mail-Session': this.mailSessionToken } });
                            _showToast(`測試信已寄出至 ${testEmail}`);
                        } catch (e) {
                            _showToast('測試寄送失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.campaignTestSending = false;
                        }
                    },


                    async createSegmentFromCampaign(campaignId, segmentName) {
                        if (!segmentName || !segmentName.trim()) return _showToast('請輸入分群名稱');
                        if (!campaignId) return _showToast('無效的活動 ID');
                        this.campaignCreateSegmentLoading = true;
                        try {
                            // Get successful recipients from campaign
                            const res = await axios.get(`${ADMIN_API}/ai/marketing/campaign/${campaignId}`, {
                                params: { status: 'sent' }
                            });
                            const recipients = Array.isArray(res.data?.recipients) ? res.data.recipients : [];
                            if (recipients.length === 0) {
                                _showToast('沒有成功寄出的收件人，無法建立分群');
                                return;
                            }
                            const emails = recipients.map(r => r.email || r).filter(Boolean);
                            await axios.post(`${ADMIN_API}/marketing/segments`, {
                                name: segmentName.trim(),
                                description: `從活動 ${campaignId} 成功寄送的收件人建立`,
                                emails
                            });
                            this.campaignCreateSegmentName = '';
                            await this.fetchSegments();
                            _showToast(`分群「${segmentName}」已建立，共 ${emails.length} 位聯絡人`);
                        } catch (e) {
                            _showToast('建立分群失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.campaignCreateSegmentLoading = false;
                        }
                    },

                    async fetchCustomTemplates() {
                        this.templateMgmt.loading = true;
                        try {
                            const res = await axios.get(`${ADMIN_API}/marketing/templates`);
                            const all = Array.isArray(res.data) ? res.data : [];
                            this.templateMgmt.items = all.filter(t => !String(t.id).startsWith('builtin_'));
                        } catch (e) {
                            this.templateMgmt.items = [];
                        } finally {
                            this.templateMgmt.loading = false;
                        }
                    },

                    async saveCustomTemplate() {
                        const f = this.templateMgmt.form;
                        if (!f.name || !f.name.trim()) return _showToast('請輸入範本名稱');
                        if (!f.content_html || !f.content_html.trim()) return _showToast('請輸入範本內容');
                        this.templateMgmt.saving = true;
                        try {
                            if (this.templateMgmt.editingId) {
                                await axios.put(`${ADMIN_API}/marketing/templates/${this.templateMgmt.editingId}`, f);
                            } else {
                                await axios.post(`${ADMIN_API}/marketing/templates`, f);
                            }
                            this.templateMgmt.form = { name: '', description: '', subject: '', content_html: '' };
                            this.templateMgmt.editingId = null;
                            await this.fetchCustomTemplates();
                        } catch (e) {
                            _showToast('儲存範本失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.templateMgmt.saving = false;
                        }
                    },

                    async deleteCustomTemplate(id) {
                        if (!confirm('確定要刪除此範本嗎？')) return;
                        this.templateMgmt.deleting = id;
                        try {
                            await axios.delete(`${ADMIN_API}/marketing/templates/${id}`);
                            await this.fetchCustomTemplates();
                        } catch (e) {
                            _showToast('刪除失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.templateMgmt.deleting = null;
                        }
                    },

                    startEditCustomTemplate(t) {
                        this.templateMgmt.editingId = t.id;
                        this.templateMgmt.form = { name: t.name || '', description: t.description || '', subject: t.subject || '', content_html: t.content_html || '' };
                    },

                    async importTemplateFromUrl() {
                        const url = (this.templateMgmt.importUrl || '').trim();
                        if (!url) return _showToast('請輸入 URL');
                        this.templateMgmt.importLoading = true;
                        try {
                            const name = prompt('請為此範本命名：', url.split('/').pop() || '匯入範本');
                            if (!name) { this.templateMgmt.importLoading = false; return; }
                            const res = await axios.post(`${ADMIN_API}/marketing/templates/import-url`, { url, name });
                            _showToast('已成功從 URL 匯入範本');
                            this.templateMgmt.importUrl = '';
                            await this.fetchCustomTemplates();
                        } catch (e) {
                            _showToast('匯入失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.templateMgmt.importLoading = false;
                        }
                    },

                    filteredTemplates() {
                        const cat = (this.templateMgmt.filterCategory || '').toLowerCase();
                        const tag = (this.templateMgmt.filterTag || '').toLowerCase();
                        return (this.templateMgmt.items || []).filter(t => {
                            if (cat && t.category !== cat) return false;
                            if (tag && !(t.tags || '').toLowerCase().includes(tag)) return false;
                            return true;
                        });
                    },

                    async fetchUnsubscribeList() {
                        this.unsubscribeMgmt.loading = true;
                        try {
                            const res = await axios.post(`${ADMIN_API}/ai/marketing/unsubscribe/list`);
                            this.unsubscribeMgmt.items = res.data || [];
                        } catch (e) {
                            console.error('fetchUnsubscribeList error', e);
                        } finally {
                            this.unsubscribeMgmt.loading = false;
                        }
                    },
                    async addUnsubscribeEmail() {
                        const email = (this.unsubscribeMgmt.addEmail || '').trim().toLowerCase();
                        if (!email || !email.includes('@')) return _showToast('請輸入有效的 Email');
                        this.unsubscribeMgmt.adding = true;
                        try {
                            await axios.post(`${ADMIN_API}/ai/marketing/unsubscribe/add`, { email });
                            this.unsubscribeMgmt.addEmail = '';
                            await this.fetchUnsubscribeList();
                        } catch (e) {
                            _showToast('新增失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.unsubscribeMgmt.adding = false;
                        }
                    },
                    async removeUnsubscribeEmail(id) {
                        if (!confirm('確定要將此 Email 從退訂名單移除（重新訂閱）嗎？')) return;
                        try {
                            await axios.delete(`${ADMIN_API}/ai/marketing/unsubscribe/${id}`);
                            await this.fetchUnsubscribeList();
                        } catch (e) {
                            _showToast('移除失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },

                    async handleAiImageUpload(event) {
                        const files = Array.from(event.target.files || []);
                        event.target.value = '';

                        const maxImages = 4;
                        const maxBytes = 3 * 1024 * 1024;

                        for (const file of files) {
                            if (!file.type || !file.type.startsWith('image/')) continue;
                            if (this.aiTest.images.length >= maxImages) break;
                            if (file.size > maxBytes) {
                                _showToast(`圖片過大，已略過：${file.name}`);
                                continue;
                            }

                            const dataUrl = await new Promise((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = () => resolve(reader.result);
                                reader.onerror = () => reject(new Error('read_failed'));
                                reader.readAsDataURL(file);
                            });

                            const commaIdx = String(dataUrl).indexOf(',');
                            const base64 = commaIdx >= 0 ? String(dataUrl).slice(commaIdx + 1) : '';
                            if (!base64) continue;

                            this.aiTest.images.push({
                                mime_type: file.type,
                                data_base64: base64
                            });
                        }
                    },

                    removeAiImage(idx) {
                        this.aiTest.images.splice(idx, 1);
                    },


                    // --- SSL ---
                    async fetchSslInfo() {
                        try {
                            const res = await axios.get(`${ADMIN_API}/ssl/certificate`);
                            this.sslInfo = res.data;
                        } catch (e) {
                            window.$toast?.error('操作失敗', e?.response?.data?.detail || e?.message || '');
                            console.error(e);
                            this.sslInfo = { exists: false, error: 'Failed to fetch info' };
                        }
                    },
                    async generateSelfSignedCert() {
                        if (!confirm('確定要生成新的自簽憑證嗎？這將覆蓋現有的憑證！')) return;
                        try {
                            const res = await axios.post(`${ADMIN_API}/ssl/generate-self-signed?domain=${this.sslForm.domain}`);
                            this.sslInfo = res.data;
                            _showToast('自簽憑證生成成功！');
                        } catch (e) {
                            _showToast('生成憑證失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    handleSslUpload(event, type) {
                        if (type === 'cert') {
                            this.sslForm.certFile = event.target.files[0];
                        } else if (type === 'key') {
                            this.sslForm.keyFile = event.target.files[0];
                        }
                    },
                    async uploadSslCert() {
                        if (!this.sslForm.certFile || !this.sslForm.keyFile) {
                            _showToast('請選擇憑證檔 (.crt) 和私鑰檔 (.key)');
                            return;
                        }
                        if (!confirm('確定要上傳新的憑證嗎？這將覆蓋現有的憑證！')) return;

                        try {
                            const formData = new FormData();
                            formData.append('cert_file', this.sslForm.certFile);
                            formData.append('key_file', this.sslForm.keyFile);

                            const res = await axios.post(`${ADMIN_API}/ssl/upload`, formData, {
                                headers: {
                                    'Content-Type': 'multipart/form-data'
                                }
                            });
                            this.sslInfo = res.data;
                            _showToast('憑證上傳成功！');
                        } catch (e) {
                            _showToast('上傳憑證失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },

                    // --- Security Wizard ---
                    async fetchSecurityStatus() {
                        if (!this.securityDomain) {
                            _showToast('請輸入網域名稱');
                            return;
                        }
                        const selector = (this.securitySelector || 'default').toString().trim() || 'default';
                        try {
                            const token = localStorage.getItem('token');
                            // Check DKIM status
                            const dkimRes = await axios.get(`${ADMIN_API}/security/dkim/${this.securityDomain}`, {
                                params: { selector },
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.securityStatus = dkimRes.data;

                            // Get DNS suggestions
                            const domainValue = (this.securityDomain || '').toString().trim();
                            const ruaInput = (this.securityRuaEmail || '').toString().trim();
                            const ruaValue = ruaInput ? ruaInput : `admin@${domainValue}`;
                            const dnsRes = await axios.get(`${ADMIN_API}/security/dns/${this.securityDomain}`, {
                                params: {
                                    selector,
                                    dmarc_policy: (this.securityDmarcPolicy || 'none').toString().trim(),
                                    rua: ruaValue,
                                    pct: Number.isFinite(this.securityDmarcPct) ? this.securityDmarcPct : 100,
                                    adkim: (this.securityDmarcAdkim || 'r').toString().trim(),
                                    aspf: (this.securityDmarcAspf || 'r').toString().trim()
                                },
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.dnsSuggestions = dnsRes.data;
                            await this.checkSecurityDns();
                        } catch (e) {
                            window.$toast?.error('操作失敗', e?.response?.data?.detail || e?.message || '');
                            console.error(e);
                            _showToast('無法取得安全性狀態: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async generateDkim() {
                        if (!this.securityDomain) return;
                        if (!confirm(`確定要為 ${this.securityDomain} 生成新的 DKIM 金鑰嗎？`)) return;

                        try {
                            const token = localStorage.getItem('token');
                            const selector = (this.securitySelector || 'default').toString().trim() || 'default';
                            const res = await axios.post(`${ADMIN_API}/security/dkim/generate`, {}, {
                                params: { domain: this.securityDomain, selector },
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.securityStatus = { exists: true, ...res.data };

                            // Refresh suggestions
                            const domainValue = (this.securityDomain || '').toString().trim();
                            const ruaInput = (this.securityRuaEmail || '').toString().trim();
                            const ruaValue = ruaInput ? ruaInput : `admin@${domainValue}`;
                            const dnsRes = await axios.get(`${ADMIN_API}/security/dns/${this.securityDomain}`, {
                                params: {
                                    selector,
                                    dmarc_policy: (this.securityDmarcPolicy || 'none').toString().trim(),
                                    rua: ruaValue,
                                    pct: Number.isFinite(this.securityDmarcPct) ? this.securityDmarcPct : 100,
                                    adkim: (this.securityDmarcAdkim || 'r').toString().trim(),
                                    aspf: (this.securityDmarcAspf || 'r').toString().trim()
                                },
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.dnsSuggestions = dnsRes.data;
                            await this.checkSecurityDns();

                            _showToast('DKIM 金鑰生成成功！請更新您的 DNS 記錄。');
                        } catch (e) {
                            _showToast('生成失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async checkSecurityDns() {
                        if (!this.securityDomain) return;
                        const selector = (this.securitySelector || 'default').toString().trim() || 'default';
                        this.dnsCheckLoading = true;
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/security/dns/check/${this.securityDomain}`, {
                                params: { selector },
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.dnsCheckResult = res.data || null;
                        } catch (e) {
                            this.dnsCheckResult = null;
                        } finally {
                            this.dnsCheckLoading = false;
                        }
                    },
                    copyToClipboard(text) {
                        navigator.clipboard.writeText(text).then(() => {
                            // _showToast('已複製到剪貼簿'); // Optional feedback
                        });
                    },
                    securityDnsIssueTitle(issue) {
                        if (issue === 'MX_NOT_FOUND') return '未偵測到 MX 記錄';
                        if (issue === 'SPF_NOT_FOUND') return '未偵測到 SPF 記錄';
                        if (issue === 'MULTIPLE_SPF_RECORDS') return '偵測到多筆 SPF 記錄';
                        if (issue === 'DMARC_NOT_FOUND') return '未偵測到 DMARC 記錄';
                        if (issue === 'MULTIPLE_DMARC_RECORDS') return '偵測到多筆 DMARC 記錄';
                        if (issue === 'DKIM_KEY_EXISTS_BUT_DNS_NOT_FOUND') return '本機已生成 DKIM，但 DNS 尚未生效/未設定';
                        if (issue === 'DKIM_DNS_MISMATCH') return 'DNS DKIM 與本機金鑰不一致';
                        return issue || '未知問題';
                    },
                    securityDnsIssueDetail(issue) {
                        if (issue === 'MX_NOT_FOUND') return '請在 DNS 新增/修正 MX，否則可能無法收信。';
                        if (issue === 'SPF_NOT_FOUND') return '請新增 SPF TXT（通常主機名稱 @），避免寄信被判定為偽造。';
                        if (issue === 'MULTIPLE_SPF_RECORDS') return '請保留單一 v=spf1 的 TXT，其他 SPF 請合併到同一筆。';
                        if (issue === 'DMARC_NOT_FOUND') return '請新增 _dmarc TXT（可先用 p=none 監控），再逐步提高政策。';
                        if (issue === 'MULTIPLE_DMARC_RECORDS') return '請保留單一 _dmarc TXT，避免 DMARC 評估不確定。';
                        if (issue === 'DKIM_KEY_EXISTS_BUT_DNS_NOT_FOUND') return '請把「建議的 DNS 記錄」中的 DKIM TXT 加到 DNS，並等候傳播。';
                        if (issue === 'DKIM_DNS_MISMATCH') return '若近期重新生成金鑰，請更新 DNS；若不應變更，請確認網域/selector 是否一致。';
                        return '';
                    },

                    // --- Server Management ---
                    async fetchDnsRecords() {
                        if (!this.selectedDnsDomain) {
                            this.dnsRecords = [];
                            return;
                        }
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/domains/${this.selectedDnsDomain}/dns`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.dnsRecords = res.data;
                        } catch (e) {
                            window.$toast?.error('操作失敗', e?.response?.data?.detail || e?.message || '');
                            console.error(e);
                            _showToast('載入 DNS 記錄失敗');
                        }
                    },
                    async addDnsRecord() {
                        if (!this.selectedDnsDomain) {
                            _showToast('請先選擇網域');
                            return;
                        }
                        try {
                            const payload = {
                                ...this.newDnsRecord,
                                domain_id: this.selectedDnsDomain,
                                priority: this.newDnsRecord.type === 'MX' ? this.newDnsRecord.priority : 0
                            };
                            const token = localStorage.getItem('token');
                            await axios.post(`${ADMIN_API}/dns`, payload, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.fetchDnsRecords();
                            this.newDnsRecord.value = ''; // clear value
                            _showToast('DNS 記錄已新增');
                        } catch (e) { _showToast('新增 DNS 記錄失敗: ' + (e.response?.data?.detail || e.message)); }
                    },
                    async deleteDnsRecord(id) {
                        if (!confirm('確定刪除此 DNS 記錄?')) return;
                        try {
                            const token = localStorage.getItem('token');
                            await axios.delete(`${ADMIN_API}/dns/${id}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.fetchDnsRecords();
                        } catch (e) { _showToast('刪除失敗: ' + (e.response?.data?.detail || e.message)); }
                    },
                    async fetchServiceStatus() {
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/service/status`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.serviceStatus = res.data;
                        } catch (e) {
                            window.$toast?.error('Fetch status failed', e?.response?.data?.detail || e?.message || '');
                            console.error('Fetch status failed', e);
                        }
                    },
                    async controlService(service, action) {
                        if (!confirm(`確定要 ${action} ${service} 嗎?`)) return;
                        try {
                            const token = localStorage.getItem('token');
                            await axios.post(`${ADMIN_API}/service/${action}/${service}`, {}, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            _showToast('操作成功');
                            setTimeout(this.fetchServiceStatus, 2000); // Wait for restart
                        } catch (e) {
                            _showToast('操作失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async fetchMailSettings() {
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/mail/settings`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.mailSettings = res.data;
                        } catch (e) {
                            window.$toast?.error('Fetch mail settings failed', e?.response?.data?.detail || e?.message || '');
                            console.error('Fetch mail settings failed', e);
                        }
                    },
                    async saveMailSettings() {
                        try {
                            const token = localStorage.getItem('token');
                            await axios.post(`${ADMIN_API}/mail/settings`, this.mailSettings, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            _showToast('設定已儲存 (需手動重啟 Postfix 服務生效)');
                        } catch (e) {
                            _showToast('儲存失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async fetchDovecotSettings() {
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/mail/dovecot/settings`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.dovecotSettings = res.data;
                        } catch (e) {
                            _showToast('無法取得 Dovecot 設定: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async saveDovecotSettings() {
                        try {
                            const token = localStorage.getItem('token');
                            await axios.post(`${ADMIN_API}/mail/dovecot/settings`, this.dovecotSettings, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            _showToast('設定已儲存 (需手動重啟 Dovecot 服務生效)');
                        } catch (e) {
                            _showToast('儲存失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async fetchDbStats() {
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/database/stats`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.dbStats = res.data;
                        } catch (e) {
                            _showToast('無法取得資料庫統計: ' + (e.response?.data?.detail || e.message));
                        }
                    },

                    async fetchRagStats() {
                        if (!this.token) return;
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/rag/stats`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.ragStats = res.data;
                        } catch (e) {
                            // Silently fail — stats panel shows '—' when unavailable
                        }
                    },
                    async performRagQuery() {
                        const q = (this.ragQueryInput || '').toString().trim();
                        if (!q) return;
                        this.ragLoading = true;
                        this.ragResult = null;
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.post(`${ADMIN_API}/rag/query`, { query: q }, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.ragResult = res.data;
                        } catch (e) {
                            this.ragResult = { error: (e.response?.data?.detail || e.message || '搜尋失敗').toString() };
                        } finally {
                            this.ragLoading = false;
                        }
                    },
                    async fetchKnowledgeDocuments() {
                        this.kbDocsLoading = true;
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/ai/knowledge_base`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const items = res.data || [];
                            this.kbDocs = Array.isArray(items) ? items : [];
                        } catch (e) {
                            this.kbDocs = [];
                        } finally {
                            this.kbDocsLoading = false;
                        }
                    },
                    async addKnowledgeDocument() {
                        const title = (this.kbNewDocTitle || '').toString().trim();
                        const content = (this.kbNewDocContent || '').toString().trim();
                        if (!title || !content) return;
                        this.kbSaving = true;
                        try {
                            const token = localStorage.getItem('token');
                            await axios.post(`${ADMIN_API}/ai/knowledge_base`, { title, content }, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.kbNewDocTitle = '';
                            this.kbNewDocContent = '';
                            await this.fetchKnowledgeDocuments();
                            await this.fetchRagStats();
                        } catch (e) {
                            _showToast('新增知識文件失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.kbSaving = false;
                        }
                    },
                    async performKnowledgeQuery() {
                        const q = (this.kbAdminQueryInput || '').toString().trim();
                        if (!q) return;
                        this.kbAdminLoading = true;
                        this.kbAdminError = '';
                        this.kbAdminResult = '';
                        this.kbAdminSources = [];
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.post(`${ADMIN_API}/rag/query_knowledge`, { query: q, n_results: 4 }, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.kbAdminResult = (res.data?.result || '').toString();
                            const sources = res.data?.sources || [];
                            this.kbAdminSources = Array.isArray(sources) ? sources : [];
                            if (res.data?.error) this.kbAdminError = res.data.error.toString();
                        } catch (e) {
                            this.kbAdminError = (e.response?.data?.detail || e.message || '查詢失敗').toString();
                        } finally {
                            this.kbAdminLoading = false;
                        }
                    },
                    async sendRagMessage() {
                        if (!this.chatInput.trim()) return;

                        const userMsg = this.chatInput;
                        this.chatMessages.push({ role: 'user', content: userMsg });
                        this.chatInput = '';
                        this.isChatLoading = true;

                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.post(`${ADMIN_API}/rag/chat`, { query: userMsg }, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });

                            if (res.data.error) {
                                this.chatMessages.push({ role: 'assistant', content: `Error: ${res.data.error}` });
                            } else {
                                this.chatMessages.push({ role: 'assistant', content: res.data.result });
                            }
                        } catch (e) {
                            this.chatMessages.push({ role: 'assistant', content: 'Error connecting to AI service.' });
                        } finally {
                            this.isChatLoading = false;
                            nextTick(() => {
                                const container = document.getElementById('chat-messages');
                                if (container) container.scrollTop = container.scrollHeight;
                            });
                        }
                    },
                    async ingestEmail() {
                        if (!this.ingestForm.body) return;
                        this.isIngesting = true;
                        this.ingestMessage = '';

                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.post(`${ADMIN_API}/rag/ingest`, this.ingestForm, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (res.data.error) {
                                this.ingestMessage = `Error: ${res.data.error}`;
                            } else {
                                this.ingestMessage = '匯入成功！';
                                this.ingestForm = { subject: '', sender: '', body: '' };
                                this.fetchRagStats();
                            }
                        } catch (e) {
                            this.ingestMessage = '匯入失敗';
                        } finally {
                            this.isIngesting = false;
                        }
                    },
                    async syncRagMaildir() {
                        this.isSyncing = true;
                        this.syncMessage = '同步中...';
                        try {
                            const token = localStorage.getItem('token');
                            const response = await axios.post(`${ADMIN_API}/rag/sync`, {}, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (response.data.error) {
                                this.syncMessage = 'Error: ' + response.data.error;
                            } else {
                                this.syncMessage = response.data.result;
                                this.fetchRagStats();
                            }
                        } catch (error) {
                            this.syncMessage = 'Error syncing maildir: ' + (error.response?.data?.detail || error.message);
                        } finally {
                            this.isSyncing = false;
                        }
                    },

                    async fetchRagDocuments(q) {
                        this.ragState.knowledgeDocsLoading = true;
                        try {
                            const token = localStorage.getItem('token');
                            const params = {};
                            if (q) params.q = q;
                            const res = await axios.get(`${ADMIN_API}/rag/list_knowledge`, {
                                headers: { 'Authorization': `Bearer ${token}` },
                                params,
                            });
                            this.ragState.knowledgeDocs = (res.data?.documents || []);
                        } catch (e) {
                            this.ragState.knowledgeDocs = [];
                        } finally {
                            this.ragState.knowledgeDocsLoading = false;
                        }
                    },
                    async uploadRagDocument(file, title, departmentId, folderId) {
                        this.ragState.uploadLoading = true;
                        this.ragState.uploadError = '';
                        try {
                            const token = localStorage.getItem('token');
                            const formData = new FormData();
                            formData.append('file', file);
                            if (title) formData.append('title', title);
                            if (departmentId != null) formData.append('department_id', String(departmentId));
                            if (folderId != null) formData.append('folder_id', String(folderId));
                            await axios.post(`${ADMIN_API}/rag/upload_document`, formData, {
                                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                            });
                            _showToast('文件上傳成功並已建立索引', 'success');
                            await this.fetchRagDocuments();
                            await this.fetchRagStats();
                        } catch (e) {
                            const msg = (e.response?.data?.detail || e.message || '上傳失敗').toString();
                            this.ragState.uploadError = msg;
                            _showToast('上傳失敗: ' + msg, 'error');
                        } finally {
                            this.ragState.uploadLoading = false;
                        }
                    },
                    // ── KB Tags ──────────────────────────────────
                    async fetchKbTags() {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${ADMIN_API}/rag/tags`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        return res.data || [];
                    },
                    async createKbTag(name, color = '#6366f1') {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/tags`, { name, color },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async deleteKbTag(tagId) {
                        const token = localStorage.getItem('token');
                        await axios.delete(`${ADMIN_API}/rag/tags/${tagId}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                    },
                    async setDocTags(kbId, tagIds) {
                        const token = localStorage.getItem('token');
                        await axios.put(`${ADMIN_API}/rag/knowledge/${kbId}/tags`, { tag_ids: tagIds },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                    },
                    // ── KB Access Logs ────────────────────────────
                    async fetchKbAccessLogs(kbId) {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${ADMIN_API}/rag/knowledge/${kbId}/access_logs`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        return res.data || [];
                    },
                    // ── KB Batch ─────────────────────────────────
                    async batchDeleteKb(docIds) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/knowledge/batch/delete`,
                            { doc_ids: docIds },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async batchMoveKb(kbIds, folderId) {
                        const token = localStorage.getItem('token');
                        await axios.patch(`${ADMIN_API}/rag/knowledge/batch/move`,
                            { kb_ids: kbIds, folder_id: folderId },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                    },
                    // ── KB Dept Share ─────────────────────────────
                    async shareKbWithDept(kbId, deptId, canPreview = true, canDownload = true) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(
                            `${ADMIN_API}/rag/knowledge/${kbId}/share/department/${deptId}`,
                            { can_preview: canPreview, can_download: canDownload },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    // ── KB Versions ─────────────────────────────────
                    async fetchKbVersions(kbId) {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${ADMIN_API}/rag/knowledge/${kbId}/versions`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        return res.data || [];
                    },
                    async restoreKbVersion(kbId, versionId) {
                        const token = localStorage.getItem('token');
                        await axios.post(`${ADMIN_API}/rag/knowledge/${kbId}/restore/${versionId}`, {},
                            { headers: { 'Authorization': `Bearer ${token}` } });
                    },
                    // ── KB Status ────────────────────────────────────
                    async setKbStatus(kbId, status) {
                        const token = localStorage.getItem('token');
                        const res = await axios.patch(`${ADMIN_API}/rag/knowledge/${kbId}/status`,
                            { status },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    // ── KB Expiry ────────────────────────────────────
                    async setKbExpiry(kbId, expiresAt) {
                        const token = localStorage.getItem('token');
                        const res = await axios.patch(`${ADMIN_API}/rag/knowledge/${kbId}/expiry`,
                            { expires_at: expiresAt },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    // ── KB AI Summary ────────────────────────────────
                    async resummKbDoc(kbId) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/knowledge/${kbId}/summarize`, {},
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    // ── KB Export ─────────────────────────────────────
                    async exportKbZip() {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${ADMIN_API}/rag/knowledge/export`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!res.ok) {
                            _showToast('匯出失敗', 'error');
                            return;
                        }
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = 'knowledge_export.zip'; a.click();
                        setTimeout(() => URL.revokeObjectURL(url), 5000);
                    },
                    // ── KB Favorites ──────────────────────────────────────
                    async toggleKbFavorite(kbId) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/knowledge/${kbId}/favorite`, {},
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async fetchKbFavorites() {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${ADMIN_API}/rag/favorites`,
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data || [];
                    },
                    // ── KB Ratings ────────────────────────────────────────
                    async submitKbRating(kbId, score, comment) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/knowledge/${kbId}/rating`,
                            { score, comment },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async fetchKbRatings(kbId) {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${ADMIN_API}/rag/knowledge/${kbId}/ratings`,
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async deleteKbRating(kbId, ratingId) {
                        const token = localStorage.getItem('token');
                        await axios.delete(`${ADMIN_API}/rag/knowledge/${kbId}/rating/${ratingId}`,
                            { headers: { 'Authorization': `Bearer ${token}` } });
                    },
                    // ── KB Analytics ──────────────────────────────────────
                    async fetchKbAnalytics() {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${ADMIN_API}/rag/analytics`,
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    // ── KB Review Workflow ────────────────────────────────
                    async submitKbReview(kbId) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/knowledge/${kbId}/submit_review`, {},
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async fetchReviewQueue() {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${ADMIN_API}/rag/knowledge/review_queue`,
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data || [];
                    },
                    async approveKbDoc(kbId, note) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/knowledge/${kbId}/approve`,
                            { note: note || null },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async rejectKbDoc(kbId, note) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/knowledge/${kbId}/reject`,
                            { note: note || null },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    // ── KB Subscriptions ──────────────────────────────────
                    async toggleKbSubscription(kbId) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/knowledge/${kbId}/subscribe`, {},
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async toggleFolderSubscription(folderId) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/folders/${folderId}/subscribe`, {},
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async fetchKbSubscriptions() {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${ADMIN_API}/rag/subscriptions`,
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data || [];
                    },
                    // ── KB Related (17) ───────────────────────────────────
                    async fetchRelatedDocs(kbId) {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${ADMIN_API}/rag/knowledge/${kbId}/related`,
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data?.related || [];
                    },
                    // ── KB Diff (18) ──────────────────────────────────────
                    async fetchKbDiff(kbId, v1, v2) {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${ADMIN_API}/rag/knowledge/${kbId}/diff`,
                            { params: { v1, v2 }, headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    // ── KB Translate (19) ─────────────────────────────────
                    async translateKbDoc(kbId, targetLang) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/knowledge/${kbId}/translate`,
                            { target_lang: targetLang },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async fetchKbFolders() {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`${ADMIN_API}/rag/folders`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        return res.data || [];
                    },
                    async createKbFolder(name) {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${ADMIN_API}/rag/folders`, { name },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async deleteKbFolder(folderId) {
                        const token = localStorage.getItem('token');
                        await axios.delete(`${ADMIN_API}/rag/folders/${folderId}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                    },
                    async renameKbFolder(folderId, name) {
                        const token = localStorage.getItem('token');
                        const res = await axios.patch(`${ADMIN_API}/rag/folders/${folderId}`, { name },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                        return res.data;
                    },
                    async moveDocToFolder(kbId, folderId) {
                        const token = localStorage.getItem('token');
                        await axios.patch(`${ADMIN_API}/rag/knowledge/${kbId}/folder`,
                            { folder_id: folderId },
                            { headers: { 'Authorization': `Bearer ${token}` } });
                    },
                    async deleteRagDocument(docId) {
                        if (!docId) return;
                        this.ragState.deletingIds = [...(this.ragState.deletingIds || []), docId];
                        try {
                            const token = localStorage.getItem('token');
                            await axios.delete(`${ADMIN_API}/rag/delete_knowledge/${encodeURIComponent(docId)}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            _showToast('已刪除文件', 'success');
                            await this.fetchRagDocuments();
                            await this.fetchRagStats();
                        } catch (e) {
                            _showToast('刪除失敗: ' + (e.response?.data?.detail || e.message || ''), 'error');
                        } finally {
                            this.ragState.deletingIds = (this.ragState.deletingIds || []).filter(id => id !== docId);
                        }
                    },
                    async fetchKbShares(kbId) {
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/rag/knowledge/${kbId}/shares`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            return res.data || [];
                        } catch (e) {
                            return [];
                        }
                    },
                    async addKbShare(kbId, userIds, canPreview = true, canDownload = true) {
                        const token = localStorage.getItem('token');
                        await axios.post(`${ADMIN_API}/rag/knowledge/${kbId}/share`,
                            { user_ids: userIds, can_preview: canPreview, can_download: canDownload },
                            { headers: { 'Authorization': `Bearer ${token}` } }
                        );
                    },
                    async removeKbShare(kbId, userId) {
                        const token = localStorage.getItem('token');
                        await axios.delete(`${ADMIN_API}/rag/knowledge/${kbId}/share/${userId}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                    },
                    async patchKbShare(kbId, userId, payload) {
                        const token = localStorage.getItem('token');
                        const res = await axios.patch(`${ADMIN_API}/rag/knowledge/${kbId}/share/${userId}`,
                            payload,
                            { headers: { 'Authorization': `Bearer ${token}` } }
                        );
                        return res.data;
                    },
                    async queryRag(query) {
                        if (!query || !query.trim()) return;
                        this.kbAdminLoading = true;
                        this.kbAdminError = '';
                        this.kbAdminResult = '';
                        this.kbAdminSources = [];
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.post(`${ADMIN_API}/rag/query_knowledge`, { query: query.trim(), n_results: 4 }, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.kbAdminResult = (res.data?.result || '').toString();
                            this.kbAdminSources = Array.isArray(res.data?.sources) ? res.data.sources : [];
                            if (res.data?.error) this.kbAdminError = res.data.error.toString();
                        } catch (e) {
                            this.kbAdminError = (e.response?.data?.detail || e.message || '查詢失敗').toString();
                        } finally {
                            this.kbAdminLoading = false;
                        }
                    },

                    // --- Mail Queue ---
                    async fetchMailQueue() {
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/mail/queue`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const payload = res.data;
                            this.mailQueue = Array.isArray(payload) ? payload : (payload && Array.isArray(payload.queue) ? payload.queue : []);
                        } catch (e) {
                            _showToast('取得郵件佇列失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async flushQueue() {
                        try {
                            const token = localStorage.getItem('token');
                            await axios.post(`${ADMIN_API}/mail/queue/flush`, {}, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            _showToast('已發送嘗試投遞請求');
                        } catch (e) {
                            _showToast('操作失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async deleteAllQueue() {
                        if (!confirm('確定要刪除所有佇列中的信件嗎？此操作無法復原！')) return;
                        try {
                            const token = localStorage.getItem('token');
                            await axios.delete(`${ADMIN_API}/mail/queue/all`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            await this.fetchMailQueue();
                            _showToast('佇列已清空');
                        } catch (e) {
                            _showToast('操作失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async deleteQueueItem(queueId) {
                        if (!confirm(`確定要刪除信件 ${queueId} 嗎？`)) return;
                        try {
                            const token = localStorage.getItem('token');
                            await axios.delete(`${ADMIN_API}/mail/queue/${queueId}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            await this.fetchMailQueue();
                        } catch (e) {
                            _showToast('操作失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    // --- Sieve Filter ---
                    async fetchSieveUsers() {
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/users`, { headers: { Authorization: `Bearer ${token}` } });
                            this.sieve.users = res.data || [];
                            if (!this.sieve.selectedUserId && this.sieve.users.length) {
                                this.sieve.selectedUserId = this.sieve.users[0].id;
                            }
                        } catch (e) {
                            _showToast('載入使用者失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
                    async fetchSieveRules(userId) {
                        const uid = userId || this.sieve.selectedUserId;
                        if (!uid) return;
                        this.sieve.loading = true;
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/sieve/rules/${uid}`, { headers: { Authorization: `Bearer ${token}` } });
                            this.sieve.enabled = res.data.enabled ?? true;
                            this.sieve.rules = res.data.rules || [];
                        } catch (e) {
                            if (e.response?.status === 404) {
                                this.sieve.enabled = true;
                                this.sieve.rules = [];
                            } else {
                                _showToast('載入過濾規則失敗: ' + (e.response?.data?.detail || e.message));
                            }
                        } finally {
                            this.sieve.loading = false;
                        }
                    },
                    async saveSieveRules() {
                        const uid = this.sieve.selectedUserId;
                        if (!uid) return;
                        this.sieve.saving = true;
                        try {
                            const token = localStorage.getItem('token');
                            await axios.put(`${ADMIN_API}/sieve/rules/${uid}`, {
                                enabled: this.sieve.enabled,
                                rules: this.sieve.rules,
                            }, { headers: { Authorization: `Bearer ${token}` } });
                            _showToast('已儲存 Sieve 過濾規則');
                            await this.fetchSieveScript(uid);
                        } catch (e) {
                            _showToast('儲存失敗: ' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.sieve.saving = false;
                        }
                    },
                    async fetchSieveScript(userId) {
                        const uid = userId || this.sieve.selectedUserId;
                        if (!uid) return;
                        this.sieve.scriptLoading = true;
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/sieve/script/${uid}`, { headers: { Authorization: `Bearer ${token}` } });
                            this.sieve.scriptPreview = res.data.script || '';
                        } catch (e) {
                            this.sieve.scriptPreview = '';
                        } finally {
                            this.sieve.scriptLoading = false;
                        }
                    },
                    // --- Alert Center ---
                    async fetchAlerts() {
                        this.alertsLoading = true;
                        try {
                            const token = localStorage.getItem('token');
                            const res = await axios.get(`${ADMIN_API}/alerts/summary`, {
                                headers: { Authorization: `Bearer ${token}` },
                            });
                            this.alertCenterAlerts = res.data.alerts || [];
                            this.alertSummary = res.data.summary || { danger: 0, warning: 0, info: 0, total: 0 };
                        } catch (e) {
                            _showToast('無法取得告警資料：' + (e.response?.data?.detail || e.message));
                        } finally {
                            this.alertsLoading = false;
                        }
                    },
                    // --- Log Analysis ---
                    async fetchServiceLogs() {
                        try {
                            const token = localStorage.getItem('token');
                            const params = new URLSearchParams({
                                service: this.logFilter.service,
                                lines: this.logFilter.lines
                            });
                            if (this.logFilter.search) {
                                params.append('search', this.logFilter.search);
                            }

                            const res = await axios.get(`${ADMIN_API}/mail/logs?${params.toString()}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            this.serviceLogs = res.data;
                        } catch (e) {
                            _showToast('取得日誌失敗: ' + (e.response?.data?.detail || e.message));
                        }
                    },
  },
})
